/**
 * EZViewport
 * @class
 * @classdesc main class to manage rendering of svgContainers
 * in a responsive way
 *
 * To redraw the whole viewport system as in a window resize, send this event:
 *
 * ```
 * ee.emitEvent('updateRender', [true]);
 * ```
 *
 * It will clear and redraw all the {@link EZsvgContainer} and active object
 * will be zoomed.
 *
 * To redraw just the sceneGroup inside each {@link EZsvgContainer}
 * and avoid the zoom scale and translation, send this alternative event:
 *
 * ```
 * ee.emitEvent('updateRender');
 * ```
 *
 * @param {EZModelScene} scene - a {@link EZModelScene} object
 * @param {Ezzing3D} layoutInstance - a {@link Ezzing3D} object
 * @param {integer} mode - (Optional) the viewport drawing mode
 *
 * @property {array} svgContainersList - array of
 * {@link EZsvgContainer} objects managed by this EZViewport
 *
 * @property {object} svgElements - list of {@link EZsvgContainer}
 *
 * @property {array} svgContainersList - array of
 * {@link EZsvgContainer} objects managed by this EZViewport
 *
 */
function EZViewport(scene, layoutInstance) {
    /**
     * Class to manage languages
     */
    class EZLanguageManager {
        /**
         * EZLanguageManager constructor
         */
        constructor () {
            this.defaultLanguages = ez3dScene.layoutRules.defaultLanguages;
            this.i18nJson = {};
            this.i18nJsonDefault = this.defaultLanguages['EN'];
        }
        /**
         * updates languages
         * @param  {string} lan - desired language
         * @return {string} language to use
         */
        updateLanguage (lan) {
            if (this.defaultLanguages) {
                // Check if translations exists, or use EN language
                if (this.defaultLanguages[lan]) {
                    this.i18nJson = this.defaultLanguages[lan];
                } else {
                    lan = 'en';
                    self.i18nJson = this.i18nJsonDefault;
                }
            }
            return lan;
        }
        /**
         * translates a key
         * @param  {string} key - key to search
         * @return {string} translation
         */
        i18n (key) {
            if (!EZLanguageManager.shouldBeTranslated(key)) {
                return key;
            }
            let translate = this.i18nJson[key] || this.i18nJsonDefault[key];
            if (!translate) {
                if (ez3dScene.layoutRules.scenePreferences.debugTranslation) {
                    console.error('LOCALIZATION', key + ' doesn\'t have translation');
                }
                translate = key;
            }
    
            // translate key
            return translate;
        }
    
        /**
         * Determines if key should be translated
         * @param  {string} key - key to check
         * @return {boolean} result
         */
        static shouldBeTranslated (key) {
            const firstChar = key.charAt(0);
            if (Number(firstChar)) {
                if (ez3dScene.layoutRules.scenePreferences.debugTranslation) {
                    console.warn('LOCALIZATION', key + ' is not going to be translated');
                }
                return false;
            }
            if (firstChar === firstChar.toUpperCase()) {
                if (ez3dScene.layoutRules.scenePreferences.debugTranslation) {
                    console.warn('LOCALIZATION', key + ' is not going to be translated');
                }
                return false;
            }
            if (/\s/.test(key)) {
                if (ez3dScene.layoutRules.scenePreferences.debugTranslation) {
                    console.warn('LOCALIZATION', key + ' is not going to be translated');
                }
                return false;
            }
            return true;
        }
    }
    /**
     *
     *  * Method to return the visibility rule of an element
     *
     * @param {object} json this is a json data for construct the block
     * @param {string} element property of the json (json[element])
     * @return {boolean} if element must be visible
     */
    EZGuiManager.prototype.checkVisibilityRule = function(json, element) {
        var visibilityRule = json[element].visibilityRule;
        var isVisible = false;
    
        if (visibilityRule === undefined || visibilityRule === true ||
        // If visibilityRule !== false, check if exists the function in the active element or in EZGuiManager and check its return value
        (visibilityRule !== false && (ez3dScene.active[visibilityRule] && ez3dScene.active[visibilityRule]() === true) ||
        (this[visibilityRule] && this[visibilityRule]() === true))) {
            isVisible = true;
        }
        return isVisible;
    };
    
    /**
     *
     *  * Method to return the value of element
     *
     * @param {object} element this is a json data for construct the block
     * @param {boolean} [element.customProperty] element custom Property
     * @param {array} [element.property] element property
     * @param {string} [element.dinamicValue] function name
     * @param {array} element.value element value
     * @return {object} value
     */
    EZGuiManager.prototype.getValueFromJsonElement = function(element) {
        var value;
        if (element.customProperty === true) {
            if (ez3dScene.active.hasOwnProperty(element.property)) {
                value = ez3dScene.active[element.property];
            } else {
                if (element.dinamicValue) {
                    value = ez3dScene.active[element.dinamicValue]();
                } else {
                    value = element.value;
                }
                ee.emitEvent('EZModelScene_setAttributeListener', [{
                    args: [ez3dScene.active.id, element.property, value],
                    updateRender: false,
                    undo: false,
                    notifications: false
                }]);
                // @todo add this inside operator
                if (ez3dScene.active.customProperties) {
                    if (ez3dScene.active.customProperties.indexOf(element.property) === -1) {
                        ez3dScene.active.customProperties.push(element.property);
                    }
                } else {
                    ez3dScene.active.customProperties = [];
                    ez3dScene.active.customProperties.push(element.property);
                }
            }
        } else if (element.property && element.property.split('.').length > 1) {
            const path = element.property.split('.');
            const scene = ['ez3dScene', 'layoutRules'].includes(path[0]);
            let object = (scene) ? ez3dScene : ez3dScene.active;
            for (var i = (path[0] === 'ez3dScene') ? 1 : 0; i < path.length; i++) {
                object = object[path[i]];
            }
            value = object;
        } else {
            value = element.value;
        }
        return value;
    };
    
    /**
     *
     *  * Method execute the eventOnChange element
     *
     * @param {object} element this is a json data for construct the block
     * @param {array} element.eventOnChange name and params to the eventOnChange
     * @param {float} value new value changed in input
     */
    EZGuiManager.prototype.eventOnchangeExecution = function(element, value) {
        // eventOnchange: [eventName, args, undo, updateRender, progressBar]
        const event = element.eventOnChange[0];
        let args = element.eventOnChange[1] || undefined;
        if (value && args) args.push(value);
        if (value && !args) args = value;
        const undo = element.eventOnChange[2] || undefined;
        const updateRender = element.eventOnChange[3] || undefined;
        const progress = element.eventOnChange[4] || undefined;
    
        if (args || undo || updateRender || progress) {
            ee.emitEvent(event, [{args: args, undo: undo, updateRender: updateRender, progress: progress}]);
        } else {
            ee.emitEvent(event);
        }
    };
    
    /**
     *
     *  * Method execute the functionOnChange element
     *
     * @param {object} element  this is a json data for construct the block
     * @param {array} element.functionOnChange  name and params to the functionOnChange
     * @param {array} value     param
     */
    EZGuiManager.prototype.functionOnchangeExecution = function(element, value) {
        var self = this;
        self[element.functionOnChange[0]](element.functionOnChange[1], value);
    };
    
    /**
     *
     *  * Method execute the eventOnClick element
     *
     * @param {object} element this is a json data for construct the block
     * @param {array} element.eventOnClick name and params to the eventOnClick
     */
    EZGuiManager.prototype.eventOnclickExecution = function(element) {
        if (ez3dScene.layoutRules.scenePreferences.recordOperators === true && element.id) {
            ez3dScene.recordedOps.push({
                event: 'benchmarkDispatchClick',
                args: element.id,
                undo: false,
                notifications: false,
                updateRender: false,
                eventFinished: 'benchmarkDispatchClick_Finished'
            });
        }
    
        var event = element.eventOnClick[0];
        var args = element.eventOnClick[1] || undefined;
        var undo = (element.eventOnClick[2] === false) ? false : element.eventOnClick[2];
        var updateRender = element.eventOnClick[3] || undefined;
        var progress = element.eventOnClick[4] || undefined;
    
        ee.emitEvent(event, [{args: args, undo: undo, updateRender: updateRender, progress: progress}]);
    };
    
    /**
     *
     *  * Method to set attribute
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} convertedValue attribute value
     * @param {string} id element id
     * @param {class} guiManager EZGuiManager
     */
    EZGuiManager.prototype.launchSetAttribute = function (element, convertedValue, id) {
        var self = this;
        ee.addOnceListener('setAttribute_Finished', function() {
            if (element.eventOnChange) {
                self.eventOnchangeExecution(element);
            }
            if (element.functionOnChange) {
                self.functionOnchangeExecution(element);
            }
        });
    
    
        let updateRender = (ez3dScene.layoutRules.scenePreferences.viewportMode === 6)
            ? {draw2d: true, draw3d: false, updatePanels: true, fitRange: true}
            : {draw2d: true, draw3d: false, updatePanels: true, fitRange: false};
        if (element.eventOnChange || element.functionOnChange) {
            updateRender = false;
        }
    
        ee.emitEvent('EZModelScene_setAttributeListener', [{
            args: [id, element.property, convertedValue, element.runMethod],
            updateRender: updateRender,
            undo: element.undo,
            progress: true,
            notifications: true
        }]);
    };
    
    /**
     *
     *  * Method to return the readOnly rule of an element
     *
     * @param {object} json this is a json data for construct the block
     * @param {string} element property of the json (json[element])
     * @return {boolean} if element must be visible
     */
    EZGuiManager.prototype.checkReadOnlyRule = function(json) {
        if (ez3dScene.layoutRules.scenePreferences.readOnly) {
            // groupBlock
            // link
            // blockResume
            // helper
            // blockList
            // foldableBlock
            // imageNavigator
            // navigator
            // subareaBlock
            // selectBySVGImage: trees/keepouts shapes selector
            // stringWidget
            // selectWidget
            // buttonsPanelWidget
            // buttonWidget
            // rangeWidget
            // dateTimeWidget
            // booleanWidget
            if (json.type !== 'buttonNav') {
                json.readOnly = true;
            }
        }
        return json;
    };
    
        /**
     *
     *  * Method that checks if it is viable to create the block
     *
     * @param {object} json this is a json data for construct the block
     * @param {string} [json.element.model] element model
     * @param {string} [json.element.customProperty] element customProperty
     * @param {string} [json.element.visibilityRule] element visibilityRule
     * @param {string} [json.element.type] element type
     *
     * @param {object} ez3dInfoPanel this is where the block is hosted
     * @param {object} ez3dBottomPanel this is where the blocks with bottom position be hosted
     * @param {string} model model element
     */
    EZGuiManager.prototype.populateBlocks = function (json, ez3dInfoPanel, ez3dBottomPanel, model) {
        var self = this;
        if (typeof json === 'object') {
            Object.keys(json).forEach(function (element) {
                var hidden = false;
                if (model) {
                    var modelName;
                    modelName = model.split('EZModel')[1].toLowerCase();
    
                    if (json[element].property && ez3dScene.layoutRules.defaultModelValues[modelName] && ez3dScene.layoutRules.defaultModelValues[modelName].hidden) {
                        if (ez3dScene.layoutRules.defaultModelValues[modelName].hidden.indexOf(json[element].property) !== -1) {
                            hidden = true;
                        }
                    }
                }
                if (json[element].customProperty === true && self.getValueFromJsonElement(json[element]) === undefined) {
                    hidden = true;
                }
                if (hidden === false) {
                    if (self.checkVisibilityRule(json, element) === true) {
                        var container = ez3dInfoPanel;
                        if (json[element].position === 'bottom') {
                            container = ez3dBottomPanel;
                        }
                        let jsonBlock;
                        if (typeof json[element] === 'object') {
                            jsonBlock = self.checkReadOnlyRule(json[element]);
                        }
                        switch (json[element].type) {
                            case 'string':
                                self.addStringInputBlock(jsonBlock, container);
                                break;
                            case 'float':
                            case 'integer':
                                self.addNumberInputBlock(jsonBlock, container);
                                break;
                            case 'boolean':
                                self.addBooleanInputBlock(jsonBlock, container);
                                break;
                            case 'select':
                                self.addSelectBlock(jsonBlock, container);
                                break;
                            case 'selectByImage':
                                self.addSelectImageBlock(jsonBlock, container);
                                break;
                            case 'selectBySVGImage':
                                self.addSelectSVGImageBlock(jsonBlock, container);
                                break;
                            case 'azimuthRange':
                                self.addInputAzimuthRangeBlock(jsonBlock, container);
                                break;
                            case 'compass':
                                self.addCompassBLock(jsonBlock, container);
                                break;
                            case 'title':
                                self.addTitleBlock(jsonBlock, container);
                                break;
                            case 'helper':
                                self.addHelperBlock(jsonBlock, container);
                                break;
                            case 'link':
                                self.addLinkBlock(jsonBlock, container);
                                break;
                            case 'groupBlock':
                                self.addGroupBlock(jsonBlock, container);
                                break;
                            case 'button':
                            case 'buttonNav':
                                self.addButtonBlock(jsonBlock, container);
                                break;
                            case 'listResume':
                                self.addResumeButtonBlock(jsonBlock, container);
                                break;
                            case 'blockList':
                                self.addBlockListBlock(jsonBlock, container);
                                break;
                            case 'foldableBlock':
                                self.addFoldableBlock(jsonBlock, container);
                                break;
                            case 'subareaBlock':
                                self.addEditDisabledResumeButtonBlock(jsonBlock, container);
                                break;
                            case 'blockResume':
                                self.addResumeBlock(jsonBlock, container);
                                break;
                                break;
                            case 'stringWidget':
                                self.addStringInputBlockWidget(jsonBlock, container);
                                break;
                            case 'selectWidget':
                                self.addSelectBlockWidget(jsonBlock, container);
                                break;
                            case 'buttonsPanelWidget':
                                self.addButtonsBlockWidget(jsonBlock.elements, container);
                                break;
                            case 'buttonWidget':
                                self.addButtonBlockWidget(jsonBlock, container);
                                break;
                            case 'rangeWidget':
                                self.addInputRangeBlockWidget(jsonBlock, container);
                                break;
                            case 'dateTimeWidget':
                                self.addInputDateTimeWidget(jsonBlock, container);
                                break;
                            case 'booleanWidget':
                                self.addBooleanInputBlockWidget(jsonBlock, container);
                                break;
                            case 'imageNavigator':
                                if (json.name === 'building-shapes-selector') {
                                    self.addBuildingShapesImageNavigator(jsonBlock.elements);
                                } else {
                                    self.addImageNavigator(jsonBlock.elements);
                                }
                                break;
                            case 'navigator':
                                self.addNavigationBlock(jsonBlock.elements);
                                break;
                            default:
                                break;
                        }
                    }
                }
            });
        }
    };
    
    // Blocks
    // Input blocks
    /**
     *
     *  * Method to create string input block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [element.id] block id
     * @param {string} [element.label] block label
     * @param {string} [element.classed] block class
     * @param {string} element.value value input attribute
     * @param {string} element.placeholder placeholder input attribute
     * @param {string} element.autofocus autofocus input attribute
     * @param {string} element.required required input attribute
     * @param {object} [element.tooltipBtn] data with title and content for create a tooltip with ? symbol
     * @param {string} [element.property] attr only model
     * @param {string} [element.validation] validation function
     *
     * @param {object} container this is where the addStringInputBlock is hosted
     */
    EZGuiManager.prototype.addStringInputBlock = function(element, container) {
        var self = this;
        var modelAttribute = container.append('div')
            .attr('class', 'ez3d-model-attribute ez3d-flex-baseline-wrap');
    
        // Add circle icon
        modelAttribute.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        if (element.id) {
            modelAttribute
                .attr('id', element.id);
        }
        if (element.label) {
            var text = self.i18n(element.label);
            modelAttribute.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
        }
        if (element.classed) {
            modelAttribute.classed(element.classed, true);
        }
    
        // Add input element
        var input = modelAttribute.append('input')
            .attr('value', self.getValueFromJsonElement(element));
    
        if (element.placeholder) {
            input
                .attr('placeholder', self.i18n(element.placeholder));
        }
        if (element.autofocus) {
            input
                .attr('autofocus', element.autofocus);
        }
        if (element.required) {
            input
                .attr('required', element.required);
        }
    
        var editable = true;
        if (typeof element.editable === 'string') {
            editable = ez3dScene.active[element.editable]();
        }
        if (element.editable === false) {
            editable = false;
        }
        if (editable === false) {
            input
                .attr('disabled', 'disabled')
                .classed('ez3d-disabled', true);
        }
        if (element.tooltipBtn) {
            input
                .classed('new-width-with-tooltip', true);
            self.addTooltip({container: modelAttribute, title: element.tooltipBtn.title, content: element.tooltipBtn.content});
        }
        if (element.helper) {
            var helperText = self.i18n(element.helper);
            modelAttribute.append('span')
                .attr('class', 'ez3d-input-helper')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.darkgray)
                .html(helperText);
        }
    
    
        if (element.property) {
            input
                .on('change', function() {
                    var convertedValue = this.value;
                    if (element.validation === undefined || ez3dScene.active[element.validation](convertedValue)) {
                        var id = ez3dScene.active.id;
                        if (element.property.split('.').length > 1 &&
                            element.property.split('.')[0] === 'layoutRules') {
                            id = '';
                        }
                        // lanzar setAttribute
                        self.launchSetAttribute(element, convertedValue, id);
                    }
                    // else {
                    //     this.value = ez3dScene.active[element.property];
                    //     this.classList.add('invalid-input');
                    //     setTimeout(function() {
                    //         self.classList.remove('invalid-input');
                    //     }, 1000);
                    // }
                });
        } else {
            input
                .on('change', function() {
                    var convertedValue = this.value;
                })
                .on('input', function () {
                    if (element.functionOninput) {
                        self[element.functionOninput[0]](element.functionOninput[1]);
                    }
                });
        }
        input
            .on('focus', function () {
                // bar style
                var parent = d3.select(this.parentNode).node();
                var prev = d3.select(parent.lastChild).node();
                d3.select(prev)
                    .classed('ez3d-bar-after', true)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
                var helper = d3.select(parent).select('.ez3d-input-helper');
                helper
                    .classed('ez3d-input-helper-after', true);
            })
            .on('blur', function () {
                // bar style
                var parent = d3.select(this.parentNode).node();
                if (parent !== null) {
                    var prev = d3.select(parent.lastChild).node();
                    d3.select(prev)
                        .classed('ez3d-bar-after', false)
                        .style('background-color', 'transparent');
    
                    var helper = d3.select(parent).select('.ez3d-input-helper');
                    helper
                        .classed('ez3d-input-helper-after', false);
                }
            });
    
        if (element.readOnly) {
            input
                .attr('disabled', true)
                .classed('ez3d-read-only', true);
    
        }
    
        // Add bar
        modelAttribute.append('div')
            .attr('class', 'ez3d-bar');
    };
    
    /**
     *
     *  * Method to create number input block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [element.id] block id
     * @param {string} [element.label] block label
     * @param {boolean} [element.universalUnits] Add the measure selector
     * @param {number} [element.numberOfDecimals] decimals fomat
     * @param {string} [element.showUnit] show unit in (m)
     * @param {string} element.value value input attribute
     * @param {number} [element.min] min input attribute
     * @param {number} [element.max] max input attribute
     * @param {number} [element.step] step input attribute
     * @param {boolean} [element.editable] disabled input attribute
     * @param {object} [element.tooltipBtn] data with title and content for create a tooltip with ? symbol
     * @param {string} [element.property] attr only model
     * @param {string} [element.validation] validation function
     *
     * @param {object} container this is where the addNumberInputBlock is hosted
     */
    EZGuiManager.prototype.addNumberInputBlock = function(element, container) {
        var self = this;
        var step = element.step;
        var max = element.max;
        var min = element.min;
    
        var modelAttribute = container.append('div')
            .attr('class', 'ez3d-model-attribute ez3d-flex-baseline-wrap');
    
        if (element.classed) {
            modelAttribute.classed(element.classed, true);
        }
    
        // Add circle icon
        modelAttribute.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        if (element.id) {
            modelAttribute
                .attr('id', element.id);
        }
        if (element.label) {
            var text = self.i18n(element.label);
            modelAttribute.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
        }
    
        // Add input number
        var input = modelAttribute.append('input')
            .attr('type', 'Number')
            .attr('title', ' ')
            .attr('step', step)
            .attr('max', max)
            .attr('min', min)
            .attr('value', function() {
                var value = self.getValueFromJsonElement(element);
                if (element.universalUnits) {
                    value = self.convertUnitsFromMeters(value, self.units[ez3dScene.unit].name);
                    self.addUnitSelector(modelAttribute);
                }
                // @todo add numberOfDecimals rule
                if (element.numberOfDecimals) {
                    value = value.toFixed(element.numberOfDecimals);
                }
                if (element.showUnit) {
                    self.addShowUnit(element.showUnit, modelAttribute);
                }
                return value;
            });
    
        if (element.tooltipBtn) {
            input
                .classed('new-width-with-tooltip', true);
            self.addTooltip({container: modelAttribute, title: element.tooltipBtn.title, content: element.tooltipBtn.content});
        }
        if (element.universalUnits || element.showUnit) {
            input
                .classed('new-width', true);
        }
        if (element.universalUnits && element.tooltipBtn || element.showUnit && element.tooltipBtn) {
            input
                .classed('new-width-two-elements', true);
        }
    
        var editable = true;
        if (typeof element.editable === 'string') {
            editable = ez3dScene.active[element.editable]();
        }
        if (element.editable === false) {
            editable = false;
        }
        if (editable === false) {
            input
                .attr('disabled', 'disabled')
                .classed('ez3d-disabled', true);
        }
        if (element.helper) {
            var helperText = self.i18n(element.helper);
            modelAttribute.append('span')
                .attr('class', 'ez3d-input-helper')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.darkgray)
                .html(helperText);
        }
    
        if (element.property) {
            input
                .on('change', function() {
                    var guiManager = self.ez3dViewport.ez3dGuiManager;
                    var convertedValue = Number(this.value);
                    var oldValue = Number(this.value);
    
                    // var validation = false;
    
                    if (element.universalUnits) {
                        convertedValue = guiManager.convertUnitsToMeters(convertedValue, guiManager.units[ez3dScene.unit].name);
                    }
                    if (convertedValue > max) {
                        // validation = true;
                        convertedValue = max;
                        this.value = guiManager.convertUnitsFromMeters(max, guiManager.units[ez3dScene.unit].name);
                        self.notificationManager(['maxLimit', {style: 'warning', spiner: false}]);
                    } else if (convertedValue < min) {
                        // validation = true;
                        convertedValue = min;
                        this.value = guiManager.convertUnitsFromMeters(min, guiManager.units[ez3dScene.unit].name);
                        self.notificationManager(['minLimit', {style: 'warning', spiner: false}]);
                    }
    
                    if (element.validation === undefined || ez3dScene.active[element.validation](convertedValue)) {
                        var id = (element.property.indexOf('tempPath') > -1) ? ez3dScene.tempPath.id : ez3dScene.active.id;
    
                        if (element.property.split('.').length > 1 &&
                            element.property.split('.')[0] === 'layoutRules') {
                            id = '';
                        }
    
                        if (element.confirmation) {
                            if (element.confirmation === true || ez3dScene.active[element.confirmation](oldValue) === true) {
                                // lanzar confirmaciones
                                var eventCancelListener = function() {
                                    ee.removeListener(element.confirmationWidget.eventOk, eventOkListener);
                                    ee.emitEvent('updatePanels');
                                    if (element.confirmationWidget.eventCancel === 'widget_roofChangeInclination_eventCancel') {
                                        ee.emitEvent('updateRoofPanel', [ez3dScene.roofPanel()]);
                                    }
                                };
    
                                var eventOkListener = function() {
                                    ee.removeListener(element.confirmationWidget.eventCancel, eventCancelListener);
                                    // lanzar setAttribute
                                    self.launchSetAttribute(element, convertedValue, id);
                                    if (element.confirmationWidget.eventOk === 'widget_edgeZone_eventOk' &&
                                    ez3dScene.active.subareas &&
                                    ez3dScene.active.subareas.length === 1) {
                                        ee.emitEvent('EZModelArea_deleteSubareaListener', [{
                                            args: [ez3dScene.active.subareas[0].id, true], undo: false}]);                                }
                                };
    
                                if (element.confirmationWidget.eventCancel) {
                                    ee.addOnceListener(element.confirmationWidget.eventCancel, eventCancelListener);
                                }
    
                                if (element.confirmationWidget.eventOk) {
                                    ee.addOnceListener(element.confirmationWidget.eventOk, eventOkListener);
                                }
    
                                ee.emitEvent('updatePanels');
                                ee.emitEvent('widget_notification', [{
                                    'name': element.confirmationWidget.name,
                                    'title': element.confirmationWidget.title,
                                    'content': element.confirmationWidget.content,
                                    'eventOk': element.confirmationWidget.eventOk,
                                    'eventCancel': element.confirmationWidget.eventCancel,
                                    'functionOnCreation': element.confirmationWidget.functionOnCreation
                                }]);
                            } else {
                                // lanzar setAttribute
                                self.launchSetAttribute(element, convertedValue, id);
                            }
                        } else {
                            // lanzar setAttribute
                            self.launchSetAttribute(element, convertedValue, id);
                        }
                    }
                    if (element.removeParentWidget) {
                        self.removeWidget(element.removeParentWidget);
                    }
                });
        } else {
            input
                .on('change', function() {
                    var guiManager = self.ez3dViewport.ez3dGuiManager;
                    var convertedValue = Number(this.value);
                    var oldValue = Number(this.value);
                    // var validation = false;
    
                    if (element.universalUnits) {
                        convertedValue = guiManager.convertUnitsToMeters(convertedValue, guiManager.units[ez3dScene.unit].name);
                    }
                    if (convertedValue > max) {
                        // validation = true;
                        convertedValue = max;
                        this.value = guiManager.convertUnitsFromMeters(max, guiManager.units[ez3dScene.unit].name);
                        self.notificationManager(['maxLimit', {style: 'warning', spiner: false}]);
                    } else if (convertedValue < min) {
                        // validation = true;
                        convertedValue = min;
                        this.value = guiManager.convertUnitsFromMeters(min, guiManager.units[ez3dScene.unit].name);
                        self.notificationManager(['minLimit', {style: 'warning', spiner: false}]);
                    }
    
                    if (element.functionOnChange) {
                        self.functionOnchangeExecution(element, convertedValue);
                    }
    
                    if (element.eventOnChange) {
                        self.eventOnchangeExecution(element, convertedValue);
                    }
                });
        }
    
        input
            .on('focus', function () {
                // bar style
                var parent = d3.select(this.parentNode).node();
                var prev = d3.select(parent.lastChild).node();
                d3.select(prev)
                    .classed('ez3d-bar-after', true)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
                var helper = d3.select(parent).select('.ez3d-input-helper');
                helper
                    .classed('ez3d-input-helper-after', true);
                if (element.d3Action) {
                    if (Array.isArray(element.d3Action)) {
                        self.ez3dViewport.svgElements.svgProject[element.d3Action[0]](element.d3Action[1], true);
                    } else {
                        self.ez3dViewport.svgElements.svgProject[element.d3Action]();
                    }
                }
            })
            .on('focusout', function () {
                if (element.d3Action) {
                    if (Array.isArray(element.d3Action)) {
                        self.ez3dViewport.svgElements.svgProject[element.d3Action[0]](element.d3Action[1], false);
                    } else {
                        self.ez3dViewport.svgElements.svgProject[element.d3Action]();
                    }
                }
            })
            .on('blur', function () {
                // bar style
                var parent = d3.select(this.parentNode).node();
                if (parent !== null) {
                    var prev = d3.select(parent.lastChild).node();
                    d3.select(prev)
                        .classed('ez3d-bar-after', false)
                        .style('background-color', 'transparent');
    
                    var helper = d3.select(parent).select('.ez3d-input-helper');
                    helper
                        .classed('ez3d-input-helper-after', false);
                }
            });
    
        if (element.readOnly) {
            input
                .attr('disabled', true)
                .classed('ez3d-read-only', true);
    
        }
    
        // Add bar
        modelAttribute.append('div')
            .attr('class', 'ez3d-bar');
    };
    
    /**
     *
     *  * Method to create boolean/check input block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [element.id] block id
     * @param {string} [element.label] block label
     * @param {string} element.value value input attribute
     * @param {string} element.labelTrue label switch if check is true
     * @param {string} element.labelFalse label switch if check is false
     * @param {string} [element.property] attr only model
     *
     * @param {object} container this is where the addBooleanInputBlock is hosted
     */
    EZGuiManager.prototype.addBooleanInputBlock = function(element, container) {
    
        const modelAttribute = container.append('div')
            .attr('class', 'ez3d-model-attribute ez3d-flex-baseline-wrap');
    
        // Add circle icon
        modelAttribute.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        if (element.id) {
            modelAttribute
                .attr('id', element.id);
        }
        if (element.label) {
            const text = this.i18n(element.label);
            const label = modelAttribute.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
    
            if (element.tooltipBtn) {
                label.classed('new-width-with-tooltip', true);
                this.addTooltip({
                    container: modelAttribute,
                    title: element.tooltipBtn.title,
                    content: element.tooltipBtn.content
                });
            }
        }
    
        // Add label for switch
        const switchLabel = modelAttribute.append('label')
            .attr('class', 'ez3d-switch');
    
        // Add input check
        const input = switchLabel.append('input')
            .attr('type', 'checkbox')
            .classed('ez3d-switch-input', true);
    
        const slider = switchLabel
            .append('div')
            .attr('class', 'slider round');
    
        // color switch
        const color = d3.hsl(d3.color(ez3dScene.layoutRules.defaultColors.cssColors.second).brighter(2.74));
        color.s = 0.34;
        color.l = 0.70;
    
        // switch labels
        const labelTrue = this.i18n(element.labelTrue || 'yes');
        const labelFalse = this.i18n(element.labelFalse || 'no');
    
        slider.append('div')
            .attr('class', 'ez3d-slider-cube')
            .style('background-color', 'white');
    
        slider.append('div')
            .attr('class', 'ez3d-label-false')
            .html(labelFalse);
    
        slider.append('div')
            .attr('class', 'ez3d-label-true')
            .html(labelTrue);
    
        input.node().checked = this.getValueFromJsonElement(element) || false;
    
    
        let convertedValue = input.node().checked;
        if (element.property) {
            input
                .on('change', () => {
                    convertedValue = input.node().checked;
                    let id = ez3dScene.active.id;
    
                    if (element.property.split('.').length > 1 && element.property.split('.')[0] === 'layoutRules') {
                        id = '';
                    }
    
                    // lanzar setAttribute
                    if (element.confirmation) {
                        if (element.confirmation === true || ez3dScene.active[element.confirmation](convertedValue) === true) {
                            convertedValue = !element.value;
                            // lanzar confirmaciones
                            const eventCancelListener = () => {
                                ee.removeListener(element.confirmationWidget.eventOk, eventOkListener);
                                ee.emitEvent('updatePanels');
                            };
    
                            const eventOkListener = () => {
                                ee.removeListener(element.confirmationWidget.eventCancel, eventCancelListener);
                                if (element.confirmationWidget.eventOk === 'widget_enablingDilatationLines_eventOk') {
                                    // Enable/disable staggered or dilatation lines
                                    if (element.property === 'system.staggered.enabled') {
                                        ez3dScene.active.system.dilatationLines.enabled = false;
                                    } else if (element.property === 'system.dilatationLines.enabled') {
                                        ez3dScene.active.system.staggered.enabled = false;
                                    }
                                }
                                // lanzar setAttribute
                                if (element.confirmationWidget.eventOk !== 'close_widget_notification') {
                                    this.launchSetAttribute(element, convertedValue, id);
                                }
                            };
    
                            if (element.confirmationWidget.eventCancel) {
                                ee.addOnceListener(element.confirmationWidget.eventCancel, eventCancelListener);
                            }
    
                            if (element.confirmationWidget.eventOk) {
                                ee.addOnceListener(element.confirmationWidget.eventOk, eventOkListener);
                            }
    
                            ee.emitEvent('widget_notification', [{
                                'name': element.confirmationWidget.name,
                                'title': element.confirmationWidget.title,
                                'content': element.confirmationWidget.content,
                                'eventOk': element.confirmationWidget.eventOk,
                                'eventCancel': element.confirmationWidget.eventCancel,
                                'functionOnCreation': element.confirmationWidget.functionOnCreation
                            }]);
                        } else {
                            this.launchSetAttribute(element, convertedValue, id);
                        }
                    } else {
                        this.launchSetAttribute(element, convertedValue, id);
                        if (element.eventOnChange) {
                            this.eventOnchangeExecution(element);
                        }
                        if (element.functionOnChange) {
                            this.functionOnchangeExecution(element);
                        }
                    }
                    ee.emitEvent('updateRender',
                        [{draw2d: true, draw3d: false, updatePanels: true, fitRange: false}]);
                    // draw widget
                    if (this.widgets.indexOf('ez3d-widget-draw') !== -1) {
                        this.drawWidget();
                    }
                });
        } else {
            if (element.eventOnChange) {
                this.eventOnchangeExecution(element);
            }
            if (element.functionOnChange) {
                this.functionOnchangeExecution(element);
            }
        }
    
        if (convertedValue === false) {
            if (element.labelTrue && element.labelTrue !== 'true') {
                switchLabel.select('.slider')
                    .classed('ez3d-slider-on', true)
                    .style('background-color', color)
                    .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
            } else {
                switchLabel.select('.slider')
                    .classed('ez3d-slider-on', false)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray)
                    .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
            }
            // labels
            slider.select('.ez3d-label-true')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.darkgray);
            slider.select('.ez3d-label-false')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
        } else {
            switchLabel.select('.slider')
                .classed('ez3d-slider-on', true)
                .style('background-color', color)
                .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
            // labels
            slider.select('.ez3d-label-true')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
            slider.select('.ez3d-label-false')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.darkgray);
        }
    
        let editable = true;
        if (typeof element.editable === 'string') {
            editable = ez3dScene.active[element.editable]();
        }
        if (element.editable === false) {
            editable = false;
        }
        if (editable === false) {
            input
                .attr('disabled', 'disabled')
                .classed('ez3d-disabled', true);
        }
        if (element.readOnly) {
            input
                .attr('disabled', true);
            switchLabel
                .classed('ez3d-read-only', true);
        }
    };
    
    
    // Selector blocks
    /**
     *
     *  * Method to create select block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [element.label] block label
     * @param {string} [element.classed] block class
     * @param {string} element.id id select attribute
     * @param {string} [element.contentLeft] Add div and class to select
     * @param {string} element.value value select attribute
     * @param {string} element.editable disabled select attribute
     * @param {object} [element.tooltipBtn] data with title and content for create a tooltip with ? symbol
     * @param {string} [element.property] attr only model
     *
     * @param {object} container this is where the addStringInputBlock is hosted
     */
    EZGuiManager.prototype.addSelectBlock = function (element, container) {
        var self = this;
        var selected = (self.getValueFromJsonElement(element)).toString();
    
        var modelAttribute = container.append('div')
            .attr('class', 'ez3d-model-attribute ez3d-flex-center-wrap');
    
        // Add circle block
        modelAttribute.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        if (element.label) {
            var text = self.i18n(element.label);
            modelAttribute.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
        }
        if (element.contentLeft) {
            modelAttribute.append('div')
                .attr('id', element.contentLeft);
        }
        // Add select
        var select = modelAttribute.append('select')
            .attr('class', 'ez3d-select')
            .style('border-bottom-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray)
            .attr('value', function() {
                return self.getValueFromJsonElement(element);
            });
    
        var arrOptionsLabel = [];
        var opt;
        if (element.optionsFunction) {
            if (element.property.split('.')[0] === 'layoutRules') {
                arrOptionsLabel = ez3dScene[element.optionsFunction]();
            } else {
                arrOptionsLabel = ez3dScene.active[element.optionsFunction]();
            }
            if (arrOptionsLabel instanceof Array) {
                var tempArrOptionsLabel = [];
                arrOptionsLabel.map(function(option) {
                    tempArrOptionsLabel[option.toLowerCase()] = option.toLowerCase();
                });
                arrOptionsLabel = tempArrOptionsLabel;
            }
        } else if (element.optionsLabels) {
            var optionsLabels = element.optionsLabels.sort();
            for (var i = 0; i < optionsLabels.length; i++) {
                arrOptionsLabel[element.optionsLabels[i]] = element.optionsLabels[i];
            }
        } else if (element.options) {
            arrOptionsLabel = element.options;
        }
    
        Object.keys(arrOptionsLabel).forEach(function(key) {
            var textvalue = null;
            if (Array.isArray(arrOptionsLabel[key])) {
                textvalue = self.i18n(arrOptionsLabel[key][1]);
                key = String(arrOptionsLabel[key][0]);
            } else {
                textvalue = self.i18n(arrOptionsLabel[key]);
            }
            if (key === selected) {
                opt = select.append('option')
                    .attr('selected', 'selected')
                    .attr('value', key)
                    .html(ez3dScene.utils.capitalize(textvalue));
    
                if (element.property === 'material') {
                    d3.select('#ez3d-roof-material-icon')
                        .attr('class', 'ez3d-roof-material-' + self.formatMaterialsClassBySelect(arrOptionsLabel[key]));
                }
            } else {
                opt = select.append('option')
                    .attr('value', key)
                    .html(ez3dScene.utils.capitalize(textvalue));
            }
        });
    
        if (element.editable === false) {
            select
                .attr('disabled', 'disabled')
                .classed('ez3d-disabled', true);
        }
        if (element.id) {
            select
                .attr('id', element.id);
        }
        if (element.contentLeft) {
            select
                .classed('new-width', true);
        }
        if (element.tooltipBtn) {
            select
                .classed('new-width-with-tooltip', true);
            self.addTooltip({container: modelAttribute, title: element.tooltipBtn.title, content: element.tooltipBtn.content});
        }
        if (element.contentLeft && element.tooltipBtn) {
            select
                .classed('new-width-two-elements', true);
        }
        if (element.property) {
            select
                .on('change', function() {
                    var convertedValue = this.value;
                    if (element.contentLeft) {
                        d3.select('#ez3d-roof-material-icon')
                            .attr('class', 'ez3d-roof-material-' + self.formatMaterialsClassBySelect(convertedValue));
                    }
    
                    var id = ez3dScene.active.id;
                    if (element.property.split('.')[0] === 'layoutRules') {
                        id = '';
                    }
                    // lanzar setAttribute
                    self.launchSetAttribute(element, convertedValue, id);
                });
        }
        if (element.functionOnCreation) {
            self[element.functionOnCreation[0]](element.functionOnCreation[1]);
        }
        if (element.readOnly) {
            select
                .attr('disabled', true)
                .classed('ez3d-read-only', true);
    
        }
    
    };
    
    /**
     *
     *  * Method to create select by image block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [element.id] block id
     * @param {string} [element.label] block label
     *
     * @param {string} element.value value is option selected
     * @param {string} element.property attr only model
     *
     * @param {object} container this is where the addSelectImageBlock is hosted
     */
    EZGuiManager.prototype.addSelectImageBlock = function (element, container) {
        var self = this;
        var selected = element.value;
    
        var modelAttribute = container.append('div')
            .attr('class', 'ez3d-model-attribute ez3d-flex-center-wrap');
    
        // Add circle icon
        modelAttribute.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
    
        if (element.id) {
            modelAttribute
                .attr('id', element.id);
        }
        if (element.label) {
            var text = self.i18n(element.label);
            var labelContainer = modelAttribute.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
    
            if (element.tooltipBtn) {
                labelContainer.classed('new-width-with-tooltip', true);
                self.addTooltip({container: modelAttribute, title: element.tooltipBtn.title, content: element.tooltipBtn.content});
            }
        }
    
        var select = modelAttribute.append('div')
            .attr('class', 'ez3d-select-by-image ez3d-flex-wrap-space-around');
    
        Object.keys(element.options).forEach(function(opt) {
            var content = select.append('div')
                .attr('id', 'ez3d-select-' + opt)
                .attr('title', element.options[opt].value)
                .attr('class', 'ez3d-select-opt ez3d-select-' + element.property + ' ez3d-' + element.options[opt].image + '-icon')
                .classed('ez3d-' + element.options[opt].image + '-on-icon', function() {
                    var value = false;
                    if (opt === selected && element.property === 'type') {
                        value = true;
                    }
                    return value;
                })
                .classed('ez3d-select-opt-on', function() {
                    var value = false;
                    if (opt === selected) {
                        value = true;
                    }
                    return value;
                })
                .on('click', function () {
                    var title = opt;
                    var id = ez3dScene.active.id;
    
                    if (element.confirmation) {
                        if (element.confirmation === true || ez3dScene.active[element.confirmation]() === true) {
                            // lanzar confirmaciones
                            var eventCancelListener = function() {
                                ee.removeListener(element.confirmationWidget.eventOk, eventOkListener);
                                ee.emitEvent('updatePanels');
                            };
    
                            var eventOkListener = function() {
                                ee.removeListener(element.confirmationWidget.eventCancel, eventCancelListener);
                                // lanzar setAttribute
                                self.launchSetAttribute(element, title, id);
                                // ee.emitEvent('updatePanels');
                            };
    
                            if (element.confirmationWidget.eventCancel) {
                                ee.addOnceListener(element.confirmationWidget.eventCancel, eventCancelListener);
                            }
    
                            if (element.confirmationWidget.eventOk) {
                                ee.addOnceListener(element.confirmationWidget.eventOk, eventOkListener);
                            }
    
                            ee.emitEvent('widget_notification', [{
                                'name': element.confirmationWidget.name,
                                'title': element.confirmationWidget.title,
                                'content': element.confirmationWidget.content,
                                'eventOk': element.confirmationWidget.eventOk,
                                'eventCancel': element.confirmationWidget.eventCancel,
                                'functionOnCreation': element.confirmationWidget.functionOnCreation
                            }]);
                        } else {
                            // lanzar setAttribute
                            self.launchSetAttribute(element, title, id);
                        }
                    } else {
                        // lanzar setAttribute
                        self.launchSetAttribute(element, title, id);
                    }
                    ee.emitEvent('updateRoofPanel');
                });
    
            content.append('div');
    
            content.append('span')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(self.i18n(element.options[opt].label));
    
            if (content.classed('ez3d-select-opt-on')) {
                content.select('span')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
                if (element.property === 'material') {
                    content.select('div')
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                }
            }
        });
    
        if (element.readOnly) {
            modelAttribute
                .classed('ez3d-read-only', true);
        }
    };
    
    /**
     *
     *  * Method to create select by SVG image block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [element.id] block id
     * @param {string} [element.label] block label
     *
     * @param {string} element.value value is option selected
     * @param {string} element.property attr only model
     *
     * @param {object} container this is where the addSelectSVGImageBlock is hosted
     */
    EZGuiManager.prototype.addSelectSVGImageBlock = function (element, container) {
        var self = this;
        var selected = element.value;
    
        var modelAttribute = container.append('div')
            .attr('class', 'ez3d-model-attribute');
    
        // Add circle icon
        modelAttribute.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        if (element.id) {
            modelAttribute
                .attr('id', element.id);
        }
        if (element.label) {
            var text = self.i18n(element.label);
            modelAttribute.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
        }
    
        var select = modelAttribute.append('div')
            .attr('class', 'ez3d-select-by-svg-image ez3d-flex-wrap-space-around');
    
        Object.keys(element.options).forEach(function(opt) {
            var content = select.append('div')
                .attr('id', 'ez3d-select-' + opt)
                .attr('title', opt)
                .on('click', function () {
                    // lanzar setAttribute
                    self.launchSetAttribute(element, opt, ez3dScene.active.id);
                });
    
            content.append('span')
                .html(self.i18n(opt));
    
            content.append('div')
                .attr('class', 'ez3d-select-opt ez3d-' + element.options[opt].value + '-icon ' + element.classed)
                .classed('ez3d-select-opt-on', function() {
                    var value = false;
                    if (opt === selected) {
                        d3.select(this)
                            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                        d3.select(this.parentNode).select('span')
                            .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
                        value = true;
                    } else {
                        d3.select(this)
                            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
                        d3.select(this.parentNode).select('span')
                            .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
                    }
                    return value;
                });
        });
    };
    
    /**
     *
     *  * Method to create azimuth input range block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [element.id] block id
     * @param {string} [element.label] block label
     * @param {number} [element.numberOfDecimals] decimals fomat
     * @param {string} element.value value input attribute
     * @param {number} [element.min] min input attribute
     * @param {number} [element.max] max input attribute
     * @param {number} [element.step] step input attribute
     * @param {boolean} [element.editable] disabled input attribute
     * @param {string} [element.property] attr only model
     * @param {string} [element.validation] validation function
     *
     * @param {object} container this is where the addInputAzimuthRangeBlock is hosted
     */
    EZGuiManager.prototype.addInputAzimuthRangeBlock = function (element, container) {
        var self = this;
    
        var step = element.step;
        var max = element.max;
        var min = element.min;
    
        var modelAttribute = container.append('div')
            .attr('class', 'ez3d-model-attribute ez3d-flex-baseline-wrap');
    
        modelAttribute.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        if (element.id) {
            modelAttribute
                .attr('id', element.id);
        }
        if (element.label) {
            var text = self.i18n(element.label);
            modelAttribute.append('div')
                .attr('class', 'ez3d-attr_label ez3d-attr-range')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
        }
    
        var input;
        var value = element.value;
    
        input = modelAttribute.append('input')
            .attr('id', element.id + '-1')
            .attr('class', 'ez3d-number-range')
            .attr('type', 'Number')
            .attr('step', step)
            .attr('max', max)
            .attr('min', min)
            .attr('value', ez3dScene.utils.toFixed(value, 0));
    
        input
            .on('change', function() {
                var convertedValue = this.value || 0;
    
                if (element.validation === undefined || ez3dScene.active[element.validation](convertedValue)) {
                    // lanzar setAttribute
                    self.launchSetAttribute(element, convertedValue, ez3dScene.active.id);
                }
                // else {
                //     this.value = ez3dScene.active[element.property];
                //     this.classList.add('invalid-input');
                //     setTimeout(function() {
                //         self.classList.remove('invalid-input');
                //     }, 1000);
                // }
            });
    
        // new azimuth input
        input = modelAttribute.append('div')
            .attr('id', element.id + '-2')
            .attr('class', 'ez3d-azimuth-compass')
            .style('width', '200px')
            .style('height', '200px')
            .style('position', 'relative')
            .style('background-size', '15em')
            .call(d3.drag()
                .on('start', function() {
                    d3.select(this).select('#ez3d-azimuth-input-arrow-temp')
                        .style('display', 'inherit');
                })
                .on('drag', function() {
                    var angle = ez3dScene.utils.radToDeg(Math.atan2(d3.mouse(this)[1] - 100, d3.mouse(this)[0] - 100));
                    angle = ez3dScene.utils.transformAtan2ToAzimuthSpace(angle) * -1;
                    angle = ez3dScene.utils.toFixed(angle, 0);
    
                    d3.select(this).select('#ez3d-azimuth-input-arrow-temp')
                        .style('transform', 'rotate(' + angle + 'deg)');
    
                    d3.select('#' + element.id + '-1')
                        .attr('value', angle);
    
                    // move system in svgContainer
                    var system = ez3dScene.activeSubarea.system;
                    var id = system.id;
                    var pivotPoint = system.activePoint;
    
                    var angle2 = angle - system.azimuth;
    
                    d3.select('#svgArea_system_' + id)
                        .style('transform-origin', pivotPoint.x + 'px ' + pivotPoint.y + 'px')
                        .style('transform', 'rotate(' + angle2 + 'deg)');
    
                    var angle3 = parseInt(angle, 10) + ez3dScene.active.parent.angle;
    
                    // console.log(angle, parseInt(ez3dScene.active.parent.angle, 10), angle3);
    
                    d3.select('#ez3d-azimuthArrow-group').select('line')
                        .style('transform', 'rotate(' + angle3 + 'deg)');
                })
                .on('end', function() {
                    d3.select(this).select('#ez3d-azimuth-input-arrow-temp')
                        .style('display', 'none');
                    var angle = ez3dScene.utils.radToDeg(Math.atan2(d3.mouse(this)[1] - 100, d3.mouse(this)[0] - 100));
                    angle = ez3dScene.utils.transformAtan2ToAzimuthSpace(angle) * -1;
                    angle = ez3dScene.utils.toFixed(angle, 0);
    
                    ee.addOnceListener('setAttribute_Finished', function () {
                        if (element.eventOnChange) {
                            self.eventOnchangeExecution(element);
                        }
                        if (element.functionOnChange) {
                            self.functionOnchangeExecution(element);
                        }
    
                        var system = ez3dScene.activeSubarea.system;
                        var id = system.id;
                        d3.select('#svgArea_system_' + id)
                            .style('transform-origin', null)
                            .style('transform', null);
                    });
                    ee.emitEvent('EZModelScene_setAttributeListener', [{
                        args: [ez3dScene.active.id, element.property, angle],
                        updateRender: {draw2d: true, draw3d: false, updatePanels: false, fitRange: false},
                        undo: true,
                        progress: true,
                        notifications: true
                    }]);
                }));
    
        // temporal azimuth dial
        input
            .append('div')
            .attr('id', 'ez3d-azimuth-input-arrow-temp')
            .attr('class', 'ez3d-azimuth-arrow')
            .style('pointer-events', 'none')
            .style('display', 'none')
            .style('opacity', '0.5')
            .style('top', '0px')
            .style('left', '90px')
            .style('width', '20px')
            .style('height', '200px')
            .style('position', 'absolute')
            .style('transform-origin', '10px 100px')
            .style('transform', function() {
                var angle = ez3dScene.active.system.azimuth;
                return 'rotate(' + angle + 'deg)';
            });
    
        // azimuth dial
        input
            .append('div')
            .attr('id', 'ez3d-azimuth-input-arrow')
            .attr('class', 'ez3d-azimuth-arrow')
            .style('pointer-events', 'none')
            .style('top', '0px')
            .style('left', '90px')
            .style('width', '20px')
            .style('height', '200px')
            .style('position', 'absolute')
            .style('transform-origin', '10px 100px')
            .style('transform', function() {
                var angle = ez3dScene.active.system.azimuth;
                return 'rotate(' + angle + 'deg)';
            });
    
        if (element.editable === false) {
            input
                .attr('disabled', 'disabled')
                .style('pointer-events', 'none')
                .classed('ez3d-disabled', true);
            d3.select('#' + element.id + '-1')
                .attr('disabled', 'disabled')
                .classed('ez3d-disabled', true);
        }
        if (element.readOnly) {
            input
                .attr('disabled', 'disabled')
                .style('pointer-events', 'none')
                .classed('ez3d-read-only', true);
            d3.select('#' + element.id + '-1')
                .attr('disabled', 'disabled')
                .classed('ez3d-read-only', true);
        }
    };
    
    /**
     *
     *  * Method to create compass block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [element.id] block id
     * @param {string} [element.label] block label
     *
     * @param {string} element.value value is option selected
     * @param {string} [element.property] attr only model
     *
     * @param {object} container this is where the addCompassBLock is hosted
     */
    EZGuiManager.prototype.addCompassBLock = function (element, container) {
        var self = this;
        var selected = element.value;
    
        var modelAttribute = container.append('div')
            .attr('class', 'ez3d-model-attribute');
    
        // Add circle icon
        modelAttribute.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        if (element.id) {
            modelAttribute
                .attr('id', element.id);
        }
        if (element.label) {
            var text = self.i18n(element.label);
            modelAttribute.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
        }
    
        const roof = ez3dScene.active;
        if (['gabled', 'gambrel', 'hipped'].includes(roof.type)) {
            var roofSelectContainer = modelAttribute.append('div')
                .attr('class', 'ez3d-flex-wrap-space-around');
    
            roofSelectContainer.append('div')
                .attr('class', 'ez3d-roof-selector ez3d-roof-selector-east-west-' + element.id)
                .attr('id', 'EW')
                .append('span')
                .html(self.i18n('structureEW'));
    
            roofSelectContainer.append('div')
                .attr('class', 'ez3d-roof-selector ez3d-roof-selector-south-north-' + element.id)
                .attr('id', 'SN')
                .append('span')
                .html(self.i18n('sn'));
        }
        /* else {
            var compass = modelAttribute.append('div')
                .attr('class', 'ez3d-compass ez3d-flex-wrap-space-around');
    
            var content = compass.append('div')
                .attr('class', 'ez3d-compass-content ez3d-compass-north-south-content');
    
            var directions = {'N': 'north', 'S': 'south', 'W': 'west', 'E': 'east'};
            Object.keys(directions).forEach(function(point) {
                if (roof.path.wallFacing.indexOf(point) !== -1) {
                    content.append('div')
                        .attr('class', 'ez3d-compass-check ez3d-compass-' + directions[point])
                        .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                        .attr('id', point)
                        .append('span')
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                        .html(self.i18n(directions[point]));
                }
            });
        }*/
        d3.selectAll('.ez3d-roof-selector')
            .classed('active', function () {
                let value = false;
                if (this.id.includes(selected)) {
                    value = true;
                }
                return value;
            })
            .on('click', function () {
                var id = this.id[0];
                var activeElementId = roof.id;
    
                if (element.confirmation) {
                    if (element.confirmation === true ||
                        roof[element.confirmation]() === true) {
                        // lanzar confirmaciones
                        var eventCancelListener = function() {
                            ee.removeListener(element.confirmationWidget.eventOk, eventOkListener);
                            ee.emitEvent('updatePanels');
                        };
    
                        var eventOkListener = function() {
                            ee.removeListener(element.confirmationWidget.eventCancel, eventCancelListener);
                            // lanzar setAttribute
                            self.launchSetAttribute(element, id, activeElementId);
                        };
    
                        if (element.confirmationWidget.eventCancel) {
                            ee.addOnceListener(element.confirmationWidget.eventCancel, eventCancelListener);
                        }
    
                        if (element.confirmationWidget.eventOk) {
                            ee.addOnceListener(element.confirmationWidget.eventOk, eventOkListener);
                        }
    
                        ee.emitEvent('widget_notification', [{
                            'name': element.confirmationWidget.name,
                            'title': element.confirmationWidget.title,
                            'content': element.confirmationWidget.content,
                            'eventOk': element.confirmationWidget.eventOk,
                            'eventCancel': element.confirmationWidget.eventCancel,
                            'functionOnCreation': element.confirmationWidget.functionOnCreation
                        }]);
                    } else {
                        // lanzar setAttribute
                        self.launchSetAttribute(element, id, activeElementId);
                    }
                } else {
                    // lanzar setAttribute
                    self.launchSetAttribute(element, id, activeElementId);
                }
            });
    
        if (element.readOnly) {
            modelAttribute
                .classed('ez3d-read-only', true);
        }
    };
    
    
    // Info blocks
    /**
     *
     *  * Method to create title block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [element.id] block id
     * @param {string} [element.title] block content
     * @param {string} [element.classed] block class
     * @param {boolean} [element.disabled] block disabled styles
     *
     * @param {object} container this is where the addTitleBlock is hosted
     */
    EZGuiManager.prototype.addTitleBlock = function (element, container) {
        var self = this;
    
        var title = container.append('h2')
            .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
    
        if (element.id) {
            title
                .attr('id', element.id);
        }
        if (element.title) {
            var text = self.i18n(element.title);
            title
                .html(text);
        }
        if (element.classed) {
            title
                .attr('class', element.classed);
            if (title.classed('ez3d-group-title')) {
                title
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
            }
        }
        if (element.disabled) {
            title
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.disabledbuttons);
        }
        if (element.readOnly) {
            title
                .classed('ez3d-read-only', true);
        }
    };
    
    /**
     *
     *  * Method to create helper block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [element.id] block id
     * @param {string} [element.classed] block class
     * @param {string} [element.content] block content
     *
     * @param {object} container this is where the addHelperBlock is hosted
     */
    EZGuiManager.prototype.addHelperBlock = function(element, container) {
        var self = this;
    
        var helperContainer = container.append('div')
            .attr('class', 'ez3d-' + element.classed + ' ez3d-flex-baseline');
    
        if (element.id) {
            helperContainer
                .attr('id', element.id);
        }
        if (element.classed.split(' ').indexOf('helperList') !== -1) {
            helperContainer.append('div')
                .attr('class', 'fa fa-check ez3d-helper-list-icon')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.third);
        }
        if (element.content) {
            var text = self.i18n(element.content);
            helperContainer.append('span')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.darkgray)
                .html(text);
        }
    };
    
    /**
     *
     *  * Method to create link block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [element.id] block id
     * @param {string} [element.classed] block class
     * @param {string} [element.url] block url
     *
     * @param {object} container this is where the addHelperBlock is hosted
     */
    EZGuiManager.prototype.addLinkBlock = function(element, container) {
        var helperContainer = container.append('div')
            .classed('ez3d-flex-baseline', true);
    
        if (element.id) {
            helperContainer
                .attr('id', element.id);
        }
        if (element.classed) {
            helperContainer.classed('ez3d-' + element.classed + ' ez3d-flex-baseline', true);
            if (element.classed.split(' ').indexOf('helperList') !== -1) {
                helperContainer.append('div')
                    .attr('class', 'fa fa-check ez3d-helper-list-icon')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.third);
            }
        }
        if (element.url) {
            var link = helperContainer.append('span')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.darkgray)
                .style('text-decoration', 'underline')
                .style('cursor', 'pointer')
                .html(element.url);
    
            link.on('click', function() {
                window.open(element.url, '_blank');
            });
        }
    };
    
    
    // Tooltip
    /**
     *
     *  * Method to create tooltip
     */
    EZGuiManager.prototype.tooltipCreate = function() {
        var myTool = d3.select('#ez3d-gui-container')
            .append('div')
            .attr('id', 'ez3d-mytooltip')
            .classed('ez3d-tooltip-hidden', true);
    
        myTool.append('div')
            .attr('class', 'ez3d-tooltip-title')
            .style('border-bottom', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        myTool.append('hr')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.second);
    
        myTool.append('div')
            .attr('class', 'ez3d-tooltip-content');
    
    };
    
    /**
     *
     *  * Method to create tooltip block
     *
     * @param {object} element this is a json data for construct the block
    
     * @param {string} [element.title] block title
     * @param {string} [element.content] block content
     * @param {object} element.container this is where the addTooltip is hosted
     */
    // ejemplo
    // tooltipBtn: {
    //     title: 'tooltipRoofTypeTitle',
    //     content: 'tooltipRoofTypeContent'
    // }
    EZGuiManager.prototype.addTooltip = function(element) {
        var self = this;
        var container = element.container;
        var myTool = d3.select('#ez3d-mytooltip');
    
        container.append('div')
            .attr('class', 'ez3d-question-tooltip')
            .on('mouseover', function() {
                if (element.title) {
                    var tooltitle = self.i18n(element.title);
                    myTool.select('.ez3d-tooltip-title')
                        .html(tooltitle);
                }
                if (element.content) {
                    var text = self.i18n(element.content);
                    myTool.select('.ez3d-tooltip-content')
                        .html(text);
                }
                var top = d3.event.y - 40;
                var left = d3.event.x + 25;
    
                myTool.style('top', top + 'px').style('left', left + 'px').classed('ez3d-tooltip-visible', true);
            })
            .on('mouseout', function() {
                return myTool.classed('ez3d-tooltip-visible', false);
            });
    
        if (element.readOnly) {
            myTool
                .classed('ez3d-read-only', true);
        }
    };
    
    
    
    // Buttons
    /**
     *
     *  * Method to create a group blocks
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [elements.classed] block class
     *
     * @param {object} container this is where the addButtonsBlock is hosted
     */
    EZGuiManager.prototype.addGroupBlock = function (element, container) {
        var self = this;
        var elements = element.elements;
    
        if (element.label) {
            var modelAttribute = container.append('div')
                .attr('class', 'ez3d-model-attribute ez3d-flex-baseline-wrap');
    
            // Add circle icon
            modelAttribute.append('div')
                .attr('class', 'ez3d-circle-block')
                .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
            var text = self.i18n(element.label);
            modelAttribute.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
        }
    
        var groupContainer = container.append('div');
    
        if (element.id) {
            groupContainer
                .attr('id', element.id);
        }
    
        if (elements.classed) {
            groupContainer
                .classed(elements.classed, true);
        }
    
        self.populateBlocks(elements, groupContainer);
    };
    
    /**
     *
     *  * Method to create a button block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} element.id id button attribute
     * @param {string} [element.classed] class button attribute
     * @param {string} [element.content] button text
     * @param {string} [element.tooltip] button tooltip
     * @param {string} [element.changeIcon] toggle button icon
     * @param {string} [element.eventOnClick] button eventOnClick
     * @param {string} [element.functionOnClick] button functionOnClick
     * @param {string} [element.confirmation] button confirmation widget
     * @param {string} [element.active] active button class
     * @param {string} element.value value button attribute
     * @param {string} [element.editable] disabled button attribute
     * @param {object} [element.elements] foldable elements list
     * @param {string} [element.property] attr only model
     *
     * @param {object} container this is where the addStringInputBlock is hosted
     */
    EZGuiManager.prototype.addButtonBlock = function(element, container) {
        var self = this;
        var buttonContainer = container.append('div')
            .attr('class', 'ez3d-btns-container');
    
        // Create provider name
        if (element.provider && element.tooltip) {
            buttonContainer
                .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
            buttonContainer
                .append('div')
                .attr('class', 'ez3d-provider-name')
                .html(element.tooltip);
        }
    
        // Add button
        var button = buttonContainer.append('button');
    
        if (element.parentClassed) {
            buttonContainer
                .classed(element.parentClassed, true);
        }
    
        if (element.id) {
            button
                .attr('id', element.id);
        }
        if (element.content) {
            var contentText = self.i18n(element.content);
            button
                .html(contentText);
        }
        if (element.tooltip) {
            var text = self.i18n(element.tooltip);
            button
                .attr('title', text);
        }
        if (element.provider) {
            button.classed('ez3d-provider-btn', true);
            if (element.id) {
                button.html(element.number);
            }
        }
        button
            .on('click', function() {
                var convertedValue;
                var id;
                if (element.property) {
                    if (element.id) {
                        if (element.id === 'snap-to-lines' || element.id === 'snap-to-vertices' || element.id === 'snap-to-guides') {
                            id = '';
                        } else {
                            id = element.id;
                        }
                    } else {
                        id = ez3dScene.active.id;
                        if (element.property.split('.').length > 1 && element.property.split('.')[0] === 'layoutRules') {
                            id = '';
                        }
                    }
                }
                if (element.confirmation) {
                    var eventOkName = undefined;
                    var eventCancelName = undefined;
                    if (Array.isArray(element.confirmationWidget.eventOk)) {
                        eventOkName = element.confirmationWidget.eventOk[0];
                    } else {
                        eventOkName = element.confirmationWidget.eventOk;
                    }
                    if (Array.isArray(element.confirmationWidget.eventCancel)) {
                        eventCancelName = element.confirmationWidget.eventCancel[0];
                    } else {
                        eventCancelName = element.confirmationWidget.eventCancel;
                    }
                    convertedValue = !element.value;
                    if (element.confirmation === true || ez3dScene.active[element.confirmation](convertedValue) === true) {
                        // lanzar confirmaciones
                        var eventCancelListener = function() {
                            ee.removeListener(eventOkName, eventOkListener);
                            if (eventCancelName === 'widget_color_eventCancel') {
                                ee.emitEvent('updateRender',
                                    [{draw2d: true, draw3d: false, updatePanels: true, fitRange: false}]);
                                if (ez3dScene.context === 'building') {
                                    ez3dScene.layoutRules.defaultColors.buildingColors.path.active.fillOpacity = 0;
                                }
                            } else {
                                ee.emitEvent('updatePanels');
                            }
                        };
    
                        var eventOkListener = function(eventData) {
                            ee.removeListener(eventCancelName, eventCancelListener);
                            if (eventOkName === 'close_widget_notification') return;
                            // lanzar setAttribute
                            if (eventOkName === 'widget_color_eventOk') {
                                if (ez3dScene.context === element.id.split('-')[2]) {
                                    var saveColor = '';
                                    ee.emitEvent('EZModelScene_setAttributeListener', [{
                                        args: [ez3dScene.active.id, self.attr, self.color],
                                        updateRender: {
                                            draw2d: true,
                                            draw3d: false,
                                            updatePanels: false,
                                            fitRange: false
                                        },
                                        undo: element.undo,
                                        notifications: true
                                    }]);
                                    saveColor = ez3dScene.layoutRules.defaultColors.customColor;
                                    ez3dScene.layoutRules.defaultColors.buildingColors.path.active.fillOpacity = 0;
                                    if (saveColor.indexOf(self.color) === -1) {
                                        saveColor.push(self.color);
                                    }
                                }
                                self.removeWidget(element.id);
                            } else if (element.property) {
                                // lanzar setAttribute
                                self.launchSetAttribute(element, convertedValue, id);
                            } else if (eventOkName === 'EZModelBuilding_deleteListener') {
                                // TODO porque se hace un update render al borrar un listener????
                                ee.emitEvent('updateRender',
                                    [{draw2d: true, draw3d: true, updatePanels: true, fitRange: true}]);
                            } else if (eventOkName === 'widget_roofChangeOrientation_eventOk') {
                                self.changeOrientation(eventData.args);
                            }
                        };
    
                        if (eventCancelName) {
                            ee.addOnceListener(eventCancelName, eventCancelListener);
                        }
    
                        if (eventOkName) {
                            ee.addOnceListener(eventOkName, eventOkListener);
                        }
    
                        ee.emitEvent('widget_notification', [{
                            'name': element.confirmationWidget.name,
                            'title': element.confirmationWidget.title,
                            'content': element.confirmationWidget.content,
                            'eventOk': element.confirmationWidget.eventOk,
                            'eventCancel': element.confirmationWidget.eventCancel,
                            'optionsButtons': element.confirmationWidget.optionsButtons,
                            'functionOnCreation': element.confirmationWidget.functionOnCreation
                        }]);
                    }
                }
                if (element.property) {
                    convertedValue = !element.value;
                    ee.addOnceListener('setAttribute_Finished', function() {
                        ee.emitEvent('updatePanels');
                    });
                    self.launchSetAttribute(element, convertedValue, id);
    
                    if (self.widgets.indexOf('ez3d-widget-draw') !== -1) {
                        self.drawWidget();
                    }
                }
                if (element.changeIcon) {
                    var classname = 'ez3d-new-' + element.changeIcon + '-icon';
                    button
                        .classed(classname, function () {
                            var value = true;
                            if (d3.select(this).classed(classname)) {
                                value = false;
                            }
                            return value;
                        });
                }
                if (element.eventOnClick) {
                    var disabled = true;
                    if (element.editable === false || typeof element.editable === 'string') {
                        if (typeof element.editable === 'string') {
                            ez3dScene.active[element.editable]();
                            disabled = ez3dScene.active[element.editable]() || false;
                        }
                    }
                    if (disabled) {
                        self.eventOnclickExecution(element);
                    }
                }
                if (element.functionOnClick) {
                    self[element.functionOnClick[0]](element.functionOnClick[1]);
                }
                if (element.active) {
                    d3.select(this)
                        .classed('active', function() {
                            return self[element.active[0]](element.active[1]);
                        })
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                    ee.emitEvent('updatePanels');
                }
                if (element.d3Action) {
                    if (Array.isArray(element.d3Action)) {
                        self.ez3dViewport.svgElements.svgProject[element.d3Action[0]](element.d3Action[1]);
                    } else {
                        self.ez3dViewport.svgElements.svgProject[element.d3Action]();
                    }
                }
            })
            .on('mouseover', function () {
                // ez3d-button round hover
                if (button.classed('ez3d-button')) {
                    button
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                }
                if (button.classed('ez3d-square-button-green') || button.classed('ez3d-square-button-blue')) {
                    button
                        .style('opacity', '0.7');
                }
                if (button.classed('ez3d-disabled-eye')) {
                    var groupBlock = d3.select('#foldable-group-' + this.id);
                    groupBlock
                        .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                    groupBlock.select('h2')
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                    groupBlock.select('div.ez3d-btns-container').select('.fa-caret-down')
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                    groupBlock.select('div.ez3d-subtitle-info').select('span')
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                }
                if (button.classed('ez3d-provider-btn')) {
                    // Set provider name color to white and buttonContainer to first
                    d3.select(button.node().previousSibling)
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white);
                    d3.select(button.node().parentNode)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                }
            })
            .on('mouseout', function () {
                // ez3d-button round blur
                if (button.classed('ez3d-button')) {
                    button
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
                }
                if (button.classed('ez3d-square-button-green') || button.classed('ez3d-square-button-blue')) {
                    button
                        .style('opacity', '1');
                }
                if (button.classed('active')) {
                    button
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                }
                if (button.classed('ez3d-disabled-eye')) {
                    var groupBlock = d3.select('#foldable-group-' + this.id);
                    groupBlock
                        .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
                    groupBlock.select('h2')
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
                    groupBlock.select('div.ez3d-btns-container').select('.fa-caret-down')
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
                    groupBlock.select('div.ez3d-subtitle-info').select('span')
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
                }
                if (button.classed('ez3d-provider-btn') && !button.classed('active')) {
                    // Set provider name color to second and buttonContainer to lightgray
                    d3.select(button.node().previousSibling)
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
                    d3.select(button.node().parentNode)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
                }
            });
    
        if (element.active) {
            button
                .classed('active', function() {
                    return self[element.active[0]](element.active[1], true);
                });
            if (button.classed('ez3d-provider-btn')) {
                // Set provider name color to white and buttonContainer to first
                d3.select(button.node().previousSibling)
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white);
                d3.select(button.node().parentNode)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
            }
        }
        if (element.editable === false || typeof element.editable === 'string') {
            var editable = false;
            if (typeof element.editable === 'string') {
                var activePanel = d3.select(container.node().parentNode).classed('ez3d-active-panel');
                if (activePanel && ez3dScene.active[element.editable]()) {
                    editable = true;
                }
            }
            if (editable === false) {
                button
                    .classed('ez3d-square-disabled', true)
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.disabledbuttons)
                    .style('opacity', '0.9')
                    .style('cursor', 'default');
            }
        }
    
        if (element.property) {
            if (element.property === 'disabled') {
                if (element.value === false) {
                    button
                        .classed('ez3d-enabled-eye', true)
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                } else {
                    button
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
                }
            } else if (element.value === false && element.invertValue === undefined || (element.value === true && element.invertValue === true)) {
                button
                    .classed('ez3d-square-disabled', true);
            } else {
                button
                    .classed('ez3d-square-disabled', false);
            }
        }
        if (element.elements) {
            var btnsContainer = '';
            // Create new mapper foldable buttons container
            if (element.id === 'ez3d-mapper-button') {
                btnsContainer = buttonContainer
                    .append('div')
                    .attr('class', 'ez3d-foldableBtns-container')
                    .style('display', 'none');
                self.populateBlocks(element.elements, btnsContainer);
            } else {
                self.populateBlocks(element.elements, buttonContainer);
            }
            // Get foldable block height
            var foldableButtonsLenght;
            if (element.elements.classed) {
                foldableButtonsLenght = Object.keys(element.elements).length - 1;
            }
            foldableButtonsLenght = Object.keys(element.elements).length + 1;
            buttonContainer
                .attr('class', 'ez3d-foldable-buttons-viewport')
                .on('mouseover', function () {
                    buttonContainer
                        .style('height', 45 * foldableButtonsLenght + 'px');
                    if (btnsContainer) {
                        btnsContainer.style('display', 'flex');
                    }
                })
                .on('mouseout', function () {
                    buttonContainer
                        .style('height', '45px');
                    if (btnsContainer) {
                        btnsContainer.style('display', 'none');
                    }
                });
        }
        if (element.classed) {
            if (Array.isArray(element.classed)) {
                var className = self[element.classed[0]](element.classed[1]);
                button
                    .classed(className, true);
            } else {
                button
                    .classed(element.classed, true);
            }
            if (button.classed('ez3d-complex-button') || button.classed('fa-caret-down')) {
                button
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
            }
            if (button.classed('ez3d-button') && !button.classed('ez3d-button-render')) {
                button
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
            }
            if (button.classed('ez3d-square-button-blue')) {
                button
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.second);
            }
            if (button.classed('active') || button.classed('ez3d-square-button-green')) {
                button
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
            }
            if (button.classed('ez3d-square-disabled')) {
                button
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.disabledbuttons)
                    .style('opacity', '0.9');
            }
        }
        if (element.disabled) {
            button.attr('disabled', element.disabled);
        }
        if (element.readOnly) {
            button
                .attr('disabled', true)
                .classed('ez3d-read-only', true);
        }
    };
    
    /**
     *
     *  * Method to create a resume button block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} element.id  block id
     * @param {string} element.label  block label
     * @param {string} element.labels  block labels group
     * @param {string} [element.eventOnClick] button eventOnClick
     * @param {string} [element.editable] disabled button attribute
     * @param {object} [element.elements] elements list
     *
     * @param {object} container this is where the addResumeButtonBlock is hosted
     */
    EZGuiManager.prototype.addResumeButtonBlock = function (element, container) {
        var self = this;
    
        var modelAttribute;
        modelAttribute = container.append('div')
            .attr('class', 'ez3d-edit-group-block ez3d-flex-center-wrap')
            .on('mouseover', function () {
                // hover
                d3.select(this).select('.ez3d-attr_labels')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                d3.select(this).select('div.ez3d-attr_buttonlist ').selectAll('.ez3d-btns-container').select('.fa-pencil')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
            })
            .on('mouseout', function () {
                // blur
                d3.select(this).select('.ez3d-attr_labels')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
                d3.select(this).select('div.ez3d-attr_buttonlist ').selectAll('.ez3d-btns-container').select('.fa-pencil')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
            });
    
        modelAttribute.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        if (element.id) {
            modelAttribute
                .attr('id', element.id);
        }
        if (element.label) {
            var text = self.i18n(element.label);
            modelAttribute.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
        }
        if (element.eventOnClick) {
            modelAttribute
                .on('click', function () {
                    self.eventOnclickExecution(element);
                });
        }
        if (element.labels) {
            var info = modelAttribute.append('div')
                .attr('class', 'ez3d-attr_labels')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
    
            Object.keys(element.labels).forEach(function(label) {
                info.append('p')
                    .html(self.i18n(element.labels[label].value));
            });
        }
        if (element.elements) {
            var buttons = modelAttribute.append('div')
                .attr('class', 'ez3d-attr_buttonlist ez3d-flex-center');
    
            self.populateBlocks(element.elements, buttons);
        }
        if (element.readOnly) {
            container
                .classed('ez3d-read-only', true);
        }
    };
    
    
    
    
    
    /**
     *
     *  * Method to create a resume block (image title)
     *
     * @param {object} element this is a json data for construct the block
     * @param {object} element.elements.button button image
     * @param {string} element.elements.name title resume
     *
     * @param {object} container this is where the addResumeBlock is hosted
     */
    EZGuiManager.prototype.addResumeBlock = function (element, container) {
        var modelAttribute = container.append('div')
            .attr('class', 'ez3d-resume-group-block ez3d-flex-center-wrap');
    
        var image = modelAttribute.append('div')
            .attr('class', 'ez3d-image-resume');
    
        this.addButtonBlock(element.elements.button, image);
    
        modelAttribute.append('div')
            .attr('class', 'ez3d-title-resume')
            .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
            .html(element.elements.name.value);
    
        if (element.elements.areasList) {
            modelAttribute.append('div')
                .attr('class', 'ez3d-roof-areas-resume');
            this.addBlockListBlock(element.elements.areasList, container);
        }
    
    };
    
    /**
     *
     *  * Method to create a block list
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} element.emptyMessage block empty message
     * @param {string} [element.classed] block class
     * @param {object} [element.elements] elements list
     *
     * @param {object} container this is where the addBlockListBlock is hosted
     */
    EZGuiManager.prototype.addBlockListBlock = function (element, container) {
        var self = this;
        var modelAttribute = container.append('div');
        var list = modelAttribute.append('div')
            .attr('class', element.classed);
        if (element.classed === 'ez3d-resume-subarea-block') {
            list
                .style('border-bottom-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
        }
        if (element.emptyMesage) {
            if (Object.keys(element.elements).length === 0) {
                var message = self.i18n(element.emptyMesage);
                list.append('div')
                    .attr('class', 'ez3d-block-list-empty')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.darkgray)
                    .html(message);
            }
        }
        self.populateBlocks(element.elements, list);
    };
    
    /**
     *
     *  * Method to create foldable block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [element.id] block id
     * @param {string} [element.title] block title
     * @param {string} [element.elements] elements list
     *
     * @param {object} container this is where the addFoldableBlock is hosted
     */
    EZGuiManager.prototype.addFoldableBlock = function (element, container) {
        var self = this;
    
        var modelAttribute = container.append('div')
            .attr('class', 'ez3d-foldable-group-block ez3d-foldable-group-block-' + element.object)
            .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray)
            .on('mouseover', function () {
                // Svg hover
                let stylesFunction;
                if (element.object === 'keepout') stylesFunction = styleKeepouts;
                if (element.object === 'area') stylesFunction = styleAreas;
                if (element.object === 'tree') stylesFunction = styleTrees;
                d3.select('#svgProject_' + element.object + '_' + element.id)
                    .select('.' + element.object + '-shape')
                    .call(stylesFunction, ez3dViewport.svgElements.svgProject, 'hover');
            })
            .on('mouseout', function () {
                // Svg hover
                const relatedSvgElement = d3.select('#svgProject_' + element.object + '_' + element.id).select('.' + element.object + '-shape');
                if (!relatedSvgElement.node()) return;
                let stylesFunction;
                if (element.object === 'keepout') stylesFunction = styleKeepouts;
                if (element.object === 'area') stylesFunction = styleAreas;
                if (element.object === 'tree') stylesFunction = styleTrees;
                relatedSvgElement
                    .call(stylesFunction, ez3dViewport.svgElements.svgProject, relatedSvgElement.datum().style.colorState);
            });
    
        if (element.id) {
            modelAttribute
                .attr('id', 'foldable-group-' + element.id);
        }
    
        var title = modelAttribute.append('div')
            .attr('class', 'ez3d-group-label ez3d-flex-baseline')
            .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
            .on('click', function () {
                if (element.functionOnClick) {
                    self[element.functionOnClick[0]](element.functionOnClick[1]);
                }
                if (element.eventOnClick) {
                    self.eventOnclickExecution(element);
                }
                if (element.updateOnClick) {
                    ee.emitEvent('updatePanels');
                }
            })
            .on('mouseover', function () {
                d3.select(this.parentElement)
                    .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                d3.select(this).select('h2')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                d3.select(this).select('div.ez3d-btns-container').select('.fa-caret-down')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                d3.select(this).select('div.ez3d-subtitle-info').select('span')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
            })
            .on('mouseout', function () {
                d3.select(this.parentElement)
                    .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
                d3.select(this).select('h2')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
                d3.select(this).select('div.ez3d-btns-container').select('.fa-caret-down')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
                d3.select(this).select('div.ez3d-subtitle-info').select('span')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
            });
    
        self.populateBlocks(element.title, title);
    
        var info = modelAttribute.append('div')
            .attr('class', 'ez3d-foldable-block')
            .classed('ez3d-foldable-block-open', function() {
                var value = false;
                if (ez3dScene.active.id === element.id) {
                    value = true;
                }
                if (ez3dScene.activeArea.id === element.id) {
                    value = true;
                }
                return value;
            });
    
        self.populateBlocks(element.elements, info);
    };
    
    /**
     *
     *  * Method to create foldable subarea block
     *
     * @param {object} element this is a json data for construct the block
     * @param {string} [element.id] block id
     * @param {string} [element.title] block title
     * @param {string} [element.disabled] block disabled
     * @param {string} [element.elements] elements list
     *
     * @param {object} container this is where the addEditDisabledResumeButtonBlock is hosted
     */
    EZGuiManager.prototype.addEditDisabledResumeButtonBlock = function (element, container) {
        var self = this;
        var containerSvg = d3.select('#svgProject_' + element.object + '_' + element.id).select('.' + element.object + '-shape').node();
    
        var modelAttribute = container.append('div')
            .attr('class', 'ez3d-foldable-group-block')
            .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray)
            .on('mouseover', function () {
                // Svg hover
                if (!element.disabled) {
                    d3.select('#svgProject_' + element.object + '_' + element.id)
                        .select('.' + element.object + '-shape')
                        .call(styleSubareas, ez3dViewport.svgElements.svgProject, 'hover');
                }
            })
            .on('mouseout', function () {
                // Svg blur
                if (!element.disabled) {
                    const relatedSvgElement = d3.select('#svgProject_' + element.object + '_' + element.id).select('.' + element.object + '-shape');
                    if (!relatedSvgElement.node()) return;
                    relatedSvgElement
                        .call(styleSubareas, ez3dViewport.svgElements.svgProject, relatedSvgElement.datum().style.colorState);
                }
            });
    
        if (element.id) {
            modelAttribute
                .attr('id', 'foldable-group-' + element.id);
        }
        if (element.disabled) {
            modelAttribute
                .attr('disabled', 'disabled')
                .classed('ez3d-disabled-subarea', true);
        }
    
        var title = modelAttribute.append('div')
            .attr('class', 'ez3d-group-label ez3d-flex-baseline')
            .on('click', function () {
                if (element.eventOnClick) {
                    self.eventOnclickExecution(element);
                }
                if (element.updateOnClick) {
                    ee.emitEvent('updatePanels');
                }
            })
            .on('mouseover', function () {
                // hover
                d3.select(this.parentElement)
                    .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                d3.select(this).select('h2')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                d3.select(this).select('div.ez3d-btns-container').select('.fa-caret-down')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                d3.select(this).select('div.ez3d-subtitle-info').select('span')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
            })
            .on('mouseout', function () {
                d3.select(this.parentElement)
                    .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
                d3.select(this).select('h2')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
                d3.select(this).select('div.ez3d-btns-container').select('.fa-caret-down')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
                d3.select(this).select('div.ez3d-subtitle-info').select('span')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
            });
    
        self.populateBlocks(element.title, title);
    
        var info = modelAttribute.append('div')
            .attr('class', 'ez3d-buttons-resume-block');
    
        self.populateBlocks(element.elements, info);
    };
    
    
    // Units
    /**
     *
     *  * Method to create unit selector
     *
     * @param {object} container this is where the addUnitSelector is hosted
     */
    EZGuiManager.prototype.addUnitSelector = function(container) {
        var self = this;
    
        // unit selector
        var unitSelector = container.append('select')
            .attr('class', 'ez3d-select-meter');
    
        self.units.forEach(function(unit, i) {
            if (i === ez3dScene.unit) {
                unitSelector.append('option')
                    .attr('selected', 'selected')
                    .attr('value', i)
                    .html(unit.name);
            } else {
                unitSelector.append('option')
                    .attr('value', i)
                    .html(unit.name);
            }
        });
    
        unitSelector
            .on('change', function() {
                // console.log('unit change to:', this.value);
                ee.addOnceListener('setUnits_Finished', function() {
                    ee.emitEvent('updatePanels');
                });
                ee.emitEvent('EZModelScene_setUnitsListener', [{
                    args: parseInt(this.value, 10),
                    undo: false
                }]);
            });
    
        if (ez3dScene.layoutRules.scenePreferences.readOnly) {
            unitSelector
                .classed('ez3d-read-only', true);
        }
    };
    
    /**
     *
     *  * Method to show units
     *
     * @param {string} element unit to show
     * @param {object} container this is where the addShowUnit is hosted
     */
    EZGuiManager.prototype.addShowUnit = function (element, container) {
        // unit
        container.append('div')
            .attr('class', 'ez3d-show-unit')
            .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
            .append('p')
            .html(element);
    };
    
    
    // Widget blocks
    EZGuiManager.prototype.addStringInputBlockWidget = function(element, container) {
        var self = this;
        var modelAttribute = container.append('div')
            .attr('class', 'ez3d-model-attribute ez3d-flex-baseline-wrap');
    
        if (element.classed) {
            modelAttribute.classed(element.classed, true);
        }
    
        modelAttribute.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        if (element.id) {
            modelAttribute
                .attr('id', element.id);
        }
        if (element.label) {
            var text = self.i18n(element.label);
            modelAttribute.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
        }
    
        var input = modelAttribute.append('input');
    
        if (element.inputType === 'email') {
            input
                .attr('type', 'email')
                .attr('required', 'required');
        } else if (element.inputType === 'password') {
            input
                .attr('type', 'password')
                .attr('required', 'required');
        }
    
        if (element.inputIid) {
            input
                .attr('id', element.inputIid);
        }
    
        if (element.autofocus) {
            input
                .attr('autofocus', element.autofocus);
        }
        if (element.placeholder) {
            input
                .attr('placeholder', self.i18n(element.placeholder));
        }
        if (element.required) {
            input
                .attr('required', element.required);
        }
    
        input
            .attr('value', self.getValueFromJsonElement(element));
    
        input
            .classed('new-width-with-tooltip', function () {
                var classed = false;
                if (element.tooltipBtn) {
                    classed = true;
                }
                return classed;
            })
            .on('change', function() {
                var convertedValue = this.value;
            })
            .on('focus', function () {
                // bar style
                var parent = d3.select(this.parentNode).node();
                var prev = d3.select(parent.lastChild).node();
                d3.select(prev)
                    .classed('ez3d-bar-after', true)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
            })
            .on('blur', function () {
                // bar style
                var parent = d3.select(this.parentNode).node();
                var prev = d3.select(parent.lastChild).node();
                d3.select(prev)
                    .classed('ez3d-bar-after', false)
                    .style('background-color', 'transparent');
                // form elements
                if (element.functionOnblur) {
                    self[element.functionOnblur[0]](element.functionOnblur[1]);
                }
            })
            .on('input', function () {
                // form elements
                if (element.functionOninput) {
                    self[element.functionOninput[0]](element.functionOninput[1]);
                }
            });
    
        modelAttribute.append('div')
            .attr('class', 'ez3d-bar');
    
        var editable = true;
        if (typeof element.editable === 'string') {
            editable = ez3dScene.active[element.editable]();
        }
        if (element.editable === false) {
            editable = false;
        }
        if (editable === false) {
            input
                .attr('disabled', 'disabled')
                .classed('ez3d-disabled', true);
        }
    };
    EZGuiManager.prototype.addSelectBlockWidget = function (element, container) {
        var self = this;
        var selected = (self.getValueFromJsonElement(element)).toString();
    
        var modelAttribute = container.append('div')
            .attr('class', 'ez3d-model-attribute ez3d-flex-center-wrap');
    
        modelAttribute.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        if (element.label) {
            var text = self.i18n(element.label);
            modelAttribute.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
        }
    
        if (element.contentLeft) {
            modelAttribute.append('div')
                .attr('id', element.contentLeft);
        }
    
        var select = modelAttribute.append('select')
            .attr('class', 'ez3d-select')
            .style('border-bottom-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray)
            .attr('value', function() {
                return self.getValueFromJsonElement(element);
            });
    
        if (element.id) {
            select
                .attr('id', element.id);
        }
    
        if (element.editable === false) {
            select
                .attr('disabled', 'disabled')
                .classed('ez3d-disabled', true);
        }
    
        var arrOptionsLabel = [];
        var opt;
        if (element.optionsFunction) {
            arrOptionsLabel = ez3dScene.active[element.optionsFunction]();
            if (arrOptionsLabel instanceof Array) {
                var tempArrOptionsLabel = [];
                arrOptionsLabel.map(function(option) {
                    tempArrOptionsLabel[option.toLowerCase()] = option.toLowerCase();
                });
                arrOptionsLabel = tempArrOptionsLabel;
            }
        } else if (element.optionsLabels) {
            var optionsLabels = element.optionsLabels.sort();
            for (var i = 0; i < optionsLabels.length; i++) {
                arrOptionsLabel[element.optionsLabels[i]] = element.optionsLabels[i];
            }
        } else if (element.options) {
            arrOptionsLabel = element.options;
        }
    
        Object.keys(arrOptionsLabel).forEach(function(key) {
            var textvalue = null;
            if (Array.isArray(arrOptionsLabel[key])) {
                textvalue = self.i18n(arrOptionsLabel[key][1]);
                key = String(arrOptionsLabel[key][0]);
            } else {
                textvalue = self.i18n(arrOptionsLabel[key]);
            }
            if (key === selected) {
                opt = select.append('option')
                    .attr('selected', 'selected')
                    .attr('value', key)
                    .html(ez3dScene.utils.capitalize(textvalue));
            } else {
                opt = select.append('option')
                    .attr('value', key)
                    .html(ez3dScene.utils.capitalize(textvalue));
            }
        });
    
        select
            .on('change', function() {
                var convertedValue = this.value;
                if (element.functionOnChange) {
                    self[element.functionOnChange[0]](element.functionOnChange[1]);
                }
            });
    };
    EZGuiManager.prototype.addButtonsBlockWidget = function(elements, container) {
        var self = this;
        var buttonsContainer = container.append('div')
            .attr('class', 'ez3d-panel-buttons ez3d-flex-around-wrap-row');
    
        if (elements.id) {
            buttonsContainer
                .attr('id', elements.id);
        }
    
        if (elements.classed) {
            buttonsContainer
                .classed(elements.classed, true);
        }
        if (elements) {
            self.populateBlocks(elements, buttonsContainer);
        }
    };
    EZGuiManager.prototype.addButtonBlockWidget = function(element, container) {
        var self = this;
        var buttonContainer = container.append('div')
            .attr('class', 'ez3d-btns-container');
        var button = buttonContainer.append('button');
    
        if (element.content) {
            var contentText = self.i18n(element.content);
            button
                .html(contentText);
        }
    
        if (element.id) {
            button
                .attr('id', element.id);
        }
    
        if (element.tooltip) {
            var text = self.i18n(element.tooltip);
            button
                .attr('title', text);
        }
    
        // sunSimulation
        if (element.value) {
            button
                .attr('value', element.value);
        }
    
        button
            .classed(element.classed, true)
            .on('click', function() {
                var convertedValue;
                if (element.changeIcon) {
                    var classname = 'ez3d-new-' + element.changeIcon + '-icon';
                    button
                        .classed(classname, function () {
                            var value = true;
                            if (d3.select(this).classed(classname)) {
                                value = false;
                            }
                            return value;
                        });
                }
                if (element.eventOnClick) {
                    self.eventOnclickExecution(element);
                }
                if (element.functionOnClick) {
                    self[element.functionOnClick[0]](element.functionOnClick[1]);
                }
                if (element.active) {
                    d3.select(this)
                        .classed('active', function() {
                            return self[element.active[0]](element.active[1]);
                        })
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                }
            })
            .on('mouseover', function () {
                // ez3d-button round hover
                if (button.classed('ez3d-button')) {
                    button
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                }
                if (button.classed('ez3d-square-button-green') || button.classed('ez3d-square-button-blue')) {
                    button
                        .style('opacity', '0.7');
                }
            })
            .on('mouseout', function () {
                // ez3d-button round blur
                if (button.classed('ez3d-button')) {
                    button
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
                }
                if (button.classed('ez3d-square-button-green') || button.classed('ez3d-square-button-blue')) {
                    button
                        .style('opacity', '1');
                }
                if (button.classed('active')) {
                    button
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                }
            });
    
        if (element.editable === false || typeof element.editable === 'string') {
            var editable = false;
            if (typeof element.editable === 'string') {
                if (self[element.editable]()) {
                    editable = true;
                }
            }
            if (editable === false) {
                button
                    .attr('disabled', 'disabled')
                    .classed('ez3d-square-disabled', true)
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.disabledbuttons)
                    .style('opacity', '0.9')
                    .style('cursor', 'default');
            }
        }
        if (element.active) {
            button
                .classed('active', function() {
                    return self[element.active[0]](element.active[1]);
                });
        }
        // only roof resume
        if (button.classed('fa-caret-down')) {
            button
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
        }
        if (button.classed('ez3d-button') && !button.classed('ez3d-button-render')) {
            button
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
        }
        if (button.classed('ez3d-square-button-blue')) {
            button
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.second);
        }
        if (button.classed('active') || button.classed('ez3d-square-button-green')) {
            button
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
        }
        if (button.classed('ez3d-square-disabled')) {
            button
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.disabledbuttons)
                .style('opacity', '0.9')
                .attr('disabled', 'disabled');
        }
        if (element.disabled) {
            button
                .attr('disabled', 'disabled')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.disabledbuttons)
                .style('cursor', 'default');
        }
    };
    EZGuiManager.prototype.addInputDateTimeWidget = function(element, container) {
        var self = this;
        var modelAttribute = container.append('div')
            .attr('class', 'ez3d-flex-center');
    
        if (element.classed) {
            modelAttribute.classed(element.classed, true);
        }
    
        var date = modelAttribute.append('div')
            .attr('class', 'ez3d-model-attribute ez3d-flex-baseline-wrap');
    
        date.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        var time = modelAttribute.append('div')
            .attr('class', 'ez3d-model-attribute ez3d-flex-baseline-wrap');
    
        time.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        if (element.label) {
            var text = self.i18n(element.label);
            date.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
        }
        if (element.label2) {
            var text2 = self.i18n(element.label2);
            time.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text2);
        }
    
        var inputEvent = (element.fromWidget) ? 'change' : 'blur';
        var input = date.append('input')
            .attr('type', 'date')
            .attr('min', '2000-04-01')
            .attr('max', '2080-04-30')
            .attr('value', (element.value) ? ez3dScene.utils.formatDateTime(element.value)[0] : '')
            .on(inputEvent, function () {
                if (element.functionOnChange) {
                    self[element.functionOnChange[0]](element.functionOnChange[1]);
                }
            });
        var inputTime = time.append('input')
            .attr('type', 'time')
            .attr('value', (element.value) ? ez3dScene.utils.formatDateTime(element.value)[1] : '')
            .on(inputEvent, function () {
                if (element.functionOnChange) {
                    self[element.functionOnChange[0]](element.functionOnChange[1]);
                }
            });
    
        var editable = true;
        if (typeof element.editable === 'string') {
            editable = ez3dScene.active[element.editable]();
        }
        if (element.editable === false) {
            editable = false;
        }
        if (editable === false) {
            input
                .attr('disabled', 'disabled')
                .classed('ez3d-disabled', true);
    
            inputTime
                .attr('disabled', 'disabled')
                .classed('ez3d-disabled', true);
        }
        if (element.id) {
            input
                .attr('id', element.id);
            inputTime
                .attr('id', element.id + 'Time');
        }
    };
    EZGuiManager.prototype.addBooleanInputBlockWidget = function(element, container) {
        const modelAttribute = container.append('div')
            .attr('class', 'ez3d-model-attribute ez3d-flex-baseline-wrap');
    
        modelAttribute.append('div')
            .attr('class', 'ez3d-circle-block')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        if (element.id) {
            modelAttribute
                .attr('id', element.id);
        }
    
        if (element.label) {
            const text = this.i18n(element.label);
            const label = modelAttribute.append('div')
                .attr('class', 'ez3d-attr_label')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray)
                .html(text);
    
            if (element.tooltipBtn) {
                label.classed('new-width-with-tooltip', true);
                this.addTooltip({container: modelAttribute, title: element.tooltipBtn.title, content: element.tooltipBtn.content});
            }
        }
    
        const switchLabel = modelAttribute.append('label')
            .attr('class', 'ez3d-switch');
    
        const input = switchLabel.append('input')
            .attr('type', 'checkbox')
            .classed('ez3d-switch-input', true);
    
        input.node().checked = this.getValueFromJsonElement(element) || false;
    
        let convertedValue = input.node().checked;
    
        const slider = switchLabel
            .append('div')
            .attr('class', 'slider round');
    
        const color = d3.hsl(d3.color(ez3dScene.layoutRules.defaultColors.cssColors.second).brighter(2.74));
        color.s = 0.34;
        color.l = 0.70;
    
        const labelTrue = this.i18n(element.labelTrue || 'yes');
        const labelFalse = this.i18n(element.labelFalse || 'no');
    
        slider.append('div')
            .attr('class', 'ez3d-slider-cube')
            .style('background-color', 'white');
    
        slider.append('div')
            .attr('class', 'ez3d-label-false')
            .html(labelFalse);
    
        slider.append('div')
            .attr('class', 'ez3d-label-true')
            .html(labelTrue);
    
        if (convertedValue === false) {
            if (element.labelTrue && element.labelTrue !== 'true') {
                switchLabel.select('.slider')
                    .style('background-color', color)
                    .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
            } else {
                switchLabel.select('.slider')
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray)
                    .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
            }
            // labels
            slider.select('.ez3d-label-true')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.darkgray);
            slider.select('.ez3d-label-false')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
        } else {
            switchLabel.select('.slider')
                .style('background-color', color)
                .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
            // labels
            slider.select('.ez3d-label-true')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
            slider.select('.ez3d-label-false')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.darkgray);
        }
    
        let editable = true;
        if (typeof element.editable === 'string') {
            editable = ez3dScene.active[element.editable]();
        }
        if (element.editable === false) {
            editable = false;
        }
        if (editable === false) {
            input
                .attr('disabled', 'disabled')
                .classed('ez3d-disabled', true);
        }
    
        input
            .on('change', () => {
                convertedValue = input.node().checked;
                // const id = ez3dScene.active.id;
                // const color = d3.hsl(d3.color(ez3dScene.layoutRules.defaultColors.cssColors.second).brighter(2.74));
                // color.s = 0.34;
                // color.l = 0.70;
                // aqui los dos seleccionado
                if (convertedValue === true) {
                    switchLabel.select('.slider')
                        .style('background-color', color);
                    // labels
                    slider.select('.ez3d-label-true')
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
                    slider.select('.ez3d-label-false')
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.darkgray);
                } else {
                    switchLabel.select('.slider')
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
    
                    // labels
                    slider.select('.ez3d-label-true')
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.darkgray);
                    slider.select('.ez3d-label-false')
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
                }
                if (element.id === 'deleteBuildingsAndTrees') {
                    var inputAddress = d3.select('#pac-input')
                        .attr('disabled', function () {
                            var disabled;
                            if (convertedValue === true) {
                                disabled = null;
                            } else {
                                disabled = 'disabled';
                            }
                            return disabled;
                        });
                    inputAddress.node().focus();
                }
                // snapshots
                if (element.functionOnChange) {
                    this.functionOnchangeExecution(element);
                }
            });
        return input;
    };
    
    
    // Navigation blocks
    /**
     *
     *  * Method to generate nav menu
     * @param {object} elements Tabs of the nav menu
     */
    EZGuiManager.prototype.addNavigationBlock = function(elements) {
        var self = this;
    
        // Navigator
        var nav = d3.select('#ez3d-nav');
    
        // clean nav
        nav.selectAll('*').remove();
    
        nav.style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
    
        Object.keys(elements).forEach(function (i) {
            var element = elements[i];
            var tab = nav.append('li')
                .classed('ez3d-main-tab ez3d-flex-center-center-column', true)
                .on('click', function () {
                    if (element.eventOnClick) {
                        self.eventOnclickExecution(element);
                    }
                });
    
            if (element.name) {
                tab.attr('id', 'ez3d-' + element.name + '-tab');
            }
    
            if (element.className) {
                tab.classed(element.className, true);
            }
    
            if (element.text) {
                tab.html(self.i18n(element.text));
            }
    
            // Check active tab and set styles
            if (self.activeTab.indexOf(element.name.split('-')[0]) >= 0) {
                tab.classed('ez3d-active-nav', true);
                tab.style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
                tab.style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
            } else {
                tab.style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
            }
        });
    };
    
    /**
     *
     *  * Method to create an object selector
     * @param {object} elements - Selectors data
     * @param {object} container - It's going to host the objects selector
     */
    EZGuiManager.prototype.addImageNavigator = function(elements) {
        var self = this;
    
        if (d3.select('#objects-panel_ez3dInfoPanel').select('.objects-selector')) {
            d3.select('.objects-selector').remove('*');
        }
    
        var nav = d3.select('#objects-panel_ez3dInfoPanel').append('div')
            .classed('objects-selector ez3d-flex-wrap-space-around', true);
    
    
        Object.keys(elements).forEach(function(i) {
            var element = elements[i];
    
            // Main container
            var objContainer = nav.append('div')
                .classed('ez3d-main-tab ez3d-flex-center-center-column', true);
            if (element.name) {
                objContainer.attr('id', 'ez3d-aside-' + element.name + '-tab-selector');
            }
    
            // Clickable container
            var clickableContainer = objContainer.append('div')
                .attr('id', 'ez3d-' + element.name + '-tab')
                .style('cursor', 'pointer')
                .on('click', function() {
                    if (element.eventOnClick) {
                        self.eventOnclickExecution(element);
                    }
                });
    
            // Image
            var image = clickableContainer.append('div');
            if (element.image.id) {
                image.attr('id', element.image.id);
            }
            if (element.image.className) {
                image.attr('class', element.image.className + ' ez3d-img-obj');
            }
    
            // Title
            var title = clickableContainer.append('h2')
                .attr('class', 'ez3d-title');
            if (element.text) {
                title.html(self.i18n(element.text));
            }
    
            // Button
            if (element.button) {
                self.populateBlocks(element, objContainer);
            }
    
            // Set styles and activate the default Objects panel or the active Objects panel tab
            if (self.activeTabObject === element.name || (!self.activeTabObject && element.defaultPanel)) {
                objContainer.classed('ez3d-active-nav', true);
                image.style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                title.style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
            } else {
                image.style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
                title.style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
            }
        });
    };
    
    /**
     *
     *  * Method to create a selector for custom building shapes.
     * @param {object} elements - Selectors data
     * @param {object} container - It's going to host the objects selector
     */
    EZGuiManager.prototype.addBuildingShapesImageNavigator = function(elements) {
        var self = this;
        const container = d3.select('#buildingShapes-panel_ez3dInfoPanel');
    
        if (container.select('.buildingShapes-selector').node()) {
            container.select('.buildingShapes-selector').selectAll('*').remove();
        } else {
            container.append('div')
                .classed('buildingShapes-selector ez3d-flex-wrap-space-around', true);
        }
        const nav = container.select('.buildingShapes-selector');
    
        Object.keys(elements).forEach(function(i) {
            var element = elements[i];
    
            // Main container
            var objContainer = nav.append('div')
                .classed('ez3d-main-tab ez3d-flex-center-center-column', true);
            if (element.name) {
                objContainer.attr('id', 'ez3d-aside-' + element.name + '-tab-selector');
            }
    
            // Clickable container
            var clickableContainer = objContainer.append('div')
                .attr('id', 'ez3d-' + element.name + '-tab')
                .style('cursor', 'pointer')
                .on('click', function() {
                    if (element.eventOnClick) {
                        self.eventOnclickExecution(element);
                    }
                });
    
            // Image
            var image = clickableContainer.append('div');
            if (element.image.id) {
                image.attr('id', element.image.id);
            }
            if (element.image.className) {
                image.attr('class', element.image.className + ' ez3d-img-obj');
            }
    
            // Title
            var title = clickableContainer.append('h2')
                .attr('class', 'ez3d-title');
            if (element.text) {
                title.html(self.i18n(element.text));
            }
    
            // Button
            if (element.button) {
                self.populateBlocks(element, objContainer);
            }
    
            // Set styles and activate the default Objects panel or the active Objects panel tab
            if (self.activeTabObject === element.name || (!self.activeTabObject && element.defaultPanel)) {
                objContainer.classed('ez3d-active-nav', true);
                image.style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                title.style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
            } else {
                image.style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
                title.style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
            }
        });
    };
    
        /**
     * Generate content in each building index.
     * @param {object} block    Building index div element.
     * @param {object} data     EZModelBuilding.
     */
    EZGuiManager.prototype.populateBlock = function(block, data) {
        var ez3dViewport = self.ez3dViewport;
        block.selectAll('*').remove();
        var container = '';
        if (ez3dScene.projectType === 'simple') {
            container = d3.select('#svgSimple_system_' +
                data.roofs[0].areas[0].subareas[0].system.id).node();
        } else {
            container = d3.select('#svgProject_building_' + data.id).select('.building-shape').node();
        }
        block.append('h1')
            .attr('class', 'building-index')
            .html(function() {
                var index = data.index;
                var indexString;
                if (index > 9) {
                    indexString = 'B' + index;
                } else {
                    indexString = 'B' + 0 + index;
                }
                return indexString;
            })
            .on('mouseover', function () {
                const svgContainerName = (ez3dScene.projectType === 'simple') ? 'svgSimple' : 'svgProject';
                const buildingId = d3.select(this).datum().id;
                // svg hover
                d3.select('#' + svgContainerName + '-L1_building_' + buildingId).select('.building-shape')
                    .call(styleBuildings, ez3dViewport.svgElements[svgContainerName], 'hover');
            })
            .on('mouseout', function () {
                const svgContainerName = (ez3dScene.projectType === 'simple') ? 'svgSimple' : 'svgProject';
                const building = d3.select(this).datum();
                // svg hover
                d3.select('#' + svgContainerName + '-L1_building_' + building.id).select('.building-shape')
                    .call(styleBuildings, ez3dViewport.svgElements[svgContainerName], building.style.colorState);
            });
    
        block.append('p')
            .attr('class', 'ez3d-index-block-label')
            .html(function() {
                return data.name;
            })
            .on('click', function() {
                ee.emitEvent('EZModelScene_setActiveListener', [{args: data.id, updateRender: false}]);
            });
    
        block.append('p')
            .attr('class', 'ez3d-index-block-energy-info')
            .html(function() {
                return data.systemInfo.modules + ' ' + ez3dViewport.ez3dGuiManager.i18n('modules').toUpperCase() +
                    ' - ' + data.systemInfo.power + 'WP';
            })
            .on('click', function() {
                ee.emitEvent('EZModelScene_setActiveListener', [{args: data.id, updateRender: false}]);
            });
    };
    
    /**
     * Function to draw all building indexes.
     * @param {array} viewdata  Array of buildings in the current page.
     */
    EZGuiManager.prototype.drawBuildingIndex = function (viewdata) {
        var self = this;
        var ez3dViewport = self.ez3dViewport;
    
        d3.select('#ez3d-buildings-list').selectAll('*').remove();
        var blocks = d3.select('#ez3d-buildings-list').selectAll('div')
            .data(viewdata);
    
        blocks
            .enter()
            .append('div')
            .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray)
            .style('background-image', function(d) {
                var buildingColor = (d.color) ? d.color : ez3dScene.layoutRules.defaultColors.buildingColors.path.standard.fill;
                var mainColor = '';
                if (d.id === ez3dScene.activeBuilding.id) {
                    mainColor = ez3dScene.layoutRules.defaultColors.cssColors.first;
                } else {
                    mainColor = ez3dScene.layoutRules.defaultColors.cssColors.lightgray;
                }
                return 'linear-gradient(' + mainColor + ' 70.9%, ' + buildingColor + ' 71%, ' + buildingColor + ')';
            })
            .attr('id', function(d) {
                return 'ez3d-block-index-' + d.index;
            })
            .attr('class', function(d) {
                self.populateBlock(d3.select(this), d);
                var classString = 'ez3d-index-block';
                if (d.id === ez3dScene.activeBuilding.id) {
                    classString = 'ez3d-index-block activeBuilding';
                    d3.select(this)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first)
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
                }
                return classString;
            })
            .on('click', function(d) {
                ez3dViewport.ez3dGuiManager.updateNavigation = true;
                ee.emitEvent('EZModelScene_setActiveListener', [{args: d.id, updateRender: {draw2d: false, draw3d: false, updatePanels: false, fitRange: true}}]);
            })
            .on('mouseover', function(d) {
                var buildingColor = (d.color) ? d.color : ez3dScene.layoutRules.defaultColors.buildingColors.path.standard.fill;
                var mainColor = '';
                if (d.id === ez3dScene.activeBuilding.id) {
                    mainColor = ez3dScene.layoutRules.defaultColors.cssColors.first;
                } else {
                    mainColor = ez3dScene.layoutRules.defaultColors.cssColors.second;
                }
                d3.select(this)
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray)
                    .style('background-image', 'linear-gradient(to right, ' + mainColor + ' 90.9%, ' + buildingColor + ' 91%, ' + buildingColor + ')');
            })
            .on('mouseout', function(d) {
                var buildingColor = (d.color) ? d.color : ez3dScene.layoutRules.defaultColors.buildingColors.path.standard.fill;
                var mainColor = '';
                var fontColor = '';
                if (d.id === ez3dScene.activeBuilding.id) {
                    mainColor = ez3dScene.layoutRules.defaultColors.cssColors.first;
                    fontColor = ez3dScene.layoutRules.defaultColors.cssColors.lightgray;
                } else {
                    mainColor = ez3dScene.layoutRules.defaultColors.cssColors.lightgray;
                    fontColor = ez3dScene.layoutRules.defaultColors.cssColors.second;
                }
                d3.select(this)
                    .style('color', fontColor)
                    .style('background-image', 'linear-gradient(' + mainColor + ' 70.9%, ' + buildingColor + ' 71%, ' + buildingColor + ')');
            });
    
        blocks
            .attr('id', function(d) {
                return 'ez3d-block-index-' + d.index;
            })
            .attr('class', function(d) {
                self.populateBlock(d3.select(this), d);
                var classString = 'ez3d-index-block';
                if (d.id === ez3dScene.activeBuilding.id) {
                    classString = 'ez3d-index-block activeBuilding';
                }
                return classString;
            })
            .on('click', function(d) {
                ee.emitEvent('EZModelScene_setActiveListener', [{args: d.id, updateRender: false}]);
            });
    
        blocks.exit()
            .remove();
    };
    
    /**
     * Function to draw pagination buttons.
     * @param {array} viewdata  Array of buildings in the current page.
     * @param {array} data      Array of buildings objects.
     */
    EZGuiManager.prototype.drawPaginationButtons = function(viewdata, data) {
        var self = this;
        var pagination = d3.select('#ez3d-building-index-pagination');
    
        pagination.selectAll('div').remove();
    
        // First buildings page
        if (self.buildingIndexPage !== 1) {
            pagination
                .append('div')
                .attr('id', 'index-pagination-prev')
                .on('click', function() {
                    self.buildingIndexPage--;
                    viewdata = data.slice((self.buildingIndexPage - 1) * self.buildingIndexBarnumber, self.buildingIndexPage * self.buildingIndexBarnumber);
                    self.drawBuildingIndex(viewdata);
                    self.drawPaginationButtons(viewdata, data);
                })
                .append('i')
                .attr('class', 'fa fa-chevron-up');
        }
    
        // Pagination info
        if (self.buildingIndexBarnumber < ez3dScene.buildings.length) {
            pagination
                .append('div')
                .attr('id', 'index-pagination-info')
                .style('display', 'inherit')
                .append('div')
                .html(self.buildingIndexPage + '/' + Math.round(ez3dScene.buildings.length / self.buildingIndexBarnumber));
        }
    
        // Second buildings page
        if ((self.buildingIndexPage * self.buildingIndexBarnumber) < data.length) {
            pagination
                .append('div')
                .attr('id', 'index-pagination-next')
                .style('display', 'inline-block')
                .on('click', function() {
                    if ((self.buildingIndexPage * self.buildingIndexBarnumber) < data.length) {
                        self.buildingIndexPage++;
                        viewdata = data.slice((self.buildingIndexPage - 1) * self.buildingIndexBarnumber, self.buildingIndexPage * self.buildingIndexBarnumber);
                        self.drawBuildingIndex(viewdata);
                        self.drawPaginationButtons(viewdata, data);
                    }
                })
                .append('i')
                .attr('class', 'fa fa-chevron-down');
        }
    };
    
    /**
     * Function to calculate the total height of the building index size.
     */
    EZGuiManager.prototype.calculateBuildingIndexSize = function() {
        // var canvas = document.getElementById('ez3d-canvas');
    
        // 6 blocks of 40px
        // add block + pageup + pageinfo + pagedown + 2 empty margin block
        const availableHeight = window.innerHeight - (6 * 40);
    
        // Check how many building index fit in each building page
        let buildingIndexBarnumber = Math.round(availableHeight / 40);
        if (buildingIndexBarnumber < 1) {
            buildingIndexBarnumber = 1;
        }
    
        this.buildingIndexBarnumber = buildingIndexBarnumber;
    };
    
    /**
     * Get the building page of the active building.
     */
    EZGuiManager.prototype.calculateBuildingPageInIndex = function() {
        var self = this;
    
        if (ez3dScene.activeBuilding.id && ez3dScene.buildings.length > 0) {
            // Calculate in which page of the building index is the new active
            var index;
            ez3dScene.buildings.forEach(function(building, i) {
                if (building.id === ez3dScene.activeBuilding.id) {
                    index = i + 1;
                }
            });
            self.buildingIndexPage = Math.ceil(index / self.buildingIndexBarnumber);
        }
    };
    
        /**
     * Function to create button to add buildings and total power info.
     * @param {object} scene    ez3dScene object.
     */
    EZGuiManager.prototype.generateBuildingIndex = function (scene) {
        var self = this;
        var data = scene.buildings;
        var viewdata = data.slice((self.buildingIndexPage - 1) * self.buildingIndexBarnumber, self.buildingIndexPage * self.buildingIndexBarnumber);
    
        d3.select('#ez3d-index-add-building').selectAll('.ez3d-index-add-symbol').remove();
        d3.select('#ez3d-index-add-building')
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.white)
            .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
            .style('display', function() {
                if (scene.layoutRules.scenePreferences.maxAllowedBuildings > 0) {
                    if (scene.buildings.length >= scene.layoutRules.scenePreferences.maxAllowedBuildings) {
                        return 'none';
                    }
                    return 'inherit';
                }
                return 'inherit';
            })
            .on('click', function() {
                ee.addOnceListener('createBuildingAfterWidget', (eventData) => {
                    self.updateNavigation = true;
                    if (ez3dViewport.svgElements.svgArea) {
                        self.closeSubareaView();
                    }
                    ee.emitEvent(eventData.args, [{updateRender: {draw2d: true, draw3d: true, updatePanels: true, fitRange: true}}]);
                });
                if (ez3dScene.layoutRules.scenePreferences.enableBuildingShapes) {
                    ee.emitEvent('widget_notification', [{
                        'name': 'create-building',
                        'title': 'buildingCreationTitle',
                        'content': 'buildingCreationMessage',
                        'optionsButtons': {
                            'customShapes': {
                                'eventOnClick': ['EZModelScene_createBuildingShapeListener',
                                    undefined,
                                    false,
                                    {draw2d: true, draw3d: false, updatePanels: true, fitRange: false},
                                    false],
                                'content': 'customShapes',
                                'imageClassName': 'ez3d-customShapes-icon',
                            },
                            'drawShape': {
                                'eventOnClick': ['EZModelScene_createBuildingListener',
                                    undefined,
                                    false,
                                    {draw2d: true, draw3d: false, updatePanels: true, fitRange: false},
                                    false],
                                'content': 'drawShape',
                                'imageClassName': 'ez3d-drawShape-icon',
                            },
                        }
                    }]);
                } else {
                    ee.emitEvent('createBuildingAfterWidget', [{args: 'EZModelScene_createBuildingListener'}]);
                }
            })
            .on('mouseover', function() {
                d3.select(this)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.darkgray);
            })
            .on('mouseout', function() {
                d3.select(this)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.white);
            })
            .append('i')
            .attr('class', 'ez3d-index-add-symbol fa fa-plus');
    
        // add total power
        if (ez3dScene.layoutRules.scenePreferences.showTotalPower) {
            const text = self.i18n({
                text: 'totalModulesInfo',
                vars: {num: ez3dScene.systemInfo.totalModules.toLocaleString(), power: ez3dScene.systemInfo.totalPower.toLocaleString()}
            });
            d3.select('#ez3d-total-scene-power')
                .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first)
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                .html(text);
        }
    
        if (ez3dScene.layoutRules.scenePreferences.readOnly) {
            d3.select('#ez3d-index-add-building')
                .classed('ez3d-read-only', true);
        }
    
        self.calculateBuildingPageInIndex();
        self.drawBuildingIndex(viewdata);
        self.drawPaginationButtons(viewdata, data);
    };
    
        /**
     * returns true if enablePlayer should be false
     * @param  {boolean} renderer ez3dScene.layoutRules.scenePreferences.activeRenderer
     * @param  {boolean} mapper   ez3dScene.layoutRules.scenePreferences.activeMapper
     * @return {boolean} returns true if enablePlayer should be false
     */
    EZGuiManager.prototype.checkRendererAndMapper = function (renderer, mapper) {
        return (renderer === false && mapper === false);
    };
    
    // PLAYER
    // Active renderer
    EZGuiManager.prototype.activeRenderer = function () {
        var ez3dViewport = this.ez3dViewport;
        if (ez3dScene.layoutRules.scenePreferences.enablePlayer === true) {
            var value = !(ez3dScene.layoutRules.scenePreferences.activeRenderer);
    
            ee.addOnceListener('setAttribute_Finished', function () {
                setTimeout(function() {
                    if (ez3dViewport.ez3dPlayer) {
                        ez3dViewport.ez3dPlayer.activateRenderer(value);
                        if (ez3dViewport.ez3dGuiManager.widgets.indexOf('ez3d-widget-snapshot') === -1) {
                            if (ez3dViewport.ez3dPlayer.getMode() === '2d' && value === true) {
                                ez3dViewport.ez3dPlayer.fitToBounds(ez3dViewport.svgElements.svgProject.center, ez3dViewport.svgElements.svgProject.widthInMeters);
                            }
                            ee.emitEvent('updateRender',
                                [{draw2d: true, draw3d: true, updatePanels: true, fitRange: true}]);
                        } else {
                            ee.emitEvent('updateRender',
                                [{draw2d: true, draw3d: false, updatePanels: true, fitRange: false}]);
                        }
                    }
                }, 1000);
    
                d3.select('.ez3d-render-icon').classed('disabled-render', !value);
            });
            ee.emitEvent('EZModelScene_setAttributeListener', [{
                args: ['', 'layoutRules.scenePreferences.activeRenderer', value],
                updateRender: false,
                undo: false,
                notifications: false
            }]);
    
            // u disabled class
            d3.select('.ez3d-render-icon').classed('disabled-render', !value);
        }
    };
    // Active mapper
    EZGuiManager.prototype.activeMapper = function () {
        var ez3dViewport = this.ez3dViewport;
        if (ez3dScene.layoutRules.scenePreferences.enablePlayer === true) {
            var value = !(ez3dScene.layoutRules.scenePreferences.activeMapper);
    
            ee.addOnceListener('setAttribute_Finished', function () {
                if (ez3dViewport.ez3dPlayer) {
                    ez3dViewport.ez3dPlayer.activateMapper(value);
                }
                // @todo fix buttons update
                ee.emitEvent('mapper-changed');
    
                setTimeout(function() {
                    if (ez3dViewport.ez3dPlayer && ez3dViewport.ez3dPlayer.getMode() === '2d' && value === true) {
                        ez3dViewport.ez3dPlayer.fitToBounds(ez3dViewport.svgElements.svgProject.center, ez3dViewport.svgElements.svgProject.widthInMeters);
                    }
                    ee.emitEvent('updateRender',
                        [{draw2d: true, draw3d: false, updatePanels: true, fitRange: false}]);
                }, 1000);
    
                d3.select('.ez3d-mapper-icon').classed('disabled-render', !value);
            });
            ee.emitEvent('EZModelScene_setAttributeListener', [{
                args: ['', 'layoutRules.scenePreferences.activeMapper', value],
                updateRender: false,
                undo: false,
                notifications: false
            }]);
    
            // u disabled class
            d3.select('.ez3d-mapper-icon').classed('disabled-render', !value);
            // If mapper is off, set initial styles properties
            d3.selectAll('.ez3d-attribution-container').style('display', (value === true) ? '' : 'none');
        }
    };
    // Get class to renderer button
    EZGuiManager.prototype.getRenderButtonClass = function () {
        var classed = 'ez3d-button ez3d-render-icon';
        if (!ez3dViewport.ez3dPlayer) {
            classed += ' ez3d-hidden';
        } else if (ez3dScene.layoutRules.scenePreferences.activeRenderer === false) {
            classed += ' disabled-render';
        }
        return classed;
    };
    
    
    // UNDO & REDO
    // Undo
    EZGuiManager.prototype.onUndoButtonClick = function () {
        return ez3dScene.history.executeUndo();
    };
    // Redo
    EZGuiManager.prototype.onRedoButtonClick = function () {
        return ez3dScene.history.executeRedo();
    };
    // Disable/Enable undo/redo from preferences panel
    EZGuiManager.prototype.enableDisableUndoRedo = function () {
        if (ez3dScene.layoutRules.scenePreferences.enableUndoRedo === false) {
            ez3dScene.history.clearQueue();
        }
    };
    
    
    // 2D/3D
    EZGuiManager.prototype.perspective = function () {
        var self = this;
        var ez3dViewport = self.ez3dViewport;
        var perspective;
    
        if (self.change3d === true) {
            if (ez3dScene.layoutRules.scenePreferences.activeRenderer === false) {
                this.activeRenderer();
            }
            if (ez3dScene.layoutRules.scenePreferences.activeMapper === false) {
                this.activeMapper();
            }
            // if (!ez3dScene.maps || ez3dScene.maps && ez3dScene.maps.length === 0) {
            //     if (ez3dScene.layoutRules.scenePreferences.activeMapper === false) {
            //         this.activeMapper();
            //     }
            // }
        }
        setTimeout(function() {
            if (self.change3d === true) {
                if (ez3dViewport.svgElements.svgArea) {
                    ez3dViewport.ez3dGuiManager.closeSubareaView();
                }
                d3.select('#svgContainer-svgProject')
                    .style('opacity', 0)
                    .style('pointer-events', 'none');
                self.change3d = false;
                if (ez3dViewport.ez3dPlayer) {
                    ez3dViewport.ez3dPlayer.set3d();
                }
                perspective = '3d';
                // Create perspective shortcuts widget
                self.widgetConstructor({
                    'name': 'perspective-shortcuts',
                    'title': 'perspectiveShortcutsWidgetTitle',
                    'eventMinimize': true,
                    'functionOnCreation': ['perspectiveShortcutsWidget', '']
                });
            } else {
                d3.select('#svgContainer-svgProject')
                    .style('opacity', 1)
                    .style('pointer-events', 'inherit');
                if (ez3dViewport.ez3dPlayer) {
                    ez3dViewport.ez3dPlayer.set2d();
                }
                self.change3d = true;
                perspective = '2d';
                // Remove perspective shortcuts widget
                self.removeWidget('ez3d-widget-perspective-shortcuts');
            }
            self.toggleAside(perspective);
            ee.emitEvent('updatePanels');
        }, 1100);
    };
    // Get class to perspective button
    EZGuiManager.prototype.getPerspectiveButtonClass = function () {
        var ez3dViewport = this.ez3dViewport;
        var classed = 'ez3d-button ez3d-perspective-icon';
        if (ez3dViewport.ez3dPlayer) {
            if (ez3dViewport.ez3dPlayer.getMode() === '3d') {
                classed += ' ez3d-new-perspective-icon';
            }
        } else {
            classed += ' ez3d-hidden';
        }
        return classed;
    };
    
    
    // FullScreen
    EZGuiManager.prototype.fullScreen = function () {
        var self = this;
        var container = d3.select('#ez3d-main-container');
        if (self.isFullScreen) {
            if (ez3dScene.layoutRules.scenePreferences.manageFullscreen === true) {
                container
                    .style('width', self.containerProperties.width)
                    .style('height', self.containerProperties.height)
                    .style('left', self.containerProperties.top)
                    .style('top', self.containerProperties.left)
                    .style('position', 'inherit');
            }
            if (navigator.userAgent.toLowerCase().indexOf('chrome') > -1) {
                document.webkitExitFullscreen();
            } else {
                document.mozCancelFullScreen();
            }
            self.isFullScreen = false;
            ee.emitEvent('ez3d-fullscreen-disabled');
        } else {
            if (ez3dScene.layoutRules.scenePreferences.manageFullscreen === true) {
                self.containerProperties.width = container.style('width');
                self.containerProperties.height = container.style('height');
                self.containerProperties.left = container.style('left');
                self.containerProperties.top = container.style('top');
                container
                    .style('width', '100%')
                    .style('height', '100%')
                    .style('left', '0px')
                    .style('top', '0px')
                    .style('position', 'fixed');
            }
            if (navigator.userAgent.toLowerCase().indexOf('chrome') > -1) {
                document.body.webkitRequestFullscreen();
            } else {
                var elem = document.getElementsByTagName('body')[0];
                elem.mozRequestFullScreen();
            }
            self.isFullScreen = true;
            ee.emitEvent('ez3d-fullscreen-enabled');
        }
    };
    
    
    // VIEWPORT
    // Open/close aside button
    EZGuiManager.prototype.toggleAside = function (perspective) {
        var self = this;
        var ez3dViewport = self.ez3dViewport;
        var btn = d3.select('.ez3d-foldable-left-icon');
        var perspectiveWidget = d3.select('#ez3d-widget-perspective-shortcuts');
    
        if (d3.select('#ez3d-aside').classed('ez3d-aside-show') && perspective === '' || perspective === '3d') {
            d3.select('#ez3d-aside')
                .classed('ez3d-aside-show', false)
                .classed('ez3d-aside-hidden', true);
    
            d3.select('#ez3d-viewport')
                .classed('ez3d-viewport-aside', false)
                .classed('ez3d-viewport-noaside', true);
    
            btn.classed('ez3d-new-foldable-left-icon', true);
    
            if (!perspectiveWidget.empty() && !perspectiveWidget.classed('ez3d-minimized-widget') && !self.ez3dViewport.snapOptionsMenu.hasBeenMoved) {
                perspectiveWidget.style('left', '20px');
            }
        } else if (d3.select('#ez3d-aside').classed('ez3d-aside-hidden') && perspective === '' || perspective === '2d') {
            d3.select('#ez3d-aside')
                .classed('ez3d-aside-show', true)
                .classed('ez3d-aside-hidden', false);
    
            d3.select('#ez3d-viewport')
                .classed('ez3d-viewport-aside', true)
                .classed('ez3d-viewport-noaside', false);
    
            btn.classed('ez3d-new-foldable-left-icon', false);
            if (!perspectiveWidget.empty() && !perspectiveWidget.classed('ez3d-minimized-widget')) {
                perspectiveWidget.style('left', self.ez3dViewport.snapOptionsMenu.openedPosition.left);
            }
        }
    
        setTimeout(function () {
            if (ez3dScene.layoutRules.scenePreferences.enablePlayer) {
                if (ez3dViewport.ez3dPlayer && ez3dViewport.ez3dPlayer.getMode() === '2d') {
                    ee.emitEvent('updateRender',
                        [{draw2d: true, draw3d: true, updatePanels: false, fitRange: true}]);
                }
            }
        }, 500);
    
    };
    // Open/close right viewport button
    EZGuiManager.prototype.toggleViewportRight = function (closeRightCanvas, update) {
        var self = this;
    
        if (update === undefined) {
            update = true;
        }
        var btn = d3.select('#ez3d-foldable-right-button');
        var toggleButton = d3.select('#ez3d-viewport-right').classed('ez3d-viewport-right-hidden');
    
        // street view
        if (closeRightCanvas === true) {
            d3.select('#ez3d-viewport-left')
                .classed('ez3d-viewport-left-full', true);
    
            d3.select('#ez3d-viewport-right')
                .classed('ez3d-viewport-right-hidden', true);
    
            btn.classed('ez3d-new-foldable-right-icon', false);
        } else if (closeRightCanvas === false) {
            d3.select('#ez3d-viewport-left')
                .classed('ez3d-viewport-left-full', false);
    
            d3.select('#ez3d-viewport-right')
                .classed('ez3d-viewport-right-hidden', false);
    
            btn.classed('ez3d-new-foldable-right-icon', true);
    
            if (ez3dScene.layoutRules.scenePreferences.viewportMode === 4 && window.google !== undefined) {
                ez3dViewport.setStreetView(ez3dScene.projectCenter.lat, ez3dScene.projectCenter.lng);
            }
        } else if (toggleButton === true) {
            d3.select('#ez3d-viewport-left')
                .classed('ez3d-viewport-left-full', false);
    
            d3.select('#ez3d-viewport-right')
                .classed('ez3d-viewport-right-hidden', false);
    
            btn.classed('ez3d-new-foldable-right-icon', true);
    
            if (ez3dScene.layoutRules.scenePreferences.viewportMode === 4 && window.google !== undefined) {
                ez3dViewport.setStreetView(ez3dScene.projectCenter.lat, ez3dScene.projectCenter.lng);
            }
        } else {
            d3.select('#ez3d-viewport-left')
                .classed('ez3d-viewport-left-full', true);
    
            d3.select('#ez3d-viewport-right')
                .classed('ez3d-viewport-right-hidden', true);
    
            btn.classed('ez3d-new-foldable-right-icon', false);
        }
    
        if (update) {
            setTimeout(function() {
                self.ez3dViewport.updateContainers();
            }, 250);
        }
    };
    // Go to index section/Change doc type in documentation (viewportMode === 7)
    EZGuiManager.prototype.viewportDocButtons = function(attr) {
        var iframe = d3.select('#ez3d-documentation').select('iframe').node();
        if (attr === 'index') {
            if (iframe.src.indexOf('#') > -1) {
                iframe.src = iframe.src;
            } else {
                // First time clicked on the button
                iframe.src += '#TOC';
            }
        } else if (attr === 'docType') {
            if (iframe.src.indexOf('README-userDoc.html') > -1) {
                // From user's to integrator's
                iframe.src = 'https://doc-layout.ezzing.com/README.html';
                d3.select('#ez3d-button-changeDocType').node().innerHTML = this.i18n('Go to users doc');
            } else {
                // From integrator's to user's
                iframe.src = 'https://doc-layout.ezzing.com/README-userDoc.html';
                d3.select('#ez3d-button-changeDocType').node().innerHTML = this.i18n('Go to integrators doc');
            }
        }
    };
    // Reload or save json editor (viewportMode === 5)
    EZGuiManager.prototype.jsonEditorFunction = function (operation) {
        var json = '';
        var property = d3.select('#jsonEditorFileSelector').node().value;
        if (operation === 'reload') {
            json = ez3dScene.layoutRules[property];
            ez3dViewport.setInJsonEditor(json);
        } else if (operation === 'save') {
            json = ez3dViewport.getInJsonEditor();
            ez3dScene.layoutRules[property] = json;
            this.updateEZGuiManager();
            d3.select('#ez3d-foldable-right-button').dispatch('click');
        }
    };
    // Get class to viewport right button
    EZGuiManager.prototype.getFoldableViewportRightButtonClass = function () {
        var classed = 'ez3d-button ez3d-foldable-right-icon';
        if (d3.select('#ez3d-viewport-right').classed('ez3d-viewport-right-hidden') === false) {
            classed += ' ez3d-new-foldable-right-icon';
        }
        return classed;
    };
    
    
    // 3D VIEW
    // Zoom in
    EZGuiManager.prototype.zoomIn = function() {
        var ez3dViewport = this.ez3dViewport;
        var svg = undefined;
        if (ez3dViewport.ez3dPlayer) {
            var playerMode = ez3dViewport.ez3dPlayer.getMode();
            if (playerMode === 'showcase' || playerMode === '3d') {
                var playerZoom = ez3dViewport.ez3dPlayer.getZoom();
                ez3dViewport.ez3dPlayer.setZoom(playerZoom + 10);
            }
        }
        if (ez3dViewport.svgElements.svgProject) {
            svg = ez3dViewport.svgElements.svgProject.svg;
            ez3dViewport.svgElements.svgProject.zoom.scaleBy(svg, 2);
        }
        if (ez3dViewport.svgElements.svgArea) {
            svg = ez3dViewport.svgElements.svgArea.svg;
            ez3dViewport.svgElements.svgArea.zoom.scaleBy(svg, 2);
        }
        if (ez3dViewport.svgElements.svgSimple) {
            svg = ez3dViewport.svgElements.svgSimple.svg;
            ez3dViewport.svgElements.svgSimple.zoom.scaleBy(svg, 2);
        }
    };
    // Zoom out
    EZGuiManager.prototype.zoomOut = function() {
        var ez3dViewport = this.ez3dViewport;
        var svg = undefined;
        if (ez3dViewport.ez3dPlayer) {
            var playerMode = ez3dViewport.ez3dPlayer.getMode();
            if (playerMode === 'showcase' || playerMode === '3d') {
                var playerZoom = ez3dViewport.ez3dPlayer.getZoom();
                ez3dViewport.ez3dPlayer.setZoom(playerZoom - 10);
            }
        }
        if (ez3dViewport.svgElements.svgProject) {
            svg = ez3dViewport.svgElements.svgProject.svg;
            ez3dViewport.svgElements.svgProject.zoom.scaleBy(svg, 1 / 2);
        }
        if (ez3dViewport.svgElements.svgArea) {
            svg = ez3dViewport.svgElements.svgArea.svg;
            ez3dViewport.svgElements.svgArea.zoom.scaleBy(svg, 1 / 2);
        }
        if (ez3dViewport.svgElements.svgSimple) {
            svg = ez3dViewport.svgElements.svgSimple.svg;
            ez3dViewport.svgElements.svgSimple.zoom.scaleBy(svg, 1 / 2);
        }
    };
    // look 3d view to north
    EZGuiManager.prototype.lookToNorth = function () {
        if (ez3dViewport.ez3dPlayer) {
            ez3dViewport.ez3dPlayer.lookToNorth();
        }
    };
    // center 3d view
    EZGuiManager.prototype.center3dView = function () {
        if (ez3dViewport.ez3dPlayer) {
            ez3dViewport.ez3dPlayer.resetCamsAndAnchors();
        }
    };
    // Get class to look 3d view to north button
    EZGuiManager.prototype.getCompassButtonClass = function () {
        var ez3dViewport = this.ez3dViewport;
        var classed = 'ez3d-button ez3d-compass-btn';
        if (ez3dViewport.ez3dPlayer) {
            var playerMode = ez3dViewport.ez3dPlayer.getMode();
            if (playerMode !== '3d' && playerMode !== 'showcase') {
                classed += ' ez3d-hidden';
            }
        } else {
            classed += ' ez3d-hidden';
        }
        return classed;
    };
    // Get class to center 3d view button
    EZGuiManager.prototype.getCenter3dViewButtonClass = function () {
        var ez3dViewport = this.ez3dViewport;
        var classed = 'ez3d-button ez3d-center3dView-btn';
        if (ez3dViewport.ez3dPlayer) {
            var playerMode = ez3dViewport.ez3dPlayer.getMode();
            if (playerMode !== '3d' && playerMode !== 'showcase') {
                classed += ' ez3d-hidden';
            }
        } else {
            classed += ' ez3d-hidden';
        }
        return classed;
    };
    
    
    // PROVIDERS
    // set provider
    EZGuiManager.prototype.setProvider = function(provider, skipRender) {
        if (typeof skipRender === 'undefined') {
            skipRender = false;
        }
        var ez3dViewport = this.ez3dViewport;
        // active mapper
        ee.addOnceListener('setAttribute_Finished', function () {
            if (ez3dViewport.ez3dPlayer) {
                ez3dViewport.ez3dPlayer.activateMapper(true);
            }
        });
        ee.emitEvent('EZModelScene_setAttributeListener', [{
            args: ['', 'layoutRules.scenePreferences.activeMapper', true],
            updateRender: false,
            undo: false,
            progress: false,
            notifications: false
        }]);
    
        // remove class active in all elements
        var id = provider.number.toString();
        var buttons = d3.select('.ez3d-foldable-buttons-viewport').selectAll('button')._groups[0] || 0;
        for (var i = 0; i < buttons.length; i++) {
            var button = d3.select(buttons[i]);
            if (id === buttons[i].id) {
                button.classed('active', true);
            } else {
                button.classed('active', false)
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
                if (button.classed('ez3d-provider-btn')) {
                    // Set provider name color to second and buttonContainer to lightgray
                    d3.select(button.node().previousSibling)
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second);
                    d3.select(button.node().parentNode)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
                }
            }
        }
        d3.select('#ez3d-mapper-button')
            .attr('class', 'ez3d-button ez3d-mapper-icon ez3d-foldable-button ez3d-foldable-button-provider ez3d-' + provider.label + '-icon', true)
            .html(provider.number);
        if (!skipRender) {
            ez3dViewport.ez3dPlayer.setProvider(provider.id);
        }
        return true;
    };
    
    EZGuiManager.prototype.getProviderButtonClass = function () {
        var classed = 'ez3d-button ez3d-mapper-icon ez3d-foldable-button ez3d-foldable-button-provider';
        if (ez3dScene.layoutRules.scenePreferences.activeMapper === false) {
            classed += ' disabled-render';
        }
        return classed;
    };
    
    
    // ENDPOINTS
    EZGuiManager.prototype.saveButtonOnClick = function () {
        if (ez3dScene.layoutRules.layoutId === 0) {
            document.getElementById('ez3d-create-account-button').click();
        } else if (ez3dScene.layoutRules.scenePreferences.enablePlayer === true) {
            var allTextures = true;
            ez3dScene.buildings.forEach(function (building) {
                if (building.texture === undefined) {
                    allTextures = false;
                }
            });
    
            if (allTextures === false) {
                ee.addOnceListener('genTexturesAndSave', function() {
                    ee.removeEvent('justSave');
                    ee.addOnceListener('getTextures_Finished', function() {
                        ez3dScene.saveProject();
                    });
    
                    ee.emitEvent('EZModelScene_getTexturesListener');
                });
                ee.addOnceListener('justSave', function() {
                    ee.removeEvent('genTexturesAndSave');
                    ez3dScene.saveProject();
                });
                ee.emitEvent('widget_notification', [{
                    'name': 'saveWithTextures',
                    'title': 'saveWithTexturesTitle',
                    'content': 'saveWithTexturesContent',
                    'eventOk': ['genTexturesAndSave', undefined, 'genTexturesAndSave'],
                    'eventCancel': ['justSave', undefined, 'justSave']
                }]);
            } else {
                ez3dScene.saveProject();
            }
        } else {
            ez3dScene.saveProject();
        }
    };
    
    
    // KEEPOUTS
    EZGuiManager.prototype.addKeepout = function () {
        var self = this;
        d3.select('#ez3d-aside-keepout-create')
            .classed('ez3d-active-panel', true);
        self.updateNavigation = false;
        // If it's added from the tree tab, set buildings[0] active and activeTabObject
        if (self.activeTabObject === 'tree-info') {
            self.activeTabObject = 'keepout-info';
            ee.emitEvent('EZModelScene_setActiveListener', [{args: ez3dScene.buildings[0].id, undo: false, updateRender: false}]);
        } else {
            ee.emitEvent('EZModelScene_setActiveListener', [{args: ez3dScene.activeBuilding.id, undo: false, updateRender: false}]);
        }
        ee.emitEvent('EZModelBuilding_createKeepoutListener', [{
            undo: true,
            updateRender: {draw2d: true, draw3d: true, updatePanels: true, fitRange: false}
        }]);
    };
    
    
    // TREE
    EZGuiManager.prototype.addTree = function () {
        ee.emitEvent('EZModelScene_createTreeListener', [{
            undo: true,
            updateRender: {draw2d: true, draw3d: true, updatePanels: true, fitRange: true}
        }]);
    };
    
    
    // AUTOCAD
    EZGuiManager.prototype.getAutocadButtoExportClass = function () {
        var classed = 'ez3d-button fa-camera-retro ez3d-hidden';
        if (d3.select('#svgContainer-svgAutoCAD').node() !== null) {
            classed += ' ez3d-visible';
        }
        return classed;
    };
    
    
    // SNAPSHOTS
    EZGuiManager.prototype.updateSnapshot = function (element) {
        var self = this;
        var downloadSnapshot;
        var sharedCrm;
        var cotas;
        var opportunityId = ez3dScene.crmProject.opportunity_id;
    
        if (self.widgets.indexOf('ez3d-widget-snapshot') !== -1 || self.widgets.indexOf('ez3d-widget-export') !== -1) {
            var name = 'snapshot';
            if (self.widgets.indexOf('ez3d-widget-export') !== -1) {
                name = 'export';
            }
            if (opportunityId !== null) {
                downloadSnapshot = d3.select('#downloadSnapshot').selectAll('.ez3d-switch-input').node().checked;
                sharedCrm = d3.select('#sharedCrm').selectAll('.ez3d-switch-input').node().checked;
            }
            if (name === 'export') {
                cotas = d3.select('#autocadCotas').selectAll('.ez3d-switch-input').node().checked;
            }
            self.managerWidgetSnapShot(name, downloadSnapshot, sharedCrm, cotas);
            if (element === 'autocadCotas') {
                self.ez3dViewport.resetMode();
                // self.ez3dViewport.updateModeAutocad();
            }
        }
    };
    
        /**
     *
     *  * Method to clean and add classes to buttons container
     *
     * @param {object} data this is a json data for construct the block
     * @param {string} [data.json] block id
     * @param {string} [data.customGuiMethods] function name to add custom GuiMethods to panel
     * @param {string} [data.customListeners] function name to add custom Listeners to panel
     */
    EZGuiManager.prototype.generateButtons = function (data, update) {
        var self = this;
        var json = data.json;
    
        if (!update) {
            // add extra GUI methods
            if (data.customGuiMethods) {
                self.customGuiMethods(data.customGuiMethods);
            }
            // add extra GUI listeners
            if (data.customListeners) {
                self.customListeners(data.customListeners);
            }
        }
    
        var ez3dTopMainButtonsPanel = d3.select('#ez3d-main-buttons-top');
        var ez3dBottomRightMainButtonsPanel = d3.select('#viewport_left_controls_bottom_right')
            .attr('class', 'ez3d-viewport-controls');
        var ez3dBottomLeftMainButtonsPanel = d3.select('#viewport_left_controls_bottom_left')
            .attr('class', 'ez3d-viewport-controls');
        var ez3dZoomMainButtonsPanel = d3.select('#viewport_left_controls_zoom')
            .attr('class', 'ez3d-viewport-controls');
        var ez3dCreateAccountMainButtonsPanel = d3.select('#viewport_left_controls_create_account')
            .attr('class', 'ez3d-viewport-controls');
        var ez3dTopMainButtonsPanelViewportRight = d3.select('#ez3d-main-buttons-top-viewport_right');
    
        ez3dTopMainButtonsPanel.selectAll('*').remove();
        ez3dBottomRightMainButtonsPanel.selectAll('*').remove();
        ez3dBottomLeftMainButtonsPanel.selectAll('*').remove();
        ez3dZoomMainButtonsPanel.selectAll('*').remove();
        ez3dCreateAccountMainButtonsPanel.selectAll('*').remove();
        ez3dTopMainButtonsPanelViewportRight.selectAll('*').remove();
    
        this.populateButtons(json, ez3dTopMainButtonsPanel, ez3dBottomRightMainButtonsPanel, ez3dBottomLeftMainButtonsPanel, ez3dZoomMainButtonsPanel, ez3dCreateAccountMainButtonsPanel, ez3dTopMainButtonsPanelViewportRight);
    };
    
    EZGuiManager.prototype.populateButtons = function (json, ez3dTopMainButtonsPanel, ez3dBottomRightMainButtonsPanel, ez3dBottomLeftMainButtonsPanel, ez3dZoomMainButtonsPanel, ez3dCreateAccountMainButtonsPanel, ez3dTopMainButtonsPanelViewportRight) {
        var self = this;
        Object.keys(json).forEach(function(element) {
            var container = ez3dTopMainButtonsPanel;
            if (json[element].position === 'bottom-right') {
                container = ez3dBottomRightMainButtonsPanel;
            } else if (json[element].position === 'bottom-left') {
                container = ez3dBottomLeftMainButtonsPanel;
            } else if (json[element].position === 'bottom-zoom') {
                container = ez3dZoomMainButtonsPanel;
            } else if (json[element].position === 'bottom-center') {
                container = ez3dCreateAccountMainButtonsPanel;
            } else if (json[element].position === 'right-viewport-right') {
                container = ez3dTopMainButtonsPanelViewportRight;
            }
            self.populateBlocks(json[element], container, '', '');
        });
    };
    
        EZGuiManager.prototype.contextPanelArea = function (data, containerName) {
        const contextPanel = d3.select('#ez3d-context-panel');
        const contextPanelContainer = d3.select('#ez3d-context-panel-container');
    
        const elements = this.getAreaAndBuildingContextPanelContent(data.element);
    
        // Set color to the selected area
        setTimeout(function() {
            d3.select(data.path)
                .call(styleAreas, ez3dViewport.svgElements[containerName], 'hover');
        }, 100);
    
        // Reset area color onmouseleave
        contextPanelContainer
            .on('mouseleave', function() {
                ee.emitEvent('resetContextPanel');
                d3.select(data.path)
                    .call(styleAreas, ez3dViewport.svgElements[containerName],
                        ez3dScene.context === 'roof' ? 'roofEditor' : data.element.style.colorState);
            });
    
        this.populateBlocks(elements, contextPanel);
    };
    
    /**
     * Function to get area and building context panel content.
     * @param  {object} area    - EZModelArea.
     * @return  {object}        - Context panel content.
     */
    EZGuiManager.prototype.getAreaAndBuildingContextPanelContent = function(area) {
        // Add area content
        let elements = {
            // Area title
            areaTitle: {
                type: 'helper',
                classed: 'context-label',
                content: 'area',
            },
            // Set area active
            activateArea: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'setActive',
                visibilityRule: ez3dScene.active.id !== area.id,
                eventOnClick: ['EZModelScene_setActiveListener', area.id, true, false]
            },
            // Enable/disable
            enableArea: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'enableArea',
                visibilityRule: area.disabled,
                functionOnClick: ['areaHabilitation', [area.id, !area.disabled]]
            },
            disableArea: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'disableArea',
                visibilityRule: !area.disabled,
                functionOnClick: ['areaHabilitation', [area.id, !area.disabled]]
            },
            // Edit subarea
            editSubarea: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'edit',
                visibilityRule: area.subareas.length === 1 && !area.disabled,
                eventOnClick: [
                    'EZModelScene_setActiveAndExecuteOperatorListener',
                    [area.id, 'EZModelArea_editSubareaListener', [
                        [area.subareas[0].id, true],
                        undefined,
                        true
                    ]]
                ],
            },
        };
        // Add building content
        elements = Object.assign(elements, this.getBuildingContextPanelContent(area.parent.id, area.parent.parent.id));
    
        return elements;
    };
    
    /**
     * Function to enable or disable an area.
     * @param  {array} [elementData]   [area id, changed area disabled value]
     */
    EZGuiManager.prototype.areaHabilitation = function(elementData) {
        // Close subarea view in case it's open
        if (ez3dViewport.svgElements.svgArea) {
            ez3dViewport.ez3dGuiManager.closeSubareaView();
        }
        // Set area active if the area current area is not active
        if (ez3dScene.active.id !== elementData[0]) {
            ee.emitEvent('EZModelScene_setActiveListener', [{args: elementData[0]}]);
        }
        ee.emitEvent('EZModelScene_setAttributeListener', [{
            args: [elementData[0], 'disabled', elementData[1]],
            updateRender: {draw2d: false, draw3d: elementData[0], updatePanels: false, fitRange: false}
        }]);
    };
    
        /**
     * Function to get building context panel content.
     * @param  {string} roofId       Roof id.
     * @param  {string} buildingId   Building id.
     * @return  {object} Context panel content.
     */
    EZGuiManager.prototype.getBuildingContextPanelContent = function(roofId, buildingId) {
        return {
            // Building title
            buildingTitle: {
                type: 'helper',
                classed: 'context-label',
                content: 'building'
            },
            // Set building active
            activateBuilding: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'setActive',
                visibilityRule: ez3dScene.active.id !== buildingId,
                functionOnClick: ['updateNavigationOnBuildingActive', buildingId],
            },
            // Edit roof¡
            editRoof: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'editRoof',
                eventOnClick: [
                    'EZModelScene_setActiveAndExecuteOperatorListener',
                    [buildingId, 'EZModelBuilding_editRoofListener', [
                        roofId,
                        true,
                        {draw2d: true, draw3d: roofId, updatePanels: true, fitRange: false}
                    ]]
                ],
            },
            // Edit building
            editBuilding: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'edit',
                eventOnClick: [
                    'EZModelScene_setActiveAndExecuteOperatorListener',
                    [buildingId, 'EZModelBuilding_editBuildingListener', [
                        undefined,
                        true,
                        {draw2d: true, draw3d: buildingId, updatePanels: true, fitRange: false}
                    ]]
                ],
            },
            // Move building
            moveBuilding: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'move',
                visibilityRule: ez3dScene.mode === 'standard' || ez3dScene.active.id !== buildingId,
                eventOnClick: [
                    'EZModelScene_setActiveAndExecuteOperatorListener',
                    [buildingId, 'EZModelBuilding_runMethodListener', [
                        'setInteractiveMoveToggle',
                        false,
                        {draw2d: true, draw3d: buildingId, updatePanels: true, fitRange: false}
                    ]]
                ],
            },
            // Clone building
            cloneBuilding: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'clone',
                eventOnClick: [
                    'EZModelScene_setActiveAndExecuteOperatorListener',
                    [buildingId, 'EZModelBuilding_cloneListener', [
                        null,
                        true,
                        {draw2d: true, draw3d: buildingId, updatePanels: true, fitRange: true},
                        true
                    ]]
                ],
            },
            // Delete building
            deleteBuilding: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'delete',
                confirmation: true,
                confirmationWidget: {
                    'name': 'delete_building',
                    'title': 'deleteBuilding',
                    'content': 'deleteBuildingConfirmation',
                    'eventOk': ['EZModelScene_setActiveAndDeleteListener', [buildingId]],
                    'eventCancel': 'widget_deleteBuilding_eventCancel'
                }
            },
        };
    };
    
    /**
     * Function to allow panel change and set building active.
     * @param  {string} [buildingId]   Building id.
     */
    EZGuiManager.prototype.updateNavigationOnBuildingActive = function(buildingId) {
        this.updateNavigation = true;
        ee.emitEvent('EZModelScene_setActiveListener', [{args: buildingId, undo: true, updateRender: false}]);
    };
    
        EZGuiManager.prototype.contextPanelEdge = function (data) {
        const contextPanel = d3.select('#ez3d-context-panel');
    
        // Edges selection
        const elements = {
            selectEdgeAsGuide: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'selectEdgeAsGuide',
                visibilityRule: !data.object.selectedEdge,
                functionOnClick: ['edgesSelectionAsGuide', [data.object, true]]
            },
            selectAllEdgesAsGuide: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'selectAllEdgesAsGuide',
                visibilityRule: !data.object.selectedEdge,
                functionOnClick: ['allEdgesSelectionAsGuide', true]
            },
            deselectEdgeAsGuide: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'deselectEdgeAsGuide',
                visibilityRule: Boolean(data.object.selectedEdge),
                functionOnClick: ['edgesSelectionAsGuide', [data.object, false]]
            },
            deselectAllEdgesAsGuide: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'deselectAllEdgesAsGuide',
                visibilityRule: Boolean(data.object.selectedEdge),
                functionOnClick: ['allEdgesSelectionAsGuide', false]
            },
        };
    
        this.populateBlocks(elements, contextPanel);
    };
    
    /**
     * Function to select or deselect an edge as guide.
     * @param  {array} [elementData]   [EZModelLocation, true for selecting and false for deselecting]
     */
    EZGuiManager.prototype.edgesSelectionAsGuide = function(elementData) {
        elementData[0].selectedEdge = elementData[1];
        ee.emitEvent('updateRender',
            [{draw2d: true, draw3d: false, updatePanels: false, fitRange: false}]);
    };
    
    /**
     * Function to select or deselect all edges as guides.
     * @param  {boolean} [select]   true for selecting and false for deselecting
     */
    EZGuiManager.prototype.allEdgesSelectionAsGuide = function(select) {
        ez3dScene.tempPath.vertices.forEach(function(loc) {
            loc.selectedEdge = select;
        });
        ee.emitEvent('updateRender',
            [{draw2d: true, draw3d: false, updatePanels: false, fitRange: false}]);
    };
    
        EZGuiManager.prototype.contextPanelKeepout = function (data, containerName) {
        const contextPanel = d3.select('#ez3d-context-panel');
        const contextPanelContainer = d3.select('#ez3d-context-panel-container');
    
        let elements = {};
        if (containerName === 'svgArea') {
            // Set keepout active
            elements = {
                activateKeepout: {
                    type: 'button',
                    classed: 'ez3d-square-button',
                    content: 'activateKeepout',
                    visibilityRule: ez3dViewport.svgElements[containerName] !== undefined,
                    functionOnClick: ['activateKeepoutFromSubareaView', data.element.id]
                },
            };
        } else {
            elements = {
                // Keepout title
                keepoutTitle: {
                    type: 'helper',
                    classed: 'context-label',
                    content: 'keepout'
                },
                // Set keepout active
                activateKeepout: {
                    type: 'button',
                    classed: 'ez3d-square-button',
                    content: 'setActive',
                    visibilityRule: ez3dScene.active.id !== data.element.id,
                    eventOnClick: ['EZModelScene_setActiveListener', data.element.id, true, false],
                },
                // Edit keepout
                editKeepout: {
                    type: 'button',
                    classed: 'ez3d-square-button',
                    content: 'edit',
                    eventOnClick: [
                        'EZModelScene_setActiveAndExecuteOperatorListener',
                        [data.element.id, 'EZModelKeepout_editKeepoutListener', [
                            undefined,
                            true,
                            {draw2d: true, draw3d: data.element.id, updatePanels: true, fitRange: false}
                        ]]
                    ],
                },
                // Move keepout
                moveKeepout: {
                    type: 'button',
                    classed: 'ez3d-square-button',
                    content: 'move',
                    visibilityRule: ez3dScene.mode === 'standard',
                    eventOnClick: [
                        'EZModelScene_setActiveAndExecuteOperatorListener',
                        [data.element.id, 'EZModelKeepout_runMethodListener', [
                            'setInteractiveMoveToggle',
                            false,
                            {draw2d: true, draw3d: data.element.id, updatePanels: false, fitRange: false}
                        ]]
                    ],
                },
                finishMoveKeepout: {
                    type: 'button',
                    classed: 'ez3d-square-button',
                    content: 'finishMove',
                    visibilityRule: ez3dScene.mode === 'interactiveMove' && ez3dScene.active.id === data.element.id,
                    eventOnClick: [
                        'EZModelKeepout_runMethodListener',
                        'setInteractiveMoveToggle',
                        false,
                        {draw2d: true, draw3d: data.element.id, updatePanels: true, fitRange: false}
                    ],
                },
                // Clone keepout
                cloneKeepout: {
                    type: 'button',
                    classed: 'ez3d-square-button',
                    content: 'clone',
                    eventOnClick: [
                        'EZModelScene_setActiveAndExecuteOperatorListener',
                        [data.element.id, 'EZModelKeepout_cloneListener', [
                            null,
                            true,
                            {draw2d: true, draw3d: data.element.id, updatePanels: true, fitRange: true}
                        ]]
                    ],
                },
                // Delete keepout
                deleteKeepout: {
                    type: 'button',
                    classed: 'ez3d-square-button',
                    content: 'delete',
                    eventOnClick: [
                        'EZModelScene_setActiveAndDeleteListener',
                        data.element.id,
                        true,
                        {draw2d: true, draw3d: data.element.id, updatePanels: true, fitRange: false}
                    ]
                },
            };
            // Add building content
            elements = Object.assign(elements, this.getBuildingContextPanelContent(data.element.parent.id));
        }
    
        // Set color to the selected keepout
        setTimeout(function() {
            d3.select(data.path)
                .call(styleKeepouts, ez3dViewport.svgElements[containerName], 'hover');
        }, 100);
    
        // Reset keepout color onmouseleave
        contextPanelContainer
            .on('mouseleave', function() {
                ee.emitEvent('resetContextPanel');
                d3.select(data.path)
                    .call(styleKeepouts, ez3dViewport.svgElements[containerName], data.element.style.colorState);
            });
    
        this.populateBlocks(elements, contextPanel);
    };
    
    /**
     * Function to set keepout active from subarea view.
     * @param  {string} [keepoutId]   Keepout id.
     */
    EZGuiManager.prototype.activateKeepoutFromSubareaView = function(keepoutId) {
        const keepout = ez3dScene.findById(keepoutId.split('_')[0]);
        ez3dViewport.svgElements.svgArea.clickOnKeepoutEvent(keepout);
    };
    
        EZGuiManager.prototype.contextPanelLocation = function (data, containerName) {
        const contextPanel = d3.select('#ez3d-context-panel');
        let elements = {};
    
        // Guides draw buttons
        if (ez3dScene.layoutRules.scenePreferences.snapToGuides) {
            const allowGuides = (data.object.drawGuides === false || data.object.drawGuides === undefined);
            elements = Object.assign(elements, {
                drawGuidesHere: {
                    type: 'button',
                    classed: 'ez3d-square-button',
                    content: 'drawGuidesHere',
                    visibilityRule: allowGuides,
                    functionOnClick: ['guidesDrawing', [data.id, true, containerName]]
                },
                removeGuidesHere: {
                    type: 'button',
                    classed: 'ez3d-square-button',
                    content: 'removeGuidesHere',
                    visibilityRule: !allowGuides,
                    functionOnClick: ['guidesDrawing', [data.id, false, containerName]]
                },
                removeAllGuides: {
                    type: 'button',
                    classed: 'ez3d-square-button',
                    content: 'removeAllGuides',
                    functionOnClick: ['guidesDrawing', [data.id, 'removeAll', containerName]]
                }
            });
        }
    
        // Point selection buttons
        const allowPointSelection = (data.object.selected === false || data.object.selected === undefined);
        elements = Object.assign(elements, {
            selectPoint: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'selectPoint',
                visibilityRule: allowPointSelection,
                functionOnClick: ['pointSelection', [data.id, true, containerName]]
            },
            deselectPoint: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'deselectPoint',
                visibilityRule: !allowPointSelection,
                functionOnClick: ['pointSelection', [data.id, false, containerName]]
            }
        });
    
        // Points selection buttons
        let allowPointsSelection = false;
        ez3dScene.tempPath.vertices.forEach(function(loc) {
            if (loc.selected) {
                allowPointsSelection = true;
            }
        });
        elements = Object.assign(elements, {
            deselectAllPoints: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'deselectAllPoints',
                visibilityRule: allowPointsSelection,
                functionOnClick: ['allPointsSelection', [false, containerName]]
            },
            selectAllPoints: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'selectAllPoints',
                visibilityRule: !allowPointsSelection,
                functionOnClick: ['allPointsSelection', [true, containerName]]
            },
            deletePoint: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'deletePoint',
                functionOnClick: ['deletePoint', data.id]
            }
        });
    
        this.populateBlocks(elements, contextPanel);
    };
    
    /**
     * Function to draw a guide, remove it or remove all of them.
     * @param  {array} [elementData]   [0: location id, 1: true for drawing, false for deleting, 'removeAll' for removing all guides, 2: containerName].
     */
    EZGuiManager.prototype.guidesDrawing = function(elementData) {
        ez3dScene.tempPath.vertices.forEach(function(loc) {
            if (elementData[1] === 'removeAll') {
                loc.drawGuides = false;
            } else if (elementData[0] === loc.id) {
                loc.drawGuides = elementData[1];
            }
        });
        self.ez3dViewport.svgElements[elementData[2]].updateVoronoi(true);
    };
    
    /**
     * Function to select or deselect a point
     *
     * @param  {array} [elementData]   [0: location id, 1: true for selecting and false for deselecting, 2: containerName]
     */
    EZGuiManager.prototype.pointSelection = function(elementData) {
        ez3dScene.tempPath.vertices.forEach(function(loc) {
            if (elementData[0] === loc.id) {
                loc.selected = elementData[1];
            }
        });
        // self.ez3dViewport.svgElements[elementData[2]].drawScenePathEditor();
        ee.emitEvent('updateRender',
            [{draw2d: true, draw3d: false, updatePanels: false, fitRange: false}]);
    };
    
    /**
     * Function to select or deselect all points
     *
     * @param  {array} [elementData]   [0: true for selecting and false for deselecting, 1: containerName]
     */
    EZGuiManager.prototype.allPointsSelection = function(elementData) {
        ez3dScene.tempPath.vertices.forEach(function(loc) {
            loc.selected = elementData[0];
        });
        self.ez3dViewport.svgElements[elementData[1]].drawInteractivePath();
    };
    
    /**
     * Function to delete a point
     *
     * @param  {string} [pointId]   Point id
     */
    EZGuiManager.prototype.deletePoint = function(pointId) {
        ee.emitEvent('EZModelScene_removeLocationListener', [{args: [pointId]}]);
        ee.emitEvent('delete_Location_Finished');
        d3.selectAll('.ez3d-path-editor-elements').raise();
    };
    
        EZGuiManager.prototype.contextPanelModule = function (data, containerName) {
        const contextPanel = d3.select('#ez3d-context-panel');
        const contextPanelContainer = d3.select('#ez3d-context-panel-container');
        const contentArray = ez3dScene.utils.countModules(data.data, data.element);
    
        // Module info
        const elements = {
            helperInfo1: {
                type: 'helper',
                classed: 'helper module-count',
                content: contentArray[0]
            },
            helperInfo2: {
                type: 'helper',
                classed: 'helper module-count',
                content: contentArray[1]
            },
            helperInfo3: {
                type: 'helper',
                classed: 'helper module-count',
                content: contentArray[2]
            },
            changeModuleOrientation: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'changeModuleOrientation',
                visibilityRule: ez3dScene.layoutRules.scenePreferences.customRowOrientation && !ez3dScene.active.system.dilatationLines.enabled,
                eventOnClick: [
                    'EZModelSubarea_changeRowOrientationListener',
                    [data.data.row],
                    true,
                    {draw2d: true, draw3d: data.element.id, updatePanels: true, fitRange: false},
                    true
                ]
            },
            addRowOffset: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'addRowOffset',
                visibilityRule: ez3dScene.layoutRules.scenePreferences.customRowOffset && !ez3dScene.active.system.dilatationLines.enabled && ez3dScene.projectType !== 'simple',
                functionOnClick: ['addRowOffset', data.data.row]
            },
        };
    
        // Set color to the selected module
        setTimeout(function() {
            const color = d3.rgb(d3.select(data.element).attr('fill'));
            d3.select(data.element)
                .call(styleModules, ez3dViewport.svgElements[containerName], data.data.type, d3.rgb(255 - color.r, 255 - color.g, 255 - color.b));
        }, 100);
    
        // Set context panel opacity and reset module color onmouseleave
        contextPanelContainer
            .style('opacity', '0.85')
            .on('mouseleave', function() {
                ee.emitEvent('resetContextPanel');
                d3.select(data.element)
                    .call(styleModules, ez3dViewport.svgElements[containerName], data.data.type, d3.select(data.element.parentElement.parentElement).attr('color'));
            });
        this.populateBlocks(elements, contextPanel);
    };
    
    /**
     * Function to add offset on a row.
     * @param  {string} [rowIndex]   Row index.
     */
    EZGuiManager.prototype.addRowOffset = function(rowIndex) {
        ee.emitEvent('widget_notification', [{
            'name': 'row-offset',
            'title': 'rowOffset',
            'functionOnCreation': ['rowOffsetWidget', rowIndex]
        }]);
    };
    
        EZGuiManager.prototype.contextPanelRoofArea = function (data, containerName) {
        const contextPanel = d3.select('#ez3d-context-panel');
        const contextPanelContainer = d3.select('#ez3d-context-panel-container');
    
        const elements = this.getRoofAreaContextPanelContent(data.element);
    
        // Set color to the selected area
        setTimeout(function() {
            d3.select(data.path)
                .call(styleAreas, ez3dViewport.svgElements[containerName], 'hover');
        }, 100);
    
        // Reset area color onmouseleave
        contextPanelContainer
            .on('mouseleave', function() {
                ee.emitEvent('resetContextPanel');
                d3.select(data.path)
                    .call(styleAreas, ez3dViewport.svgElements[containerName],
                        data.element.style.colorState);
            });
    
        this.populateBlocks(elements, contextPanel);
    };
    
    /**
     * Function to get area and building context panel content.
     * @param  {object} area    - EZModelArea.
     * @return  {object}        - Context panel content.
     */
    EZGuiManager.prototype.getRoofAreaContextPanelContent = function(area) {
        // Add area content
        const roof = area.parent;
        const face = roof.geometry
            .find((geo) => (geo.link === area.id));
        // WIP ROOF EDITING : changeInclination
        if (face.wallIndex === undefined) {
            console.warn('WIP: Edit Linked Roof');
            return {};
        }
        if (roof.path.wallType[face.wallIndex] === 'default') {
            console.warn('WIP: Edit Flat Surface');
            return {};
        }
        const loc = roof.path.vertices[face.wallIndex];
        return this.getRoofEdgesContextPanelContent(loc);
    };
    
        EZGuiManager.prototype.contextPanelRoofEdges = function (data, containerName) {
        const contextPanel = d3.select('#ez3d-context-panel');
        const contextPanelContainer = d3.select('#ez3d-context-panel-container');
    
        const elements = this.getRoofEdgesContextPanelContent(data.element);
    
        // Reset area color onmouseleave
        contextPanelContainer
            .on('mouseleave', function() {
                ee.emitEvent('resetContextPanel');
            });
    
        this.populateBlocks(elements, contextPanel);
    };
    
    /**
     * Function to get roof and building context panel content.
     * @param  {object} loc    - EZModelLocation.
     * @return  {object}        - Context panel content.
     */
    EZGuiManager.prototype.getRoofEdgesContextPanelContent = function(loc) {
        // Add area content
        const roof = loc.parent.parent;
        const wallType = ez3dScene.active.path.wallType[loc.index];
        const elements = {
            changeInclination: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'changeInclination',
                visibilityRule: wallType === 'main',
                functionOnClick: ['changeInclination', roof]
            },
            changeInclinationStandard: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'changeInclination',
                visibilityRule: wallType === 'standard',
                functionOnClick: ['changeInclinationStandard', loc]
            },
            changeOrientation: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'changeOrientation',
                visibilityRule: wallType !== 'main',
                functionOnClick: (ez3dScene.activeRoof.roofNeedsUpdate())
                    ? undefined : ['changeOrientation', loc],
                confirmation: 'roofNeedsUpdate',
                confirmationWidget: {
                    'name': 'orientation',
                    'title': 'alertRoofChangeTitle',
                    'content': 'alertRoofChangeContent',
                    'eventOk': ['widget_roofChangeOrientation_eventOk', loc],
                    'eventCancel': 'widget_roofChangeOrientation_eventCancel'
                },
            },
        };
    
        return elements;
    };
    
    
    /**
     * Function to change roof orientation.
     * @param  {object} loc   EZModelLocation.
     */
    EZGuiManager.prototype.changeOrientation = function(loc) {
        const roof = loc.parent.parent;
        let callback;
        if (roof.path.wallType[loc.index] === 'standard') {
            const delta = loc.index - roof.orientation;
            const newMainIndex = (delta >= 0) ? delta
                : roof.path.vertices.length + delta;
            const newMainPoints = roof.geometry[newMainIndex].index;
            const oldMainPoints = roof.geometry[0].index;
            const steps = newMainPoints[0] - oldMainPoints[0];
            roof.roofPoints = ez3dScene.utils.rotate(roof.roofPoints, steps);
            roof.__data.inclination = roof.geometry[newMainIndex].inclination;
            callback = 'roofSoftUpdate';
        } else {
            callback = 'roofHardUpdate';
        }
        roof.orientation = loc.index;
        ee.emitEvent('EZModelRoof_runMethodListener', [{
            'args': [callback, true],
            'updateRender': {draw2d: true, draw3d: false, updatePanels: false, fitRange: false},
            'undo': true,
            'notifications': false,
        }]);
    };
    
    /**
     * Function to change roof inclination.
     * @param  {object} roof    - EZModelRoof.
     */
    EZGuiManager.prototype.changeInclination = function(roof) {
        ee.emitEvent('widget_notification', [{
            'name': 'roof-change-inclination',
            'title': 'roofInclination',
            'functionOnCreation': ['changeInclinationWidget', roof]
        }]);
    };
    
    /**
     * Function to change roof inclination.
     * @param  {object} roof    - EZModelRoof.
     */
    EZGuiManager.prototype.changeInclinationStandard = function(loc) {
        const roof = loc.parent.parent;
        this.changeOrientation(loc);
        ee.emitEvent('widget_notification', [{
            'name': 'roof-change-inclination',
            'title': 'roofInclination',
            'functionOnCreation': ['changeInclinationWidget', roof]
        }]);
    };
    
        EZGuiManager.prototype.contextPanelSubarea = function (data, containerName) {
        const contextPanel = d3.select('#ez3d-context-panel');
        const contextPanelContainer = d3.select('#ez3d-context-panel-container');
        const subareaId = data.element.id;
        const areaId = data.element.parent.id;
    
        let elements = {
            // Subarea title
            subareaTitle: {
                type: 'helper',
                classed: 'context-label',
                content: 'subarea'
            },
            // Set subarea active
            activateSubarea: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'setActive',
                visibilityRule: ez3dScene.active.id !== subareaId,
                eventOnClick: ['EZModelScene_setActiveListener', subareaId, true, false],
            },
            // Edit subarea
            editSubarea: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'edit',
                eventOnClick: [
                    'EZModelScene_setActiveAndExecuteOperatorListener',
                    [areaId, 'EZModelArea_editSubareaListener', [
                        [subareaId, true],
                        undefined,
                        {draw2d: true, draw3d: false, updatePanels: false, fitRange: false}
                    ]]
                ],
            },
            // Move subarea
            moveSubarea: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'move',
                visibilityRule: ez3dScene.mode === 'standard' || ez3dScene.active.id !== areaId,
                eventOnClick: [
                    'EZModelScene_setActiveAndExecuteOperatorListener',
                    [areaId, 'EZModelArea_interactiveMoveSubareaListener', [
                        [subareaId, true],
                        false,
                        {draw2d: true, draw3d: true, updatePanels: false, fitRange: false}
                    ]]
                ],
            },
            finishMoveSubarea: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'finishMove',
                visibilityRule: ez3dScene.mode === 'interactiveMove' && ez3dScene.active.id === areaId,
                eventOnClick: [
                    'EZModelArea_interactiveMoveSubareaListener',
                    [subareaId, false],
                    false,
                    {draw2d: true, draw3d: false, updatePanels: false, fitRange: false}
                ],
            },
            // Clone subarea
            cloneSubarea: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'clone',
                eventOnClick: [
                    'EZModelScene_setActiveAndExecuteOperatorListener',
                    [areaId, 'EZModelArea_cloneSubareaListener', [
                        [subareaId],
                        true,
                        {draw2d: true, draw3d: subareaId, updatePanels: true, fitRange: false}
                    ]]
                ],
            },
            // Delete subarea
            deleteSubarea: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: (data.element.parent.subareas.length === 1) ? 'reset' : 'delete',
                eventOnClick: [
                    'EZModelScene_setActiveAndExecuteOperatorListener',
                    [areaId, 'EZModelArea_deleteSubareaListener', [
                        [subareaId, true],
                        true,
                        {draw2d: true, draw3d: true, updatePanels: true, fitRange: false},
                        true
                    ]]
                ],
            },
        };
    
        // Add area and building content
        elements = Object.assign(elements, this.getAreaAndBuildingContextPanelContent(data.element.parent));
    
        // Set color to the selected subarea
        setTimeout(function() {
            d3.select(data.path)
                .call(styleSubareas, ez3dViewport.svgElements[containerName], 'hover');
        }, 100);
    
        // Reset subarea color onmouseleave
        contextPanelContainer
            .on('mouseleave', function() {
                ee.emitEvent('resetContextPanel');
                d3.select(data.path)
                    .call(styleSubareas, ez3dViewport.svgElements[containerName], data.element.style.colorState);
            });
    
        this.populateBlocks(elements, contextPanel);
    };
    
        EZGuiManager.prototype.contextPanelSubareaLabel = function (data) {
        const contextPanel = d3.select('#ez3d-context-panel');
    
        // Azimuth button
        const elements = {
            azimuth: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'setAzimuthPerpendicularToAxis',
                eventOnClick: ['EZModelScene_setAttributeListener', 
                    [data.id, 'system.azimuth', data.azimuth],
                    true,
                    {draw2d: true, draw3d: false, updatePanels: true, fitRange: false},
                    true
                ]
            }
        };
        this.populateBlocks(elements, contextPanel);
    };
    
        EZGuiManager.prototype.contextPanelTree = function (data, containerName) {
        const contextPanel = d3.select('#ez3d-context-panel');
        const contextPanelContainer = d3.select('#ez3d-context-panel-container');
    
        const elements = {
            // Tree title
            title: {
                type: 'helper',
                classed: 'context-label',
                content: 'tree'
            },
            // Set tree active
            activateTree: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'setActive',
                visibilityRule: ez3dScene.active.id !== data.element.id,
                eventOnClick: ['EZModelScene_setActiveListener', data.element.id, true, false],
            },
            // Move tree
            moveTree: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'move',
                visibilityRule: ez3dScene.mode === 'standard',
                eventOnClick: [
                    'EZModelScene_setActiveAndExecuteOperatorListener', 
                    [data.element.id, 'EZModelTree_runMethodListener', [
                        'setInteractiveMoveToggle',
                        false,
                        {draw2d: true, draw3d: data.element.id, updatePanels: true, fitRange: true}
                    ]]
                ],
            },
            finishMoveTree: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'finishMove',
                visibilityRule: ez3dScene.mode === 'interactiveMove' && ez3dScene.active.id === data.element.id,
                eventOnClick: [
                    'EZModelTree_runMethodListener',
                    'setInteractiveMoveToggle',
                    false,
                    {draw2d: true, draw3d: data.element.id, updatePanels: false, fitRange: false}
                ],
            },
            // Clone tree
            cloneTree: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'clone',
                eventOnClick: [
                    'EZModelScene_setActiveAndExecuteOperatorListener',
                    [data.element.id, 'EZModelTree_cloneListener', [
                        null,
                        true,
                        {draw2d: true, draw3d: data.element.id, updatePanels: true, fitRange: false}
                    ]]
                ],
            },
            // Delete tree
            deleteTree: {
                type: 'button',
                classed: 'ez3d-square-button',
                content: 'delete',
                eventOnClick: [
                    'EZModelScene_setActiveAndDeleteListener', 
                    data.element.id,
                    true,
                    {draw2d: true, draw3d: data.element.id, updatePanels: true, fitRange: false}
                ]
            },
        };
    
        // Set color to the selected tree
        setTimeout(function() {
            d3.select(data.circle)
                .call(styleTrees, ez3dViewport.svgElements[containerName], 'hover');
        }, 100);
    
        // Reset tree color onmouseleave
        contextPanelContainer
            .on('mouseleave', function() {
                ee.emitEvent('resetContextPanel');
                d3.select(data.circle)
                    .call(styleTrees, ez3dViewport.svgElements[containerName], data.element.style.colorState);
            });
    
        this.populateBlocks(elements, contextPanel);
    };
    
        EZGuiManager.prototype.generateContextPanel = function(mouse, data, containerName) {
        var self = this;
        var contextPanelContainer = '';
        var contextPanel = '';
    
        // Create function to reset context panel. Called on contextPanel button click and contextPanel mouseleave (with events if it's a custom function)
        const resetContextPanel = (mouseEvent) => {
            // Close subarea view if a button has been clicked
            if (ez3dViewport.svgElements.svgArea && containerName !== 'svgArea' && mouseEvent) {
                self.closeSubareaView(undefined, true);
            }
            ee.removeListener('resetContextPanel', resetContextPanel);
            contextPanelContainer.style('display', 'none');
            contextPanel.selectAll('*').remove();
            if (ez3dScene.layoutRules.scenePreferences.readOnly) {
                contextPanel
                    .classed('ez3d-read-only', true);
            }
        };
    
        // Create context panel or reset its position
        if (d3.select('#ez3d-context-panel-container').empty()) {
            contextPanelContainer = d3.select('#ez3d-main-container')
                .append('div')
                .attr('id', 'ez3d-context-panel-container')
                .style('display', 'none');
            contextPanel = contextPanelContainer
                .append('div')
                .attr('id', 'ez3d-context-panel')
                .attr('class', 'ez3d-context-panel ez3d-flex-left-center-column');
        } else {
            contextPanelContainer = d3.select('#ez3d-context-panel-container')
                .style('left', '0px')
                .style('top', '0px')
                .style('display', 'flex')
                .style('opacity', '1');
            contextPanel = d3.select('#ez3d-context-panel');
            ee.addOnceListener('resetContextPanel', resetContextPanel);
        }
        // Set/reset mouseleave function
        contextPanelContainer
            .on('mouseleave', resetContextPanel);
    
        // Add container arrow
        contextPanel.append('div')
            .attr('class', 'ez3d-container-arrow')
            .style('top', '25px')
            .style('left', '20px');
    
        if (data && data.elementData) {
            self[data.functionOnCreation](data.elementData, containerName);
        }
    
        if (mouse) {
            self.setContextPanelPosition(mouse);
        }
    
        // Close context panel when any of its buttons is clicked
        contextPanel.selectAll('BUTTON').nodes().forEach((button) => {
            button.addEventListener('click', resetContextPanel);
        });
    };
    
    /**
     * Function to position the context panel
     *
     * @param  {object} [mouse]   mouse position ([0]: left, [1]: top)
     */
    EZGuiManager.prototype.setContextPanelPosition = function(mouse) {
        var contextPanelContainer = d3.select('#ez3d-context-panel-container');
        var contextPanelContainerHeight = parseFloat(contextPanelContainer.style('height'));
        var contextPanelContainerWidth = parseFloat(contextPanelContainer.style('width'));
        var containerArrow = d3.select('#ez3d-context-panel').select('.ez3d-container-arrow');
    
        contextPanelContainer
            .style('left', mouse[0] - 10 + 'px')
            .style('top', mouse[1] - 40 + 'px');
    
        // Fit context panel top
        if (parseFloat(contextPanelContainer.style('top')) < 0) {
            contextPanelContainer.style('top', '5px');
            containerArrow.style('top', parseFloat(contextPanelContainer.style('top')) - 4 + 'px');
        }
        // Fit context panel bottom
        if ((mouse[1] + contextPanelContainerHeight) > parseFloat(d3.select('#ez3d-main-container').style('height'))) {
            contextPanelContainer.style('top', (mouse[1] - contextPanelContainerHeight + 30) + 'px');
            containerArrow.style('top', (contextPanelContainerHeight - 45) + 'px');
        }
        // Fit context panel right
        if ((mouse[0] + contextPanelContainerWidth) > parseFloat(d3.select('#ez3d-main-container').style('width'))) {
            contextPanelContainer.style('left', (mouse[0] - contextPanelContainerWidth - 45) + 'px');
            containerArrow.style('left', contextPanelContainerWidth + 'px');
        }
    };
    
        /**
     * Method to add extra GUI methods.
     * @param {number} data Json data for constructing the block. (data[i] this is a json with customGuiMethods attributes)
     */
    EZGuiManager.prototype.customGuiMethods = function(data) {
        var self = this;
        Object.keys(data).forEach(function (method) {
            if (method) {
                self.ez3dViewport.EZGuiManager.prototype[method] = data[method];
            }
        });
    };
    
    /**
     * Method to add extra GUI listeners.
     * @param {object} data this is a json data for constructing the block. (data[i] is an array with listener name and method)
     */
    EZGuiManager.prototype.customListeners = function(data) {
        var self = this;
        Object.keys(data).forEach(function(listener) {
            if (listener) {
                ee.addListener(data[listener][0], function(attr, val) {
                    self[data[listener][1]](attr, val);
                });
            }
        });
    };
    
    /**
      * Method to add extra methods.
      * @param {object} data this is a json data for constructing the block (data[i] is an array with listener name and method)
      * @param {string} data.model model
      * @param {object} data.customMethods custom methods list
      */
    EZGuiManager.prototype.customMethods = function(data) {
        Object.keys(data.customMethods).forEach(function(method) {
            if (method) {
                if (data.model === '' || data.model === 'EZModelNav') {
                    EZModelScene.prototype[method] = data.customMethods[method];
                } else {
                    window[data.model].prototype[method] = data.customMethods[method];
                }
            }
        });
    };
    
        // update EZGuiManager
    EZGuiManager.prototype.updateEZGuiManager = function() {
        if (ez3dScene.projectType !== 'simple') {
            // Navigator
            if (ez3dScene.layoutRules.scenePreferences.showNav) {
                this.updatePanel(ez3dScene.asideNavPanel());
            }
        }
    
        ee.emitEvent('updateProgressBarCancelBtn');
        this.configureMainButtonsClicks();
    
        ee.emitEvent('updatePanels');
    };
    
    EZGuiManager.prototype.configureMainButtonsClicks = function() {
        var self = this;
        console.log('configureMainButtonsClicks');
        self.toggleViewportRight(ez3dScene.layoutRules.scenePreferences.closeRightCanvas, false);
    
        d3.select('#ez3d-snap-to-guides-value')
            .on('click', function() {
                ez3dScene.layoutRules.scenePreferences.snapToGuides = this.checked;
            });
    };
    
    EZGuiManager.prototype.hideElementsByShowCase = function() {
        var self = this;
        self.toggleAside('');
    
        d3.select('#viewport_left_controls_bottom_right')
            .style('display', 'none');
    
        d3.select('#viewport_left_controls_bottom_left')
            .style('display', 'none');
    
        d3.select('#ez3d-main-buttons-top')
            .style('display', 'none');
    
        d3.select('#ez3d-aside-buildings-index')
            .style('display', 'none');
    };
    
    EZGuiManager.prototype.clickOnFoldableBlockTrees = function(elementId) {
        this.clickOnFoldableBlock(elementId);
    };
    
    
    EZGuiManager.prototype.clickOnFoldableBlock = function(elementId, updateRender = {draw2d: false, draw3d: false, updatePanels: true, fitRange: true}) {
        let parentId = ez3dScene.activeBuilding.id;
        this.updateNavigation = false;
        if (elementId === ez3dScene.active.id) {
            if (ez3dScene.activeTree) {
                parentId = ez3dScene.active.parentId;
            }
            ee.emitEvent('EZModelScene_setActiveListener', [{args: parentId, undo: true, updateRender: updateRender}]);
            this.updateNavigation = true;
        } else {
            ee.emitEvent('EZModelScene_setActiveListener', [{args: elementId, undo: true, updateRender: updateRender}]);
        }
    };
    
    /**
     * Method to check if the Player is on.
     * @return {boolean} isPlayerOn
     */
    EZGuiManager.prototype.checkPlayerOn = function() {
        var isPlayerOn = true;
        if (ez3dScene.layoutRules.scenePreferences.enablePlayer === false) {
            isPlayerOn = false;
        }
        return isPlayerOn;
    };
    
    /**
     * Method to check if layoutRule to set undo/redo buttons visibilityRule.
     * @return {boolean} enableUndoRedo layoutRule
     */
    EZGuiManager.prototype.checkEnabledUndo = function() {
        if (!ez3dScene.layoutRules.scenePreferences.enableUndoRedo) return false;
        return (ez3dScene.history.redo === undefined);
    };
    EZGuiManager.prototype.checkEnabledRedo = function () {
        if (!ez3dScene.layoutRules.scenePreferences.enableUndoRedo) return false;
        return (ez3dScene.history.redo !== undefined);
    };
    EZGuiManager.prototype.checkDisabledUndo = function () {
        return (ez3dScene.history.undo === undefined);
    };
    
    EZGuiManager.prototype.checkDemoMode = function() {
        var demoMode = true;
        if (ez3dScene.layoutRules.urlHash || ez3dScene.layoutRules.layoutId !== 0) {
            demoMode = false;
        }
        return demoMode;
    };
    
    EZGuiManager.prototype.isEditorClosed = function() {
        return !ez3dScene.activeEditor;
    };
    
    EZGuiManager.prototype.viewportDragbar = function() {
        var viewportDragbarDragging = false;
        var mouseOrigin;
    
        // Dragbar to resize viewport left and right
        // Event onmousedown to resize split view
        document.getElementById('ez3d-viewport-dragbar')
            .onmousedown = function(e) {
                e.preventDefault();
                viewportDragbarDragging = true;
                mouseOrigin = e.pageX;
    
                var viewportRight = document.getElementById('ez3d-viewport-right');
    
                var ghostbar = document.createElement('div');
    
                var mainContainer = document.getElementById('ez3d-main-container');
    
                mainContainer.appendChild(ghostbar);
    
                ghostbar.setAttribute('id', 'ez3d-ghostbar');
                ghostbar.style.height = ez3dScene.utils.getElementOuterHeight(viewportRight) + 'px';
                ghostbar.style.top = '0px';
                var leftOffset = ez3dScene.utils.getElementOffset(mainContainer).left;
    
                ghostbar.style.left = e.pageX - leftOffset + 'px';
    
                document.onmousemove = function(ev) {
                    ghostbar.style.left = ev.pageX - leftOffset + 'px';
                };
            };
    
        // Dragbar to reset viewport left and right sizes
        // Event ondblclick to reset split view sizes to 50%-50%
        document.getElementById('ez3d-viewport-dragbar')
            .ondblclick = function(e) {
                e.preventDefault();
                var viewportRight = document.getElementById('ez3d-viewport-right');
                var viewportLeft = document.getElementById('ez3d-viewport-left');
                viewportRight.style.width = '50%';
                viewportLeft.style.width = '50%';
    
                ee.emitEvent('updateRender',
                    [{draw2d: true, draw3d: false, updatePanels: false, fitRange: false}]);
            };
    
        /**
         * [finishDragViewport finish dragging action]
         * @param  {[type]} e [mouse event]
         */
        var finishDragViewport = function(e) {
            if (viewportDragbarDragging) {
                if (mouseOrigin !== e.pageX) {
                    var viewportRight = document.getElementById('ez3d-viewport-right');
                    var viewportLeft = document.getElementById('ez3d-viewport-left');
                    var viewContainer = document.getElementById('ez3d-viewport-container');
    
                    var leftOffset = ez3dScene.utils.getElementOffset(viewportLeft).left;
                    var viewContainerWidth = viewContainer.getBoundingClientRect().width;
    
                    var percentage = ((e.pageX - leftOffset) / viewContainerWidth) * 100;
    
                    if (percentage > 90) {
                        viewportRight.classList.add('ez3d-viewport-right-hidden');
                        viewportLeft.classList.add('ez3d-viewport-left-full');
    
                        var dragbar = document.getElementById('ez3d-viewport-dragbar');
                        dragbar.classList.add('ez3d-hidden');
                    } else {
                        if (percentage < 20) {
                            percentage = 20;
                        }
                        var viewportRightPercentage = 100 - percentage;
                        viewportRight.style.width = viewportRightPercentage + '%';
                        viewportLeft.style.width = percentage + '%';
                    }
                }
    
                var ghostbar = document.getElementById('ez3d-ghostbar');
                ghostbar.parentNode.removeChild(ghostbar);
                document.onmousemove = null;
                viewportDragbarDragging = false;
    
                ee.emitEvent('updateRender',
                    [{draw2d: true, draw3d: false, updatePanels: true, fitRange: true}]);
            }
        };
    
        /**
         * DRAGBAR dragging action is finished
         * Event onmouseup to trigger finish dragging action
         */
        document.addEventListener('mouseup', finishDragViewport);
    };
    
    EZGuiManager.prototype.showEnableDragMovementButton = function() {
        var value = false;
        if (ez3dScene.mode === 'interactiveMove' && ez3dViewport.svgElements.svgProject.hideAreaContainer === true) {
            value = true;
        }
        return value;
    };
    
    EZGuiManager.prototype.openSubareaView = function() {
        if (!ez3dViewport.svgElements.svgArea) {
            ez3dViewport.svgElements.svgProject.hideAreaContainer = false;
            ez3dViewport.svgElements.svgProject.areaContainerChanging = true;
            ez3dViewport.svgElements.svgProject.splitView();
            ez3dViewport.addAreaContainer(ez3dViewport.svgElements.svgProject);
            ee.emitEvent('updatePanels');
        }
    };
    
    EZGuiManager.prototype.closeSubareaView = function(closePanel, avoidChangeMode) {
        if (!avoidChangeMode && ez3dScene.mode === 'interactiveMove') {
            ez3dScene.active.setInteractiveMoveToggle();
        }
        if (ez3dViewport.svgElements.svgArea) {
            ez3dViewport.removeContainer('svgArea');
        }
        ez3dViewport.svgElements.svgProject.hideAreaContainer = true;
        ez3dViewport.svgElements.svgProject.areaContainerChanging = true;
        ez3dViewport.svgElements.svgProject.collapseView();
        ee.emitEvent('updatePanels');
        if (closePanel) {
            ee.emitEvent('closeSubareaInfoPanel');
        }
    };
    
    EZGuiManager.prototype.shouldSimButtonVisible = function() {
        return this.checkPlayerOn() && this.isEditorClosed();
    };
    
    EZGuiManager.prototype.showOpenSubareaView = function() {
        var value = false;
        if (ez3dViewport.svgElements.svgProject &&
            !ez3dViewport.svgElements.svgProject.split &&
            ez3dScene.active.constructor.name === 'EZModelSubarea') {
            value = true;
        }
        return value;
    };
    
    EZGuiManager.prototype.showCloseSubareaView = function() {
        var value = false;
        if (ez3dViewport.svgElements.svgProject &&
            ez3dViewport.svgElements.svgProject.split &&
            ez3dScene.active.constructor.name === 'EZModelSubarea') {
            value = true;
        }
        return value;
    };
    
    
    // todo da color a los console log dependiendo si son de area, building...
    EZGuiManager.prototype.setColorConsoles = function() {
        /*eslint-disable */
        console.area = function(message) {
            if (ez3dScene.layoutRules.scenePreferences.debugContainers) {
                console.log("%c" + message, 'color:DodgerBlue');
            }
        };
    
        console.project = function(message) {
            if (ez3dScene.layoutRules.scenePreferences.debugContainers) {
                console.log("%c" + message, 'color:Orange');
            }
        };
    
        console.simple = function(message) {
            if (ez3dScene.layoutRules.scenePreferences.debugContainers) {
                console.log("%c" + message, 'color:lightsalmon');
            }
        };
    
        console.clickOn = function(message) {
            if (ez3dScene.layoutRules.scenePreferences.debugContainers) {
                console.log("%c" + message, 'color:lightgreen');
            }
        };
    
        console.pathEditor = function(message) {
            if (ez3dScene.layoutRules.scenePreferences.debugContainers) {
                console.log("%c" + message, 'color:Chartreuse');
            }
        };
        /*eslint-disable */
    }
    
    
    // todo esto deberia ser de utils
    EZGuiManager.prototype.convertUnitsFromMeters = function(value, unit) {
        var conversion;
        switch (unit) {
            case 'm':
                conversion = Number(value) * 1;
                break;
            case 'cm':
                conversion = Number(value) * 100;
                break;
            case 'mm':
                conversion = Number(value) * 1000;
                break;
            case 'ft':
                conversion = Number(value) * 3.28084;
                break;
            case 'in':
                conversion = Number(value) * 39.3701;
                break;
            default:
                conversion = Number(value);
        }
        return conversion;
    };
    EZGuiManager.prototype.convertUnitsToMeters = function(value, unit) {
        var conversion;
        switch (unit) {
            case 'm':
                conversion = Number(value) / 1;
                break;
            case 'cm':
                conversion = Number(value) / 100;
                break;
            case 'mm':
                conversion = Number(value) / 1000;
                break;
            case 'ft':
                conversion = Number(value) / 3.28084;
                break;
            case 'in':
                conversion = Number(value) / 39.3701;
                break;
            default:
                conversion = Number(value);
        }
        return conversion;
    };
    EZGuiManager.prototype.formatMaterialsClassBySelect = function(data) {
        var value = data.toLowerCase();
        var items = value.split(' ');
        for (var i = 0; i < items.length; i++) {
            items[i] = items[i].replace(/[^A-Za-z0-9]/g, '');
            if (items[i] === '') {
                items.splice(i, 1);
            }
        }
        value = items.join('-');
        return value;
    };
    
        /*
     * Recalculate header height to buildings index top position and aside and viewport height.
     */
    EZGuiManager.prototype.recalculateHeaderHeight = function () {
        const headerHeight = d3.select('#ez3d-header').node().offsetHeight;
        this.setAsideAndViewportContainersHeight(headerHeight);
        this.setBuildingsIndexTop();
    };
    
    EZGuiManager.prototype.setAsideAndViewportContainersHeight = function(headerHeight) {
        d3.select('#ez3d-aside')
            .style('height', 'calc(100% - ' + headerHeight + 'px)');
    
        d3.select('#ez3d-viewport')
            .style('height', 'calc(100% - ' + headerHeight + 'px)');
    };
    
    
    EZGuiManager.prototype.setBuildingsIndexTop = function() {
        if (ez3dScene.layoutRules.scenePreferences.showTotalPower) {
            d3.select('#ez3d-aside-buildings-index').style('top', '40px');
        } else {
            d3.select('#ez3d-aside-buildings-index').style('top', 0);
        }
    };
    
    
        EZGuiManager.prototype.generateHeader = function () {
        let headerWidth = 0;
        let headerHeight = 50;
        const header = d3.select('#ez3d-header')
            .style('height', headerHeight + 'px');
        // LOGO
        if (ez3dScene.layoutRules.scenePreferences.showLogo) {
            const logo = header.append('div').attr('id', 'ez3d-logo');
            headerWidth = 314;
            // Check custom logo url or use the default one and set it as background image
            if (ez3dScene.layoutRules.scenePreferences.customLogoUrl !== 'default') {
                ez3dScene.getBase64Image(ez3dScene.layoutRules.scenePreferences.customLogoUrl, function (res) {
                    logo.style('background-image', 'url("' + res + '")');
                });
            }
        }
        // NAV
        if (ez3dScene.layoutRules.scenePreferences.showNav) {
            // NAV
            const nav = header.append('div').attr('id', 'ez3d-nav');
            nav.style('width', 'calc(100% - ' + headerWidth + 'px)');
            this.generateNav();
        }
    };
        'use strict';
    /**
     * translates a key
     * @param  {string} key - key to search
     * @return {string} translation
     */
    EZGuiManager.prototype.i18n = function (key) {
        const keyToSearch = typeof key === 'string' ? key : key.text;
    
        let translate = this.ez3dLanguageManager.i18n(keyToSearch);
    
        if (typeof key !== 'string') {
            Object.keys(key.vars).forEach(function(varName) {
                translate = translate.replace('_' + varName + '_', key.vars[varName]);
            });
        }
        return translate;
    };
    
    /**
     * updates language
     */
    EZGuiManager.prototype.updateLanguage = function () {
        // IMPORTANT: defaultLanguage property must be in lowerCase
        // Parse in case defaultLanguage has 'en_EN' format and set to lower case
        ez3dScene.layoutRules.scenePreferences.defaultLanguage = ez3dScene.layoutRules.scenePreferences.defaultLanguage
        .substring(0, 2)
        .toLowerCase();
        const upperCaseLanguage = ez3dScene.layoutRules.scenePreferences.defaultLanguage.toUpperCase();
        this.ez3dLanguageManager.updateLanguage(upperCaseLanguage);
    };
    
    EZGuiManager.prototype.changeLanguage = function () {
        this.updateLanguage();
        this.updateEZGuiManager();
    };
    
        EZGuiManager.prototype.listeners = function() {
        var self = this;
    
        ee.addOnceListener('viewportReady', function() {
            if (ez3dScene.active === 'none') {
                ee.emitEvent('openBuildingStartPanel');
            }
            // Widget to display when building populated is false
            if (!ez3dScene.layoutRules.defaultModelValues.building.populated) {
                ee.addOnceListener('changePopulateValue', function() {
                    ee.emitEvent('EZModelScene_setAttributeListener', [{
                        args: ['', 'layoutRules.defaultModelValues.building.populated', true],
                        undo: false,
                        notifications: true}]);
                });
                ee.emitEvent('widget_notification', [{
                    'name': 'buildingPopulate',
                    'title': 'buildingPopulateAlertTitle',
                    'content': 'buildingPopulateAlertMessage',
                    'eventOk': [''],
                    'eventCancel': ['changePopulateValue', undefined, 'buildingPopulateAlertBtnContent']
                }]);
            }
        });
    
        ee.addListener('playerReady', function() {
            ee.emitEvent('updatePanels');
        });
    
        ee.addListener('mapper-changed', function() {
            ee.emitEvent('updatePanels');
        });
    
        if (ez3dScene.layoutRules.scenePreferences.enableApi === true) {
            EZApi.createGeneralFunctions('setCustomPanel', 'setCustomPanel');
        }
    
        ee.addListener('setCustomPanel', function(attr, val) {
            var container = self.generatePanel(attr);
        });
    
        // listen clone finished
        ee.addListener('clone_Finished', function () {
            ee.emitEvent('updatePanels');
        });
    
        // listen subarea clone finished
        ee.addListener('cloneSubarea_Finished', function () {
            ee.emitEvent('updatePanels');
        });
    
        // listen widget notification
        ee.addListener('widget_notification', function (obj) {
            self.widgetConstructor(obj);
        });
    
        // Widgets update
        ee.addListener('EZModelScene_setAttributeListener', function(e) {
            if (e.args[1] === 'layoutRules.scenePreferences.defaultLanguage' ||
            (e.args[1] === 'layoutRules.scenePreferences.activeMapper' && ez3dViewport.ez3dGuiManager.widgets.includes('ez3d-widget-search-adress')) ||
            e.args[1] === 'layoutRules.scenePreferences.defaultDateTime' ||
            (e.args[1] === 'shape' && ez3dViewport.ez3dGuiManager.widgets.includes('ez3d-widget-treeShape'))) {
                ez3dViewport.ez3dGuiManager.updateWidgets();
            }
            if (e.args[1] === 'type' && ez3dScene.active.constructor === EZModelRoof) {
                self.manageRoofEditorInfoWidget();
            }
        });
    
        // Building index listeners
        ee.addListener('lockInterface', function() {
            var displayValue = 'none';
            if (ez3dScene.buildings.length === 0 && ez3dScene.active === 'none') {
                displayValue = 'inherit';
            }
            d3.select('#ez3d-aside-buildings-index')
                .style('display', displayValue);
        });
        ee.addListener('unlockInterface', function() {
            d3.select('#ez3d-aside-buildings-index')
                .style('display', 'inherit');
        });
    
        // disabled perspective
        ee.addListener('disable_perspective_button', function () {
            if (!ez3dScene.activeRoof === false) {
                d3.select('#ez3d-perspective-button')
                    .attr('disabled', 'disabled')
                    .classed('ez3d-disabled', true);
            } else {
                d3.select('#ez3d-perspective-button')
                    .attr('disabled', null)
                    .classed('ez3d-disabled', false);
            }
        });
    
        // disabled save Button
        ee.addListener('disable_save_button', function () {
            if (ez3dScene.layoutRules.scenePreferences.readOnly) {
                d3.select('#ez3d-button-save')
                    .attr('title', self.i18n('readOnly'))
                    .attr('disabled', 'disabled')
                    .classed('ez3d-disabled', true);
            } else {
                d3.select('#ez3d-button-save')
                    .attr('disabled', null)
                    .classed('ez3d-disabled', false);
            }
        });
    
        ee.addListener('activeUpdated', function() {
            if (!ez3dScene.active.creatingBuildingShape &&
                (ez3dScene.context === 'pathEditor' ||
                ez3dScene.context === 'location')) {
                var draw = {
                    'name': 'draw',
                    'title': 'snapOptions',
                    'eventOk': '',
                    'eventCancel': '',
                    'eventMinimize': true,
                    'functionOnCreation': ['drawWidget', '']
                };
                // create
                self.widgetConstructor(draw);
            } else if (self.widgets.includes('ez3d-widget-draw')) {
                // destroy
                self.removeWidget('ez3d-widget-draw');
            }
            if (ez3dScene.buildings.length === 0) {
                ee.emitEvent('lockInterface');
            }
            // Roof editor widget
            self.manageRoofEditorInfoWidget();
        });
    
        ee.addListener('setInteractiveMove', function(value) {
            var name = 'move-shortcuts';
            // Create if it doesn't exist, or remove it
            if (self.widgets.indexOf('ez3d-widget-' + name) === -1) {
                if (value === true) {
                    // Remove 'EZModel' from constructor name
                    var title = 'move' + ez3dScene.active.constructor.name.slice(7) + 'ShortcutsWidgetTitle';
                    var moveShortcutsWidget = {
                        'name': name,
                        'title': title,
                        'eventOk': '',
                        'eventCancel': '',
                        'eventMinimize': true,
                        'functionOnCreation': ['moveShortcutsWidget', '']
                    };
                    // Create widget
                    self.widgetConstructor(moveShortcutsWidget);
                } else if (value === false) {
                    self.removeWidget('ez3d-widget-' + name);
                }
            } else {
                self.removeWidget('ez3d-widget-' + name);
            }
        });
    
        // // Listen dilatation lines
        // ee.addListener('updateSystemGrid', function(system) {
        //     self.updateSystemGrid(system);
        // });
    
        // Listen projectCenter
        ee.addListener('EZModelScene_changeProjectCenterListener', function () {
            ez3dScene.customDateTime();
        });
    
        ee.addListener('projectCenterChanged', function() {
            setTimeout(function () {
                ez3dViewport.svgElements.svgProject.flyTo(ez3dScene.projectCenter, 0);
                ez3dViewport.ez3dGuiManager.widgetConstructor({
                    'name': 'projectCenterChanged',
                    'title': 'projectCenterChangedTitle',
                    'content': {
                        text: 'projectCenterChangedMessage',
                        vars: {
                            distance: ez3dScene.layoutRules.scenePreferences.maxDistanceFromCenter
                        }
                    }
                });
            }, 1000);
        })
    
        // TODO: clean this code when refactoring buttons
        if (ez3dScene.layoutRules.scenePreferences.enableUndoRedo) {
            ee.addListener('updateUndoRedoButtons', function () {
                setTimeout(function () {
                    // Undo button
                    const undoButton = d3.select('#ez3d-button-undo');
                    ez3dScene.history.checkSubcontext();
                    let hEvent;
                    let hClass;
                    if (ez3dScene.history.operator) {
                        hEvent = EZModelUtils.capitalize(ez3dScene.history.operator.event);
                        hClass = EZModelUtils.capitalize(ez3dScene.history.operator.class);
                    }
                    undoButton
                        .attr('display',
                            (self.checkEnabledUndo()) ? 'inherit' : 'none'
                        );
    
                    if (self.checkDisabledUndo()) {
                        undoButton
                            .attr('title', 'Undo')
                            .attr('disabled', 'disabled')
                            .classed('ez3d-disabled', true)
                            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
                    } else {
                        undoButton
                            .attr('title', 'Undo: ' + hEvent + ' ' + hClass)
                            .attr('disabled', null)
                            .classed('ez3d-disabled', false)
                            .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
                        // .attr('title', self.i18n('undoRedoModulesExceded'));
                    }
    
                    // Redo button
                    const redoButton = d3.select('#ez3d-button-redo');
                    redoButton
                        .attr('title', 'Redo: ' + hEvent + ' ' + hClass)
                        .attr('display',
                            (self.checkEnabledRedo()) ? 'inherit' : 'none'
                        );
                    // .attr('title', self.i18n('undoRedoModulesExceded'));
                }, 0);
            });
        }
    
        // Listener to show widget when generating textures progress is taking too long
        ee.addListener('manageCancelTexturesWidget', function(create) {
            var widgetName = 'cancelTextures';
            if (create) {
                // Create widget
                var cancelTexturesWidget = {
                    'name': widgetName,
                    'title': 'cancelTexturesTitle',
                    'functionOnCreation': ['cancelTexturesWidget', '']
                };
                ez3dViewport.ez3dGuiManager.widgetConstructor(cancelTexturesWidget);
                d3.select('#ez3d-widget-' + widgetName).style('z-index', 1010);
            } else if (self.widgets.indexOf('ez3d-widget-' + widgetName) > -1) {
                // Remove widget if it exists
                ez3dViewport.ez3dGuiManager.removeWidget('ez3d-widget-' + widgetName);
            }
        });
    
        // roof
        ee.addListener('roofTypeChanged', function () {
            self.roofTypeDisabledScreen();
        });
    };
    
        EZGuiManager.prototype.generateProgressBar = function() {
        var progressBarScreen = d3.select('#ez3d-progressBar-screen')
            .classed('ez3d-flex-center-center', true);
    
        // Background
        progressBarScreen
            .append('div')
            .attr('id', 'ez3d-progressBar-screen-background');
    
        var content = progressBarScreen
            .append('div')
            .attr('id', 'ez3d-progressBar-screen-content')
            .classed('ez3d-flex-center-center-column', true);
    
        // Image
        content
            .append('div')
            .attr('id', 'ez3d-progressBar-gif');
    
        var title = content
            .append('p')
            .attr('id', 'ez3d-progressBar-title');
    
        var label1 = content
            .append('p')
            .attr('id', 'ez3d-progressBar-label1');
    
        var label2 = content
            .append('p')
            .attr('id', 'ez3d-progressBar-label2');
    
        var progress = content
            .append('div')
            .attr('id', 'ez3d-progressBar-barContainer');
    
        var progressBar = progress
            .append('div')
            .attr('id', 'ez3d-progressBar-bar')
            .classed('ez3d-progressBar-animation', true)
            .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.third);
    
        // Close button
        this.createProgressBarCancelBtn(false);
    
        /*
         * ee.emitEvent('showProgressBar');
         *
         * Shows an empty progress bar.
         * This event must be emitted if the changes on the progress bar want to be displayed.
         */
        ee.addListener('showProgressBar', function() {
            ez3dViewport.ez3dGuiManager.showProgressBar();
        });
    
        /*
         * ee.emitEvent('resetProgressBar');
         *
         * Resets progress bar width and animation.
         * Emit this event before starting a new step (new title or label1) with values.
         */
        ee.addListener('resetProgressBar', function() {
            progressBar.style('width', '0%');
            progressBar.classed('ez3d-progressBar-transition', false);
            progressBar.classed('ez3d-progressBar-animation', true);
        });
    
        /*
         * ee.emitEvent('updateProgressBar', [{
         *     title: 'Title',
         *     label1: 'Label 1',
         *     label2: 'Label 2',
         *     value: 0
         * }]);
         */
        ee.addListener('updateProgressBar', function(data) {
            var newStep = false;
    
            // Set title and reset progress bar
            if (data.title && data.title !== title.html()) {
                newStep = true;
                ee.emitEvent('resetProgressBar');
                title.html(ez3dViewport.ez3dGuiManager.i18n(data.title));
            }
    
            // Set label1 or reset it if title has been changed
            if (data.label1 && data.label1 !== label1.html()) {
                newStep = true;
                label1.html(ez3dViewport.ez3dGuiManager.i18n(data.label1));
            } else if (newStep) {
                label1.html('');
            }
    
            // Set label2
            label2.html((data.label2) ? ez3dViewport.ez3dGuiManager.i18n(data.label2) : '');
    
            // Reset progress bar when it's a new step without value
            if (newStep && isNaN(data.value) && !progressBar.classed('ez3d-progressBar-animation')) {
                ee.emitEvent('resetProgressBar');
            }
    
            // Set progress bar width
            if (!isNaN(data.value)) {
                progressBar.classed('ez3d-progressBar-animation', false);
                progressBar.classed('ez3d-progressBar-transition', true);
                progressBar.style('width', parseFloat(progressBar.style('width').slice(0, -1)) + data.value + '%');
            }
        });
    
        /*
         * ee.emitEvent('hideProgressBar');
         *
         * Hides the progress bar and resets its values.
         */
        ee.addListener('hideProgressBar', function() {
            d3.select('#ez3d-progressBar-screen')
                .transition()
                .duration(0)
                .style('opacity', 0)
                .style('pointer-events', 'none');
            title.html('');
            label1.html('');
            label2.html('');
            ez3dViewport.ez3dGuiManager.createProgressBarCancelBtn(false);
            ee.emitEvent('resetProgressBar');
        });
    
        /*
         * ee.emitEvent('showProgressBarCancelBtn', [boolean]);
         *
         * Manages the visibility of the progress bar cancel button.
         */
        ee.addListener('showProgressBarCancelBtn', function(enableButton) {
            ez3dViewport.ez3dGuiManager.createProgressBarCancelBtn(enableButton);
        });
    
        /*
         * ee.emitEvent('updateProgressBarCancelBtn');
         *
         * Removes and recreates the cancel button (used when changing language).
         */
        ee.addListener('updateProgressBarCancelBtn', function() {
            ez3dViewport.ez3dGuiManager.createProgressBarCancelBtn(false);
        });
    };
    
    /**
     * Sets progress bar visible.
     */
    EZGuiManager.prototype.showProgressBar = function() {
        if (ez3dScene.layoutRules.scenePreferences.viewportMode !== 3) {
            d3.select('#ez3d-progressBar-screen')
                .transition()
                .duration(0)
                .style('opacity', 1)
                .style('pointer-events', 'auto');
        }
    };
    
    /**
     * Recreates cancel button when language has changed or it has to be enabled/disabled.
     * @param  {boolean} enableButton   Set button visibility visible or hidden.
     */
    EZGuiManager.prototype.createProgressBarCancelBtn = function(enableButton) {
        // Remove button if it exists
        if (d3.select('#ez3d-progressBar-cancelBtn').node()) {
            d3.select('#ez3d-progressBar-cancelBtn').node().parentNode.remove();
        }
        // Create class list
        var classList = 'ez3d-square-button ez3d-square-button-blue';
        if (!enableButton) {
            classList += ' ez3d-square-disabled';
        }
    
        // Create button
        var cancelBtn = {
            cancelProgressBar: {
                type: 'button',
                id: 'ez3d-progressBar-cancelBtn',
                classed: classList,
                content: 'cancel',
                tooltip: 'Esc',
                eventOnClick: ['cancelProgressBar']
            }
        };
        this.populateBlocks(cancelBtn, d3.select('#ez3d-progressBar-screen-content'));
    };
    
        EZGuiManager.prototype.generateSpinner = function(svg) {
        const loader = svg.svgContainer
            .append('div')
            .attr('id', '#ez3d-drawing-spinner_' + svg.name)
            .attr('class', 'ez3d-drawing-spinner ez3d-flex-center-center');
    
        loader.classed('hide-spinner', true);
    
        loader.append('i')
            .attr('class', 'ez3d-drawing-spinner-icon fa fa-cog fa-spin');
    
        /*
         * ee.emitEvent('show_containerName_spinner');
         *
         * Shows an empty progress bar
         * This event must be emited if the changes on the progress bar want to be displayed
         */
        ee.addListener('show_' + svg.name + '_spinner', () => {
            loader.classed('hide-spinner', false);
        });
    
        /*
         * ee.emitEvent('hide_containerName_spinner');
         *
         * Hides the progress bar and resets its values
         */
        ee.addListener('hide_' + svg.name + '_spinner', () => {
            loader.classed('hide-spinner', true);
        });
    };
    
        /**
     * EZGuiManager manages the aside panels, the building index,
     * all buttons, widgets, progress bar and modal windows
     * @class
     * @param {[type]} ez3dViewport [description]
     */
    function EZGuiManager(ez3dViewport) {
        // ez3dScene = ez3dScene;
        this.ez3dViewport = ez3dViewport;
    
        /**
         * [units description]
         * @name units
         * @type {Array}
         */
        this.units = [
            {
                name: 'm',
                conversion: 1
            },
            {
                name: 'cm',
                conversion: 100
            },
            {
                name: 'mm',
                conversion: 1000
            },
            {
                name: 'ft',
                conversion: 3.28084
            },
            {
                name: 'in',
                conversion: 39.3701
            }
        ];
        this.ez3dLanguageManager = new EZLanguageManager();
        this.updateLanguage();
        this.updateNavigation = true;
    
        this.tabs = [];
        this.panels = [];
    
        this.listeners();
    
        // Generate panels container
        if (ez3dScene.layoutRules.scenePreferences.showHeader) {
            this.generateHeader();
        }
        this.recalculateHeaderHeight();
    
        if (ez3dScene.projectType !== 'simple') {
            this.generateTabsAsociatedPanel(ez3dScene.asideNavPanel());
            this.generateTabsAsociatedPanel(ez3dScene.objectsPanel(), d3.select('#objects-panel_ez3dInfoPanel'));
    
            this.generatePanel(ez3dScene.startInfoPanel());
    
            this.generatePanel(ez3dScene.buildingEditPanel());
            this.generatePanel(ez3dScene.buildingCreatePanel());
            this.generatePanel(ez3dScene.roofCreatePanel());
            this.generatePanel(ez3dScene.roofEditPanel());
    
            this.generatePanel(ez3dScene.subareaInfoPanel());
            this.generatePanel(ez3dScene.subareaEditPanel());
            this.generatePanel(ez3dScene.subareaCreatePanel());
    
            this.generatePanel(ez3dScene.keepoutEditPanel());
            this.generatePanel(ez3dScene.keepoutCreatePanel());
    
            if (ez3dScene.layoutRules.scenePreferences.enableBuildingShapes) {
                this.generatePanel(ez3dScene.buildingCreateShapePanel());
            }
        }
    
        this.updateEZGuiManager();
    
        this.generateButtons(ez3dScene.mainButtonsInfoPanel(), false);
        this.generateContextPanel();
        this.generateProgressBar();
    
        this.viewportDragbar();
        this.temporalShortcuts();
    
        this.calculateBuildingIndexSize();
        this.buildingIndexPage = 1;
    
        this.widgets = [];
        this.widgetElements = [];
    
        /* player */
        this.activePlayer = true;
        /* main buttons*/
        this.change3d = true;
        this.isFullScreen = false;
        this.checkedplaySun = true;
        this.containerProperties = {};
    
        this.updateFunctions = [];
    
        if (ez3dScene.projectType === 'simple') {
            this.generatePanel(ez3dScene.simpleBuildingPanel());
            this.generatePanel(ez3dScene.simpleSubareaPanel());
            ee.emitEvent('openSimpleBuildingPanel');
        }
    
        if (ez3dScene.layoutRules.scenePreferences.enablePlayer) {
            // d3.select('#svgContainer-svgProject')
            //     .style('background-color', 'transparent !important');
        } else {
            d3.select('#playerContainer')
                .style('display', 'none !important');
        }
        // create the tooltip
        this.tooltipCreate();
        this.setColorConsoles();
    
        // // count events in event emitter
        // var count = 0;
        // Object.keys(ee._getEvents()).forEach(function(event) {
        //     if (Array.isArray(ee._getEvents()[event])) {
        //         var length = ee._getEvents()[event].length;
        //         count += length;
        //     }
        // });
        // console.log('event count', count);
    }
    
        
        EZGuiManager.prototype.generateNav = function () {
       d3.select('#ez3d-nav')
            .classed('ez3d-flex-around-center-row', true);
    };
    
        EZGuiManager.prototype.notificationManager = function(notification) {
        var self = this;
        if (ez3dScene.layoutRules.scenePreferences.enableNotifications) {
            const notificationText = self.i18n(notification[0]);
            const notificationData = notification[1];
    
            self.notificationsList = d3.select('#ez3d-notification-container').selectAll('.notification').nodes();
            const lastNotification = d3.select(self.notificationsList[self.notificationsList.length - 1]);
    
            if (!lastNotification.node() || lastNotification.select('p').html() !== notificationText) {
                // Create notification
                const notificationDiv = d3.select('#ez3d-notification-container')
                    .append('div')
                    .attr('id', 'ez3d-notification-div_' + self.notificationsList.length)
                    .attr('class', 'notification notification-open notification-' + notificationData.style)
                    .attr('index', self.notificationsList.length)
                    .attr('pointer-events', 'none');
                notificationDiv
                    .append('p')
                    .html(notificationText);
                notificationDiv
                    .transition()
                    .duration(500)
                    .ease(d3.easePoly)
                    .style('width', '100%');
    
                if (notificationData.event) {
                    const finishEvent = notificationData.event;
                    const cancelEvent = notificationData.event.split('Finished')[0] + 'Cancelled';
    
                    const cancelEventFunction = function() {
                        ee.removeListener(notificationData.event, finishEventFunction);
                        self.removeNotification(notificationDiv, true);
                    };
    
                    const finishEventFunction = function() {
                        ee.removeListener(cancelEvent, cancelEventFunction);
                        self.removeNotification(notificationDiv, true);
                    };
                    ee.addOnceListener(finishEvent, finishEventFunction);
                    ee.addOnceListener(cancelEvent, cancelEventFunction);
                } else {
                    self.removeNotification(notificationDiv);
                }
    
                if (notificationData.spiner) {
                    notificationDiv
                        .append('div')
                        .attr('id', 'ez3d-notification-spinner')
                        .classed('notification-spiner', notificationData.spiner)
                        .lower();
                }
            }
        }
    };
    
    EZGuiManager.prototype.removeNotification = function(notificationDiv, isFromEvent) {
        notificationDiv
            .transition()
            .delay(isFromEvent ? 500 : 1000)
            .duration(500)
            .ease(d3.easePoly)
            .style('width', '0%')
            .remove();
    };
    
        /**
     *
     *  * Method to prepare the panel to be create
     *
     * @param {object} data this is a json data for construct the block
     * @param {string} [data.name] panel name
     * @param {string} [data.json] json name
     * @param {string} [data.model] model element
     * @param {string} [data.object] object name
     * @param {string} [data.populate] function name to generate panel
     * @param {array} [data.customMethods] Add extra custom methods to the panel
     * @param {string} [data.customGuiMethods] Add custom methods to the panel
     * @param {string} [data.customListeners] Add custom listeners to the panel
     * @param {object} customContainer The panel created will be appended to this container
     * @return {string} container
     */
    EZGuiManager.prototype.generatePanel = function (data, customContainer) {
        var self = this;
        var name = data.name;
        var camelCaseName = name;
        if (data.name.split('-').length > 1) {
            camelCaseName = ez3dScene.utils.camelCaseWithSeparator(data.name, '-');
        }
    
        // add extra methods
        if (data.customMethods) {
            self.customMethods(data);
        }
        // add extra GUI methods
        if (data.customGuiMethods) {
            self.customGuiMethods(data.customGuiMethods);
        }
        // add extra GUI listeners
        if (data.customListeners) {
            self.customListeners(data.customListeners);
        }
    
        // add to this.panels
        if (this.panels.indexOf('ez3d-aside-' + name) === -1) {
            this.panels.push('ez3d-aside-' + name);
        }
    
        var parentContainer = (customContainer) ? customContainer : d3.select('#ez3d-gui-container');
    
        var openEvent = 'open' + ez3dScene.utils.capitalize(camelCaseName) + 'Panel';
        var closeEvent = 'close' + ez3dScene.utils.capitalize(camelCaseName) + 'Panel';
        var updateEvent = 'update' + ez3dScene.utils.capitalize(camelCaseName) + 'Panel';
    
        var container = parentContainer.append('div')
            .attr('id', 'ez3d-aside-' + name)
            .classed('ez3d-main-template', true)
            .classed('ez3d-panel', true);
    
        if (!customContainer) {
            container.ez3dBottomPanel = container.append('div')
                .attr('id', name + '-panel_ez3dBottomPanel')
                .attr('class', 'ez3d-bottom-panel ez3d-flex-center-center');
        }
    
        container.ez3dInfoPanel = container.append('div')
            .attr('id', name + '-panel_ez3dInfoPanel')
            .attr('class', 'ez3d-info-panel');
    
        ee.addListener(openEvent, function() {
            if (ez3dScene.active.constructor.name === data.model || data.model === '') {
                d3.selectAll('.ez3d-main-template')
                    .classed('ez3d-active-panel', function() {
                        var value = false;
                        if (this.id === 'ez3d-aside-' + name) {
                            value = true;
                        }
                        return value;
                    });
                ee.emitEvent(openEvent + '_Finished');
                self.activePanel = 'ez3d-aside-' + name;
            } else {
                console.log('custom panel failed to open; First select a ' + data.model + 'object');
            }
        });
    
        ee.addListener(closeEvent, function() {
            d3.select('#ez3d-aside-' + name)
                .classed('ez3d-active-panel', false);
        });
    
        // generic update on universal unit inputs
        ee.addOnceListener('setUnits_Finished', function() {
            ee.emitEvent(updateEvent);
        });
    
        ee.addListener(updateEvent, function(attr) {
            if (data.populate) {
                if (attr) {
                    self[data.populate](attr);
                } else {
                    self[data.populate](data);
                }
            }
            ee.emitEvent(updateEvent + '_Finished');
        });
    
        ee.emitEvent(updateEvent);
    
        if (ez3dScene.layoutRules.scenePreferences.enableApi === true) {
            EZApi.createGeneralFunctions(openEvent, openEvent);
            EZApi.createGeneralFunctions(closeEvent, closeEvent);
            EZApi.createGeneralFunctions(updateEvent, updateEvent);
        }
    
        return container;
    };
    
    /**
     *
     *  * Method to generate a div panel associated with each navigation element in the main menu
     *
     * @param {object} data             Json data for construct the block
            [data.customGuiMethods] Add custom methods to the panel
            [data.customListeners] Add custom listeners to the panel
            [data.json] nav elements
     * @param {object} customContainer  Json data for construct the block
     */
    EZGuiManager.prototype.generateTabsAsociatedPanel = function (data, customContainer) {
        var self = this;
    
        // Add extra methods
        if (data.customMethods) {
            self.customMethods(data);
        }
        // Add extra Gui methods
        if (data.customGuiMethods) {
            self.customGuiMethods(data.customGuiMethods);
        }
        // Add extra GUI listeners
        if (data.customListeners) {
            self.customListeners(data.customListeners);
        }
    
        var tabElements = data.json.elements;
    
        Object.keys(tabElements).forEach(function(jsonIndex) {
            var name = tabElements[jsonIndex].name;
    
            var parentContainer = (customContainer) ? customContainer : d3.select('#ez3d-gui-container');
            var container = parentContainer.select('div#ez3d-aside-' + name);
    
            if (container.node() === null) {
                container = parentContainer.append('div')
                    .attr('id', 'ez3d-aside-' + name)
                    .classed('ez3d-main-template', true);
                if (data.json.type === 'imageNavigator') {
                    container.classed('ez3d-asociated-panel', true);
                }
    
                container.ez3dInfoPanel = container.append('div')
                    .attr('id', name + '-panel_ez3dInfoPanel')
                    .attr('class', 'ez3d-info-panel ez3d-info-panel-tab');
            }
    
            var tab = 'ez3d-' + name + '-tab';
            self.tabs.push(tab);
        });
    
        self.activeTab = self.tabs[0];
    };
    
    /**
     * Method to select and clean the panel before putting the different blocks.
     * @param {object} data this is a json data for construct the block
     * @param {string} [data.name] panel name
     * @param {string} [data.json] json name
     * @param {string} [data.model] panel model
     */
    EZGuiManager.prototype.updatePanel = function (data) {
        var name = data.name;
        var json = data.json;
        var ez3dBottomPanel = d3.select('#' + name + '-panel_ez3dBottomPanel');
        var ez3dInfoPanel = d3.select('#' + name + '-panel_ez3dInfoPanel');
        ez3dBottomPanel.selectAll('*').remove();
        ez3dInfoPanel.selectAll('*').remove();
    
        // get json
        if (json.constructor.name === 'String') {
            if (ez3dScene.active.constructor.name === data.model) {
                json = ez3dScene.active[json]();
            } else if (data.model === '' || data.model === 'EZModelScene') {
                json = ez3dScene[json]();
            }
        }
    
        if (ez3dScene.active.constructor.name === data.model || data.model === '' ||
            data.model === 'EZModelObjects' || data.model === 'EZModelKeepout' || data.model === 'EZModelTree' ||
            data.model === 'EZModelArea' || data.model === 'EZModelNav' || data.model === 'EZModelScene') {
            this.populateBlocks((data.model === 'EZModelNav') ? data : json, ez3dInfoPanel, ez3dBottomPanel, data.model);
        }
    };
    
        EZGuiManager.prototype.temporalShortcuts = function() {
        var initialTime = 0;
        var elapsedTime = 0;
    
        /**
         * Function to check if its Ctrl in Windows and Command in Mac.
         * @param {type} e      Event.
         * @return {boolean}    Key.
         */
        function modifierKeyChecker (e) {
            var key;
            if (ez3dScene.layoutRules.scenePreferences.userData.OS === 'MacOS') {
                key = e.metaKey;
            } else {
                key = e.ctrlKey;
            }
    
            return key;
        }
    
        /**
         * Check mode.
         * @param {type} e  Event.
         * @return {number} Mode.
         */
        function checkMode(e) {
            // mode = 1: Arrows
            // mode = 2: Shift + Arrows
            // mode = 3: Shift + Ctrl/Command + Arrows
            var mode = (e.shiftKey) ? 1 : 0;
            var modifierKey = modifierKeyChecker(e);
    
            mode = (modifierKey && mode) ? 2 : mode;
            return mode;
        }
    
        /**
         * Function to toggle between lines an vertices in pathEditor.
         * @param {type} e  Event.
         */
        function toggleLinesVertices(e) {
            var modifierKey = modifierKeyChecker(e);
            if (modifierKey && ez3dScene.context === 'pathEditor') {
                var svgProject = ez3dViewport.svgElements.svgProject;
                // console.log('shift down');
                if (svgProject.draggingLocation === false) {
                    svgProject.snapFastToggle = true;
                    ee.emitEvent('updateRender',
                        [{draw2d: true, draw3d: false, updatePanels: false, fitRange: false}]);
                }
                // Check repetitive Ctrl in Windows
                if (e.ctrlKey && ez3dScene.layoutRules.scenePreferences.userData.OS === 'Windows') {
                    if (initialTime === 0) {
                        initialTime = new Date().getTime();
                        elapsedTime = new Date().getTime();
                    } else {
                        elapsedTime = new Date().getTime();
                        // Check time between first Ctrl and now
                        if (elapsedTime - initialTime >= 1000 && elapsedTime - initialTime <= 1500) {
                            ee.emitEvent('widget_notification', [{
                                'name': 'ctrlAlert',
                                'title': 'ctrlAlertTitle',
                                'functionOnCreation': ['ctrlAlertWidget']
                            }]);
                            initialTime = 0;
                            elapsedTime = 0;
                        }
                    }
                }
            } else if (ez3dScene.context === 'pathEditor' && ez3dScene.layoutRules.scenePreferences.userData.OS === 'Windows') {
                initialTime = 0;
                elapsedTime = 0;
            }
        }
    
        /**
         * Function to save project.
         * @param {type} e  Event.
         */
        function saveProjectShortcut (e) {
            var modifierKey = modifierKeyChecker(e);
            if (e.keyCode === 83 && modifierKey && e.shiftKey) {
                d3.select('#ez3d-button-save').dispatch('click');
            }
        }
    
        /**
         * [checkKeyUp description]
         * @param  {[type]} e [description]
         */
        function checkKeyUp(e) {
            // If there isn't any panels element focused
            if (document.activeElement.tagName === 'BODY') {
                // Check modifier key
                var modifierKey;
                if (ez3dScene.layoutRules.scenePreferences.userData.OS === 'MacOS') {
                    modifierKey = e.keyCode === 91 || e.keyCode === 93;
                } else {
                    modifierKey = e.keyCode === 17;
                }
                // Shift || Ctrl/Command
                if (e.keyCode === 16 || modifierKey) {
                    var svgProject = ez3dViewport.svgElements.svgProject;
                    if (svgProject && svgProject.draggingLocation === false) {
                        // Ctrl/Command
                        if (modifierKey && ez3dScene.context === 'pathEditor') {
                            svgProject.snapFastToggle = false;
                            // console.log('shift up');
                        }
                        ee.emitEvent('updateRender',
                            [{draw2d: true, draw3d: false, updatePanels: false, fitRange: false}]);
                    }
                }
            }
        }
    
        /**
         * [checkKey description]
         * @param  {[type]} e [description]
         */
        function checkKey(e) {
            // If there isn't any panels element focused
            if (document.activeElement.tagName === 'BODY' || document.activeElement.tagName === 'BUTTON') {
                e = e || window.event;
                const delta = {x: 0, y: 0, z: 5};
    
                // Navigate through tabs
                if (ez3dScene.mode === 'standard' && ez3dViewport.lockedInterface !== true &&
                (e.keyCode === 49 || e.keyCode === 50 || e.keyCode === 51 || e.keyCode === 52)) {
                    // 1 || 2 || 3 || 4
                    d3.select('#' + ez3dViewport.ez3dGuiManager.tabs[e.key - 1]).dispatch('click');
                }
    
                if (ez3dScene.mode === 'interactiveMove') {
                    if (e.keyCode === 103 || e.keyCode === 36 ||
                        e.keyCode === 104 || e.keyCode === 38 ||
                        e.keyCode === 105 || e.keyCode === 33) {
                        // Numpad 7 || Home || Numpad 8 || Up arrow || Numpad 9 || Page up
                        // Up
                        delta.y = -1;
                        delta.z += 3;
                    } else if (
                        e.keyCode === 97 || e.keyCode === 35 ||
                        e.keyCode === 98 || e.keyCode === 40 ||
                        e.keyCode === 99 || e.keyCode === 34) {
                        // Numpad 1 || End || Numpad 2 || Down arrow || Numpad 3 || Page down
                        // Down
                        delta.y = 1;
                        delta.z -= 3;
                    }
                    if (e.keyCode === 97 || e.keyCode === 35 ||
                        e.keyCode === 100 || e.keyCode === 37 ||
                        e.keyCode === 103 || e.keyCode === 36) {
                        // Numpad 1 || End || Numpad 4 || Left arrow || Numpad 7 || Home
                        // Left
                        delta.x = -1;
                        delta.z -= 1;
                    } else if (
                        e.keyCode === 99 || e.keyCode === 34 ||
                        e.keyCode === 102 || e.keyCode === 39 ||
                        e.keyCode === 105 || e.keyCode === 33) {
                        // Numpad 3 || Page down || Numpad 6 || Right arrow || Numpad 9 || Page up
                        // Right
                        delta.x = 1;
                        delta.z += 1;
                    }
                    if (e.keyCode === 101 || e.keyCode === 12) {
                        // Numpad 5 || Clear
                        // Center
                        delta.z = 0;
                    }
                    if (delta.x !== 0 || delta.y !== 0 || delta.z === 0) {
                        e.preventDefault();
    
                        // Check interactiveMove mode
                        const mode = checkMode(e);
    
                        const index = (delta.z) ? delta.z : 5;
    
                        // @todo get real grid size
                        delta.x *= ez3dScene.layoutRules.scenePreferences.movementStep;
                        delta.y *= ez3dScene.layoutRules.scenePreferences.movementStep;
    
                        if (ez3dScene.context === 'subarea') {
                            ee.emitEvent('EZModelSubarea_moveSystemListener', [{
                                args: [delta, mode, index],
                                updateRender: {draw2d: true, draw3d: true, updatePanels: false, fitRange: false},
                                progress: true
                            }]);
                        } else if (ez3dScene.context === 'area') {
                            const subarea = ez3dScene.getMovingObject();
                            const loc = subarea.path.center;
                            delta.x += loc.x;
                            delta.y += loc.y;
                            const translatedPos = ez3dScene.utils.translateSphericalByCartesianOffset(loc, delta);
                            const newPos = {
                                lat: translatedPos.lat - loc.lat,
                                lng: translatedPos.lng - loc.lng
                            };
    
                            ee.emitEvent('EZModelArea_moveSubareaListener', [{
                                args: [ez3dScene.active.subareaToMove, newPos],
                                updateRender: {draw2d: true, draw3d: false, updatePanels: false, fitRange: false},
                                progress: true
                            }]);
                        } else {
                            ee.emitEvent(ez3dScene.active.constructor.name + '_moveByCartesianCoordsListener', [{
                                args: delta,
                                undo: true,
                                updateRender: {draw2d: true, draw3d: ez3dScene.active, updatePanels: false, fitRange: false},
                                progress: true
                            }]);
                        }
                    }
                }
    
                // Esc: close progress bar if there aren't any widgets and close button is displayed
                if (e.keyCode === 27 && ez3dViewport.ez3dGuiManager.widgets.length === 0 && d3.select('#ez3d-progressBar-cancelBtn').style('display') === 'inherit') {
                    d3.select('#ez3d-progressBar-cancelBtn').dispatch('click');
                }
    
                // Closing widgets (Ok, Cancel and cross)
                if (ez3dViewport.ez3dGuiManager.widgets.length > 0) {
                    var activeWidget = ez3dViewport.ez3dGuiManager.widgetElements.filter((widget) => {
                        return widget.active === true;
                    })[0];
                    if (e.keyCode === 13) {
                        // Intro
                        activeWidget.mainContainer.select('.ok').dispatch('click');
                    } else if (e.keyCode === 27) {
                        // Esc
                        activeWidget.mainContainer.select('.cancel').dispatch('click');
                        activeWidget.mainContainer.select('.fa-times').dispatch('click');
                    }
                }
    
                // Toggle between lines and vertices
                toggleLinesVertices(e);
    
                // Enable/disable snap to guides: s
                if (e.keyCode === 83 && ez3dScene.context === 'pathEditor') {
                    d3.select('#snap-to-guides').dispatch('click');
                }
    
                if (ez3dScene.layoutRules.scenePreferences.enableUndoRedo) {
                    // Undo: Ctrl + z
                    if (e.ctrlKey && e.keyCode === 90) {
                        d3.select('#ez3d-button-undo').dispatch('click');
                    }
                    // Redo: Ctrl + y
                    if (e.ctrlKey && e.keyCode === 89) {
                        d3.select('#ez3d-button-redo').dispatch('click');
                    }
                }
    
                // Save: Shift + Ctrl + s
                saveProjectShortcut(e);
            }
        }
    
        document.onkeydown = checkKey;
        document.onkeyup = checkKeyUp;
    };
    
        EZGuiManager.prototype.cancelTexturesWidget = function () {
        var self = this;
        var name = 'ez3d-widget-cancelTextures';
        var widget = d3.select('#' + name);
        var container = widget.select('.ez3d-widget-content');
    
        container.selectAll('*').remove();
    
        var elements = {
            issueInfo: {
                type: 'helper',
                classed: 'helper',
                content: 'cancelTexturesMessage1'
            },
            buttonInfo: {
                type: 'helper',
                classed: 'helper',
                content: 'cancelTexturesMessage2'
            },
            cancelButton: {
                type: 'blockList',
                classed: '',
                elements: {
                    cancelProgressBar: {
                        type: 'button',
                        id: 'ez3d-progressBar-cancelBtn',
                        classed: 'ez3d-square-button ez3d-square-button-blue',
                        content: 'cancel',
                        eventOnClick: ['cancelProgressBar']
                    }
                }
            }
        };
        self.populateBlocks(elements, container, '');
    };
    
        EZGuiManager.prototype.colorPaletteWidget = function (element) {
        var self = this;
        var name = 'ez3d-widget-color-palette-selector-' + element;
        var widget = d3.select('#' + name);
        var container = widget.select('.ez3d-widget-content');
        self.customColor = [];
        self.attr = 'color';
        self.pathFill = '';
    
        if (element === 'subarea') {
            self.attr = 'system.color';
            self.pathFill = ez3dScene.layoutRules.defaultColors.moduleColors.path.module.fill;
        } else {
            self.pathFill = ez3dScene.layoutRules.defaultColors[ez3dScene.context + 'Colors'].path.standard.fill;
        }
    
        self.customColor = self.customColor.concat(ez3dScene.layoutRules.defaultColors.customColor);
    
        var widgetContentColor = container.append('div')
            .attr('class', 'model-attribute model-attribute ez3d-flex-between-align-items-start');
    
        var customColorContainer = widgetContentColor.append('div')
            .attr('class', 'ez3d-custom-color-container ez3d-flex-baseline-wrap');
    
        /**
         * [temporalColor description]
         * @param  {[type]} color [description]
         */
        function temporalColor(color) {
            if (ez3dScene.context === 'building') {
                ez3dScene.layoutRules.defaultColors.buildingColors.path.active.fillOpacity = 1;
                ee.emitEvent('updateRender',
                    [{draw2d: true, draw3d: ez3dScene.active, updatePanels: false, fitRange: false}]);
                d3.select('#svgProject-L1_building_' + ez3dScene.active.id).select('.building-shape').attr('fill', color);
            } else if (ez3dScene.context === 'keepout') {
                d3.select('#svgProject_keepout_' + ez3dScene.active.id).select('.keepout-shape').attr('fill', color);
            } else if (ez3dScene.context === 'subarea') {
                d3.select('#svgProject_system_' + ez3dScene.active.system.id).selectAll('.ez3d-module').attr('fill', color);
                d3.select('#svgArea_system_' + ez3dScene.active.system.id).selectAll('.ez3d-module').attr('fill', color);
            }
        }
    
        /**
         * [addCustomColors description]
         */
        function addCustomColors () {
            customColorContainer.selectAll('*').remove();
    
            for (var i = 0; i < self.customColor.length; i++) {
                customColorContainer.append('div')
                    .attr('class', 'ez3d-custom-modules-color')
                    .attr('value', self.customColor[i])
                    .style('background', self.customColor[i])
                    .on('click', function () {
                        self.color = this.attributes[1].nodeValue;
                        customColorContainer.selectAll('.ez3d-custom-modules-color').style('border', 'none');
                        d3.select(this).style('border', '2px solid' + ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
                        temporalColor(self.color);
                    });
            }
        }
    
        addCustomColors();
    
        if (ez3dScene.layoutRules.defaultModelValues.subarea.hidden.indexOf('moreColors') === -1) {
            var moreColors = widgetContentColor.append('div')
                .attr('class', 'ez3d-more-colors ez3d-flex-end-wrap-row');
    
            // more colors button
            var a = moreColors.append('button')
                .attr('class', 'ez3d-square-button ez3d-square-button-blue default ez3d-button-color')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray)
                .html(self.i18n('moreColors'))
                .on('mouseover', function () {
                    d3.select(this)
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                })
                .on('mouseout', function () {
                    d3.select(this)
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
                });
    
            a.append('input')
                .attr('type', 'color')
                .attr('value', '#00ffa7')
                .attr('class', 'ez3d-input-color')
                .on('change', function () {
                    self.color = event.target.value;
    
                    if (self.customColor.indexOf(self.color) === -1) {
                        self.customColor.push(self.color);
                    }
    
                    addCustomColors();
    
                    var parent = d3.select('.ez3d-custom-color-container').node();
                    var lastChild = d3.select(parent.lastChild).node();
                    d3.select(lastChild).dispatch('click');
                });
    
            // default color button
            moreColors.append('button')
                .attr('class', 'ez3d-square-button ez3d-square-button-blue default')
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray)
                .html(self.i18n('setDefaultColor'))
                .on('click', function () {
                    self.color = self.pathFill;
                    ee.emitEvent('EZModelScene_setAttributeListener', [{
                        args: [ez3dScene.active.id, self.attr, self.pathFill]
                    }]);
                })
                .on('mouseover', function () {
                    d3.select(this)
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                })
                .on('mouseout', function () {
                    d3.select(this)
                        .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
                });
        }
    };
    
        // create account btn
    EZGuiManager.prototype.createAccountFormWidget = function() {
        var self = this;
        var container = d3.select('#ez3d-widget-create-account');
    
        var form = container.select('.ez3d-widget-content')
            .append('div')
            .attr('id', 'createAccountForm')
            .attr('class', 'ez3d-form-widget');
        // Add form inputs
        var formContainer = form
            .append('div')
            .attr('class', 'ez3d-form-container-first-part');
    
        formContainer.append('div')
            .html(self.i18n('userInformation'))
            .attr('class', 'ez3d-form-title')
            .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        var btnsContainer = formContainer.append('div')
            .attr('class', 'ez3d-widget-content-buttons ez3d-flex-end-wrap-row');
    
        var formContainerSecond = form
            .append('div')
            .attr('class', 'ez3d-form-container-second-part ez3d-hide-content');
    
        formContainerSecond.append('div')
            .html(self.i18n('companyInformation'))
            .attr('class', 'ez3d-form-title')
            .style('color', ez3dScene.layoutRules.defaultColors.cssColors.first);
    
        // Add button continue to  company information
        var btnsContainerSecond = formContainerSecond.append('div')
            .attr('class', 'ez3d-widget-content-buttons ez3d-flex-end-wrap-row');
    
        var elementsForm1 = {
            userName: {
                type: 'stringWidget',
                inputIid: 'userName',
                name: 'userName',
                classed: 'ez3d-total-width',
                value: '',
                label: 'name',
                tooltip: 'userName',
                placeholder: 'placeholderUserName',
                required: 'required',
                functionOninput: ['validationCreateAccount', ''],
                form: true
            },
            userSurname: {
                type: 'stringWidget',
                inputIid: 'userSurname',
                name: 'userSurname',
                classed: 'ez3d-total-width',
                value: '',
                label: 'surname',
                tooltip: 'userSurname',
                placeholder: 'placeholderUserSurname',
                required: 'required',
                functionOninput: ['validationCreateAccount', ''],
                form: true
            },
            userEmail: {
                type: 'stringWidget',
                inputType: 'email',
                inputIid: 'userEmail',
                name: 'userEmail',
                classed: 'ez3d-total-width',
                value: '',
                label: 'email',
                tooltip: 'email',
                placeholder: 'placeholderEmail',
                required: 'required',
                functionOninput: ['validationCreateAccount', ''],
                form: true
            },
            userPassword: {
                type: 'stringWidget',
                inputType: 'password',
                inputIid: 'userPassword',
                name: 'userPassword',
                label: 'password',
                tooltip: 'password',
                placeholder: 'placeholderPassword',
                required: 'required',
                functionOninput: ['validationCreateAccount', ''],
                form: true
            },
            userPasswordConfirm: {
                type: 'stringWidget',
                inputType: 'password',
                inputIid: 'userPasswordConfirm',
                name: 'userPasswordConfirm',
                label: 'password',
                tooltip: 'password',
                placeholder: 'placeholderPassword',
                required: 'required',
                functionOninput: ['validationCreateAccount', ''],
                form: true
            },
            continue: {
                type: 'buttonWidget',
                tooltip: 'continueToCompanyInformation',
                content: 'continueToCompanyInformation',
                classed: 'ez3d-square-button ez3d-square-button-blue ez3d-continue-form ez3d-square-disabled',
                functionOnClick: ['nextPreviousWidgetForm', 'continueToCompanyInformation'],
                position: 'bottom'
            }
        };
        self.populateBlocks(elementsForm1, formContainer, btnsContainer);
    
        // get select  info
        ez3dScene.fillCreateAccount(function (data) {
            // company information
            var elementsForm2 = {
                companyName: {
                    type: 'stringWidget',
                    inputIid: 'companyName',
                    name: 'companyName',
                    classed: 'ez3d-total-width',
                    value: '',
                    label: 'name',
                    tooltip: 'companyName',
                    placeholder: 'placeholderCompanyName',
                    required: 'required',
                    functionOninput: ['onSubmitCreateAccount', ''],
                    form: true
                },
                companyAdress: {
                    type: 'stringWidget',
                    inputIid: 'companyAdress',
                    name: 'companyAdress',
                    classed: 'ez3d-total-width',
                    value: '',
                    label: 'address',
                    tooltip: 'address',
                    placeholder: 'placeholderAddress',
                    required: 'required',
                    functionOninput: ['onSubmitCreateAccount', '']
                },
                companyTaxId: {
                    type: 'stringWidget',
                    inputIid: 'companyTaxId',
                    name: 'companyTaxId',
                    label: 'taxId',
                    tooltip: 'taxId',
                    placeholder: 'placeholderTaxId',
                    required: 'required',
                    functionOninput: ['onSubmitCreateAccount', '']
                },
                companyLocality: {
                    type: 'stringWidget',
                    inputIid: 'companyLocality',
                    name: 'companyLocality',
                    label: 'locality',
                    tooltip: 'locality',
                    placeholder: 'placeholderLocality',
                    required: 'required',
                    functionOninput: ['onSubmitCreateAccount', '']
                },
                companyProvince: {
                    type: 'stringWidget',
                    inputIid: 'companyProvince',
                    name: 'companyProvince',
                    label: 'province',
                    tooltip: 'province',
                    placeholder: 'placeholderProvince',
                    required: 'required',
                    functionOninput: ['onSubmitCreateAccount', '']
                },
                companyZipCode: {
                    type: 'stringWidget',
                    inputIid: 'companyZipCode',
                    name: 'companyZipCode',
                    label: 'zipCode',
                    tooltip: 'zipCode',
                    placeholder: 'placeholderZipCode',
                    required: 'required',
                    functionOninput: ['onSubmitCreateAccount', '']
                },
                companyCountry: {
                    type: 'selectWidget',
                    id: 'companyCountry',
                    name: 'companyCountry',
                    classed: '',
                    value: 'ES',
                    options: data.countries,
                    label: 'country',
                    tooltip: 'country',
                },
                companyTimezone: {
                    type: 'selectWidget',
                    id: 'companyTimezone',
                    name: 'companyTimezone',
                    classed: '',
                    value: 'Europe/Madrid',
                    options: data.timezones,
                    label: 'timezone',
                    tooltip: 'timezone',
                },
                previous: {
                    type: 'buttonWidget',
                    position: 'bottom',
                    tooltip: 'previous',
                    content: 'previous',
                    classed: 'ez3d-square-button ez3d-square-button-blue ez3d-previous-form',
                    functionOnClick: ['nextPreviousWidgetForm', 'previous']
                },
                createAccount: {
                    type: 'buttonWidget',
                    position: 'bottom',
                    tooltip: 'createAccount',
                    content: 'createAccount',
                    classed: 'ez3d-square-button ez3d-square-button-blue ez3d-create-accont ez3d-square-disabled',
                    functionOnClick: ['createAccount', '']
                }
            };
            self.populateBlocks(elementsForm2, formContainerSecond, btnsContainerSecond);
        });
    };
    
    // previos form
    EZGuiManager.prototype.nextPreviousWidgetForm = function(content) {
        if (content === 'previous') {
            d3.select('.ez3d-form-container-first-part')
                .classed('ez3d-hide-content', false);
    
            d3.select('.ez3d-form-container-second-part')
                .classed('ez3d-hide-content', true);
        } else {
            d3.select('.ez3d-form-container-first-part')
                .classed('ez3d-hide-content', true);
    
            d3.select('.ez3d-form-container-second-part')
                .classed('ez3d-hide-content', false);
        }
    };
    
    // form first part validation
    EZGuiManager.prototype.validationCreateAccount = function() {
        var firstPartValidation = true;
        var password = d3.select('#userPassword').node().value;
        var passwordConfirm = d3.select('#userPasswordConfirm').node().value;
        var fistForm = d3.select('.ez3d-form-container-first-part').selectAll('input')._groups[0];
    
        // get  forms input
        for (var i = 0; i < fistForm.length; i++) {
            if (fistForm[i].validity.valid === false) {
                firstPartValidation = false;
                break;
            }
    
            if (password === null || password !== passwordConfirm) {
                firstPartValidation = false;
            }
        }
        if (firstPartValidation === true) {
            d3.select('.ez3d-continue-form')
                .classed('ez3d-square-disabled', false)
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                .attr('disabled', null);
        } else {
            d3.select('.ez3d-continue-form')
                .classed('ez3d-square-disabled', true)
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.disabledbuttons)
                .style('opacity', '0.9')
                .attr('disabled', 'disabled');
        }
    };
    
    // form submit  validation
    EZGuiManager.prototype.onSubmitCreateAccount = function() {
        var formPartValidation = true;
        var secondForm = d3.select('.ez3d-form-container-second-part').selectAll('input')._groups[0];
    
        // get  forms input
        for (var i = 0; i < secondForm.length; i++) {
            if (secondForm[i].validity.valid === false) {
                formPartValidation = false;
                break;
            }
        }
    
        if (formPartValidation === true) {
            d3.select('.ez3d-create-accont')
                .classed('ez3d-square-disabled', false)
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                .attr('disabled', null);
        } else {
            d3.select('.ez3d-create-accont')
                .classed('ez3d-square-disabled', true)
                .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.disabledbuttons)
                .style('opacity', '0.9')
                .attr('disabled', 'disabled');
        }
    };
    
    // geocode
    EZGuiManager.prototype.geocodeBuilding = function(geocoder, callback) {
        var firstBuildingLat;
        var firstBuildingLng;
        if (ez3dScene.buildings[0]) {
            firstBuildingLat = ez3dScene.buildings[0].path.center.lat;
            firstBuildingLng = ez3dScene.buildings[0].path.center.lng;
        } else {
            firstBuildingLat = ez3dScene.projectCenter.lat;
            firstBuildingLng = ez3dScene.projectCenter.lng;
        }
        var latlng = new google.maps.LatLng(firstBuildingLat, firstBuildingLng);
        geocoder.geocode({'location': latlng}, function(results, status) {
            if (status === 'OK') {
                if (results[1]) {
                    var response = results[1];
                    var components = {};
                    response.address_components.forEach(function (component) {
                        var type = component.types[0];
                        components[type] = type === 'country' ? component.short_name : component.long_name;
                    });
                    components['lat'] = response.geometry.location.lat();
                    components['lng'] = response.geometry.location.lng();
                    callback(components);
                }
            } else {
                alert('Geocode was not successful for the following reason: ' + status);
            }
        });
    };
    
    // geocode
    EZGuiManager.prototype.geocodeAddress = function(geocoder, callback) {
        var zipcode = d3.select('#companyZipCode').node().value;
        var address = d3.select('#companyAdress').node().value;
        var province = d3.select('#companyProvince').node().value;
        var country = 'ES';
    
        var clientAddress = address + ',' + zipcode + ',' + province + ',' + country;
    
        geocoder.geocode({'address': clientAddress}, function(results, status) {
            if (status === 'OK') {
                var response = results[0];
                var clientGeolocation = {};
                clientGeolocation.longitude = response.geometry.location.lng();
                clientGeolocation.latitude = response.geometry.location.lat();
                callback(clientGeolocation);
            } else {
                alert('Geocode was not successful for the following reason: ' + status);
            }
        });
    };
    
    // create account btn
    EZGuiManager.prototype.createAccount = function() {
        var self = this;
        var buildingAddress;
        var buildingLocality;
        var buildingProvince;
        var buildingZipcode;
        var clientGeolocation;
        var buildingCountry;
        var buildingLng;
        var buildingLat;
        var geocoder = new google.maps.Geocoder();
    
        // building geodata
        self.geocodeBuilding(geocoder, function (components) {
            buildingAddress = (components.route || '') + (components.street_number ? ', ' + components.street_number : '');
            buildingLocality = components.locality || '';
            buildingProvince = components.administrative_area_level_2 || components.administrative_area_level_1 || '';
    
            // components.country && $scope.countries.forEach(function (country) {
            //     country.code === components.country && ($scope.country = country);
            // });
            buildingCountry = components.country;
            buildingZipcode = parseInt(components.postal_code || '0', 10);
            buildingLng = components.lng;
            buildingLat = components.lat;
            //  get geocodeAddress
            self.geocodeAddress(geocoder, function (data) {
                clientGeolocation = data;
                //  data to send crm
                var payload = {
                    user_data: {
                        name: d3.select('#userName').node().value,
                        surname: d3.select('#userSurname').node().value,
                        email: d3.select('#userEmail').node().value,
                        password: d3.select('#userPassword').node().value,
                        timezone: d3.select('#companyTimezone').node().value,
                        country: d3.select('#companyCountry').node().value,
                        language_code: 'en'
                    },
                    client_data: {
                        name_company: d3.select('#companyName').node().value,
                        cif_company: d3.select('#companyTaxId').node().value,
                        email_company: d3.select('#userEmail').node().value,
                        address_company: d3.select('#companyAdress').node().value,
                        zip_company: d3.select('#companyZipCode').node().value,
                        city_company: d3.select('#companyLocality').node().value,
                        province_company: d3.select('#companyProvince').node().value,
                        country_company: d3.select('#companyCountry').node().value,
                        longitude_company: clientGeolocation.longitude,
                        latitude_company: clientGeolocation.latitude
                    },
                    layout_data: {
                        address: buildingAddress,
                        zip: buildingZipcode,
                        city: buildingLocality,
                        province: buildingProvince,
                        country: buildingCountry,
                        longitude: buildingLng,
                        latitude: buildingLat,
                        data: JSON.stringify(ez3dScene.getJson())
                    }
                };
                ez3dScene.saveAccountCreated(payload);
            });
        });
    };
    
        EZGuiManager.prototype.drawWidget = function () {
        var self = this;
        var name = 'ez3d-widget-draw';
        var widget = d3.select('#' + name);
        var container = widget.select('.ez3d-widget-content');
    
        container.selectAll('*').remove();
    
        var elements = {
            buttonsPanel: {
                type: 'groupBlock',
                elements: {
                    classed: 'ez3d-panel-buttons ez3d-flex-center-center',
                    snapToGuides: {
                        type: 'button',
                        tooltip: 'snapToGuides',
                        property: 'layoutRules.scenePreferences.snapToGuides',
                        undo: false,
                        id: 'snap-to-guides',
                        value: ez3dScene.layoutRules.scenePreferences.snapToGuides,
                        content: 'guideLines',
                        classed: 'ez3d-square-line-button ez3d-line-start ez3d-square-button-green'
                    },
                    snapToGrid: {
                        type: 'button',
                        tooltip: 'snapToGrid',
                        property: 'layoutRules.scenePreferences.snapToGrid',
                        undo: false,
                        value: ez3dScene.layoutRules.scenePreferences.snapToGrid,
                        content: 'grid',
                        classed: 'ez3d-square-line-button ez3d-line-end ez3d-square-button-green'
                    }
                }
            },
            buttonsPanel2: {
                type: 'groupBlock',
                visibilityRule: ez3dScene.layoutRules.scenePreferences.snapToGuides,
                elements: {
                    classed: 'ez3d-panel-buttons ez3d-flex-center-center',
                    snapToLines: {
                        type: 'button',
                        tooltip: 'enableSnapGuideLinesEdges',
                        id: 'snap-to-lines',
                        property: 'layoutRules.scenePreferences.snapToLines',
                        undo: false,
                        value: ez3dScene.layoutRules.scenePreferences.snapToLines,
                        content: 'lines',
                        classed: 'ez3d-square-line-button ez3d-line-start ez3d-square-button-green'
                    },
                    snapToVertices: {
                        type: 'button',
                        tooltip: 'enableSnapVerticesIntersections',
                        id: 'snap-to-vertices',
                        property: 'layoutRules.scenePreferences.snapToVertices',
                        undo: false,
                        value: ez3dScene.layoutRules.scenePreferences.snapToVertices,
                        content: 'vertices',
                        classed: 'ez3d-square-line-button ez3d-square-button-green'
                    }
                }
            },
            helperInfo1: {
                type: 'helper',
                classed: 'helperList',
                // visibilityRule: ez3dScene.layoutRules.scenePreferences.snapToGuides,
                content: {
                    text: 'snapOptionsShortcut1',
                    vars: {
                        s: '<span class="ez3d-key-shortcut">S</span>'
                    }
                },
            },
            helperInfo2: {
                type: 'helper',
                classed: 'helperList',
                visibilityRule: ez3dScene.layoutRules.scenePreferences.snapToGuides,
                content: {
                    text: 'snapOptionsShortcut2',
                    vars: {
                        modifierKey: '<span class="ez3d-key-shortcut">' +
                            ((ez3dScene.layoutRules.scenePreferences.userData.OS === 'MacOS') ? '⌘' : 'Ctrl') +
                            '</span>'
                    }
                }
            },
            helperInfo3: {
                type: 'helper',
                classed: 'helperList',
                visibilityRule: ez3dScene.layoutRules.scenePreferences.snapToGuides,
                content: {
                    text: 'snapOptionsShortcut3',
                    vars: {
                        shift: '<span class="ez3d-key-shortcut">Shift</span>'
                    }
                }
            },
            gridSize: {
                type: 'float',
                property: 'layoutRules.scenePreferences.gridSize',
                undo: false,
                step: 0.1,
                min: 0.1,
                max: 50,
                label: 'gridSize',
                value: ez3dScene.layoutRules.scenePreferences.gridSize,
                visibilityRule: ez3dScene.layoutRules.scenePreferences.snapToGrid,
                editable: ez3dScene.layoutRules.scenePreferences.snapToGrid,
                universalUnits: true
                // showUnit: 'm'
            },
            gridOffsetX: {
                type: 'float',
                property: 'layoutRules.scenePreferences.gridOffsetX',
                undo: false,
                step: 0.1,
                min: 0,
                max: 50,
                label: 'gridOffsetX',
                universalUnits: true,
                value: ez3dScene.layoutRules.scenePreferences.gridOffsetX,
                visibilityRule: ez3dScene.layoutRules.scenePreferences.snapToGrid,
                editable: ez3dScene.layoutRules.scenePreferences.snapToGrid
                // showUnit: 'm'
            },
            gridOffsetY: {
                type: 'float',
                property: 'layoutRules.scenePreferences.gridOffsetY',
                undo: false,
                step: 0.1,
                min: 0,
                max: 50,
                label: 'gridOffsetY',
                universalUnits: true,
                value: ez3dScene.layoutRules.scenePreferences.gridOffsetY,
                visibilityRule: ez3dScene.layoutRules.scenePreferences.snapToGrid,
                editable: ez3dScene.layoutRules.scenePreferences.snapToGrid
                // showUnit: 'm'
            }
        };
        self.populateBlocks(elements, container, '');
    };
    
        EZGuiManager.prototype.moduleNotFoundContent = function (modulesList) {
        var self = this;
        var widget = d3.select('#ez3d-widget-module_not_found');
        var container = widget.select('.ez3d-widget-content');
    
        var modulestoShow = {
            helperInfo: {
                type: 'helper',
                classed: 'helper',
                content: 'moduleNotFoundHelper'
            },
            modulesNotFound: {
                type: 'blockList',
                classed: 'ez3d-block-list ez3d-flex-center-wrap',
                elements: {}
            }
        };
    
        modulesList.forEach(function(module) {
            modulestoShow.modulesNotFound.elements[module.id] = {
                type: 'helper',
                classed: 'helperList',
                content: {
                    text: 'moduleInfo',
                    vars: {
                        id: module.id,
                        name: module.name + ' ' + module.reference + ' ' + module.power
                    }
                }};
        });
    
        self.populateBlocks(modulestoShow, container, '');
    };
    
    // edge zone widget
    EZGuiManager.prototype.formatEdgeZoneTooltip = function() {
        var self = this;
        var widget = d3.select('#ez3d-widget-edgeZone');
        var container = widget.select('.ez3d-widget-content');
    
        var edgeZone = {
            helperInfo: {
                type: 'helper',
                classed: 'helper',
                content: 'edgeZoneAlertMessage'
            },
            list: {
                type: 'blockList',
                classed: 'ez3d-block-list ez3d-flex-center-wrap',
                elements: {
                    uno: {
                        type: 'helper',
                        classed: 'helperList',
                        content: 'edgeZoneAlertList1'
                    },
                    dos: {
                        type: 'helper',
                        classed: 'helperList',
                        content: 'edgeZoneAlertList2'
                    }
                }
            }
        };
    
        self.populateBlocks(edgeZone, container, '');
    };
    
    // generate all textures widget
    EZGuiManager.prototype.formatGetTexturesTooltip = function() {
        var self = this;
        var widget = d3.select('#ez3d-widget-generateTextures');
        var container = widget.select('.ez3d-widget-content');
    
        var getTextures = {
            helperInfo: {
                type: 'helper',
                classed: 'helper',
                content: 'generateTexturesAlertMessage'
            },
            list1: {
                type: 'blockList',
                classed: 'ez3d-block-list ez3d-flex-center-wrap',
                elements: {
                    preventFailure: {
                        type: 'helper',
                        classed: 'helperList',
                        content: 'generateTexturesAlertList1'
                    },
                    saveProject: {
                        type: 'helper',
                        classed: 'helperList',
                        content: 'generateTexturesAlertList2'
                    }
                }
            },
            button1: {
                type: 'blockList',
                classed: '',
                elements: {
                    save: {
                        type: 'button',
                        tooltip: 'save',
                        id: 'ez3d-button-save',
                        classed: 'ez3d-button fa-floppy-o',
                        functionOnClick: ['saveButtonOnClick', '']
                    }
                }
            },
            list2: {
                type: 'blockList',
                classed: 'ez3d-block-list ez3d-flex-center-wrap',
                elements: {
                    regenerateTexture: {
                        type: 'helper',
                        classed: 'helperList',
                        content: 'generateTexturesAlertList3'
                    }
                }
            },
            button2: {
                type: 'blockList',
                classed: '',
                elements: {
                    regenTexture: {
                        type: 'button',
                        tooltip: 'regenerateSatelliteTexture',
                        classed: 'ez3d-button fa-refresh'
                    }
                }
            },
        };
    
        self.populateBlocks(getTextures, container, '');
    
        widget.style('top', (widget.node().parentElement.offsetHeight - widget.node().offsetHeight) / 2 + 'px');
    };
    
    // Row offset reset or staggered disable on adding dilatation lines
    EZGuiManager.prototype.formatRowOffsetOrStaggeredTooltip = function() {
        var self = this;
        var widget = d3.select('#ez3d-widget-enablingDilatations');
        var container = widget.select('.ez3d-widget-content');
    
        var reset = {
            // Enabling DL
            helperInfo1: {
                type: 'helper',
                classed: 'helper',
                visibilityRule: !ez3dScene.active.system.dilatationLines.enabled,
                content: 'enablingDilatationsAlertMessage1'
            },
            helperInfo2: {
                type: 'helper',
                classed: 'helper',
                visibilityRule: !ez3dScene.active.system.dilatationLines.enabled,
                content: 'enablingDilatationsAlertMessage2'
            },
            // Enabling staggered
            helperInfo3: {
                type: 'helper',
                classed: 'helper',
                visibilityRule: ez3dScene.active.system.dilatationLines.enabled,
                content: 'enablingDilatationsAlertMessage3'
            },
        };
    
        self.populateBlocks(reset, container, '');
    };
    
    EZGuiManager.prototype.enablingModuleSailsTooltip = function() {
        var widget = d3.select('#ez3d-widget-enablingSails');
        var container = widget.select('.ez3d-widget-content');
    
        var textBlocks = {
            helperInfoDiscardingValues: {
                type: 'helper',
                classed: 'helper',
                content: 'enablingSailsAlertDiscardingValues',
            },
            // Enabling sails with staggered already enabled
            helperInfoStaggeredEnabled: {
                type: 'helper',
                classed: 'helper',
                visibilityRule: ez3dScene.active.system.staggered.enabled,
                content: 'enablingSailsAlertWithStaggeredEnabled'
            },
            helperInfoBlockingValues: {
                type: 'helper',
                classed: 'helper',
                content: 'enablingSailsAlertBlockingValues'
            },
        };
    
        this.populateBlocks(textBlocks, container, '');
    };
    
        EZGuiManager.prototype.moveShortcutsWidget = function () {
        var self = this;
        var name = 'ez3d-widget-move-shortcuts';
        var widget = d3.select('#' + name);
        var container = widget.select('.ez3d-widget-content');
    
        container.selectAll('*').remove();
    
        var elements = {
            movementStep: {
                type: 'float',
                property: 'ez3dScene.layoutRules.scenePreferences.movementStep',
                step: 0.1,
                min: 0.1,
                max: 50,
                label: 'movementStep',
                universalUnits: true,
                value: ez3dScene.layoutRules.scenePreferences.movementStep
            },
            helperInfo1: {
                type: 'helper',
                classed: 'helperList',
                content: {
                    text: 'moveObjectShortcut1',
                    vars: {
                        arrowKeys: '<span class="ez3d-arrow-keys-shortcut"></span>'
                    }
                }
            },
            helperInfo2: {
                type: 'helper',
                classed: 'helperList',
                content: {
                    text: 'moveObjectShortcut2',
                    vars: {
                        shift: '<span class="ez3d-key-shortcut">Shift</span>',
                        arrowKeys: '<span class="ez3d-arrow-keys-shortcut"></span>'
                    }
                },
                visibilityRule: (ez3dScene.context === 'subarea')
            },
            helperInfo3: {
                type: 'helper',
                classed: 'helperList',
                content: {
                    text: 'moveObjectShortcut3',
                    vars: {
                        shift: '<span class="ez3d-key-shortcut">Shift</span>',
                        modifierKey: '<span class="ez3d-key-shortcut">' +
                            ((ez3dScene.layoutRules.scenePreferences.userData.OS === 'MacOS') ? '⌘' : 'Ctrl') +
                            '</span>',
                        arrowKeys: '<span class="ez3d-arrow-keys-shortcut"></span>'
                    }
                },
                visibilityRule: (ez3dScene.context === 'subarea')
            }
        };
        self.populateBlocks(elements, container, '');
    };
    
        EZGuiManager.prototype.perspectiveShortcutsWidget = function () {
        var self = this;
        var name = 'ez3d-widget-perspective-shortcuts';
        var widget = d3.select('#' + name);
        var container = widget.select('.ez3d-widget-content');
    
        container.selectAll('*').remove();
    
        var elements = {
            zoom: {
                type: 'helper',
                classed: 'helperList',
                content: 'perspectiveShortcut1'
            },
            translation: {
                type: 'helper',
                classed: 'helperList',
                content: 'perspectiveShortcut2'
            },
            rotation: {
                type: 'helper',
                classed: 'helperList',
                content: {
                    text: 'perspectiveShortcut3',
                    vars: {
                        shift: '<span class="ez3d-key-shortcut">Shift</span>'
                    }
                }
            }
        };
        self.populateBlocks(elements, container, '');
    };
    
        /**
     * Widget to change inclination of roof.
     * @param  {object} roof    - EZModelRoof.
     */
    EZGuiManager.prototype.changeInclinationWidget = function (roof) {
        const name = 'ez3d-widget-roof-change-inclination';
        const widget = d3.select('#' + name);
        const container = widget.select('.ez3d-widget-content');
    
        const changeInclinationElements = {
            changeInclination: {
                type: 'integer',
                property: 'inclination',
                value: roof.inclination,
                max: 75,
                min: 5,
                step: 1,
                removeParentWidget: name,
                runMethod: [
                    'EZModelRoof_runMethodListener',
                    ['roofSoftUpdate', true],
                    true,
                    {draw2d: true, draw3d: ez3dScene.active, updatePanels: true, fitRange: false}
                ],
                confirmation: 'roofNeedsUpdate',
                confirmationWidget: {
                    'name': 'inclination',
                    'title': 'alertRoofChangeTitle',
                    'content': 'alertRoofChangeContent',
                    'eventOk': 'widget_roofChangeInclination_eventOk',
                    'eventCancel': 'widget_roofChangeInclination_eventCancel'
                }
            },
        };
        this.populateBlocks(changeInclinationElements, container, '');
    };
    
        EZGuiManager.prototype.roofTypeDisabledScreen = function () {
        const fullscreen = d3.select('#ez3d-loading-spinner-fullscreen');
    
        if (fullscreen.classed('ez3d-disabled-fullscreen')) {
            fullscreen.classed('ez3d-disabled-fullscreen', false);
        } else if (ez3dViewport.ez3dGuiManager.widgets.includes('ez3d-widget-type')) {
            fullscreen.classed('ez3d-disabled-fullscreen', true);
        }
    };
    
        EZGuiManager.prototype.roofEditorInfo = function () {
        const name = 'ez3d-widget-roof-editor-info';
        const widget = d3.select('#' + name);
        const container = widget.select('.ez3d-widget-content');
        const activeRoof = ez3dScene.activeRoof;
    
        container.selectAll('*').remove();
    
        const elements = {
            roofPoints: {
                type: 'helper',
                classed: 'helperList',
                content: 'roofEditorInfoRoofPoints',
                visibilityRule: !!(activeRoof.roofPoints.length && ['pent', 'pergola', 'pitched'].includes(activeRoof.type))
            },
            orientation: {
                type: 'helper',
                classed: 'helperList',
                content: 'roofEditorInfoOrientation',
                visibilityRule: !!activeRoof.orientation
            },
            inclination: {
                type: 'helper',
                classed: 'helperList',
                content: 'roofEditorInfoInclination',
                visibilityRule: !!activeRoof.inclination
            },
        };
        this.populateBlocks(elements, container, '');
    };
    
    EZGuiManager.prototype.manageRoofEditorInfoWidget = function() {
        const activeRoof = ez3dScene.activeRoof;
        const visibileWidget = (activeRoof)
            ? !!(activeRoof.roofPoints.length || activeRoof.orientation || activeRoof.inclination)
            : undefined;
        if (visibileWidget && ez3dScene.context === 'roof' && ez3dScene.projectType !== 'simple') {
            if (this.widgets.includes('ez3d-widget-roof-editor-info')) {
                this.updateWidgets();
            } else {
                ee.emitEvent('widget_notification', [{
                    'name': 'roof-editor-info',
                    'title': 'roofEditorInfo',
                    'eventMinimize': true,
                    'functionOnCreation': ['roofEditorInfo']
                }]);
            }
        } else if (this.widgets.includes('ez3d-widget-roof-editor-info')) {
            this.removeWidget('ez3d-widget-roof-editor-info');
        }
    };
    
        EZGuiManager.prototype.rowOffsetWidget = function (rowIndex) {
        var self = this;
        var name = 'ez3d-widget-row-offset';
        var widget = d3.select('#' + name);
        var container = widget.select('.ez3d-widget-content');
    
        var rowOffsetElements = {
            offsetX: {
                type: 'float',
                label: 'offsetX',
                value: ez3dScene.active.system.grid.rows[rowIndex].offset.x,
                max: 'infinity',
                min: 0,
                step: 0.1,
                numberOfDecimals: 2,
                universalUnits: true,
                functionOnChange: ['changeOffsetX', rowIndex]
            },
            offsetY: {
                type: 'float',
                label: 'offsetY',
                value: ez3dScene.active.system.grid.rows[rowIndex].offset.y,
                max: 'infinity',
                min: 0,
                step: 0.1,
                numberOfDecimals: 2,
                universalUnits: true,
                functionOnChange: ['changeOffsetY', rowIndex]
            },
        };
        self.populateBlocks(rowOffsetElements, container, '');
    };
    
    EZGuiManager.prototype.changeOffsetX = function(rowIndex, value) {
        ee.emitEvent('EZModelSubarea_addRowOffsetListener',
            [{args: [
                rowIndex,
                {
                    x: value,
                    y: ez3dScene.active.system.grid.rows[rowIndex].offset.y
                }
            ],
            updateRender: {draw2d: true, draw3d: ez3dScene.active, updatePanels: false, fitRange: false},
            progress: true}]
        );
    };
    
    EZGuiManager.prototype.changeOffsetY = function(rowIndex, value) {
        ee.emitEvent('EZModelSubarea_addRowOffsetListener',
            [{args: [
                rowIndex,
                {
                    x: ez3dScene.active.system.grid.rows[rowIndex].offset.x,
                    y: value
                }
            ],
            updateRender: {draw2d: true, draw3d: ez3dScene.active, updatePanels: false, fitRange: false},
            progress: true}]
        );
    };
    
        EZGuiManager.prototype.searchAdressWidget = function () {
        const currentProvider = ez3dViewport.ez3dPlayer.getProvider();
    
        var self = this;
        var widget = d3.select('#ez3d-widget-search-adress');
        var container = widget.select('.ez3d-widget-content');
    
        const initAutocomplete = () => {
            const _ajaxGet = ( url, responseType, callback) => {
                fetch(url).then(response => {
                    return response.json();
                }).then(data => {
                    callback(data);
                }).catch(err => {
                    // Do something for an error here
                });
            };
    
            if( currentProvider === 1) { // Google
                // Create the search box and link it to the UI element.
                var input = document.getElementById('pac-input');
                var searchBox = new google.maps.places.SearchBox(input);
    
                searchBox.addListener('places_changed', function() {
                    ez3dScene.projectCenterChanged = true;
                    var loc;
                    var places = searchBox.getPlaces();
                    if (places[0] && places[0].geometry.location) {
                        loc = places[0].geometry.location;
                        loc = {
                            lat: loc.lat(),
                            lng: loc.lng()
                        };
                        ee.emitEvent('EZModelScene_changeProjectCenterListener', [{
                            args: loc,
                            undo: true,
                            updateRender: false
                        }]);
                    }
                });
            }
    
            if( currentProvider === 2) { // Bing
                var input = document.getElementById('pac-input');
                var container = input.parentNode;
                Microsoft.Maps.loadModule('Microsoft.Maps.AutoSuggest', {
                    callback: onLoad,
                    errorCallback: onError
                });
                function onLoad() {
                    var options = { maxResults: 5 };
                    var manager = new Microsoft.Maps.AutosuggestManager(options);
                    manager.attachAutosuggest(input, container, selectedSuggestion);
                }
                function onError(message) {
                    //document.getElementById('printoutPanel').innerHTML = message;
                }
                function selectedSuggestion(suggestionResult) {
                    ez3dScene.projectCenterChanged = true;
                    var loc = {
                        lat : suggestionResult.location.latitude,
                        lng : suggestionResult.location.longitude
                    };
                    ee.emitEvent('EZModelScene_changeProjectCenterListener', [{
                        args: loc,
                        undo: true,
                        updateRender: false
                    }]);
                }
            }
    
            if( currentProvider !== 1 && currentProvider !== 2) { // Open Street Maps
                const input = document.getElementById('pac-input');
                const searchBox = document.createElement('select');
                searchBox.style.display = 'none';
                input.parentElement.appendChild(searchBox);
                input.addEventListener('input', (e) => {
                    var url = `https://nominatim.openstreetmap.org/search?format=json&q=${input.value}`;
                    _ajaxGet(url, 'json', (response) => {
                        searchBox.innerHTML = '';
                        searchBox.matrix = [];
                        response.forEach((obj, index) => {
                            searchBox.matrix.push({lat: Number(obj.lat), lng: Number(obj.lon)});
                            searchBox.innerHTML += '<option value="' + index + '">' + obj.display_name + '</option>';
                        });
                        searchBox.style.display = 'block';
                        searchBox.setAttribute('size', response.length);
                    });
                });
                searchBox.addEventListener('click', (e) => {
                    console.log('estoy en el change');
                    searchBox.style.display = 'none';
                    ez3dScene.projectCenterChanged = true;
                    ee.emitEvent('EZModelScene_changeProjectCenterListener', [{
                        args: searchBox.matrix[e.target.value],
                        undo: true,
                        updateRender: false
                    }]);
                });
            }
        };
    
        var visibilityRule = false;
        // include check
        if (ez3dScene.buildings.length > 0 || ez3dScene.trees.length > 0) {
            visibilityRule = true;
        }
    
        var elements = {
            reset: {
                type: 'booleanWidget',
                id: 'deleteBuildingsAndTrees',
                classed: '',
                label: 'deleteBuildingsAndTrees',
                value: false,
                tooltip: 'deleteBuildingsAndTrees',
                visibilityRule: visibilityRule
            },
            search: {
                type: 'stringWidget',
                inputIid: 'pac-input',
                autofocus: 'autofocus',
                placeholder: 'Escribe tu consulta',
                editable: !visibilityRule
            }
        };
        self.populateBlocks(elements, container, '');
        initAutocomplete();
    };
    
        EZGuiManager.prototype.managerWidgetSnapShot = function (element, downloadSnapshot, sharedCrm, cotas) {
        var self = this;
        // remove widget
        var deleteWidget = 'snapshot';
        if (element === 'snapshot') {
            deleteWidget = 'export';
        }
        self.removeWidget('ez3d-widget-' + deleteWidget);
    
        if (ez3dScene.activeSubarea) {
            // activar parent
            ee.emitEvent('unlockInterface');
            ee.emitEvent('EZModelScene_setActiveListener', [{args: ez3dScene.activeSubarea.parentId, updateRender: {draw2d: true, draw3d: true, updatePanels: true, fitRange: true}}]);
        }
    
        var name = 'ez3d-widget-' + element;
        var widget = d3.select('#' + name);
        var container = widget.select('.ez3d-widget-content');
        var crm = ez3dScene.layoutRules.scenePreferences.snapShotCrm;
        var opportunityId = ez3dScene.crmProject.opportunity_id;
    
        if (opportunityId === null) {
            crm = false;
        }
    
        container.selectAll('*').remove();
    
        var snapshotElements = {
            downloadSnapshot: {
                type: 'booleanWidget',
                id: 'downloadSnapshot',
                label: 'downloadSnapshot',
                value: (downloadSnapshot !== undefined) ? downloadSnapshot : true,
                tooltip: 'downloadSnapshot',
                visibilityRule: crm,
                functionOnChange: ['updateSnapshot', 'downloadSnapshot']
            },
            sharedCrm: {
                type: 'booleanWidget',
                id: 'sharedCrm',
                label: 'sharedCrm',
                value: (sharedCrm !== undefined) ? sharedCrm : false,
                tooltip: 'sharedCrm',
                visibilityRule: crm,
                functionOnChange: ['updateSnapshot', 'sharedCrm']
            },
            buttonsPanel: {
                type: 'buttonsPanelWidget',
                visibilityRule: (element === 'snapshot'),
                elements: {
                    saveSnapshotModules: {
                        type: 'buttonWidget',
                        tooltip: 'saveSnapshotModules',
                        content: 'saveSnapshotModules',
                        classed: 'ez3d-button fa-camera-retro ez3d-button-widget',
                        active: ['isInteractiveMode', this.id],
                        functionOnClick: ['mainSnapShot', 'with-modules'],
                        editable: 'snapshotValidation',
                    },
                    saveSnapshotWithModules: {
                        type: 'buttonWidget',
                        tooltip: 'saveSnapshotWithoutModules',
                        content: 'saveSnapshotWithoutModules',
                        classed: 'ez3d-button fa-camera-retro ez3d-button-widget',
                        functionOnClick: ['mainSnapShot', 'without-modules'],
                        editable: 'snapshotValidation',
    
                    },
                    saveSnapshotSvg: {
                        type: 'buttonWidget',
                        tooltip: 'saveSnapshotSvg',
                        content: 'saveSnapshotSvg',
                        classed: 'ez3d-button fa-camera-retro ez3d-button-widget',
                        functionOnClick: ['mainSnapShot', 'svg'],
                        editable: 'snapshotValidation',
    
                    }
                }
            },
            buttonsPanelAutocad: {
                type: 'buttonsPanelWidget',
                visibilityRule: (element === 'export'),
                elements: {
                    autocadCotas: {
                        type: 'booleanWidget',
                        id: 'autocadCotas',
                        label: 'autocadCotas',
                        value: (cotas !== undefined) ? cotas : true,
                        tooltip: 'autocadCotas',
                        functionOnChange: ['updateSnapshot', 'autocadCotas']
                    },
                    saveSnapshotSvgAutocad: {
                        type: 'buttonWidget',
                        tooltip: 'saveSnapshotSvgAutocad',
                        content: 'saveSnapshotSvgAutocad',
                        classed: 'ez3d-button fa-camera-retro ez3d-button-widget',
                        functionOnClick: ['mainSnapShot', 'svgAutocad'],
                        editable: 'snapshotValidation'
                    },
                    saveSnapshotDxf: {
                        type: 'buttonWidget',
                        tooltip: 'saveSnapshotDxfAutocad',
                        content: 'saveSnapshotDxfAutocad',
                        classed: 'ez3d-button fa-camera-retro ez3d-button-widget',
                        functionOnClick: ['mainSnapShot', 'dxf'],
                        editable: 'snapshotValidation'
                    }
                }
            }
        };
        self.populateBlocks(snapshotElements, container, '');
    };
    
    EZGuiManager.prototype.getSnapShot = function (canvas) {
        var blob;
        if (typeof (canvas) === 'string') {
            blob = new Blob([canvas], {type: 'image/svg+xml'});
            saveAs(blob, 'myProfile.svg');
        } else if (typeof (canvas) === 'object' && canvas.content !== undefined) {
            blob = new Blob([canvas.content]);
            saveAs(blob, 'autocad.dxf');
        } else {
            canvas.toBlob(function(blob) {
                saveAs(blob, 'snapshot.jpg');
            }, 'image/jpeg', 0.5);
            // canvas.toBlob(function(blob) {
            //     saveAs(blob, 'snapshot.png');
            // });
        }
        ee.emitEvent('downloadSnapshot_Finished');
    };
    
    EZGuiManager.prototype.mainSnapShot = function (data) {
        var self = this;
        var download = true;
        var sharedCrm = false;
    
        if (!d3.select('#downloadSnapshot').empty()) {
            download = d3.select('#downloadSnapshot').selectAll('.ez3d-switch-input').node().checked;
            sharedCrm = d3.select('#sharedCrm').selectAll('.ez3d-switch-input').node().checked;
        }
    
        if (download === true && sharedCrm === true) {
            self.saveSnapshotModules({'activity': 'all', 'slug': data});
            ez3dViewport.ez3dGuiManager.notificationManager(['saveSnapshotInProgress', {style: 'warning', spiner: true, event: 'saveSnapshot_Finished'}]);
            ez3dViewport.ez3dGuiManager.notificationManager(['downloadSnapshotInProgress', {style: 'warning', spiner: true, event: 'downloadSnapshot_Finished'}]);
        } else if (download === true && sharedCrm === false) {
            self.saveSnapshotModules({'activity': 'download', 'slug': data});
            ez3dViewport.ez3dGuiManager.notificationManager(['downloadSnapshotInProgress', {style: 'warning', spiner: true, event: 'downloadSnapshot_Finished'}]);
        } else if (sharedCrm === true && download === false) {
            self.saveSnapshotModules({'activity': 'crm', 'slug': data});
            ez3dViewport.ez3dGuiManager.notificationManager(['saveSnapshotInProgress', {style: 'warning', spiner: true, event: 'saveSnapshot_Finished'}]);
        }
    };
    
    EZGuiManager.prototype.saveSnapshotModules = function (data) {
        var self = this;
        var canvas;
        var timeout = 0;
        var rendererWasDisabled = false;
    
        if (data.slug === 'with-modules' || data.slug === 'without-modules') {
            // Turn on renderer
            if (ez3dScene.layoutRules.scenePreferences.activeRenderer === false) {
                timeout += 1000;
                rendererWasDisabled = true;
                d3.select('#ez3d-renderer-button').dispatch('click');
                setTimeout(function() {
                    ee.emitEvent('updateRender',
                        [{draw2d: true, draw3d: true, updatePanels: true, fitRange: true}]);
                }, timeout / 2);
            }
        }
        if (data.slug === 'without-modules') {
            // modules visibility
            setTimeout(function() {
                self.ez3dViewport.ez3dPlayer.setModulesVisibility(false);
            }, timeout);
        }
        if (data.slug === 'svg') {
            // fit project
            self.ez3dViewport.svgElements.svgProject.fitRangeToContainer(self.ez3dViewport.svgElements.svgProject.projectRange, 0);
            d3.select('#svgProject-limitsGroup').style('display', 'none');
        }
        if (data.slug === 'dxf') {
            self.ez3dViewport.svgElements.svgAutoCAD.setAutoCADSceneToExport(data.slug);
            canvas = d3.select('#svgAutoCAD')
                .attr('title', 'testAutocadDxf')
                .attr('version', 1.0)
                .attr('xmlns', 'http://www.w3.org/2000/svg')
                .node().outerHTML;
    
            // convert autocad canvas to dxf
            ez3dScene.convertSvgToDxf(canvas, function(autocadImage) {
                canvas = JSON.parse(autocadImage);
                var obj = {
                    id: ez3dScene.layoutRules.layoutId,
                    image: canvas.content,
                    slug: data.slug
                };
    
                if (data.activity === 'crm') {
                    ez3dScene.saveLayoutSnapShot(obj);
                } else if (data.activity === 'download') {
                    return self.getSnapShot(canvas);
                } else {
                    // save in crm and download
                    ez3dScene.saveLayoutSnapShot(obj);
                    self.getSnapShot(canvas);
                }
            });
        } else {
            setTimeout(function() {
                if (data.slug === 'svg') {
                    // fit project
                    canvas = d3.select('svg')
                        .attr('title', 'test2')
                        .attr('version', 1.1)
                        .attr('xmlns', 'http://www.w3.org/2000/svg')
                        .node().outerHTML;
                } else if (data.slug === 'svgAutocad') {
                    self.ez3dViewport.svgElements.svgAutoCAD.setAutoCADSceneToExport(data.slug);
                    // fit project
                    canvas = d3.select('#svgAutoCAD')
                        .attr('title', 'testAutocadSvg')
                        .attr('version', 1.1)
                        .attr('xmlns', 'http://www.w3.org/2000/svg')
                        .node().outerHTML;
                } else {
                    canvas = document.getElementById('playerContainer').childNodes[0];
                }
                var obj = {
                    id: ez3dScene.layoutRules.layoutId,
                    image: canvas,
                    slug: data.slug
                };
    
                if (data.activity === 'crm') {
                    ez3dScene.saveLayoutSnapShot(obj);
                } else if (data.activity === 'download') {
                    return self.getSnapShot(canvas);
                } else {
                    // save in crm and download
                    ez3dScene.saveLayoutSnapShot(obj);
                    self.getSnapShot(canvas);
                }
            }, timeout += 1000);
        }
    
        setTimeout(function() {
            if (self.ez3dViewport.ez3dPlayer) {
                self.ez3dViewport.ez3dPlayer.setModulesVisibility(true);
            }
            d3.select('#svgProject-limitsGroup').style('display', 'inherit');
            if (rendererWasDisabled === true) {
                d3.select('#ez3d-renderer-button').dispatch('click');
            }
            if (data.slug === 'dxf' || data.slug === 'svgAutocad') {
                self.ez3dViewport.resetMode();
            }
            setTimeout(function() {
                ee.emitEvent('updateRender',
                    [{draw2d: true, draw3d: true, updatePanels: true, fitRange: true}]);
            }, 1000);
        }, timeout += 1000);
    };
    
    EZGuiManager.prototype.snapshotValidation = function () {
        var crm = ez3dScene.layoutRules.scenePreferences.snapShotCrm;
        var opportunityId = ez3dScene.crmProject.opportunity_id;
        var snapshotValidation = true;
        var download;
        var sharedCrm;
        if (crm !== false && opportunityId !== null) {
            download = d3.select('#downloadSnapshot').selectAll('.ez3d-switch-input').node().checked;
            sharedCrm = d3.select('#sharedCrm').selectAll('.ez3d-switch-input').node().checked;
            if (download === false && sharedCrm === false) {
                snapshotValidation = false;
            }
        }
    
        return snapshotValidation;
    };
    
        EZGuiManager.prototype.moveSubareaShorcutsWidget = function () {
        var self = this;
        var name = 'ez3d-widget-move-subarea-shorcuts';
        var widget = d3.select('#' + name);
        var container = widget.select('.ez3d-widget-content');
    
        container.selectAll('*').remove();
    
        var elements = {
            movementStep: {
                type: 'float',
                property: 'ez3dScene.layoutRules.scenePreferences.movementStep',
                step: 0.1,
                min: 0.1,
                max: 50,
                label: 'movementStep',
                universalUnits: true,
                value: ez3dScene.layoutRules.scenePreferences.movementStep
            },
            helperInfo1: {
                type: 'helper',
                classed: 'helperList',
                content: 'moveSubareaShorcut1',
            },
            helperInfo2: {
                type: 'helper',
                classed: 'helperList',
                content: 'moveSubareaShorcut2',
            },
            helperInfo3: {
                type: 'helper',
                classed: 'helperList',
                content: 'moveSubareaShorcut3',
            }
        };
        self.populateBlocks(elements, container, '');
    };
    
        EZGuiManager.prototype.sunSimulationWidget = function () {
        var self = this;
        var widget = d3.select('#ez3d-widget-simulation-sun');
        var container = widget.select('.ez3d-widget-content');
    
        if (ez3dScene.layoutRules.scenePreferences.activeRenderer === false) {
            self.activeRenderer();
        }
    
        if (!self.sunSimulationWidgetInitialized) {
            self.sunSimulationWidgetInitialized = true;
            ee.addListener('Player.DateChanged', function(data) {
                if (ez3dViewport.ez3dGuiManager.widgets[0] === 'ez3d-widget-simulation-sun') {
                    d3.select('#sunDate')._groups[0][0].valueAsNumber = data;
                    d3.select('#sunDateTime')._groups[0][0].value = data.toTimeString().substring(0, 5);
                }
            });
        }
    
        var elements;
        elements = {
            sunDate: {
                type: 'dateTimeWidget',
                id: 'sunDate',
                value: ez3dScene.layoutRules.scenePreferences.defaultDateTime,
                classed: 'ez3d-date-time',
                label: 'date',
                label2: 'time',
                fromWidget: true,
                functionOnChange: ['dateSunWidget', '']
            },
            sunSpeed: {
                type: 'selectWidget',
                id: 'sunSpeed',
                name: '',
                classed: '',
                value: ez3dScene.layoutRules.scenePreferences.sunSpeed,
                options: ['x1', 'x10', 'x100', 'x1000', 'x10000'],
                label: 'speed',
                tooltip: 'speed',
                functionOnChange: ['rangeSunWidget', '']
            },
            buttonsPlayReset: {
                type: 'buttonsPanelWidget',
                elements: {
                    playSun: {
                        type: 'buttonWidget',
                        id: 'playSun',
                        classed: 'ez3d-button ez3d-play-icon',
                        changeIcon: 'play',
                        value: false,
                        functionOnClick: ['playSunWidget', ''],
                        tooltip: 'playSun'
                    },
                    reset: {
                        type: 'buttonWidget',
                        tooltip: 'resetToDefaultTimeZone',
                        classed: 'ez3d-button fa-refresh',
                        eventOnClick: ['EZModelScene_setDateTimeListener', ['', 'default', '#sunDate', '#sunDateTime']]
                    }
                }
            },
            buttonsPanel: {
                type: 'buttonsPanelWidget',
                elements: {
                    shorterShadow: {
                        type: 'buttonWidget',
                        tooltip: 'shorterShadow',
                        content: 'shorterShadow',
                        classed: 'ez3d-square-button ez3d-square-button-green ez3d-button-shadow',
                        eventOnClick: ['EZModelScene_setDateTimeListener', ['', 'shorterShadow', '#sunDate', '#sunDateTime']]
                    },
                    longerShadow: {
                        type: 'buttonWidget',
                        tooltip: 'longerShadow',
                        content: 'longerShadow',
                        classed: 'ez3d-square-button ez3d-square-button-green ez3d-button-shadow',
                        eventOnClick: ['EZModelScene_setDateTimeListener', ['', 'longerShadow', '#sunDate', '#sunDateTime']]
                    }
                }
            }
        };
        self.populateBlocks(elements, container, '');
        // first time
        self.dateSunWidget();
    };
    
    // date in sun simulation widget
    EZGuiManager.prototype.dateSunWidget = function() {
        var date = d3.select('#sunDate').node().value;
        var time = d3.select('#sunDateTime').node().value;
        var value = date + 'T' + time;
        ee.emitEvent('EZModelScene_setDateTimeListener', [{args: value}]);
    };
    
    // btn play in sun simulation widget
    EZGuiManager.prototype.playSunWidget = function () {
        var self = this;
        var sunSpeed = Math.pow(10, d3.select('#sunSpeed').node().value);
        if (self.checkedplaySun) {
            ez3dViewport.ez3dPlayer.playSun(sunSpeed);
            self.checkedplaySun = false;
            d3.select('#playSun')
                .attr('value', 'on');
        } else {
            ez3dViewport.ez3dPlayer.stopSun();
            self.checkedplaySun = true;
            d3.select('#playSun')
                .attr('value', 'off');
        }
    };
    
    // btn play in sun simulation widget
    EZGuiManager.prototype.rangeSunWidget = function () {
        var sunSpeed = Math.pow(10, d3.select('#sunSpeed').node().value);
        var playSun = d3.select('#playSun').node().value;
        if (playSun === 'on') {
            ez3dViewport.ez3dPlayer.playSun(sunSpeed);
        }
    };
    
    /**
     * Method to set defaultDateTime in preferences panel, and change the date in sun simulation widget
     */
    EZGuiManager.prototype.setDateSunPreferences = function () {
        var date = d3.select('#sunDatePreferences').node().value;
        var time = d3.select('#sunDatePreferencesTime').node().value;
        var value = date + 'T' + time;
    
        ee.emitEvent('EZModelScene_setAttributeListener', [{
            args: ['', 'layoutRules.scenePreferences.defaultDateTime', value],
            updateRender: false,
            undo: false,
            notifications: false
        }]);
    
        ee.emitEvent('EZModelScene_setDateTimeListener', [{args: value, updateRender: false, undo: false, notifications: false}]);
    };
    
        EZGuiManager.prototype.treeShapeSelectorWidget = function () {
        var self = this;
        var name = 'ez3d-widget-treeShape';
    
        if (ez3dScene.active.constructor.name === 'EZModelTree') {
            var widget = d3.select('#' + name);
            var container = widget.select('.ez3d-widget-content');
            container.selectAll('*').remove();
    
            var elements = {
                shape: {
                    type: 'selectBySVGImage',
                    property: 'shape',
                    label: 'treeShape',
                    classed: 'ez3d-tree-shape',
                    value: ez3dScene.active.shape,
                    options: ez3dScene.active.getDefaultAvailableTreeShapes()
                }
            };
            self.populateBlocks(elements, container, '');
        } else {
            self.removeWidget(name);
        }
    };
    
        EZGuiManager.prototype.ctrlAlertWidget = function () {
        var self = this;
        var name = 'ez3d-widget-ctrlAlert';
        var widget = d3.select('#' + name);
        var container = widget.select('.ez3d-widget-content');
    
        container.selectAll('*').remove();
    
        var elements = {
            infoMessage: {
                type: 'helper',
                classed: 'helper',
                content: 'ctrlAlertMessage1'
            },
            link: {
                type: 'link',
                classed: 'helperList',
                url: 'https://answers.microsoft.com/en-us/windows/forum/windows_xp-performance/can-you-disable-the-keyboard-autorepeat-function/ce1b29bc-544b-e011-8dfc-68b599b31bf5'
            },
            refreshMessage: {
                type: 'helper',
                classed: 'helper',
                content: 'ctrlAlertMessage2'
            },
        };
        self.populateBlocks(elements, container, '');
    };
    
        /* EXAMPLE
    ez3dViewport.ez3dGuiManager.widgetConstructor(
        {'name': 'nombre del widget',
        'title': 'building',
        'content': {'text': 'modulesInfo',
            'vars': {'num': ez3dScene.layoutRules.scenePreferences.gridSize,
            'power': ez3dScene.layoutRules.scenePreferences.gridSize}}})
    */
    // desde el json
    // confirmation: 'confirmOffsetChange',
    //     confirmationWidget: {
    //     'name': 'vdvEdgeZone',
    //         'title': 'edge_zone_alert_title',
    //         'content': 'ezge_zone_alert_message',
    //         'eventOk': 'vdvEdgeZoneUpdateOk',
    //         'eventCancel': 'vdvEdgeZoneUpdateCancel',
    //         'functionOnCreation': ['formatEdgeZoneTooltip', '']
    // }
    EZGuiManager.prototype.widgetConstructor = function (obj) {
        var self = this;
        var name = 'ez3d-widget-' + obj.name;
        var title;
        var text;
        if (obj.title) {
            title = self.i18n(obj.title);
        }
        if (obj.content) {
            text = self.i18n(obj.content);
        }
        if (self.widgets.indexOf(name) === -1) {
            // create a widget
            var notification = new EZWidget(this, name, title, obj.eventOk, obj.eventCancel, obj.eventMinimize, obj.optionsButtons);
            var container = notification.widgetContent;
    
            var widgetContentNotification = container.append('div')
                .attr('class', 'model-attribute model-attribute ez3d-flex-between-align-items-start');
    
            if (obj.name === 'moduleCountError') {
                widgetContentNotification.append('pre')
                    .attr('class', 'ez3d-widget-notification')
                    .html(text);
            } else {
                widgetContentNotification.append('div')
                    .attr('class', 'ez3d-widget-notification')
                    .html(text);
            }
    
            notification.json = obj;
            if (obj.functionOnCreation) {
                self[obj.functionOnCreation[0]](obj.functionOnCreation[1]);
            }
        } else if (name !== 'ez3d-widget-draw') {
            self.removeWidget(name);
            // self.generateWidgetNotification(obj);
        }
    };
    
    EZGuiManager.prototype.removeWidget = function (name) {
        var self = this;
        self.widgetElements.forEach(function(widget) {
            if (widget.name === name) {
                widget.widgetDestroy();
            }
        });
    };
    
    EZGuiManager.prototype.updateWidgets = function () {
        var self = this;
        if (self.widgets.length > 0) {
            // Reset widgets placing them in the same position
            setTimeout(function () {
                self.widgets.forEach(function(widgetElement) {
                    var position = {
                        top: d3.select('#' + widgetElement).style('top'),
                        left: d3.select('#' + widgetElement).style('left')
                    };
                    var widgetObject = self.widgetElements.filter((wid) => {
                        return wid.name === widgetElement;
                    })[0];
    
                    self.removeWidget(widgetElement);
                    self.widgetConstructor(widgetObject.json);
                    d3.select('#' + widgetElement)
                        .style('top', position.top)
                        .style('left', position.left);
                });
            }, 0);
        }
    };
    
        /**
     * EZWidget class
     * @class
     * @param {object} ez3dGuiManager       - EZGuiManager
     * @param {string} name                 - Widget name
     * @param {string} widgetTitle          - Header widget title
     * @param {array/string} eventOk        - Event emitted when clicking on Ok button
     * @param {array/string} eventCancel    - Event emitted when clicking on Cancel button
     * @param {boolean} eventMinimize       - Option to allow widget minimize
     * @param {object} optionsButtons       - Object with an optional image and button data
     */
    function EZWidget (ez3dGuiManager, name, widgetTitle, eventOk, eventCancel, eventMinimize, optionsButtons) {
        const self = this;
        // ez3dScene = ez3dScene;
        this.ez3dGuiManager = ez3dGuiManager;
        this.name = name;
    
        this.mainContainer = d3.select('#ez3d-main-container')
            .append('div')
            .attr('id', this.name)
            .attr('class', 'ez3dWidget')
            .style('border-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray);
    
        this.widgetFilter = this.mainContainer
            .append('div')
            .attr('class', 'ez3d-widget-filter');
    
        this.widgetHeader = this.mainContainer
            .append('div')
            .attr('class', 'ez3d-widget-header');
    
        this.widgetContent = this.mainContainer
            .append('div')
            .attr('class', 'ez3d-widget-content')
            .style('color', ez3dScene.layoutRules.defaultColors.cssColors.mediumgray);
    
        this.mainContainer
            .call(d3.drag()
                .on('start', function () {
                    const htmlWidget = d3.select(this);
                    htmlWidget
                        .raise();
                    if (eventMinimize) {
                        htmlWidget.style('transition', 'none');
                    }
                    self.setActiveWidget(htmlWidget);
                })
                .on('drag', function () {
                    // Allow drag when there isn't any element on focus
                    if (document.activeElement.tagName === 'BODY') {
                        d3.select(this)
                            .style('top', function() {
                                const height = ez3dScene.utils.getElementOuterHeight(this);
                                const parentHeight = ez3dScene.utils.getElementOuterHeight(this.parentElement);
                                const oldValue = parseInt(d3.select(this).style('top').split('px')[0], 10);
                                let value = oldValue + d3.event.dy;
                                if (value > parentHeight - height) {
                                    value = parentHeight - height;
                                } else if (value < 0) {
                                    value = 0;
                                }
                                return value + 'px';
                            })
                            .style('left', function() {
                                const width = ez3dScene.utils.getElementOuterWidth(this);
                                const parentWidth = ez3dScene.utils.getElementOuterWidth(this.parentElement);
                                const oldValue = parseInt(d3.select(this).style('left').split('px')[0], 10);
                                let value = oldValue + d3.event.dx;
                                if (value > parentWidth - width) {
                                    value = parentWidth - width;
                                } else if (value < 0) {
                                    value = 0;
                                }
                                return value + 'px';
                            });
                    }
                })
                .on('end', function () {
                    if (eventMinimize) {
                        ez3dViewport.snapOptionsMenu.hasBeenMoved = true;
                        // Save the new widget position
                        if (ez3dViewport.snapOptionsMenu.isMinimized === true) {
                            ez3dViewport.snapOptionsMenu.minimizedPosition = {
                                'top': d3.select(this).style('top'),
                                'left': d3.select(this).style('left')
                            };
                        } else {
                            ez3dViewport.snapOptionsMenu.openedPosition = {
                                'top': d3.select(this).style('top'),
                                'left': d3.select(this).style('left')
                            };
                            // Transition when minimize
                            d3.select(this).style('transition', '0.25s');
                        }
                    }
                }));
    
        this.widgetHeader
            .append('h1')
            .attr('class', 'ez3d-widget-title')
            .style('border-bottom-color', ez3dScene.layoutRules.defaultColors.cssColors.lightgray)
            .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
            .html(widgetTitle);
    
        // Initialize variable
        ez3dViewport.snapOptionsMenu = {
            isMinimized: false,
            hasBeenMoved: false,
            openedPosition: {
                top: this.mainContainer.style('top'),
                left: this.mainContainer.style('left'),
            },
            minimizedPosition: {
                'top': (d3.select('#ez3d-canvas').node().clientHeight - 45 - 35.25 - 10) + 'px',
                // top: calc(canvas height - 45px bottom - 35.25px scaled widget height - 10px margin)
                'left': '340px'
            }
        };
    
        // Widget header
        if (eventMinimize) {
            this.widgetHeader
                .on('click', function () {
                    const widget = this.parentElement;
                    if (ez3dViewport.snapOptionsMenu.isMinimized && d3.event.target.id === 'minimizeOption') {
                        // If the minimize button has been clicked (i.onclick)
                        ez3dViewport.snapOptionsMenu.isMinimized = d3.select(widget).classed('ez3d-minimized-widget');
                        d3.select(widget).style('top', ez3dViewport.snapOptionsMenu.minimizedPosition.top);
                        d3.select(widget).style('left', ez3dViewport.snapOptionsMenu.minimizedPosition.left);
                    } else {
                        // If its minimized, maximize it; then, place it in the last opened position
                        if (ez3dViewport.snapOptionsMenu.isMinimized) {
                            ez3dViewport.snapOptionsMenu.isMinimized = widget.classList.toggle('ez3d-minimized-widget');
                        }
                        d3.select(widget).style('top', ez3dViewport.snapOptionsMenu.openedPosition.top);
                        d3.select(widget).style('left', ez3dViewport.snapOptionsMenu.openedPosition.left);
                    }
                });
            this.widgetHeader
                .append('i')
                .attr('id', 'minimizeOption')
                .attr('class', 'fa fa-window-minimize ez3d-header-icons-widget')
                .on('click', function () {
                    ez3dViewport.snapOptionsMenu.isMinimized = this.parentElement.parentElement.classList.toggle('ez3d-minimized-widget');
                });
        } else {
            this.widgetHeader
                .append('i')
                .attr('class', 'fa fa-times ez3d-header-icons-widget')
                .attr('title', 'Esc')
                .on('click', function () {
                    if (eventCancel) {
                        if (Array.isArray(eventCancel) && eventCancel.length > 1) {
                            ee.emitEvent(eventCancel[0], [{args: eventCancel[1]}]);
                        } else {
                            ee.emitEvent(eventCancel);
                        }
                    }
                    self.widgetDestroy();
                });
        }
    
        if (eventOk || eventCancel) {
            const btns = this.mainContainer
                .append('div')
                .attr('class', 'ez3d-widget-content-buttons ez3d-flex-end-wrap-row');
            if (eventOk) {
                btns.append('button')
                    .attr('class', 'ez3d-square-button ez3d-square-button-blue ok')
                    .attr('title', 'Enter')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                    .html(function () {
                        let value = 'Ok';
                        if (Array.isArray(eventOk) && eventOk[2] && typeof eventOk[2] === 'string') {
                            value = eventOk[2];
                        }
                        if (Array.isArray(eventOk) && Array.isArray(eventOk[0]) && typeof eventOk[1] === 'string') {
                            // eventOk: [[eventName, args, undo, updateRender, progress], buttonText]
                            value = eventOk[1];
                        }
                        return self.ez3dGuiManager.i18n(value);
                    })
                    .on('click', function () {
                        if (Array.isArray(eventOk)) {
                            const eventData = (Array.isArray(eventOk[0])) ? eventOk[0] : eventOk;
                            ee.emitEvent(eventData[0], [{args: eventData[1], undo: eventData[2], updateRender: eventData[3], progress: eventData[4]}]);
                        } else {
                            ee.emitEvent(eventOk);
                        }
                        self.widgetDestroy();
                    })
                    .on('mouseover', function () {
                        d3.select(this)
                            .style('opacity', '0.7');
                    })
                    .on('mouseout', function () {
                        d3.select(this)
                            .style('opacity', '1');
                    });
            }
            if (eventCancel) {
                btns.append('button')
                    .attr('class', 'ez3d-square-button ez3d-square-button-blue cancel')
                    .attr('title', 'Esc')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.darkgray)
                    .html(function () {
                        let value = 'Cancel';
                        if (Array.isArray(eventCancel) && eventCancel[2] && typeof eventCancel[2] === 'string') {
                            value = eventCancel[2];
                        }
                        if (Array.isArray(eventCancel) && Array.isArray(eventCancel[0]) && typeof eventCancel[1] === 'string') {
                            // eventCancel: [[eventName, args, undo, updateRender, progress], buttonText]
                            value = eventCancel[1];
                        }
                        return self.ez3dGuiManager.i18n(value);
                    })
                    .on('click', function () {
                        if (Array.isArray(eventCancel)) {
                            const eventData = (Array.isArray(eventCancel[0])) ? eventCancel[0] : eventCancel;
                            ee.emitEvent(eventData[0], [{args: eventData[1], undo: eventData[2], updateRender: eventData[3], progress: eventData[4]}]);
                        } else {
                            ee.emitEvent(eventCancel);
                        }
                        self.widgetDestroy();
                    })
                    .on('mouseover', function () {
                        d3.select(this)
                            .style('opacity', '0.7');
                    })
                    .on('mouseout', function () {
                        d3.select(this)
                            .style('opacity', '1');
                    });
            }
        } else if (optionsButtons) {
            const btns = this.mainContainer
                .append('div')
                .attr('class', 'ez3d-widget-content-buttons ez3d-flex-between-center');
            Object.values(optionsButtons).forEach((button) => {
                const buttonContainer = btns.append('div')
                    .attr('class', 'ez3d-widget-button-container ez3d-flex-center-center-column');
                // Image
                if (button.imageClassName) {
                    buttonContainer.append('div')
                        .attr('class', 'ez3d-widget-img ' + button.imageClassName)
                        .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.second);
                }
                // Button
                buttonContainer.append('button')
                    .attr('class', 'ez3d-square-button ez3d-widget-button')
                    .attr('title', 'Enter')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                    .html(self.ez3dGuiManager.i18n(button.content))
                    .on('click', function () {
                        if (Array.isArray(button.eventOnClick)) {
                            ee.emitEvent(button.eventOnClick[0], [{args: button.eventOnClick[1], undo: button.eventOnClick[2], updateRender: button.eventOnClick[3], progress: button.eventOnClick[4]}]);
                        } else {
                            ee.emitEvent(button.eventOnClick);
                        }
                        self.widgetDestroy();
                    })
                    .on('mouseover', function() {
                        d3.select(this).style('opacity', '0.7');
                    })
                    .on('mouseout', function() {
                        d3.select(this).style('opacity', '1');
                    });
            });
        }
    
        // Widget position
        if (eventMinimize) {
            if (ez3dViewport.snapOptionsMenu.isMinimized) {
                // If the last time was minimized, keep it minimized
                this.mainContainer.classed('ez3d-minimized-widget', ez3dViewport.snapOptionsMenu.isMinimized);
                this.mainContainer.style('top', ez3dViewport.snapOptionsMenu.minimizedPosition.top);
                this.mainContainer.style('left', ez3dViewport.snapOptionsMenu.minimizedPosition.left);
            } else if (ez3dViewport.snapOptionsMenu.openedPosition) {
                this.mainContainer.style('top', ez3dViewport.snapOptionsMenu.openedPosition.top);
                this.mainContainer.style('left', ez3dViewport.snapOptionsMenu.openedPosition.left);
            }
        }
        this.ez3dGuiManager.widgets.push(name);
        this.ez3dGuiManager.widgetElements.push(this);
        self.setActiveWidget(this.mainContainer);
    }
    
    EZWidget.prototype.widgetDestroy = function () {
        const self = this;
        if (self.name === 'ez3d-widget-simulation-sun') {
            ee.removeListener('Player.DateChanged');
    
            ez3dViewport.ez3dGuiManager.checkedplaySun = false;
            ez3dViewport.ez3dGuiManager.playSunWidget();
        }
    
        this.ez3dGuiManager.widgets.forEach(function(widget, i, widgets) {
            if (widget === self.name) {
                if (widget === 'ez3d-widget-draw' && ez3dViewport.snapOptionsMenu) {
                    ez3dViewport.snapOptionsMenu.isMinimized = d3.select('#' + widget).classed('ez3d-minimized-widget');
                }
                widgets.splice(i, 1);
            }
        });
        this.ez3dGuiManager.widgetElements.forEach(function(widget, i, widgets) {
            if (widget.name === self.name) {
                widgets.splice(i, 1);
            }
        });
        d3.select('#' + self.name).remove();
    
        // Set as active the last created widget (if it exists)
        if (this.ez3dGuiManager.widgetElements.length !== 0) {
            self.setActiveWidget(d3.select('#' + this.ez3dGuiManager.widgetElements[this.ez3dGuiManager.widgetElements.length - 1].name));
        }
    };
    
    EZWidget.prototype.setActiveWidget = function(htmlWidget) {
        ez3dViewport.ez3dGuiManager.widgetElements.forEach(function(widget) {
            if (htmlWidget.node().id === widget.name) {
                widget.active = true;
                d3.select('#' + widget.name).classed('ez3d-active-widget', true);
            } else {
                widget.active = false;
                d3.select('#' + widget.name).classed('ez3d-active-widget', false);
            }
        });
    };
    
    EZsvgContainer.prototype.appendInfoDiv = function() {
        d3.select('#' + self.parentId).select('div.info').remove();
        this.info = d3.select('#' + this.parentId)
            .append('div')
            .attr('id', this.name + '-info-div')
            .attr('class', 'info')
            .style('display', 'none');
    
        /**
         * function to retrieve mouse position and draw info in the debugger GUI
         */
        function onMouseMove() {
    
            var coordinates = d3.mouse(this);
            var transform = d3.zoomTransform(this);
    
            var labelX = (coordinates[0], coordinates[0] - transform.x) / transform.k;
            var labelY = (coordinates[1], coordinates[1] - transform.y) / transform.k;
            var value = '<span>';
            value += 'xS: ' + EZModelUtils.formatDecimalComma (labelX) + ', ';
            value += 'yS: ' + EZModelUtils.formatDecimalComma (labelY);
            value += '</span>';
            if (ez3dScene.activeBuilding.constructor.name === 'EZModelBuilding' && ez3dScene.activeBuilding.path) {
                value += '<span>';
                value += 'xB: ' + EZModelUtils.formatDecimalComma (labelX - ez3dScene.activeBuilding.path.center.x) + ', ';
                value += 'yB: ' + EZModelUtils.formatDecimalComma (labelY - ez3dScene.activeBuilding.path.center.y);
                value += '</span>';
                value += '<span> building center:</span>';
                value += '<span>';
                value += ' xC: ' + EZModelUtils.formatDecimalComma (ez3dScene.activeBuilding.path.center.x) + ', ';
                value += ' yC: ' + EZModelUtils.formatDecimalComma (ez3dScene.activeBuilding.path.center.y);
                value += '</span>';
            }
    
            d3.select(this.parentElement.parentElement).select('.info')
                .style('display', 'inherit')
                .html(value);
        }
    
        /**
         * function to hide info box in the debugger GUI
         */
        function onMouseOut() {
            d3.select(this.parentElement.parentElement).select('.info')
                .style('display', 'none');
        }
    
        this.svg
            .on('mousemove', onMouseMove)
            .on('mouseout', onMouseOut);
    };
    
    EZsvgContainer.prototype.appendSvgContainer = function() {
        const svgContainer = d3.select('#' + this.parentId)
            .append('div')
            .attr('id', 'svgContainer-' + this.name)
            .attr('class', 'ez3d-svg-container')
            .style('width', function() {
                var width = '100%';
                if (this.parentId === 'ez3d-canvas') {
                    width = 'calc(100% - 3px)';
                }
                return width;
            })
            .style('height', '100%')
            .style('background-color', (ez3dScene.layoutRules.scenePreferences.activeMapper) ? 'transparent' : '');
    
        return svgContainer;
    };
    
    EZsvgContainer.prototype.appendMainGroups = function() {
        this.elementsGroup = this.svg.append('g')
            .attr('id', this.name + '-elementsGroup')
            .attr('class', 'mainGroup');
    
        this.iconsGroup = this.svg.append('g')
            .attr('id', this.name + '-iconsGroup')
            .attr('class', 'mainGroup');
    
        this.layers = this.elementsGroup.append('g')
            .attr('id', this.name + '-layers')
            .attr('class', 'mainGroup');
    
        // Maps
        this.layers.append('g').attr('id', this.name + '-L0').attr('class', 'mainGroup');
        // Buildings
        this.layers.append('g').attr('id', this.name + '-L1').attr('class', 'mainGroup');
        // Areas
        this.layers.append('g').attr('id', this.name + '-L2').attr('class', 'mainGroup');
        // Subareas, system and DL
        this.layers.append('g').attr('id', this.name + '-L3').attr('class', 'mainGroup');
        // Keepouts
        this.layers.append('g').attr('id', this.name + '-L4').attr('class', 'mainGroup');
        // Trees
        this.layers.append('g').attr('id', this.name + '-L5').attr('class', 'mainGroup');
        // Overlapping layer for moving objects
        this.layers.append('g').attr('id', this.name + '-L6').attr('class', 'mainGroup ez3d-moving-objects-overlap')
            .append('rect');
    
        this.limitsGroup = this.svg.append('g')
            .attr('id', this.name + '-limitsGroup')
            .attr('class', 'mainGroup');
    };
    
    EZsvgContainer.prototype.appendDefs = function() {
        // add defs to svg (patterns, gradients, textures)
        this.svgDefs = this.svg
            .append('defs')
            .attr('class', 'mainGroup');
    
        this.addGradient(this.svgDefs);
        this.addArrowDef(this.svgDefs);
        this.addCompassDef(this.svgDefs);
    };
    
    EZsvgContainer.prototype.appendEditorGroups = function() {
        // add editors layers
        this.editorGroup = this.svg.append('g')
            .attr('id', this.name + '-editorGroup')
            .attr('class', 'mainGroup scene-locations')
            .raise();
    
        this.editorGroup.append('g')
            .attr('class', 'ez3d-path-editor-grid mainGroup')
            .attr('id', 'ez3d-path-editor-grid');
    
        this.editorGroup.append('g')
            .attr('class', 'ez3d-path-editor-voronoi-group mainGroup')
            .attr('id', 'ez3d-path-editor-voronoi-group');
    
        this.editorGroup.append('g')
            .attr('class', 'ez3d-path-editor-elements mainGroup')
            .attr('id', 'ez3d-path-editor-elements');
    
        this.editorGroup.append('g')
            .attr('class', 'ez3d-path-editor-overlap mainGroup')
            .attr('id', 'ez3d-path-editor-overlap');
    };
    
    EZsvgContainer.prototype.appendGrid = function() {
        const prefs = ez3dScene.layoutRules.scenePreferences;
        this.gridGroup = this.svg.append('g')
            .attr('id', this.name + '-gridGroup')
            .attr('class', 'mainGroup');
        if (this.options.rasterMap === true) {
            this.addSphericalGrid();
        } else if (prefs.snapToGrid === false ||
            (ez3dScene.context !== 'pathEditor' &&
            ez3dScene.context !== 'location')) {
            this.addGrid();
        }
    };
    
    EZsvgContainer.prototype.addGrid = function() {
        this.svg.select('#' + this.name + '-gridGroup').selectAll('*').remove();
    
        // console.log('draw grid ' +  this.name);
    
        this.x = d3.scaleLinear()
            .domain([-1, this.width + 1])
            .range([-1, this.width + 1]);
    
        this.y = d3.scaleLinear()
            // .domain([-this.height - 1, this.height + 1])
            // .range([this.height + 1, -this.height - 1]);
            .domain([-1, this.height + 1])
            .range([-1, this.height + 1]);
    
        this.xAxis = d3.axisTop(this.x)
            .ticks((this.width + 2) / (this.height + 2) * 10)
            .tickSize(-this.height)
            .tickPadding(-28);
    
        this.yAxis = d3.axisRight(this.y)
            .ticks(10)
            .tickSize(this.width)
            .tickPadding(-20);
    
        this.gX = this.gridGroup.append('g')
            .attr('class', 'axis axis--x')
            .call(this.xAxis);
    
        this.gY = this.gridGroup.append('g')
            .attr('class', 'axis axis--y')
            .call(this.yAxis);
    };
    
        // generic functions (gradients, filters, etc)
    
    /**
     * add gradient definition
     * @param {d3Container} defsContainer - description
     */
    EZsvgContainer.prototype.addGradient = function(defsContainer) {
        var mainGradient = defsContainer.append('linearGradient')
            .attr('id', 'mainGradient');
        // Create the stops of the main gradient. Each stop will be assigned
        // a class to style the stop using CSS.
        mainGradient.append('stop')
            .attr('class', 'stop-bottom')
            .attr('offset', '0');
        mainGradient.append('stop')
            .attr('class', 'stop-medium')
            .attr('offset', '0.2');
        mainGradient.append('stop')
            .attr('class', 'stop-top')
            .attr('offset', '1');
    
        var stripesGradient = defsContainer.append('pattern')
            .attr('id', 'stripesGradient')
            .attr('x', '10px')
            .attr('y', '10px')
            .attr('width', '10px')
            .attr('height', '10px')
            .attr('patternUnits', 'userSpaceOnUse')
            .style('transform', 'scale(0.05)');
    
        stripesGradient.append('line')
            .attr('x1', '0')
            .attr('y1', '10')
            .attr('x2', '10')
            .attr('y2', '0')
            .style('stroke', 'red')
            .style('stroke-width', '2');
    
        stripesGradient.append('line')
            .attr('x1', '-1')
            .attr('y1', '1')
            .attr('x2', '1')
            .attr('y2', '-1')
            .style('stroke', 'red')
            .style('stroke-width', '2');
    
        stripesGradient.append('line')
            .attr('x1', '9')
            .attr('y1', '11')
            .attr('x2', '11')
            .attr('y2', '9')
            .style('stroke', 'red')
            .style('stroke-width', '2');
    };
    
    EZsvgContainer.prototype.addArrowDef = function(defsContainer) {
        defsContainer
            .append('marker')
            .attr('id', 'arrow-end')
            .attr('viewBox', '0 -5 10 10')
            .attr('refX', 5)
            .attr('refY', 0)
            .attr('markerWidth', 4)
            .attr('markerHeight', 4)
            .attr('orient', 'auto')
            .append('path')
            .attr('d', 'M0,-5L10,0L0,5');
    
        defsContainer
            .append('marker')
            .attr('id', 'arrow-start')
            .attr('viewBox', '0 -5 10 10')
            .attr('refX', 5)
            .attr('refY', 0)
            .attr('markerWidth', 4)
            .attr('markerHeight', 4)
            .attr('orient', 'auto')
            .append('path')
            .attr('d', 'M10,-5L10,5L0,0');
    };
    
    EZsvgContainer.prototype.addProjectTextureDef = function(defsContainer, id) {
        defsContainer
            .append('svg:pattern')
            .attr('id', 'texture_' + id)
            .attr('width', '100%')
            .attr('height', '100%')
            .attr('patternUnits', 'objectBoundingBox')
            .attr('viewBox', '0 0 1 1')
            .attr('preserveAspectRatio', 'none')
            .append('svg:image')
            .attr('width', '1')
            .attr('height', '1')
            .attr('x', '0')
            .attr('y', '0')
            .attr('preserveAspectRatio', 'none');
        // .attr('href', 'http://upload.wikimedia.org/wikipedia/commons/thumb/b/bc/Gl%C3%BChwendel_brennt_durch.jpg/399px-Gl%C3%BChwendel_brennt_durch.jpg');
    };
    
    EZsvgContainer.prototype.addSubareaDlMaskDefs = function(subarea) {
        const container = d3.select('#' + this.name + '_subarea_' + subarea.id).select('.EZModelSystem');
        // Get mask name and lineData
        let dlMaskName;
        let lineData;
        if (this.options.coord === 'scene') {
            dlMaskName = 'dlMask_';
            lineData = subarea.path.lineData;
        } else {
            dlMaskName = 'dlMask-flatten_';
            lineData = subarea.path.lineDataFlatten;
        }
        // Update clipPath mask or create it
        if (d3.select('#' + dlMaskName + subarea.id).node()) {
            d3.select('#' + dlMaskName + subarea.id).select('path').attr('d', lineData);
        } else {
            this.svgDefs
                .append('svg:clipPath')
                .attr('id', dlMaskName + subarea.id)
                .append('path')
                .attr('d', lineData);
        }
        // Set clipPath to element
        container.attr('clip-path', 'url(#' + dlMaskName + subarea.id + ')');
    };
    
    EZsvgContainer.prototype.addroofPattern = function(defsContainer, area) {
        if (d3.select('#roofPattern_' + area.id).node() === null) {
            var pattern = defsContainer
                .append('svg:pattern')
                .attr('id', 'roofPattern_' + area.id)
                .attr('class', 'roofPattern')
                .attr('width', '1')
                .attr('height', '1')
                .attr('viewBox', '0 0 4 4')
                .attr('patternUnits', 'userSpaceOnUse')
                .attr('patternTransform', 'rotate(' + (-area.angle + 180) + ')');
    
            if (area.inclination === 0) {
                pattern
                    .append('circle')
                    .attr('cx', 1)
                    .attr('cy', 1)
                    .attr('r', 0.2)
                    .attr('fill', 'lightblue');
            } else {
                pattern
                    .append('path')
                    .attr('fill', 'lightblue')
                    // .attr('d', 'M1,3L1.5,0L2,3');
                    .attr('d', 'M0,4L0.5,2L1,4Q0.5,3,0,4M2,2L2.5,0L3,2Q2.5,1,2,2');
            }
        }
    };
    
    EZsvgContainer.prototype.addCadDimensionLineDef = function(defsContainer) {
        defsContainer
            .append('marker')
            .attr('id', 'cad-dimension-line')
            .attr('viewBox', '-0.5 -0.5 1 1')
            .attr('refX', '0.0')
            .attr('refY', '0.0')
            .attr('markerWidth', 20)
            .attr('markerHeight', 20)
            .attr('orient', 'auto')
            .append('line')
            .attr('x1', '-0.5')
            .attr('y1', '0.5')
            .attr('x2', '0.5')
            .attr('y2', '-0.5')
            .attr('stroke', ez3dScene.layoutRules.defaultColors.areaColors.autocad.strokeLine)
            .attr('stroke-width', ez3dScene.layoutRules.defaultColors.areaColors.autocad.strokeWidthLine * 2);
    };
    
    EZsvgContainer.prototype.addCompassDef = function(defsContainer) {
        var newPattern = defsContainer
            .append('pattern')
            .attr('id', 'compass-pattern')
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', '100%')
            .attr('height', '100%')
            .attr('patternUnits', 'objectBoundingBox');
        var svgInBase64 = 'PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiIHN0YW5kYWxvbmU9Im5vIj8+CjwhLS0gQ3JlYXRlZCB3aXRoIElua3NjYXBlIChodHRwOi8vd3d3Lmlua3NjYXBlLm9yZy8pIC0tPgoKPHN2ZwogICB4bWxuczpkYz0iaHR0cDovL3B1cmwub3JnL2RjL2VsZW1lbnRzLzEuMS8iCiAgIHhtbG5zOmNjPSJodHRwOi8vY3JlYXRpdmVjb21tb25zLm9yZy9ucyMiCiAgIHhtbG5zOnJkZj0iaHR0cDovL3d3dy53My5vcmcvMTk5OS8wMi8yMi1yZGYtc3ludGF4LW5zIyIKICAgeG1sbnM6c3ZnPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIKICAgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIgogICB4bWxuczpzb2RpcG9kaT0iaHR0cDovL3NvZGlwb2RpLnNvdXJjZWZvcmdlLm5ldC9EVEQvc29kaXBvZGktMC5kdGQiCiAgIHhtbG5zOmlua3NjYXBlPSJodHRwOi8vd3d3Lmlua3NjYXBlLm9yZy9uYW1lc3BhY2VzL2lua3NjYXBlIgogICB3aWR0aD0iNzA4LjY2MTQ0IgogICBoZWlnaHQ9IjcwOC42NjE0NCIKICAgdmlld0JveD0iMCAwIDcwOC42NjE0MiA3MDguNjYxNDEiCiAgIGlkPSJzdmcyIgogICB2ZXJzaW9uPSIxLjEiCiAgIGlua3NjYXBlOnZlcnNpb249IjAuOTEgcjEzNzI1IgogICBzb2RpcG9kaTpkb2NuYW1lPSJjb21wYXNzMi5zdmciPgogIDxkZWZzCiAgICAgaWQ9ImRlZnM0IiAvPgogIDxzb2RpcG9kaTpuYW1lZHZpZXcKICAgICBpZD0iYmFzZSIKICAgICBwYWdlY29sb3I9IiNmZmZmZmYiCiAgICAgYm9yZGVyY29sb3I9IiM2NjY2NjYiCiAgICAgYm9yZGVyb3BhY2l0eT0iMS4wIgogICAgIGlua3NjYXBlOnBhZ2VvcGFjaXR5PSIwLjAiCiAgICAgaW5rc2NhcGU6cGFnZXNoYWRvdz0iMiIKICAgICBpbmtzY2FwZTp6b29tPSIxLjQiCiAgICAgaW5rc2NhcGU6Y3g9IjE4My42MjI0NiIKICAgICBpbmtzY2FwZTpjeT0iMjczLjg3Mjc3IgogICAgIGlua3NjYXBlOmRvY3VtZW50LXVuaXRzPSJweCIKICAgICBpbmtzY2FwZTpjdXJyZW50LWxheWVyPSJsYXllcjEiCiAgICAgc2hvd2dyaWQ9ImZhbHNlIgogICAgIHVuaXRzPSJweCIKICAgICBpbmtzY2FwZTp3aW5kb3ctd2lkdGg9IjE5MjAiCiAgICAgaW5rc2NhcGU6d2luZG93LWhlaWdodD0iMTAxNyIKICAgICBpbmtzY2FwZTp3aW5kb3cteD0iMCIKICAgICBpbmtzY2FwZTp3aW5kb3cteT0iMjciCiAgICAgaW5rc2NhcGU6d2luZG93LW1heGltaXplZD0iMSIgLz4KICA8bWV0YWRhdGEKICAgICBpZD0ibWV0YWRhdGE3Ij4KICAgIDxyZGY6UkRGPgogICAgICA8Y2M6V29yawogICAgICAgICByZGY6YWJvdXQ9IiI+CiAgICAgICAgPGRjOmZvcm1hdD5pbWFnZS9zdmcreG1sPC9kYzpmb3JtYXQ+CiAgICAgICAgPGRjOnR5cGUKICAgICAgICAgICByZGY6cmVzb3VyY2U9Imh0dHA6Ly9wdXJsLm9yZy9kYy9kY21pdHlwZS9TdGlsbEltYWdlIiAvPgogICAgICAgIDxkYzp0aXRsZSAvPgogICAgICA8L2NjOldvcms+CiAgICA8L3JkZjpSREY+CiAgPC9tZXRhZGF0YT4KICA8ZwogICAgIGlua3NjYXBlOmxhYmVsPSJMYXllciAxIgogICAgIGlua3NjYXBlOmdyb3VwbW9kZT0ibGF5ZXIiCiAgICAgaWQ9ImxheWVyMSIKICAgICB0cmFuc2Zvcm09InRyYW5zbGF0ZSgwLC0zNDMuNzAwNzkpIj4KICAgIDxwYXRoCiAgICAgICBpbmtzY2FwZTpjb25uZWN0b3ItY3VydmF0dXJlPSIwIgogICAgICAgaWQ9InBhdGg0MTYzIgogICAgICAgZD0ibSAtNDk5LjAxNzg2LDM4OS41MDUwNCAwLC0xODEuODc1IDE4MS44NzUsMCAxODEuODc1LDAgMCwxODEuODc1IDAsMTgxLjg3NSAtMTgxLjg3NSwwIC0xODEuODc1LDAgMCwtMTgxLjg3NSB6IgogICAgICAgc3R5bGU9ImZpbGw6I2ZkZmRmZCIgLz4KICAgIDxwYXRoCiAgICAgICBzdHlsZT0iZmlsbDojMDA3NWEwO2ZpbGwtb3BhY2l0eToxO3N0cm9rZTpub25lO3N0cm9rZS13aWR0aDoyMDtzdHJva2UtbWl0ZXJsaW1pdDo0O3N0cm9rZS1kYXNoYXJyYXk6bm9uZTtzdHJva2Utb3BhY2l0eToxIgogICAgICAgZD0iTSAzNTQuMzMyNTcsMzU0LjgzMTUgQSAzNDMuMTk4NzIsMzQzLjE5ODcyIDAgMCAwIDExLjEzMDc0NCw2OTguMDMzMzQgMzQzLjE5ODcyLDM0My4xOTg3MiAwIDAgMCAzNTQuMzMyNTcsMTA0MS4yMzE1IDM0My4xOTg3MiwzNDMuMTk4NzIgMCAwIDAgNjk3LjUzMDY5LDY5OC4wMzMzNCAzNDMuMTk4NzIsMzQzLjE5ODcyIDAgMCAwIDM1NC4zMzI1NywzNTQuODMxNSBaIG0gLTEuNDQwMzcsNTcuNiBBIDI4Ny45OTg5NCwyODEuNzU4OTMgMCAwIDEgNjQwLjg4ODQ3LDY5NC4xOTExIDI4Ny45OTg5NCwyODEuNzU4OTMgMCAwIDEgMzUyLjg5MjIsOTc1Ljk1MDc5IDI4Ny45OTg5NCwyODEuNzU4OTMgMCAwIDEgNjQuODkyMjEyLDY5NC4xOTExIDI4Ny45OTg5NCwyODEuNzU4OTMgMCAwIDEgMzUyLjg5MjIsNDEyLjQzMTUgWiIKICAgICAgIGlkPSJwYXRoNDE3NCIKICAgICAgIGlua3NjYXBlOmNvbm5lY3Rvci1jdXJ2YXR1cmU9IjAiIC8+CiAgICA8cGF0aAogICAgICAgc3R5bGU9ImZpbGw6I2ZmZmZmZjtmaWxsLW9wYWNpdHk6MSIKICAgICAgIGQ9Im0gNDIwLjA4OTg0LDM1NC4yMTA5NCAtMTMxLjUxNzU3LDAuODg2NzIgMy43NTE5NSwxMC4yNDgwNCBjIDIuMDYyOTYsNS42MzY2OSAxNS42MDU2NSw1Ni43NTY5MiAzMC4wOTU3LDExMy41OTk2MSAxNC40OTAwNSw1Ni44NDI2OCAyNy4zOTE5MSwxMDcuMzU5NTUgMjguNjY5OTIsMTEyLjI1OTc3IDEuMjc3OTcsNC45MDAyNCAzLjI1NjczLDguOTEwMTUgNC4zOTg0NCw4LjkxMDE1IDEuMTQxNzEsMCAxNC41MDEyNiwtNTAuNTE2ODMgMjkuNjg3NSwtMTEyLjI1OTc2IDE1LjE4NjIyLC02MS43NDI5MSAyOS4yNTM2MywtMTE3LjA3Mjg1IDMxLjI2MTcyLC0xMjIuOTUzMTMgeiIKICAgICAgIHRyYW5zZm9ybT0idHJhbnNsYXRlKDAsMzQzLjcwMDc5KSIKICAgICAgIGlkPSJwYXRoNDE2MSIKICAgICAgIGlua3NjYXBlOmNvbm5lY3Rvci1jdXJ2YXR1cmU9IjAiCiAgICAgICBzb2RpcG9kaTpub2RldHlwZXM9ImNjY3Nzc3NjYyIgLz4KICAgIDxwYXRoCiAgICAgICBpbmtzY2FwZTpjb25uZWN0b3ItY3VydmF0dXJlPSIwIgogICAgICAgaWQ9InBhdGg0MTQ5IgogICAgICAgZD0ibSAyODkuNjY4NTIsNzAwLjUzNjY1IGMgLTEuNzU1MTgsLTIuODM5OTggNTkuOTI5NDcsLTI1MS45Njk3MyA2My4xOTY5LC0yNTUuMjM3MTYgMS4zODU2MiwtMS4zODU2IDIuNTE5MzMsLTEuNjE3NzIgMi41MTkzMywtMC41MTU3NyAwLDEuMTAxOTEgMTQuNTc0NjksNTguNTg4OTcgMzIuMzg4MTgsMTI3Ljc0OSAxNy44MTM1Miw2OS4xNjAwMiAzMi4wODIyMywxMjUuOTg2OTcgMzEuNzA4MjEsMTI2LjI4MjE0IC0yLjI4MTI5LDEuODAwNDggLTEyOC43Mjc0NiwzLjQ3NzU5IC0xMjkuODEyNjIsMS43MjE3OSB6IgogICAgICAgc3R5bGU9ImZpbGw6I2MwMGYwMDtmaWxsLW9wYWNpdHk6MSIgLz4KICA8L2c+Cjwvc3ZnPgo=';
        newPattern.append('image')
            .attr('href', 'data:image/svg+xml;base64,' + svgInBase64)
            .attr('x', 0)
            .attr('y', 0)
            .attr('width', 80)
            .attr('height', 80);
    };
    
        // world coordinate system methods
    
    /**
     * method to draw raster map
     */
    EZsvgContainer.prototype.addRasterMap = function() {
        this.svg
            .on('mousemove', mousemoved);
    
        var pi = Math.PI;
        var tau = 2 * pi;
    
        this.projection = d3.geoMercator()
            .scale(1 / tau)
            .translate([0, 0]);
    
        this.geoPath = d3.geoPath()
            .projection(this.projection);
    
        this.zoom = d3.zoom()
            .scaleExtent([1 << 1, 1 << 25])
            .on('zoom', zoomedMap);
    
        // Compute the projected initial center.
        var center = this.projection([ez3dScene.projectCenter.lng, ez3dScene.projectCenter.lat]);
    
        var rasterTile = d3.tile()
            .size([this.width, this.height]);
    
        var raster = this.svg.append('g')
            .attr('id', 'rasterMap')
            .attr('width', this.width);
    
        // Apply a zoom transform equivalent to projection.{scale,translate,center}.
        this.svg
            .call(this.zoom)
            .call(this.zoom.transform, d3.zoomIdentity
                .translate(this.width / 2, this.height / 2)
                .scale(1 << 25)
                .translate(-center[0], -center[1])
            );
    
        // update projection to actual zoom
        var transform = d3.zoomTransform(this.svg.node());
        this.projection
            .scale(transform.k / tau)
            .translate([transform.x, transform.y]);
    
        // draw
        if (ez3dScene.projectCenter) {
            // this.drawProjectCenterWorld();
            // this.drawSceneLocationsWorld();
            // this.drawEZModelLocationWorldCoords(ez3dScene.projectCenter);
        }
    
        /**
         * [zoomedMap description]
         */
        function zoomedMap() {
            var self = this;
            var svgContainer = {};
            if (ez3dViewport.svgContainersList) {
                ez3dViewport.svgContainersList.forEach(function(container) {
                    if (container.name === self.id) {
                        svgContainer = container;
                    }
                });
    
                var transform = d3.event.transform;
    
                if (!svgContainer.projection) {
                    svgContainer.projection = d3.geoMercator()
                        .scale(1 / tau)
                        .translate([0, 0]);
                }
    
                svgContainer.projection
                    .scale(transform.k / tau)
                    .translate([transform.x, transform.y]);
    
                if (svgContainer.options) {
                    // redraw sceneGroup
                    // svgContainer.drawProject();
                    svgContainer.draw();
    
                    // grid
                    if (svgContainer.options && svgContainer.options.grid) {
                        svgContainer.gX.call(svgContainer.xAxis.scale(transform.rescaleX(svgContainer.x)));
                        svgContainer.gY.call(svgContainer.yAxis.scale(transform.rescaleY(svgContainer.y)));
                    }
    
                    if (svgContainer.projectCenterPath) {
                        svgContainer.projectCenterPath
                            .attr('d', svgContainer.geoPath);
                    }
    
                    if (ez3dScene.locations.length > 0) {
                        if (svgContainer.sceneLocationsPath) {
                            svgContainer.sceneLocationsPath
                                .attr('d', svgContainer.geoPath);
                        }
                    }
                }
    
                // raster
                var rasterTiles = rasterTile
                    .scale(transform.k)
                    .translate([transform.x, transform.y])();
    
                var rasterImage = raster
                    .attr('transform', rasterTransform(rasterTiles.scale, rasterTiles.translate))
                    .selectAll('image')
                    .data(rasterTiles, function(d) {
                        return d;
                    });
    
                rasterImage.exit().remove();
    
                rasterImage.enter().append('image')
                    .attr('href', function(d) {
                        return 'http://' + 'abc'[d[1] % 3] + '.tile.openstreetmap.org/' + d[2] + '/' + d[0] + '/' + d[1] + '.png';
                    })
                    .attr('x', function(d) {
                        return d[0] * 256;
                    })
                    .attr('y', function(d) {
                        return d[1] * 256;
                    })
                    .attr('width', 256)
                    .attr('height', 256);
            }
        }
    
        function rasterTransform(scale, translate) {
            var k = scale / 256;
            var r = scale % 1 ? Number : Math.round;
            return 'translate(' + r(translate[0] * scale) + ',' + r(translate[1] * scale) + ') scale(' + k + ')';
        }
    
        function mousemoved() {
            var self = this;
            var svgContainer = {};
            if (ez3dViewport.svgContainersList) {
                ez3dViewport.svgContainersList.forEach(function(container) {
                    if (container.name === self.id) {
                        svgContainer = container;
                    }
                });
                // console.log('mouse', d3.mouse(self));
                // console.log('location', svgContainer.projection.invert(d3.mouse(self)));
                // svgContainer.info.text(formatLocation(svgContainer.projection.invert(d3.mouse(self)), d3.zoomTransform(self).k));
            }
        }
    
        function formatLocation(p, k) {
            var format = d3.format('.' + Math.floor(Math.log(k) / 2 - 2) + 'f');
            return (p[1] < 0 ? format(-p[1]) + '°S' : format(p[1]) + '°N') + ' ' +
                     (p[0] < 0 ? format(-p[0]) + '°W' : format(p[0]) + '°E');
        }
    };
    
    /**
     * method to draw a grid in spherical coords
     */
    EZsvgContainer.prototype.addSphericalGrid = function() {
        // console.log('draw grid ' + this.name);
        this.svg.select('#gridGroup').remove();
    
        this.gridGroup = this.svg.append('g')
            .attr('id', 'gridGroup');
    
        this.x = d3.scaleLinear()
            .domain([-1, this.width + 1])
            .range([-1, this.width + 1]);
    
        this.y = d3.scaleLinear()
            .domain([-this.height - 1, this.height + 1])
            .range([this.height + 1, -this.height - 1]);
    
        var longDegrees = d3.scaleLinear()
            .domain([-0.5, 0.5])
            .range([-180, 180]);
    
        var latDegrees = d3.scaleLinear()
            .domain([-0.5, 0.5])
            .range([-90, 90]);
    
        var format = d3.format('.4f');
    
        this.xAxis = d3.axisBottom(this.x)
            .ticks((this.width + 2) / (this.height + 2) * 10)
            .tickSize(this.height)
            .tickPadding(8 - this.height)
            .tickFormat(function(d) {
                return format(longDegrees(d));
            });
    
        // the y axis doesn't show latitude degrees correctly,
        // the vertical scale is not linear
        this.yAxis = d3.axisRight(this.y)
            .ticks(20)
            .tickSize(this.width)
            .tickPadding(8 - this.width)
            .tickFormat(function(d) {
                return format(latDegrees(d));
            });
    
        var center = this.projection([ez3dScene.projectCenter.lng, ez3dScene.projectCenter.lat]);
    
        this.gX = this.gridGroup.append('g')
            .attr('class', 'axis axis--x')
            .call(this.xAxis);
    
        this.gY = this.gridGroup.append('g')
            .attr('class', 'axis axis--y')
            .call(this.yAxis);
    
        // update grid scale to actual zoom
        var transform = d3.zoomTransform(this.svg.node());
        this.gX.call(this.xAxis.scale(transform.rescaleX(this.x)));
        this.gY.call(this.yAxis.scale(transform.rescaleY(this.y)));
    
        // gridGroup.attr('transform', )
    };
    
    /**
     * method to draw project center in world coord system
     */
    EZsvgContainer.prototype.drawProjectCenterWorld = function() {
        var dataset = [ez3dScene.projectCenter.getGeoJSON()];
    
        if (d3.selectAll('#projectCenterGroup')._groups[0].length === 0) {
            var projectCenterGroup = this.svg.append('g')
                .attr('id', 'projectCenterGroup');
            this.projectCenterPath = projectCenterGroup.append('path');
        } else {
            var projectCenterGroup = d3.selectAll('#projectCenterGroup');
            this.projectCenterPath = projectCenterGroup.selectAll('path');
        }
    
        this.projectCenterPath
            .datum({
                type: 'FeatureCollection',
                features: dataset
            });
    
        this.projectCenterPath
            .attr('d', this.geoPath)
            .attr('fill', 'red');
    };
    
    /**
     * method to draw scene locations in world coord system
     * <br><br>
     *
     * scene locations are a set of EZModelLocations stored in the root of the scene
     * <br><br>
     *
     * this are locations being edited or a set of vertices to create an element
     * (building, subarea or keepouts)
     *
     */
    EZsvgContainer.prototype.drawSceneLocationsWorld = function() {
        if (d3.selectAll('#sceneLocationsGroup')._groups[0].length === 0) {
            var sceneLocationsGroup = this.svg.append('g')
                .attr('id', 'sceneLocationsGroup');
            this.sceneLocationsPath = sceneLocationsGroup.append('path');
        } else {
            var sceneLocationsGroup = d3.selectAll('#sceneLocationsGroup');
            this.sceneLocationsPath = sceneLocationsGroup.selectAll('path');
        }
    
        var dataset = [];
    
        if (ez3dScene.locations.length > 0) {
            ez3dScene.locations.forEach(function(location) {
                dataset.push(location.getGeoJSON());
            });
        }
    
        this.sceneLocationsPath
            .datum({
                type: 'FeatureCollection',
                features: dataset
            });
    
        this.sceneLocationsPath
            .attr('d', this.geoPath)
            .attr('fill', 'yellow')
            .attr('stroke-width', 2)
            .attr('stroke', 'green')
            .attr('opacity', 0.7);
    };
    
    /**
     * main project drawing method in world coords system
     */
    EZsvgContainer.prototype.drawProjectWorld = function() {
        var self = this;
    
        ez3dScene.buildings.forEach(function(building) {
            if (building.isCreated === true) {
                self.drawBuildingWorld(building);
            }
        });
    
        self.drawSceneLocationsWorld();
    };
    
    /**
     * method to draw a building in world coord system
     * @param  {EZModelBuilding} building - the building being drawn
     */
    EZsvgContainer.prototype.drawBuildingWorld = function(building) {
        var projection = this.projection;
        var EZModelBuildingGroup = this.sceneGroup.append('g')
            .attr('class', 'EZModelBuilding')
            .attr('id', 'buildingGroup_' + this.id);
    
        const container = EZModelBuildingGroup;
    
        // draw shape  (EZModelPath)
        this.drawPathWorld(container, projection, building.path);
    
        // draw area shapes
        var colors = ['red', 'yellow', 'green', 'blue'];
        if (building.roofs) {
            building.roofs.forEach((roof) => {
                roof.areas.forEach((area, i) => {
                    this.drawPathWorld(container, projection, area.path, colors[i]);
                });
            });
        }
    
        // // draw vertices  (EZModelLocation)
        building.path.vertices.forEach((vertex, i) => {
            this.drawLocationWorld(container, projection, vertex, 'blue');
        });
    
        // // draw center   (EZModelLocation)
        this.drawLocationWorld(container, projection, building.path.center, 'orange');
    };
    
    /**
     * method to draw a path in world coord system
     * @param  {d3Container} container  - the container where the path will be drawed
     * @param  {d3Projection} projection - the container projection to be applied
     * @param  {EZModelPath} path       - the path to be drawed
     * @param  {string} color      - the color of the path fill
     */
    EZsvgContainer.prototype.drawPathWorld = function(container, projection, path, color) {
        if (!color) {
            color = path.color;
        }
    
        var points = [];
        path.vertices.forEach(function(vertex) {
            var point = projection([vertex.lng, vertex.lat]);
            points.push(point);
        });
    
        var EZModelPathGroup = container.append('g')
            .attr('class', 'EZModelPath')
            .attr('id', 'pathGroup_' + this.id);
    
        var lineData = points;
        lineData.push(lineData[0]);
    
        var lineFunction = d3.line()
            .x(function(d) {
                return d[0];
            })
            .y(function(d) {
                return d[1];
            });
    
        var lineGraph = EZModelPathGroup.append('path')
            .attr('d', lineFunction(lineData))
            .attr('stroke', 'red')
            .attr('stroke-width', 1)
            .attr('fill', color)
            .attr('opacity', 0.5);
    };
    
    /**
     * method to draw a location in world coord system
     * @param  {d3Container} container  - the container where the location will be drawed
     * @param  {d3Projection} projection - the container projection to be applied
     * @param  {EZModelLocation} location   - the location to be drawn
     * @param  {string} color      - the color of the location fill
     */
    EZsvgContainer.prototype.drawLocationWorld = function(container, projection, location, color) {
        if (!color) {
            color = 'black';
        }
    
        var point = [projection([location.lng, location.lat])];
    
        var EZModelLocationGroup = container.append('g')
            .attr('class', 'EZModelLocation')
            .attr('id', 'locationGroup_' + location.id);
    
        EZModelLocationGroup.selectAll('circle')
            .data(point).enter().append('circle')
            .attr('cx', function(d) {
                return d[0];
            })
            .attr('cy', function(d) {
                return d[1];
            })
            .attr('r', 1)
            .attr('fill', color);
    };
    
        /**
     * Draw areas with d3.
     * @param {array} areasData         - Array with the areas to draw.
     * @param {boolean} visible         - If the area wants to be visible.
     * @param {string} buildingId       - Parent building id.
     * @param {boolean} movingSubarea   - If a subarea is being moved (parameter sent after checking interactiveMove).
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.drawAreas = function(areasData, visible, buildingId, movingSubarea, svgContainer, deferCallback) {
        const container = (movingSubarea)
            ? d3.select('#' + svgContainer.name + '-L6_building_' + ez3dScene.activeBuilding.id)
            : d3.select('#' + svgContainer.name + '-L2_building_' + buildingId);
    
        const enterAreas = function(selection) {
            selection
                .attr('class', 'EZModelArea')
                .attr('id', (d) => svgContainer.name + '_area_' + d.id);
    
            if (visible) {
                selection
                    .filter((d) => d.path.offsettedPaths[0] !== undefined)
                    .append('g')
                    .attr('class', 'offsetted-area-shape-group')
                    .append('path')
                    .attr('class', 'offsetted-area-shape');
    
                selection
                    .append('g')
                    .attr('class', 'area-shape-group')
                    .append('path')
                    .attr('class', 'area-shape')
                    .on('mouseover', function(d) {
                        d3.select(this)
                            .call(styleAreas, svgContainer, (d.style.colorState === 'active') ? d.style.colorState : 'hover');
                    })
                    .on('mouseout', function(d) {
                        d3.select(this)
                            .call(styleAreas, svgContainer, d.style.colorState);
                    })
                    .on('click', (d) => (svgContainer.name === 'svgProject') ? svgContainer.clickOnAreaEvent(d) : undefined)
                    .on('contextmenu', function (d) {
                        d3.event.preventDefault();
                        if (svgContainer.name === 'svgProject' && this.parentElement.className.baseVal === 'area-shape-group') {
                            const contextPanelData = {
                                functionOnCreation: 'contextPanelArea',
                                elementData: {
                                    element: d,
                                    path: this
                                }
                            };
                            const mouse = d3.mouse(d3.select('#ez3d-main-container').node());
                            self.ez3dViewport.ez3dGuiManager.generateContextPanel(mouse, contextPanelData, svgContainer.name);
                        }
                    });
            }
        };
    
        const updateAreas = function(selection) {
            selection
                .select('path.offsetted-area-shape')
                .style('display', (d) => (ez3dScene.active.id === d.id) ? 'inherit' : 'none')
                .filter((d) => ez3dScene.active.id === d.id)
                .attr('d', (d) => (svgContainer.options.coord === 'scene' ? d.path.offsettedPaths[0].lineData : d.path.offsettedPaths[0].lineDataFlatten))
                .call(styleAreas, svgContainer, 'offsettedPath');
    
            selection
                .select('path.area-shape')
                .attr('d', (d) => (svgContainer.options.coord === 'scene' ? d.path.lineData : d.path.lineDataFlatten))
                .call(styleAreas, svgContainer)
                .each(function(d) {
                    // Draw or remove labels
                    if (ez3dScene.activeArea.id === d.id) {
                        svgContainer.drawLabels(d3.select(this.parentElement), d.path, svgContainer.options.coord !== 'scene', 0.5);
                    } else {
                        d3.select(this.parentElement).selectAll('.path-label').remove();
                    }
                });
        };
    
        // Start d3 join
        const update = container.selectAll('g.EZModelArea')
            .data(areasData, (d) => d.id);
    
        const enter = update.enter()
            .append('g')
            .call(enterAreas);
    
        if (visible) {
            update.merge(enter)
                .call(updateAreas);
        }
    
        update.exit().remove();
    
        // Check if deferCallback exists (when moving a subarea it is undefined, see EZsvgContainer.drawMovingObject())
        if (deferCallback) {
            deferCallback(null);
        }
    };
    
    const styleAreas = function(selection, svgContainer, colorState) {
        const colors = ez3dScene.layoutRules.defaultColors['areaColors'];
        const strokeScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.2, 1 / 50]);
    
        selection
            .attr('fill', (d) => colors.path[colorState || d.style.colorState].fill)
            .attr('fill-opacity', (d) => colors.path[colorState || d.style.colorState].fillOpacity)
            .attr('stroke', (d) => colors.path[colorState || d.style.colorState].stroke)
            .attr('stroke-width', (d) => {
                let strokeWidth = strokeScale(svgContainer.zoomValue) * colors.path[colorState || d.style.colorState].strokeWidth;
                if (isNaN(strokeWidth) || strokeWidth < 0) {
                    strokeWidth = 0.02;
                }
                return strokeWidth;
            })
            .attr('stroke-dasharray', (d) => colors.path[colorState || d.style.colorState].strokeDasharray)
            .filter(() => ez3dScene.context === 'roof')
            .attr('fill', (d) => 'url(#roofPattern_' + d.id + ')');
    };
    
        /**
     * Draw buildings with d3.
     * @param {array} buildingsData     - Array with the buildings to draw.
     * @param {boolean} visible         - If the building wants to be visible.
     * @param {object} layer            - Container where the building is going to be drawn.
     * @param {object} svgContainer     - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.drawBuildings = function (buildingsData, visible, layer, svgContainer, deferCallback) {
        const enterBuildings = function(selection) {
            selection
                .attr('class', 'EZModelBuilding')
                .attr('id', (d) => layer.attr('id') + '_building_' + d.id);
    
            if (visible) {
                selection
                    .append('g')
                    .attr('class', 'building-shape-group')
                    .append('path')
                    .attr('class', 'building-shape');
            }
        };
    
        const updateBuildings = function(selection) {
            if (svgContainer.options.coord === 'scene') {
                selection
                    .attr('transform', (d) => 'translate(' + d.path.center.x + ', ' + d.path.center.y + ')');
            }
            if (visible) {
                selection
                    .select('path.building-shape')
                    .attr('d', (d) => d.path.lineData)
                    .call(styleBuildings, svgContainer)
                    .each(function(d) {
                        if (d.texture === undefined) {
                            if (d3.select('#texture_' + d.id).node() !== null) {
                                d3.select('#texture_' + d.id).remove();
                            }
                        } else {
                            if (d3.select('#texture_' + d.id).node() === null) {
                                svgContainer.addProjectTextureDef(svgContainer.svgDefs, d.id);
                            }
                            d3.select('#texture_' + d.id).select('image').attr('href', d.texture);
                        }
    
                        if (ez3dScene.layoutRules.scenePreferences.enablePlayer && !ez3dScene.layoutRules.scenePreferences.activeRenderer && d.texture) {
                            d3.select('#svgProject-L1_building_' + d.id)
                                .select('path.building-shape')
                                .attr('fill', 'url(#texture_' + d.id + ')')
                                .attr('fill-opacity', '1');
                        } else {
                            d3.select('#svgProject-L1_building_' + d.id)
                                .select('path.building-shape')
                                .call(styleBuildings, svgContainer);
                        }
                    });
    
                selection
                    .classed('ez3d-interactive-move', (d) => ez3dScene.isObjectBeingMoved(d.id))
                    .style('opacity', (d) => (ez3dScene.isObjectBeingMoved(d.id) ? 0.7 : 1))
                    .call(d3.drag()
                        .on('start', function(d) {
                            return (ez3dScene.isObjectBeingMoved(d.id) ? svgContainer.elementDragStarted.bind(this)() : null);
                        })
                        .on('drag', function(d) {
                            return (ez3dScene.isObjectBeingMoved(d.id) ? svgContainer.elementDragged.bind(this)() : null);
                        })
                        .on('end', function(d) {
                            return (ez3dScene.isObjectBeingMoved(d.id) ? svgContainer.elementDragEnded.bind(this)() : null);
                        })
                    );
            }
        };
    
        // Start d3 join
        const update = layer.selectAll('g.EZModelBuilding')
            .data(buildingsData, (d) => d.id);
    
        const enter = update.enter()
            .append('g')
            .call(enterBuildings);
    
        update.merge(enter)
            .call(updateBuildings);
    
        update.exit().remove();
    
        deferCallback(null, buildingsData.length);
    };
    
    const styleBuildings = function(selection, svgContainer, colorState) {
        const colors = ez3dScene.layoutRules.defaultColors['buildingColors'];
        const strokeScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.2, 1 / 50]);
    
        selection
            .attr('fill', (d) => {
                if (!d.path.isInLimits(svgContainer.limits)) return colors.path.outOfLimits.fill;
                if (colorState === 'standard' && d.color) return d.color;
                return colors.path[colorState || d.style.colorState].fill;
            })
            .attr('fill-opacity', (d) => {
                if (!d.path.isInLimits(svgContainer.limits)) return colors.path.outOfLimits.fillOpacity;
                return colors.path[colorState || d.style.colorState].fillOpacity;
            })
            .attr('stroke', (d) => {
                if (!d.path.isInLimits(svgContainer.limits)) return colors.path.outOfLimits.stroke;
                return colors.path[colorState || d.style.colorState].stroke;
            })
            .attr('stroke-width', (d) => {
                const defaultWidth = (d.path.isInLimits(svgContainer.limits))
                    ? colors.path[colorState || d.style.colorState].strokeWidth
                    : colors.path.outOfLimits.strokeWidth;
                let strokeWidth = strokeScale(svgContainer.zoomValue) * defaultWidth;
                if (isNaN(strokeWidth) || strokeWidth < 0) {
                    strokeWidth = 0.02;
                }
                return strokeWidth;
            })
            .attr('stroke-dasharray', (d) => {
                if (!d.path.isInLimits(svgContainer.limits)) return colors.path.outOfLimits.strokeDasharray;
                return colors.path[colorState || d.style.colorState].strokeDasharray;
            });
    };
    
        /**
     * Draw dilatation lines with d3.
     * @param {object} lines            - Dilatation lines coords.
     * @param {object} system           - System.
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.drawDilatationLines = function(lines, system, svgContainer, deferCallback) {
        let container = d3.select('#' + svgContainer.name + '_systemtransform_' + system.id);
        if (container.select('.dilatationLinesGroup').node()) {
            container = container.select('.dilatationLinesGroup');
        } else {
            container = container.append('g')
                .attr('class', 'dilatationLinesGroup');
        }
    
        const enterDilatationLines = function(selection) {
            selection
                .attr('id', (d) => svgContainer.name + '-' + d[4])
                .attr('class', 'dilatationLine')
                .attr('stroke', (d) => (d[2] === 'vertical')
                    ? ez3dScene.layoutRules.defaultColors.dilatationLinesColors.cols.stroke
                    : ez3dScene.layoutRules.defaultColors.dilatationLinesColors.rows.stroke
                )
                .attr('stroke-width', (d) => d[3]);
        };
    
        const updateDilatationLines = function(selection) {
            selection
                .attr('x1', (d) => d[0][0])
                .attr('y1', (d) => d[0][1])
                .attr('x2', (d) => d[1][0])
                .attr('y2', (d) => d[1][1]);
    
            // Draw dilatation lines clip paths
            const subarea = system.parent;
            if (system.dilatationLines.enabled) {
                svgContainer.addSubareaDlMaskDefs(subarea);
            } else if (d3.select('#dlMask_' + subarea.id).node() !== null) {
                d3.select('#dlMask_' + subarea.id).remove();
                d3.select('#dlMask-flatten_' + subarea.id).remove();
            }
        };
    
        const update = container.selectAll('line.dilatationLine')
            .data(lines, (d) => d[4]);
    
        const enter = update.enter()
            .append('line')
            .call(enterDilatationLines);
    
        update.merge(enter)
            .call(updateDilatationLines);
    
        update.exit().remove();
    
        deferCallback(null);
    };
    
        /**
     * Function to draw layer of modules box (displayed when moving system).
     * @param {object} container   System container.
     * @param {array} data         List of modules drawn.
     */
    EZsvgContainer.prototype.drawFlattenModulesBox = function(container, data) {
        var self = this;
        if (self.name === 'svgArea') {
            container.selectAll('g.svgArea_moduleBox').remove();
    
            if (data.length > 0) {
                var system = ez3dScene.findById(data[0].id.split('_')[2]);
    
                container
                    .append('g')
                    .attr('class', 'svgArea_moduleBox')
                    .append('rect')
                    .attr('class', 'EZModulesBox-path')
                    .style('fill', 'red')
                    .style('pointer-events', 'none')
                    .style('fill-opacity', 0.3)
                    .attr('width', function() {
                        return system.modulesBox[2].x - system.modulesBox[0].x;
                    })
                    .attr('height', function() {
                        return Math.abs(system.modulesBox[1].y - system.modulesBox[0].y);
                    })
                    .attr('x', function() {
                        return system.modulesBox[0].x;
                    })
                    .attr('y', function() {
                        return system.modulesBox[1].y;
                    })
                    .attr('display', function() {
                        var value = 'none';
                        if (ez3dScene.mode === 'interactiveMove' &&
                            ez3dScene.active.constructor.name === 'EZModelSubarea' &&
                            this.parentElement.parentElement.parentElement.id.split('svgArea_system_')[1] === ez3dScene.active.system.id) {
                            value = 'inherit';
                        }
                        return value;
                    });
            }
        }
    };
    
        /**
     * Draw keepouts with d3.
     * @param {array} keepoutsData          - Array with the keepouts to draw.
     * @param {string} buildingId           - Parent building id.
     * @param {boolean} movingKeepout       - If a keepout is being moved (parameter sent after checking interactiveMove).
     * @param {boolean} svgContainer        - Svg container.
     * @param {function} deferCallback      - Defer callback function.
     */
    EZsvgContainer.prototype.drawKeepouts = function(keepoutsData, buildingId, movingKeepout, svgContainer, deferCallback) {
        const container = (movingKeepout)
            ? d3.select('#' + svgContainer.name + '-L6_building_' + ez3dScene.activeBuilding.id)
            : d3.select('#' + svgContainer.name + '-L4_building_' + buildingId);
    
        const enterKeepouts = function(selection) {
            selection
                .attr('class', 'EZModelKeepout')
                .attr('id', (d) => svgContainer.name + '_keepout_' + d.id);
    
            if (svgContainer.options.coord === 'scene') {
                selection
                    .append('g')
                    .attr('class', 'offsetted-keepout-shape-group')
                    .append('path')
                    .attr('class', 'offsetted-keepout-shape');
            }
            selection
                .append('g')
                .attr('class', 'keepout-shape-group')
                .append('path')
                .attr('class', 'keepout-shape')
                .on('mouseover', function() {
                    d3.select(this)
                        .call(styleKeepouts, svgContainer, 'hover');
                })
                .on('mouseout', function(d) {
                    d3.select(this)
                        .call(styleKeepouts, svgContainer, d.style.colorState);
                })
                .on('click', (d) => {
                    if (svgContainer.options.coord === 'scene') {
                        svgContainer.clickOnKeepoutEvent(d);
                    }
                })
                .on('contextmenu', function (d) {
                    d3.event.preventDefault();
                    if (this.parentElement.className.baseVal === 'keepout-shape-group') {
                        const contextPanelData = {
                            functionOnCreation: 'contextPanelKeepout',
                            elementData: {
                                element: d,
                                path: this
                            }
                        };
                        const mouse = d3.mouse(d3.select('#ez3d-main-container').node());
                        self.ez3dViewport.ez3dGuiManager.generateContextPanel(mouse, contextPanelData, svgContainer.name);
                    }
                });
        };
    
        const updateKeepouts = function(selection) {
            if (svgContainer.options.coord === 'scene') {
                selection
                    .select('path.offsetted-keepout-shape')
                    .attr('d', (d) => d.path.offsettedPaths[0].lineData)
                    .attr('transform', function() {
                        return d3.select(this.parentElement.parentElement).select('path.keepout-shape').attr('transform');
                    })
                    .call(styleKeepouts, svgContainer, 'offsettedPath');
            }
            selection
                .select('path.keepout-shape')
                .attr('d', (d) => (svgContainer.options.coord === 'scene' ? d.path.lineData : d.path.lineDataFlatten))
                .call(styleKeepouts, svgContainer);
    
            selection
                .classed('ez3d-interactive-move', (d) => ez3dScene.isObjectBeingMoved(d.id))
                .call(d3.drag()
                    .on('start', function(d) {
                        return (ez3dScene.isObjectBeingMoved(d.id) ? svgContainer.elementDragStarted.bind(this)() : null);
                    })
                    .on('drag', function(d) {
                        return (ez3dScene.isObjectBeingMoved(d.id) ? svgContainer.elementDragged.bind(this)() : null);
                    })
                    .on('end', function(d) {
                        return (ez3dScene.isObjectBeingMoved(d.id) ? svgContainer.elementDragEnded.bind(this)() : null);
                    })
                );
        };
    
        // Start d3 join
        const update = container.selectAll('g.EZModelKeepout')
            .data(keepoutsData, (d) => d.id);
    
        const enter = update.enter()
            .append('g')
            .call(enterKeepouts);
    
        update.merge(enter)
            .call(updateKeepouts);
    
        update.exit().remove();
    
        deferCallback(null, keepoutsData.length);
    };
    
    const styleKeepouts = function(selection, svgContainer, colorState) {
        const colors = ez3dScene.layoutRules.defaultColors['keepoutColors'];
        const strokeScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.2, 1 / 50]);
    
        selection
            .attr('fill', (d) => (colorState === 'standard' && d.color) ? d.color : colors.path[colorState || d.style.colorState].fill)
            .attr('fill-opacity', (d) => colors.path[colorState || d.style.colorState].fillOpacity)
            .attr('stroke', (d) => colors.path[colorState || d.style.colorState].stroke)
            .attr('stroke-width', (d) => {
                let strokeWidth = strokeScale(svgContainer.zoomValue) * colors.path[colorState || d.style.colorState].strokeWidth;
                if (strokeWidth < 0) {
                    strokeWidth = 0.02;
                }
                return strokeWidth;
            })
            .attr('stroke-dasharray', (d) => colors.path[colorState || d.style.colorState].strokeDasharray);
    };
    
        EZsvgContainer.prototype.drawLabels = function(container, data, ortogonal, scaleFactor) {
        var self = this;
    
        var labelScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([1, 0.1]);
    
        var offsetX = 0;
        var offsetY = 0;
        var centerX;
        var centerY;
        var bbox;
    
        if (!scaleFactor) {
            var scaleFactor = 1;
        }
    
        if (ortogonal === undefined) {
            var offset = d3.select(container.node().parentElement.parentElement).select('path').attr('transform');
            if (offset) {
                offsetX = Number(offset.split('(')[1].split(')')[0].split(',')[0]);
                offsetY = Number(offset.split('(')[1].split(')')[0].split(',')[1]);
            }
        }
    
        var enterLabels = function(selection) {
            selection
                .attr('class', function(d, i) {
                    return 'path-label index_' + i;
                })
                .on('contextmenu', labelContextMenu);
    
            selection
                .append('text')
                .attr('class', 'path-label-text');
    
            selection
                .append('rect')
                .attr('class', 'path-label-rect');
        };
    
        var updateLabels = function(selection) {
            selection
                .attr('transform', function(d, i) {
                    if (ortogonal === true) {
                        centerX = data.edgeCentersFlatten[i].x;
                        centerY = data.edgeCentersFlatten[i].y;
                    } else {
                        centerX = data.edgeCenters[i].x;
                        centerY = data.edgeCenters[i].y;
                    }
                    var translateX = offsetX + centerX;
                    var translateY = offsetY + centerY;
                    var rotate = ez3dScene.utils.cleanDegrees(data.edgeAzimuths[i] - 90);
                    if (ortogonal === true) {
                        rotate = ez3dScene.utils.cleanDegrees(data.edgeAzimuthsFlatten[i] - 90);
                    }
                    if (rotate > 45 || rotate < -135) {
                        rotate -= 180;
                    }
                    return 'translate(' + translateX + ', ' + translateY + ') rotate(' + rotate + ')';
                })
                .raise();
    
            selection
                .selectAll('.path-label-text')
                .attr('font-size', labelScale(self.zoomValue) * scaleFactor)
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'middle')
                .attr('fill-opacity', 1)
                .attr('fill', 'black')
                .attr('stroke', 'none')
                .text(function(d) {
                    var dimension;
                    if (ortogonal === true) {
                        dimension = data.edgeDimensionsFlatten[d.index];
                    } else {
                        dimension = data.edgeDimensions[d.index];
                    }
                    var angle;
                    if (ez3dScene.active.parent.type !== 'flat' && d.index === 0 && d.parent.parent.constructor.name === 'EZModelArea') {
                        angle = d.parent.parent.inclination;
                        return EZModelUtils.formatDecimalComma(dimension) + ' m / ' + EZModelUtils.formatDecimalComma(angle) + 'º';
                    } else {
                        return EZModelUtils.formatDecimalComma(dimension) + ' m';
                    }
                });
    
            selection
                .selectAll('rect')
                .each(function(d) {
                    bbox = d3.select(this.parentElement).select('text').node().getBBox();
                    d3.select(this)
                        .attr('x', bbox.x)
                        .attr('y', bbox.y)
                        .attr('width', bbox.width)
                        .attr('height', bbox.height);
    
                    d3.select(this.parentElement)
                        .attr('display', function(d) {
                            var value = 'inherit';
                            if (data.edgeDimensions[d.index] < bbox.width + 0.1) {
                                value = 'none';
                            }
                            return value;
                        });
                })
                .attr('fill', 'white')
                .attr('fill-opacity', 1)
                .attr('stroke', 'none')
                .lower();
        };
    
        var labelContextMenu = function () {
            d3.event.preventDefault();
            if (this.parentElement.className.baseVal === 'subarea-shape-group') {
                var index = Number(this.className.baseVal.split(' ')[1].split('_')[1]);
    
                var angle = ez3dScene.utils.cleanDegrees(data.edgeAzimuths[index] - 90);
                if (ez3dScene.activeRoof.type !== 'flat') {
                    angle = ez3dScene.utils.cleanDegrees(data.edgeAzimuthsFlatten[index] - 90);
                    angle -= ez3dScene.activeArea.angle;
                }
    
                var contextPanelData = {
                    functionOnCreation: 'contextPanelSubareaLabel',
                    elementData: {
                        id: ez3dScene.active.id,
                        azimuth: angle,
                        element: this
                    }
                };
                var mouse = d3.mouse(d3.select('#ez3d-main-container').node());
                self.ez3dViewport.ez3dGuiManager.generateContextPanel(mouse, contextPanelData, self.name);
            }
        };
    
        var update = container.selectAll('g.path-label')
            .data(data.vertices, function(d) { return d.id; });
    
        var enter = update.enter()
            .append('g')
            .call(enterLabels);
    
        update.merge(enter)
            .call(updateLabels);
    
        update.exit().remove();
    };
    
    EZsvgContainer.prototype.drawLabel = function(container, path, index) {
        var self = this;
        var labelScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.75, 0.1]);
    
        // path.updateCalculations();
        self.edgeCenter = path.edgeCenters[index];
        self.edgeDimension = path.edgeDimensions[index];
        self.edgeAzimuth = path.edgeAzimuths[index];
    
        container.selectAll('g').remove();
    
        var labelGroup = container.append('g')
            .attr('class', 'path-label')
            .attr('id', 'label_' + index)
            .attr('transform', function(d) {
                var vector = {
                    x: self.edgeCenter.x,
                    y: self.edgeCenter.y
                };
    
                var rotate = ez3dScene.utils.cleanDegrees(self.edgeAzimuth - 90);
                if (rotate > 45 || rotate < -135) {
                    rotate -= 180;
                }
                return 'translate(' + vector.x + ', ' + vector.y + ') rotate(' + rotate + ')';
            })
            .raise();
    
        labelGroup
            .append('text')
            .attr('font-size', labelScale(self.zoomValue))
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('fill-opacity', 1)
            .attr('fill', 'black')
            .attr('stroke', 'none')
            .text(function() {
                return EZModelUtils.formatDecimalComma(self.edgeDimension) + ' m';
            });
    
        var bbox;
    
        labelGroup
            .append('rect')
            .each(function() {
                bbox = d3.select(this.parentElement).select('text').node().getBBox();
    
                d3.select(this)
                    .attr('x', bbox.x)
                    .attr('y', bbox.y)
                    .attr('width', bbox.width)
                    .attr('height', bbox.height);
    
                if (self.edgeDimension < bbox.width + 0.2) {
                    this.parentElement.remove();
                }
            })
            .attr('fill', 'white')
            .attr('fill-opacity', 1)
            .attr('stroke', 'none')
            .lower();
    
        return labelGroup;
    };
    
        EZsvgContainer.prototype.drawModules = function(container, data, moduleSize, angle, finalContainer, active, svgContainer) {
        var self = this;
    
        var enterSystem = function(selection) {
            selection
                .attr('id', function(d) {
                    return svgContainer.name + '_' + d.id;
                })
                .on('mouseover', function(d) {
                    const color = finalContainer.attr('color');
    
                    if (ez3dScene.active.id !== this.parentElement.parentElement.parentElement.id.split('_')[2] &&
                    (ez3dScene.projectType !== 'simple' || ez3dScene.active.constructor.name !== 'EZModelSubarea')) {
                        return;
                    }
                    if (d.type === 'collision') {
                        return;
                    }
    
                    // Shortcuts
                    if (d3.event.shiftKey) {
                        if (ez3dScene.layoutRules.scenePreferences.userData.OS === 'MacOS') {
                            if (d3.event.metaKey) {
                                // Select all modules
                                d3.select(this.parentElement).selectAll('rect.ez3d-module')
                                    .filter((module) => module.type !== 'collision')
                                    .each(function(module) {
                                        d3.select(this)
                                            .call(styleModules, svgContainer, module.type + 'Hover', color);
                                    });
                            } else {
                                // Select row
                                svgContainer.shiftMouseOverEventNew(this);
                            }
                        } else if (d3.event.ctrlKey) {
                            // Select all modules
                            d3.select(this.parentElement).selectAll('rect.ez3d-module')
                                .filter((module) => module.type !== 'collision')
                                .each(function(module) {
                                    d3.select(this)
                                        .call(styleModules, svgContainer, module.type + 'Hover', color);
                                });
                        } else {
                            // Select row
                            svgContainer.shiftMouseOverEventNew(this);
                        }
                    } else if (d3.event.ctrlKey) {
                        // Select col
                        if (ez3dScene.layoutRules.scenePreferences.userData.OS !== 'MacOS') {
                            svgContainer.ctrlCmdMouseOverEventNew(this);
                        }
                    } else if (d3.event.metaKey) {
                        // Select col
                        svgContainer.ctrlCmdMouseOverEventNew(this);
                    } else {
                        // Select module
                        d3.select(this)
                            .call(styleModules, svgContainer, d.type + 'Hover', color);
                    }
                })
                .on('mouseout', function(d) {
                    if (d.type !== 'collision') {
                        d3.select(this.parentElement).selectAll('rect.ez3d-module')
                            // TODO: check why this condition
                            .filter(() => this.classList.value.split(' ').indexOf('paint') === -1)
                            .each(function() {
                                const svgModuleElement = d3.select(this);
                                svgModuleElement
                                    .call(styleModules, svgContainer, svgModuleElement.datum().type, finalContainer.attr('color'));
                            });
                    }
                })
                .on('contextmenu', function (d) {
                    if (d.type === 'module') {
                        d3.event.preventDefault();
                        if (this.parentElement.parentElement.className.baseVal === 'EZModelSystem' && ez3dScene.active.constructor === EZModelSubarea) {
                            var contextPanelData = {
                                functionOnCreation: 'contextPanelModule',
                                elementData: {
                                    data: d,
                                    element: this
                                }
                            };
                            var mouse = d3.mouse(d3.select('#ez3d-main-container').node());
                            self.ez3dViewport.ez3dGuiManager.generateContextPanel(mouse, contextPanelData, self.name);
                        }
                    }
                })
                .call(d3.drag()
                    .on('start', svgContainer.moduleDragStartedNew)
                    .on('drag', svgContainer.moduleDraggedNew)
                    .on('end', svgContainer.moduleDragEndedNew)
                );
        };
    
        // Change row orientation
        var rotateRows = function() {
            if (data.rotatedRows) {
                data.rotatedRows.forEach(function(row) {
                    container.selectAll('.ez3d-module[row="' + row + '"]')
                        .attr('width', moduleSize.y)
                        .attr('height', moduleSize.x);
                });
            }
        };
    
        var updateSystem = function(selection) {
            selection
                .attr('class', 'ez3d-module')
                .attr('x', function(d) {
                    return d.x;
                })
                .attr('y', function(d) {
                    return d.y;
                })
                .attr('row', function(d) {
                    return d.row;
                })
                .attr('col', function(d) {
                    return d.col;
                })
                .attr('width', moduleSize.x)
                .attr('height', moduleSize.y)
                .style('transform', 'rotate(' + angle + 'deg) scale(1, -1)')
                .style('transform-origin', function(d) {
                    return d.x + 'px ' + d.y + 'px';
                })
                .style('cursor', (ez3dScene.mode === 'interactiveMove' && svgContainer.name === 'svgArea') ? 'move' : 'inherit')
                .call(rotateRows)
                .call(styleModules, svgContainer, undefined, finalContainer.attr('color'));
        };
    
        // Set pointer-event on system
        container
            .style('pointer-events', function() {
                var value = 'none';
                if (active) {
                    value = 'inherit';
                }
                return value;
            });
    
        var update = container.selectAll('.ez3d-module')
            .data(data, function(d) {
                return d.id;
            });
    
        // enter
        var enter = update.enter()
            .append('rect')
            .call(enterSystem);
    
        update.merge(enter)
            .call(updateSystem);
    
        update.exit().remove();
    
        self.drawFlattenModulesBox(container, data);
    };
    
    const styleModules = function(selection, svgContainer, colorState, customColor) {
        const colors = ez3dScene.layoutRules.defaultColors['moduleColors'];
        const strokeScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.2, 1 / 50]);
    
        selection
            .attr('fill', (d) => ((colorState === 'standard' || colorState !== 'collision') && customColor)
                ? customColor
                : colors.path[colorState || d.type].fill
            )
            .attr('fill-opacity', (d) => colors.path[colorState || d.type].fillOpacity)
            .attr('stroke', (d) => colors.path[colorState || d.type].stroke)
            .attr('stroke-width', (d) => {
                let strokeWidth = strokeScale(svgContainer.zoomValue) * colors.path[colorState || d.type].strokeWidth;
                if (strokeWidth < 0) {
                    strokeWidth = 0.02;
                }
                return strokeWidth;
            })
            .attr('stroke-dasharray', (d) => colors.path[colorState || d.type].strokeDasharray);
    };
    
        /**
     * Draw moving object in overlapping moving layer.
     * @param {d3} queue    - D3 queue.
     */
    EZsvgContainer.prototype.drawMovingObject = function(queue) {
        const movingLayer = d3.select('#' + this.name + '-L6');
        movingLayer.selectAll('g').remove();
    
        if (ez3dScene.mode === 'interactiveMove') {
            // List of drawing functions to send to queue.defer
            const drawingFunctions = {
                'building': (deferCallback) => {
                    // Remove building from layer 1 (it is always drawn)
                    d3.select('#' + this.name + '-L1_building_' + ez3dScene.active.id).remove();
                    this.drawBuildings([ez3dScene.active], true, movingLayer, this, deferCallback);
                },
                'area': (deferCallback) => {
                    // Draw area for displaying offsetted area
                    this.drawAreas([ez3dScene.active], true, ez3dScene.activeBuilding.id, true, this);
                    const subarea = ez3dScene.active.subareas.find(({id}) => id === ez3dScene.active.subareaToMove);
                    this.drawSubareas([subarea], true, ez3dScene.activeBuilding.id, true, this, deferCallback);
                },
                'subarea': (deferCallback) => {
                    // Remove subarea from layer 3 (subarea.style.visible has to be true for drawing the system)
                    d3.select('#' + this.name + '-L3 #' + this.name + '_subarea_' + ez3dScene.activeSubarea.id).remove();
                    this.drawSubareas([ez3dScene.activeSubarea], true, ez3dScene.activeBuilding.id, true, this);
                    this.drawSystems([ez3dScene.activeSubarea], true, this, deferCallback);
                },
                'keepout': this.drawKeepouts.bind(undefined, [ez3dScene.active], ez3dScene.activeBuilding.id, true, this),
                'tree': this.drawTrees.bind(undefined, [ez3dScene.active], movingLayer, this),
            };
    
            // Draw invisible building in layer 6
            if (ez3dScene.context !== 'building' && ez3dScene.context !== 'tree') {
                queue.defer(this.drawBuildings, [ez3dScene.activeBuilding], false, movingLayer, this);
            }
            // Draw moving object
            queue.defer(drawingFunctions[ez3dScene.context]);
        } else if (movingLayer.select('rect').attr('width') || movingLayer.select('rect').attr('height')) {
            // Clear moving object layer
            movingLayer.select('rect')
                .attr('width', null)
                .attr('height', null);
        }
    };
    
        /**
     * Draw subareas with d3.
     * @param {array} subareasData          - Array with the subareas to draw.
     * @param {boolean} visible             - If the subarea wants to be visible.
     * @param {string} buildingId           - Parent building id.
     * @param {boolean} movingSubarea       - If a subarea is being moved (parameter sent after checking interactiveMove).
     * @param {boolean} svgContainer        - Svg container.
     * @param {function} deferCallback      - Defer callback function.
     */
    EZsvgContainer.prototype.drawSubareas = function(subareasData, visible, buildingId, movingSubarea, svgContainer, deferCallback) {
        var container = (movingSubarea)
            ? d3.select('#' + svgContainer.name + '-L6_building_' + ez3dScene.activeBuilding.id)
            : d3.select('#' + svgContainer.name + '-L3_building_' + buildingId);
    
        var enterSubareas = function(selection) {
            selection
                .attr('class', 'EZModelSubarea')
                .attr('id', (d) => svgContainer.name + '_subarea_' + d.id);
    
            if (visible) {
                selection
                    .on('click', (d) => svgContainer.clickOnSubareaEvent(d))
                    .filter((d) => d.path.offsettedPaths[0] !== undefined)
                    .append('g')
                    .attr('class', 'offsetted-subarea-shape-group')
                    .append('path')
                    .attr('class', 'offsetted-subarea-shape');
    
                selection
                    .append('g')
                    .attr('class', 'subarea-shape-group')
                    .append('path')
                    .attr('class', 'subarea-shape')
                    .on('mouseover', function(d) {
                        d3.select(this)
                            .call(styleSubareas, svgContainer, (d.style.colorState === 'standard') ? 'hover' : d.style.colorState);
                    })
                    .on('mouseout', function(d) {
                        d3.select(this)
                            .call(styleSubareas, svgContainer, d.style.colorState);
                    })
                    .on('contextmenu', function (d) {
                        d3.event.preventDefault();
                        if (svgContainer.name === 'svgProject' && this.parentElement.className.baseVal === 'subarea-shape-group') {
                            var contextPanelData = {
                                functionOnCreation: 'contextPanelSubarea',
                                elementData: {
                                    element: d,
                                    path: this
                                }
                            };
                            var mouse = d3.mouse(d3.select('#ez3d-main-container').node());
                            self.ez3dViewport.ez3dGuiManager.generateContextPanel(mouse, contextPanelData, svgContainer.name);
                        }
                    });
            }
        };
    
        var updateSubareas = function(selection) {
            selection
                .attr('color', (d) => {
                    return d.system.model.colour || d.system.color;
                });
    
            if (visible) {
                selection
                    .select('path.subarea-shape')
                    .attr('d', (d) => (svgContainer.options.coord === 'scene' ? d.path.lineData : d.path.lineDataFlatten))
                    .each(function(d) {
                        // Draw labels
                        if (ez3dScene.active.id === d.id) {
                            svgContainer.drawLabels(d3.select(this.parentElement), d.path, svgContainer.options.coord !== 'scene', 0.6);
                        } else {
                            d3.select(this.parentElement).selectAll('.path-label').remove();
                        }
                    })
                    .call(styleSubareas, svgContainer);
    
                selection
                    .select('path.offsetted-subarea-shape')
                    .attr('d', (d) => (svgContainer.options.coord === 'scene' ? d.path.offsettedPaths[0].lineData : d.path.offsettedPaths[0].lineDataFlatten))
                    .attr('transform', function() {
                        return d3.select(this.parentElement.parentElement.parentElement.parentElement).select('path').attr('transform');
                    })
                    .attr('display', (d) => (d.id !== ez3dScene.active.id || d.isEditing || (d.disabled || d.parent.disabled)) ? 'none' : 'inherit')
                    .call(styleSubareas, svgContainer, 'offsettedPath');
    
                selection
                    .classed('ez3d-interactive-move', (d) => ez3dScene.isObjectBeingMoved(d.id))
                    .call(d3.drag()
                        .on('start', function(d) {
                            return (ez3dScene.isObjectBeingMoved(d.id) ? svgContainer.elementDragStarted.bind(this)() : null);
                        })
                        .on('drag', function(d) {
                            return (ez3dScene.isObjectBeingMoved(d.id) ? svgContainer.elementDragged.bind(this)() : null);
                        })
                        .on('end', function(d) {
                            return (ez3dScene.isObjectBeingMoved(d.id) ? svgContainer.elementDragEnded.bind(this)() : null);
                        })
                    );
            }
        };
    
        // Start d3 join
        var update = container.selectAll('g.EZModelSubarea')
            .data(subareasData, (d) => d.id);
    
        var enter = update.enter()
            .append('g')
            .call(enterSubareas);
    
        update.merge(enter)
            .call(updateSubareas);
    
        update.exit().remove();
    
        // Check if deferCallback exists (when moving a system it is undefined, see EZsvgContainer.drawMovingObject())
        if (deferCallback) {
            deferCallback(null, subareasData.length);
        }
    };
    
    const styleSubareas = function(selection, svgContainer, colorState) {
        const colors = ez3dScene.layoutRules.defaultColors['subareaColors'];
        const strokeScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.2, 1 / 50]);
    
        selection
            .attr('fill', (d) => colors.path[colorState || d.style.colorState].fill)
            .attr('fill-opacity', (d) => colors.path[colorState || d.style.colorState].fillOpacity)
            .attr('stroke', (d) => colors.path[colorState || d.style.colorState].stroke)
            .attr('stroke-width', (d) => {
                let strokeWidth = strokeScale(svgContainer.zoomValue) * colors.path[colorState || d.style.colorState].strokeWidth;
                if (strokeWidth < 0) {
                    strokeWidth = 0.02;
                }
                return strokeWidth;
            })
            .attr('stroke-dasharray', (d) => colors.path[colorState || d.style.colorState].strokeDasharray);
    };
    
        /**
     * Draw systems with d3.
     * @param {array} subareasData      - Array of subareas with the systems to draw.
     * @param {boolean} movingSystem    - If a system is being moved (parameter sent after checking interactiveMove).
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.drawSystems = function(subareasData, movingSystem, svgContainer, deferCallback) {
        var queue = d3.queue();
    
        // Draw modules
        subareasData.forEach(function(subarea) {
            const container = (movingSystem)
                ? d3.select('#' + svgContainer.name + '-L6 #' + svgContainer.name + '_subarea_' + subarea.id)
                : d3.select('#' + svgContainer.name + '-L3 #' + svgContainer.name + '_subarea_' + subarea.id);
            queue.defer(svgContainer.filterAndDrawModules, subarea.system, movingSystem, svgContainer, container);
        });
    
        queue.awaitAll(function(error, results) {
            if (error) throw error;
            var drawnModules = 0;
            for (var j = 0; j < results.length; j++) {
                drawnModules += results[j];
            }
            deferCallback(null, drawnModules);
        });
    };
    
        EZsvgContainer.prototype.drawTrees = function(trees, container, svgContainer, deferCallback) {
        const enterTrees = function(selection) {
            selection
                .attr('class', 'EZModelTree')
                .attr('id', (d) => svgContainer.name + '_tree_' + d.id)
                .append('g')
                .attr('class', 'tree-shape-group')
                .append('circle')
                .attr('class', 'tree-shape')
                .on('mouseover', function() {
                    d3.select(this)
                        .call(styleTrees, svgContainer, 'hover');
                })
                .on('mouseout', function(d) {
                    d3.select(this)
                        .call(styleTrees, svgContainer, d.style.colorState);
                })
                .on('contextmenu', function (d) {
                    d3.event.preventDefault();
                    if (this.parentElement.className.baseVal === 'tree-shape-group') {
                        const contextPanelData = {
                            functionOnCreation: 'contextPanelTree',
                            elementData: {
                                element: d,
                                circle: this
                            }
                        };
                        const mouse = d3.mouse(d3.select('#ez3d-main-container').node());
                        self.ez3dViewport.ez3dGuiManager.generateContextPanel(mouse, contextPanelData, svgContainer.name);
                    }
                })
                .on('click', svgContainer.clickOnTreeEvent);
        };
    
        const updateTrees = function(selection) {
            selection
                .select('circle.tree-shape')
                .attr('cx', (d) => d.position.x)
                .attr('cy', (d) => d.position.y)
                .attr('r', (d) => d3.max([d.crownHigherRadius, d.crownMiddleRadius, d.crownLowerRadius]))
                .call(styleTrees, svgContainer);
    
            selection
                .classed('ez3d-interactive-move', (d) => ez3dScene.isObjectBeingMoved(d.id))
                .style('opacity', (d) => ez3dScene.isObjectBeingMoved(d.id) ? 0.7 : 1)
                .call(d3.drag()
                    .on('start', function(d) {
                        return (ez3dScene.isObjectBeingMoved(d.id) ? svgContainer.elementDragStarted.bind(this)() : null);
                    })
                    .on('drag', function(d) {
                        return (ez3dScene.isObjectBeingMoved(d.id) ? svgContainer.elementDragged.bind(this)() : null);
                    })
                    .on('end', function(d) {
                        return (ez3dScene.isObjectBeingMoved(d.id) ? svgContainer.elementDragEnded.bind(this)() : null);
                    })
                );
        };
    
        const update = container.selectAll('g.EZModelTree')
            .data(trees, (d) => d.id);
    
        const enter = update.enter()
            .append('g')
            .call(enterTrees);
    
        update.merge(enter)
            .call(updateTrees);
    
        update.exit().remove();
    
        deferCallback(null);
    };
    
    const styleTrees = function(selection, svgContainer, colorState) {
        const colors = ez3dScene.layoutRules.defaultColors['treeColors'];
        const strokeScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.2, 1 / 50]);
    
        selection
            .attr('fill', (d) => colors[colorState || d.style.colorState].fill)
            .attr('fill-opacity', (d) => colors[colorState || d.style.colorState].fillOpacity)
            .attr('stroke', (d) => {
                if (svgContainer.name === 'svgAutoCAD') return;
                return colors[colorState || d.style.colorState].stroke;
            })
            .attr('stroke-width', (d) => {
                let strokeWidth = strokeScale(svgContainer.zoomValue) * colors[colorState || d.style.colorState].strokeWidth;
                if (strokeWidth < 0) {
                    strokeWidth = 0.02;
                }
                return strokeWidth;
            })
            .attr('stroke-dasharray', (d) => colors[colorState || d.style.colorState].strokeDasharray);
    };
    
        /**
     * Filter dilatation lines and draw them with d3.
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.filterAndDrawFlattenAndSimpleDilatationLines = function (svgContainer, deferCallback) {
        const queue = d3.queue();
        const subarea = (ez3dScene.active.constructor.name === 'EZModelSubarea')
            ? ez3dScene.active
            : ez3dScene.activeBuilding.roofs[0].areas[0].subareas[0];
    
        if (subarea.style.visible && subarea.path.isInLimits(svgContainer.limits, true)) {
            const dilatationLinesData = (subarea.system.dilatationLines.enabled && subarea.system.dilatationLines.draw)
                ? svgContainer.parseDilatationLines(subarea.system.dilatationLines.draw)
                : [];
    
            // Draw dilatation lines
            queue.defer(svgContainer.drawDilatationLines, dilatationLinesData, subarea.system, svgContainer);
        }
    
        queue
            .awaitAll(function(error) {
                if (error) throw error;
                deferCallback(null);
            });
    };
    
        EZsvgContainer.prototype.filterAndDrawModules = function(system, movingSystem, svgContainer, finalContainer, deferCallback) {
        var oldContainer = (movingSystem)
            ? d3.select('#' + svgContainer.name + '-L6 #' + svgContainer.name + '_system_' + system.id)
            : d3.select('#' + svgContainer.name + '-L3 #' + svgContainer.name + '_system_' + system.id);
        var container;
        let containerWithMask;
        if (oldContainer.node() === null) {
            containerWithMask = d3.create('svg:g')
                .attr('class', 'EZModelSystem')
                .attr('id', svgContainer.name + '_system_' + system.id);
    
            container = containerWithMask.append('g')
                .attr('class', 'EZModelSystemTransform')
                .attr('id', svgContainer.name + '_systemtransform_' + system.id);
        } else {
            containerWithMask = oldContainer;
            container = oldContainer.select('.EZModelSystemTransform');
        }
    
        var area = system.getAncestor('EZModelArea');
        var building = system.getAncestor('EZModelBuilding');
        var transformOrigin = {
            x: area.flattenOrigin.x - area.axis[1].x,
            y: area.flattenOrigin.y - area.axis[1].y
        };
        transformOrigin = ez3dScene.utils.changeAxis(
            transformOrigin, ez3dScene.utils.degToRad(area.angle));
        transformOrigin = {
            x: transformOrigin.x + area.axis[1].x,
            y: transformOrigin.y + area.axis[1].y
        };
        var inclination = ez3dScene.utils.degToRad(area.inclination);
    
        if (svgContainer.options.coord === 'scene') {
            container
                .attr('transform', function() {
                    return 'translate(' + transformOrigin.x + ', ' + transformOrigin.y + ') rotate(' + -area.angle + ') scale(1, ' + Math.cos(inclination) + ')';
                });
        }
    
        var data = [];
        var angle = system.azimuth + area.angle - 180;
        var limits = svgContainer.limits;
        if (limits === undefined) {
            limits = svgContainer.options.limits();
        }
        limits = EZModelUtils.expandLimitsByModuleDiagonal(limits, system.model, system.placement);
        if (svgContainer.options.coord === 'scene') {
            var center = building.path.center;
            var offset = {
                x: -center.x - transformOrigin.x,
                y: -center.y - transformOrigin.y
            };
            limits = EZModelUtils.moveLimitsByValue(limits, offset);
            var transformedLimits = [];
            limits.forEach(function(point) {
                transformedLimits.push(ez3dScene.utils.rotatePoint(point, ez3dScene.utils.degToRad(area.angle)));
            });
            var scaledLimits = [];
            transformedLimits.forEach(function(point) {
                scaledLimits.push({
                    x: point.x,
                    y: point.y / Math.cos(inclination)
                });
            });
            limits = scaledLimits;
        }
        data = svgContainer.getModulesToDraw(data, system, limits);
    
        var active = ez3dScene.projectType === 'simple' ||
            (ez3dScene.active.constructor.name === 'EZModelSubarea' &&
            ez3dScene.active.system && ez3dScene.active.system.id === system.id);
    
        svgContainer.drawModules(container, data, system.model[system.placement], angle, finalContainer, active, svgContainer);
    
        if (oldContainer.node() === null) {
            finalContainer
                .append(function() {
                    return containerWithMask.node();
                });
        }
    
        if (deferCallback) {
            deferCallback(null, data.length);
        }
    
        return data.length;
    };
    
        EZsvgContainer.prototype.getModulesToDraw = function(data, system, limits) {
        if (system.grid && system.grid.rows) {
            system.grid.rows.forEach(function(row, i) {
                // Check row orientation change
                if (row.placement !== system.placement) {
                    if (!data.rotatedRows) {
                        data.rotatedRows = [];
                    }
                    data.rotatedRows.push(i);
                }
                row.modules.forEach(function(module) {
                    if (module && module.type !== 'collision') {
                        if (ez3dScene.utils.moduleIsInLimits({x: module.x, y: module.y}, limits)) {
                            data.push({
                                x: module.x,
                                y: module.y,
                                type: module.type,
                                id: module.id,
                                row: module.row,
                                col: module.col
                            });
                        }
                    }
                });
            });
        }
        return data;
    };
    
        /**
     * method to set the clicked area active
     * @param  {EZModelArea} area - the area clicked
     */
    EZsvgContainer.prototype.clickOnAreaEvent = function(area) {
        console.clickOn('Area ' + area.name + ' clicked');
        d3.event.stopPropagation();
    
        var scene = ez3dScene;
        if (scene.active.id !== area.id && scene.context !== 'pathEditor') {
            // console.clickOn('clic on area');
            ez3dViewport.ez3dGuiManager.notificationManager(['selectingArea', {style: 'info'}]);
            ee.emitEvent('unlockInterface');
            if (ez3dViewport.svgElements.svgArea) {
                ez3dViewport.ez3dGuiManager.closeSubareaView();
            }
            ee.emitEvent('EZModelScene_setActiveListener', [{args: area.id, updateRender: false}]);
        }
    };
    
    /**
     * method to set the clicked subarea active
     * @param  {EZModelSubarea} subarea - the subarea clicked
     */
    EZsvgContainer.prototype.clickOnSubareaEvent = function(subarea) {
        console.clickOn('Subarea ' + subarea.name + ' clicked');
        d3.event.stopPropagation();
    
        var scene = ez3dScene;
        if (scene.active.id !== subarea.id && scene.context !== 'pathEditor') {
            if (subarea.disabled === false && subarea.parent.disabled === false) {
                ez3dViewport.ez3dGuiManager.notificationManager(['selectingSubarea', {style: 'info'}]);
                ee.emitEvent('EZModelScene_setActiveListener', [{args: [subarea.id, 'EZModelSubarea'], updateRender: false}]);
            }
        }
    };
    
    /**
     * method to set clicked keepout active
     * @param  {EZModelKeepout} keepout - the clicked keepout
     */
    EZsvgContainer.prototype.clickOnKeepoutEvent = function(keepout) {
        console.clickOn('Keepout ' + keepout.name + ' clicked');
        d3.event.stopPropagation();
    
        if (ez3dScene.context !== 'pathEditor') {
            if (ez3dScene.active.id !== keepout.id) {
                ez3dViewport.ez3dGuiManager.notificationManager(['selectingKeepout', {style: 'info'}]);
                if (ez3dViewport.svgElements.svgArea) {
                    ez3dViewport.ez3dGuiManager.closeSubareaView();
                }
                ee.emitEvent('EZModelScene_setActiveListener', [{args: keepout.id, updateRender: false}]);
                ez3dViewport.ez3dGuiManager.updateNavigation = false;
            }
        }
    };
    
    /**
     * method to set clicked tree active
     * @param  {EZModelTree} tree - the clicked tree
     */
    EZsvgContainer.prototype.clickOnTreeEvent = function(tree) {
        console.clickOn('Tree ' + tree.name + ' clicked');
        d3.event.stopPropagation();
    
        if (ez3dScene.context !== 'pathEditor') {
            if (ez3dScene.active.id !== tree.id) {
                ez3dViewport.ez3dGuiManager.notificationManager(['selectingTree', {style: 'info'}]);
                if (ez3dViewport.svgElements.svgArea) {
                    ez3dViewport.ez3dGuiManager.closeSubareaView();
                }
                ee.emitEvent('EZModelScene_setActiveListener', [{args: tree.id, undo: true, updateRender: false}]);
                ee.emitEvent('unlockInterface');
            }
        }
    };
    
    /**
     * drag start function (move buildings, subareas, keepouts, trees)
     */
    EZsvgContainer.prototype.elementDragStarted = function() {
        this.origin = {x: 0,y: 0};
    
        if (d3.select(this).attr('transform')) {
            this.origin = {
                x: Number(d3.select(this).attr('transform').split('(')[1].split(',')[0]),
                y: Number(d3.select(this).attr('transform').split('(')[1].split(',')[1].split(')')[0])
            };
        }
    
        this.move = {x: 0 + this.origin.x,y: 0 + this.origin.y};
    };
    
    /**
     * drag event function (move buildings, subareas, keepouts, trees)
     */
    EZsvgContainer.prototype.elementDragged = function() {
        this.move.x += d3.event.dx;
        this.move.y += d3.event.dy;
        var translation = 'translate(' + this.move.x + ', ' + this.move.y + ')';
        d3.select('#' + this.id)
            .attr('transform', translation);
    };
    
    /**
     * drag end function (move buildings, subareas, keepouts, trees)
     */
    EZsvgContainer.prototype.elementDragEnded = function() {
        var ob = ez3dScene.findById(this.id.split('_')[2]);
        var loc;
        if (ob.constructor.name === 'EZModelTree') {
            loc = ob.position;
        } else {
            loc = ob.path.center;
        }
    
        this.move.x += loc.x - this.origin.x;
        this.move.y += loc.y - this.origin.y;
        var newPos = ez3dScene.utils.translateSphericalByCartesianOffset(loc, this.move);
        newPos = {
            lat: newPos.lat - loc.lat,
            lng: newPos.lng - loc.lng
        };
        if (ob.constructor.name === 'EZModelSubarea') {
            ee.emitEvent('EZModelArea_moveSubareaListener', [{
                args: [ob.id, newPos],
                updateRender: {draw2d: true, draw3d: ob, updatePanels: false, fitRange: false},
                progress: true
            }]);
        } else {
            ee.emitEvent(ob.constructor.name + '_moveListener', [{
                args: newPos,
                updateRender: {draw2d: true, draw3d: ob, updatePanels: false, fitRange: false},
                progress: true
            }]);
        }
    
        var translation = 'translate(' + this.origin.x + ', ' + this.origin.y + ')';
        d3.select('#' + this.id)
            .attr('transform', translation);
    
        delete this.move;
        delete this.origin;
    };
    
        EZsvgContainer.prototype.drawFlattenArrowAndCompass = function(svgContainer, deferCallback) {
        if (ez3dScene.active.constructor.name === 'EZModelSubarea' && ez3dScene.projectType !== 'simple') {
            // COMPASS
            svgContainer.select('#ez3d-compass-icon').remove();
            svgContainer
                .append('rect')
                .attr('id', 'ez3d-compass-icon')
                .attr('fill', 'url(#compass-pattern)')
                .attr('x', d3.select('svg#svgArea').style('width').slice(0, -2) - 110)
                .attr('y', 50)
                .attr('height', 80)
                .attr('width', 80);
    
            // AZIMUTH ARROW
            svgContainer.select('#ez3d-system-azimuth-arrow').remove();
            const azimuthAngle = ez3dScene.active.system.azimuth + ez3dScene.active.parent.angle;
            // Container
            const arrowContainer = svgContainer
                .append('g')
                .attr('id', 'ez3d-system-azimuth-arrow')
                .attr('transform', function() {
                    var positionX = d3.select('svg#svgArea').style('width').slice(0, -2) - 100;
                    return 'translate(' + positionX + ', ' + 150 + ')';
                });
    
            // Azimuth text
            arrowContainer
                .append('text')
                .attr('x', 0)
                .attr('y', 60)
                // y = line y2 value
                .style('cursor', 'default')
                .html(ez3dViewport.ez3dGuiManager.i18n('azimuth'));
    
            // Azimuth arrow
            const arrowContainerWidth = arrowContainer.node().getBoundingClientRect().width;
            arrowContainer
                .append('line')
                .attr('marker-start', 'url(#arrow-start)')
                .attr('marker-end', (ez3dScene.active.system.structure === 'EW') ? 'url(#arrow-end)' : '')
                .attr('x1', arrowContainerWidth / 2)
                .attr('y1', 0)
                .attr('x2', arrowContainerWidth / 2)
                .attr('y2', 40)
                .attr('stroke', 'black')
                .attr('stroke-width', 3)
                .style('transform-origin', function() {
                    // Half width of the arrow container, half height of the arrow
                    return arrowContainer.node().getBoundingClientRect().width / 2 + 'px ' + (this.getBoundingClientRect().height / 2) + 'px';
                })
                .style('transform', 'rotate(' + azimuthAngle + 'deg');
    
            svgContainer.raise();
        }
    
        deferCallback(null);
    };
    
        /**
     * Check area in limits and draw it with d3.
     * @param {array} area              - Active EZModelArea.
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.filterAndDrawFlattenArea = function (area, svgContainer, deferCallback) {
        const queue = d3.queue();
    
        // Filter area
        const areaData = (area.style.visible && area.path.isInLimits(svgContainer.limits, true)) ? [area] : [];
        // Draw area
        queue
            .defer(svgContainer.drawAreas, areaData, true, area.parent.parent.id, undefined, svgContainer)
            .awaitAll(function(error) {
                if (error) throw error;
                deferCallback(null);
            });
    };
    
        /**
     * Get keepouts in limits and draw them with d3.
     * @param {object} area             - Active EZModelArea.
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.filterAndDrawFlattenKeepouts = function (area, svgContainer, deferCallback) {
        const queue = d3.queue();
        let keepoutsCounter = 0;
        const parentBuilding = area.parent.parent;
    
        // Filter keepouts
        let keepoutsData;
        if (ez3dScene.mode === 'interactiveMove' && ez3dScene.active.constructor === EZModelSubarea) {
            keepoutsData = [];
        } else {
            // Create an array with the keepout id, path and style attributes (style is obtained from building.keepouts)
            keepoutsData = Object.entries(area.keepouts)
                .map(([keepoutId, projectedPaths]) => projectedPaths
                    .map((path, index) => ({
                        id: `${keepoutId}_${index}`,
                        path: path,
                        style: parentBuilding.keepouts.find(({id}) => id === keepoutId).style,
                    }))
                )
                .flat()
                .filter((keepout) => keepout.style.visible && keepout.path.isInLimits(svgContainer.limits, true));
        }
    
        // Draw keepouts
        queue.defer(svgContainer.drawKeepouts, keepoutsData, area.parent.parent.id, false, svgContainer);
        keepoutsCounter += keepoutsData.length;
    
        queue
            .awaitAll(function(error) {
                if (error) throw error;
                deferCallback(null, keepoutsCounter);
            });
    };
    
        EZsvgContainer.prototype.drawAreaPromise = function(area) {
        var self = this;
        var promise = new Promise((resolve, reject) => {
            if (ez3dScene.layoutRules.scenePreferences.debugContainers) {
                console.area('▓▒░▒▓ draw area promise');
            }
            var debugOpTime = Date.now();
            const queue = d3.queue();
    
            // Filter visible areas in limits
            let subareasData = [];
            if (area.style.visible && area.path.isInLimits(self.limits, true)) {
                // Filter visible subareas in limits
                subareasData = area.subareas
                    .filter((subarea) => {
                        if (!subarea.style.visible) return false;
                        return subarea.path.isInLimits(self.limits, true);
                    });
            }
    
            queue
                // Arrow and compass in svgArea.iconsGroup
                .defer(self.drawFlattenArrowAndCompass, self.iconsGroup)
                // Draw invisible buildings group to append elements in each building layer container
                .defer(self.drawBuildings, [ez3dScene.activeBuilding], false, d3.select('#' + self.name + '-L2'), self)
                .defer(self.drawBuildings, [ez3dScene.activeBuilding], false, d3.select('#' + self.name + '-L3'), self)
                .defer(self.drawBuildings, [ez3dScene.activeBuilding], false, d3.select('#' + self.name + '-L4'), self)
                // Areas in layer 2
                .defer(self.filterAndDrawFlattenArea, area, self)
                // Subareas, system and DL in layer 3
                .defer(self.drawSubareas, subareasData, true, ez3dScene.activeBuilding.id, false, self)
                .defer(self.drawSystems, subareasData, false, self)
                .defer(self.filterAndDrawFlattenAndSimpleDilatationLines, self)
                // Keepouts in layer 4
                .defer(self.filterAndDrawFlattenKeepouts, area, self);
            // Moving object in layer 6
            self.drawMovingObject(queue, self);
    
            queue
                .awaitAll(function(error, results) {
                    if (error) throw error;
                    setTimeout(function () {
                        debugOpTime = Date.now() - debugOpTime;
                        resolve({status: 'FINISHED', args: debugOpTime,
                            subareasPainted: results[5],
                            modulesPainted: results[6],
                            keepoutsPainted: results[8]
                        });
                    }, 0);
                });
        });
    
        return promise;
    };
    
        /**
     * [svgAreaOptions description]
     * @type {[type]}
     */
    EZViewport.prototype.svgAreaOptions = {
        grid: true,
        zoom: true,
        coord: 'area',
        limits: function() {
            return ez3dScene.utils.calculateSubareaRange(ez3dScene.activeSubarea);
        },
        zoomed: function zoomed() {
            console.area('zoomed');
            var that = ez3dViewport.svgElements.svgArea;
            if (d3.event.sourceEvent &&
                (d3.event.sourceEvent.type === 'mousemove' ||
                d3.event.sourceEvent.type === 'wheel')) {
                that.needsDraw = true;
            }
            if (d3.select(this).attr('width') !== null && d3.select(this).attr('width') !== '0') {
                that.transform = d3.zoomTransform(this);
                that.getCanvasLimits(that.transform);
    
                that.elementsGroup.attr('transform', d3.event.transform);
                that.limitsGroup.attr('transform', d3.event.transform);
                if (that.options && that.options.grid) {
                    if (ez3dScene.layoutRules.scenePreferences.snapToGrid === false ||
                        ez3dScene.context !== 'pathEditor') {
                        that.gX.call(that.xAxis.scale(d3.event.transform.rescaleX(that.x)));
                        that.gY.call(that.yAxis.scale(d3.event.transform.rescaleY(that.y)));
                    }
                }
            }
        },
        zoomEnd: function (svgElement) {
            console.area('zoomEnd');
            if (d3.select(svgElement).attr('width') !== null && d3.select(svgElement).attr('width') !== '0') {
                var that = ez3dViewport.svgElements.svgArea;
                if (that.needsDraw === true) {
                    that.draw();
                    that.needsDraw = false;
                }
            }
        },
        fitRange: function(duration) {
            console.area('fitRange');
            duration = duration || 250;
            var that = ez3dViewport.svgElements.svgArea;
    
            if ((ez3dScene.lastOperatorExecuted &&
                ez3dScene.lastOperatorExecuted === 'setActive' &&
                ez3dScene.active.constructor.name === 'EZModelSubarea') ||
                !this.limits ||
                !ez3dScene.active.path.isInLimits(this.limits, true)) {
                // new subarea active or active out of limits
                console.area('starting fit range');
                this.fitRangeToContainer(that.options.limits(), duration);
                this.needsDraw = true;
            } else {
                console.area('║║║ skip fit range');
                that.draw();
            }
    
            if (this.transform) {
                this.gX.call(this.xAxis.scale(this.transform.rescaleX(this.x)));
                this.gY.call(this.yAxis.scale(this.transform.rescaleY(this.y)));
            }
        },
        draw: function() {
            ee.emitEvent('show_svgArea_spinner');
            var that = ez3dViewport.svgElements.svgArea;
            if (ez3dScene.activeArea) {
                this.drawAreaPromise(ez3dScene.activeArea)
                    .then((result) => {
                        console.area('drawn area in ' + (result.args / 1000) + '  sec  (' +
                        result.subareasPainted + ' subareas, ' +
                        result.modulesPainted + ' modules, ' +
                        result.keepoutsPainted + ' keepouts)'
                        );
                        ee.emitEvent('hide_svgArea_spinner');
                    });
            }
        },
        listeners: function() {
            const runMethodAction = function() {
                if (ez3dViewport.ez3dGuiManager.showOpenSubareaView()) {
                    ez3dViewport.ez3dGuiManager.openSubareaView();
                }
            };
            ee.addListener('EZModelSubarea_runMethodFinished_setInteractiveMoveToggle', runMethodAction);
    
            const closeSubareaView = function() {
                if (ez3dViewport.svgElements.svgArea && ez3dScene.active.constructor !== EZModelSubarea) {
                    ez3dViewport.ez3dGuiManager.closeSubareaView(true);
                }
            };
    
            ee.addListener('EZModelScene_setActiveListener', closeSubareaView);
        }
    
    };
    
        // scene coordinate system methods
    
    /**
     * method to draw the project limits in scene coord system
     */
    EZsvgContainer.prototype.drawProjectLimitsScene = function() {
        var self = this;
        this.projectRange = ez3dScene.utils.calculateProjectRange(ez3dScene);
    
        this.projectWidth = this.projectRange[2] - this.projectRange[0];
        this.projectHeight = this.projectRange[3] - this.projectRange[1];
    
        var clickOnProjectLimits = function() {
            self.clickOnProjectLimitsEvent();
            setTimeout(function () {
                ee.emitEvent('updateRender',
                    [{draw2d: true, draw3d: false, updatePanels: false, fitRange: false}]);
            }, 250);
        };
    
        d3.select('#' + this.name + '-projectLimits').remove();
        d3.select('#' + this.name + '-projectCenter').remove();
    
        this.projectLimits = this.limitsGroup.append('g')
            .attr('id', this.name + '-projectLimits')
            .attr('class', 'EZProject-limits');
    
        // project area
        this.projectLimits.append('rect')
            // .attr('class', 'view')
            .attr('x', this.projectRange[0])
            .attr('y', this.projectRange[1])
            .attr('width', this.projectWidth)
            .attr('height', this.projectHeight)
            .on('click', clickOnProjectLimits);
    
        // draw project center
        if (ez3dScene.buildings.length < 1) {
            this.projectCenter = this.limitsGroup.append('g')
                .attr('class', 'EZProject-center')
                .attr('id', this.name + '-projectCenter');
    
            this.projectCenter.append('text')
                .attr('font-family', 'FontAwesome3D')
                .text(function() {
                    return '\uf041';
                });
        }
    
        if (this.limits === undefined) {
            console.clickOn('generating limits for ' + this.name);
            clickOnProjectLimits();
        }
    };
    
    /**
     * method to draw the AutoCAD scene in viewport
     */
    EZsvgContainer.prototype.drawProjectAutoCAD = function() {
        d3.select('#svgAutoCAD-elementsGroup').selectAll('*').remove();
    
        this.drawProjectLimitsScene();
    
        this.drawAutoCADScene(ez3dScene.buildings);
        this.drawAutoCADTrees(ez3dScene.trees);
    };
    
    /**
     * method to unset all active objects when clicking on project limits
     */
    EZsvgContainer.prototype.clickOnProjectLimitsEvent = function() {
        console.clickOn('projectLimits clicked');
        if (ez3dScene.context !== 'pathEditor') {
            ez3dViewport.ez3dGuiManager.notificationManager(['fitProjectLimits', {style: 'info'}]);
            this.fitRangeToContainer(this.projectRange, 250);
        }
    };
    
        // drawDimensionLine
    EZsvgContainer.prototype.drawDimensionLine = function(container, d, nextVertex, subContainer) {
        var self = this;
        var containerName = container.node().id.replace('-elementsGroup', '');
        var vertices = d.parent.vertices;
        var space = 0.5;
        var angle;
        var offsetx;
        var offsety;
        var x1;
        var y1;
        var x2;
        var y2;
    
        var strokeScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.2, 1 / 50]);
    
        if (subContainer !== undefined) {
            space = 0.1;
        }
    
        if (d3.select('#svgContainer-svgAutoCAD').select('#cad-dimension-line').node() === null) {
            self.addCadDimensionLineDef(self.svgDefs);
        }
    
        if (containerName === 'svgAutoCAD') {
            x1 = vertices[d.index].x;
            y1 = vertices[d.index].y;
            x2 = vertices[nextVertex].x;
            y2 = vertices[nextVertex].y;
            angle = ez3dScene.utils.degToRad(d.parent.edgeAzimuths[d.index] - 90);
        } else {
            x1 = vertices[d.index].xF;
            y1 = vertices[d.index].yF;
            x2 = vertices[nextVertex].xF;
            y2 = vertices[nextVertex].yF;
            angle = ez3dScene.utils.degToRad(d.parent.edgeAzimuthsFlatten[d.index] - 90);
        }
        space = [space * Math.sin(angle), -space * Math.cos(angle)];
    
        container.append('line')
            .attr('stroke', ez3dScene.layoutRules.defaultColors.areaColors.autocad.strokeLine)
            .attr('stroke-width', ez3dScene.layoutRules.defaultColors.areaColors.autocad.strokeWidthLine)
            .attr('x1', x1)
            .attr('y1', y1)
            .attr('x2', x2)
            .attr('y2', y2)
            .attr('marker-start', 'url(#cad-dimension-line)')
            .attr('marker-end', 'url(#cad-dimension-line)')
            .attr('transform', function() {
                if (containerName === 'svgAutoCAD') {
                    offsetx = d.parent.parent.path.center.x + space[0];
                    offsety = d.parent.parent.path.center.y + space[1];
                    if (subContainer === 'keepout') {
                        offsetx = d.parent.parent.parent.path.center.x + space[0];
                        offsety = d.parent.parent.parent.path.center.y + space[1];
                    }
                } else {
                    offsetx = d.parent.parent.flattenOrigin.x - d.parent.parent.axis[1].x + space[0];
                    offsety = d.parent.parent.flattenOrigin.y - d.parent.parent.axis[1].y + space[1];
                }
                return 'translate(' + offsetx + ', ' + offsety + ')';
            });
    
        self.drawCotas(containerName, d, nextVertex, space);
    };
    
    // drawDimensionLabel
    EZsvgContainer.prototype.drawDimensionLabel = function(container, d, subContainer) {
        var containerName = container.node().id.replace('-elementsGroup', '');
        var space = 0.9;
        var angle;
        var zoom = 0.7;
    
        if (subContainer !== undefined) {
            space = 0.25;
            zoom = 0.1;
        }
    
        var labelScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([1, 0.1]);
    
        if (ez3dViewport.svgElements.svgProject.zoomValue) {
            zoom = ez3dViewport.svgElements.svgProject.zoomValue;
            zoom = labelScale(zoom);
            if (subContainer !== undefined) {
                zoom = 0.1;
            }
        }
    
        if (containerName === 'svgAutoCAD') {
            angle = ez3dScene.utils.degToRad(d.parent.edgeAzimuths[d.index] - 90);
        } else {
            angle = ez3dScene.utils.degToRad(d.parent.edgeAzimuthsFlatten[d.index] - 90);
        }
        space = [space * Math.sin(angle), -space * Math.cos(angle)];
    
        container.append('text')
            .attr('dominant-baseline', 'middle')
            .attr('text-anchor', 'middle')
            .attr('dominant-baseline', 'middle')
            .attr('fill', ez3dScene.layoutRules.defaultColors.areaColors.autocad.strokeLine)
            .text(function () {
                var a;
                if (containerName === 'svgAutoCAD') {
                    a = d.parent.edgeDimensions[d.index];
                } else {
                    a = d.parent.edgeDimensionsFlatten[d.index];
                }
                return EZModelUtils.formatDecimalComma(a);
            })
            .attr('font-size', zoom)
            .attr('transform', function () {
                var rotate;
                var offsetx;
                var offsety;
                if (containerName === 'svgAutoCAD') {
                    rotate = ez3dScene.utils.cleanDegrees(d.parent.edgeAzimuths[d.index] - 90);
    
                    offsetx = d.parent.parent.path.center.x + d.parent.edgeCenters[d.index].x + space[0];
                    offsety = d.parent.parent.path.center.y + d.parent.edgeCenters[d.index].y + space[1];
                    if (subContainer === 'keepout') {
                        offsetx = d.parent.parent.parent.path.center.x + d.parent.edgeCenters[d.index].x + space[0];
                        offsety = d.parent.parent.parent.path.center.y + d.parent.edgeCenters[d.index].y + space[1];
                    }
                } else {
                    rotate = ez3dScene.utils.cleanDegrees(d.parent.edgeAzimuthsFlatten[d.index] - 90);
                    offsetx = d.parent.edgeCentersFlatten[d.index].x + space[0];
                    offsety = d.parent.edgeCentersFlatten[d.index].y + space[1];
                }
                if (rotate > 45 || rotate < -135) {
                    rotate -= 180;
                }
                return 'translate(' + offsetx + ', ' + offsety + ') rotate(' + rotate + ')';
            });
    };
    
    EZsvgContainer.prototype.drawCotas = function(containerName, d, nextVertex, space) {
        var container = d3.select('#' + containerName + '-elementsGroup');
        var vertices = d.parent.vertices;
        var angle;
        var offsetx;
        var offsety;
    
        var strokeScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.2, 1 / 50]);
    
        angle = ez3dScene.utils.degToRad(d.parent.edgeAzimuths[d.index] - 90);
    
        container.append('line')
            .attr('stroke', ez3dScene.layoutRules.defaultColors.areaColors.autocad.strokeLine)
            .attr('stroke-width', ez3dScene.layoutRules.defaultColors.areaColors.autocad.strokeWidthLine)
            .attr('x1', vertices[d.index].x)
            .attr('y1', vertices[d.index].y)
            .attr('x2', vertices[d.index].x + space[0])
            .attr('y2', vertices[d.index].y + space[1])
            .attr('transform', function() {
                offsetx = d.parent.parent.path.center.x;
                offsety = d.parent.parent.path.center.y;
                return 'translate(' + offsetx + ', ' + offsety + ')';
            });
    
        container.append('line')
            .attr('stroke', ez3dScene.layoutRules.defaultColors.areaColors.autocad.strokeLine)
            .attr('stroke-width', ez3dScene.layoutRules.defaultColors.areaColors.autocad.strokeWidthLine)
            .attr('x1', vertices[nextVertex].x)
            .attr('y1', vertices[nextVertex].y)
            .attr('x2', vertices[nextVertex].x + space[0])
            .attr('y2', vertices[nextVertex].y + space[1])
            .attr('transform', function() {
                offsetx = d.parent.parent.path.center.x;
                offsety = d.parent.parent.path.center.y;
                return 'translate(' + offsetx + ', ' + offsety + ')';
            });
    };
    
    // drawAutoCAD trees
    EZsvgContainer.prototype.drawAutoCADTrees = function(trees) {
        var self = this;
    
        var container = d3.select('#' + self.name + '-elementsGroup');
    
        var treeContainer = container.selectAll('path.EZModelTree')
            .data(trees, function(d) {
                return d.id;
            });
    
        // enter
        treeContainer
            .enter()
            .append('circle')
            .attr('class', 'EZModelTree')
            .attr('id', function(d) {
                return self.name + '_tree_' + d.id;
            })
            .attr('cx', function(d) {
                return d.position.x;
            })
            .attr('cy', function(d) {
                return d.position.y;
            })
            .attr('r', function(d) {
                var value = d3.max([d.crownHigherRadius, d.crownMiddleRadius, d.crownLowerRadius]);
                return value;
            })
            .call(styleTrees, this)
    };
    
    EZsvgContainer.prototype.setAutoCADSceneToExport = function(slug) {
        var self = this;
    
        // remove containers
        d3.select('#' + self.name + '-gridGroup').remove();
        d3.select('#' + self.name + '-info-div').remove();
        d3.select('#' + self.name + '-addDotsGroup').remove();
        d3.select('#' + self.name + '-limitsGroup').remove();
        d3.select('#' + self.name + '-pathEditorGroup').remove();
    
        if (slug === 'dxf') {
            self.svgContainer.selectAll('path, line')
                .attr('fill', function (d) {
                    return d3.select(this).style('stroke');
                })
                .attr('stroke-width', 0);
        } else {
            self.svgContainer.selectAll('path, line')
                .attr('fill', 'none');
        }
        // tiene que ser de al scene
        var sceneRange = ez3dScene.utils.calculateProjectRange(ez3dScene);
        var range = [
            sceneRange[0] + 40, sceneRange[2] - 40,
            sceneRange[1] + 40, sceneRange[3] - 40
        ];
        var rangeWidth = Math.abs(range[1] - range[0]);
        var rangeHeight;
    
        if (self.options.coord === 'scene') {
            rangeHeight = range[3] - range[2];
        } else if (self.options.coord === 'area') {
            rangeHeight = range[2] - range[3];
        }
    
        rangeHeight = Math.abs(rangeHeight);
    
        var viewBox = {
            x: range[0] + (rangeWidth / 2),
            y: range[3] - (rangeHeight / 2)
        };
    
        d3.select('#' + self.name)
            .attr('width', (rangeWidth * 1000) + 'mm')
            .attr('height', (rangeHeight * 1000) + 'mm')
            .attr('viewBox', String(viewBox.x) + ' ' + viewBox.y + ' ' + rangeWidth + ' ' + rangeHeight);
    
        var container = d3.select('#' + self.name + '-elementsGroup')
            .attr('transform', function() {
                var offsetx = rangeWidth / 2;
                var offsety = rangeHeight / 2;
                return 'translate(' + offsetx + ', ' + offsety + ')';
            });
    };
    
    // drawAutoCADScene without dimensions
    EZsvgContainer.prototype.drawAutoCADScene = function(buildings) {
        var self = this;
        var subContainer;
    
        var container = d3.select('#' + self.name + '-elementsGroup');
    
        var strokeScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.2, 1 / 50]);
    
        var buildingContainer = container.selectAll('path.EZModelBuilding')
            .data(buildings, function(d) {
                return d.id;
            });
    
        // enter
        buildingContainer
            .enter()
            .append('path')
            .attr('class', 'EZModelBuilding')
            .attr('id', function(d) {
                return self.name + '_building_' + d.id;
            })
            .filter(function(d) {
                return d.isCreated === true && d.isEditing === false;
            })
            .attr('transform', function(d) {
                var offsetx = d.path.center.x;
                var offsety = d.path.center.y;
                return 'translate(' + offsetx + ', ' + offsety + ')';
            })
            .attr('d', function(d) {
                return d.path.lineData;
            })
            .attr('pointer-events', 'none')
            .attr('stroke', ez3dScene.layoutRules.defaultColors.buildingColors.autocad.stroke)
            .attr('stroke-width', strokeScale(self.zoomValue) * ez3dScene.layoutRules.defaultColors.buildingColors.autocad.strokeWidth)
            .attr('fill', ez3dScene.layoutRules.defaultColors.buildingColors.autocad.fill)
            .attr('fill-opacity', ez3dScene.layoutRules.defaultColors.buildingColors.autocad.fillOpacity)
            .each(function(d) {
                var cotas = true;
                if (d3.select('#autocadCotas').selectAll('.ez3d-switch-input').node()) {
                    cotas = d3.select('#autocadCotas').selectAll('.ez3d-switch-input').node().checked;
                }
    
                if (cotas === true) {
                    var nextVertex;
                    var length = d.path.vertices.length - 1;
                    d.path.vertices.forEach(function (vertex) {
                        if (vertex.index < length) {
                            nextVertex = vertex.index + 1;
                        } else {
                            nextVertex = 0;
                        }
                        self.drawDimensionLine(container, vertex, nextVertex);
                        self.drawDimensionLabel(container, vertex);
                    });
                }
            });
    
        // update
        buildingContainer
            .filter(function(d) {
                return d.isCreated === true && d.isEditing === false;
            })
            .attr('d', function(d) {
                return d.path.lineData;
            })
            .attr('stroke-width', strokeScale(self.zoomValue) * ez3dScene.layoutRules.defaultColors.buildingColors.autocad.strokeWidth);
    
        var lineFunction = d3.line()
            .x(function(d) {
                return d.x;
            })
            .y(function(d) {
                return d.y;
            });
    
        var transformOrigin;
        var modules = [];
        var modulesContainer;
    
        container.selectAll('path.EZSystemElement').remove();
    
        buildings.forEach((building) => {
            if (building.isCreated === true && building.isEditing === false) {
                building.roofs.forEach((roof) => {
                    roof.areas.forEach((area) => {
                        if (area.disabled === false) {
                            transformOrigin = {
                                x: area.flattenOrigin.x - area.axis[1].x,
                                y: area.flattenOrigin.y - area.axis[1].y
                            };
                            transformOrigin = ez3dScene.utils.changeAxis(
                                transformOrigin, ez3dScene.utils.degToRad(area.angle));
                            transformOrigin = {
                                x: transformOrigin.x + area.axis[1].x,
                                y: transformOrigin.y + area.axis[1].y
                            };
                            transformOrigin = {
                                x: transformOrigin.x + building.path.center.x,
                                y: transformOrigin.y + building.path.center.y
                            };
                            area.subareas.forEach((subarea) => {
                                if (subarea.disabled === false) {
                                    subarea.system.loadCollisionBox();
                                    subarea.system.grid.rows.forEach((row) => {
                                        row.modules.forEach((module) => {
                                            if (module) {
                                                var inclination = ez3dScene.utils.degToRad(area.inclination);
                                                module.transform = 'translate(' + transformOrigin.x + ', ' + transformOrigin.y + ') rotate(' + -area.angle + ') scale(1, ' + Math.cos(inclination) + ')';
                                                modules.push(module);
                                            }
                                        });
                                    });
                                }
                            });
                        }
                    });
                });
            }
        });
    
        modulesContainer = container.selectAll('path.EZSystemElement')
            .data(modules);
    
        // enter
        modulesContainer
            .enter()
            .filter(function (d) {
                return !(d.type === 'collision' || d.type === 'empty');
            })
            .append('path')
            .attr('class', 'EZSystemElement')
            .attr('transform', function (d) {
                return d.transform;
            })
            .attr('d', function (d) {
                var lineData = [];
                d.box.forEach(function (vertex) {
                    lineData.push(vertex);
                });
                lineData.push(lineData[0]);
                return lineFunction(lineData);
            })
            .attr('pointer-events', 'none')
            .attr('stroke', ez3dScene.layoutRules.defaultColors.moduleColors.autocad.stroke)
            .attr('stroke-width', strokeScale(self.zoomValue) * ez3dScene.layoutRules.defaultColors.moduleColors.autocad.strokeWidth)
            .attr('fill', ez3dScene.layoutRules.defaultColors.moduleColors.autocad.fill)
            .attr('fill-opacity', ez3dScene.layoutRules.defaultColors.moduleColors.autocad.fillOpacity);
    
        modulesContainer
            .enter()
            .each(function(d) {
                d.box = undefined;
                delete d.box;
            });
    
        var areas;
        var areaContainer;
        buildings.forEach(function (building) {
            if (building.isCreated === true) {
                areas = building.getAreas();
                areaContainer = container.selectAll('path.EZModelArea')
                    .data(areas, function (d) {
                        return d.id;
                    });
                // enter
                areaContainer
                    .enter()
                    .append('path')
                    .attr('class', 'EZModelArea')
                    .attr('id', function (d) {
                        return self.name + '_area_' + d.id;
                    })
                    .attr('d', function (d) {
                        return d.path.lineData;
                    })
                    .attr('transform', function (d) {
                        var offsetx = d.parent.parent.path.center.x;
                        var offsety = d.parent.parent.path.center.y;
                        return 'translate(' + offsetx + ', ' + offsety + ')';
                    })
                    .attr('pointer-events', 'none')
                    .attr('stroke', ez3dScene.layoutRules.defaultColors.areaColors.autocad.stroke)
                    .attr('stroke-width', strokeScale(self.zoomValue) * ez3dScene.layoutRules.defaultColors.areaColors.autocad.strokeWidth)
                    .attr('fill', ez3dScene.layoutRules.defaultColors.areaColors.autocad.fill)
                    .attr('fill-opacity', ez3dScene.layoutRules.defaultColors.areaColors.autocad.fillOpacity);
    
                areaContainer
                    .attr('d', function (d) {
                        return d.path.lineData;
                    })
                    .attr('stroke-width', strokeScale(self.zoomValue) * ez3dScene.layoutRules.defaultColors.areaColors.autocad.strokeWidth);
            }
        });
    
        var keepouts;
        var keepoutContainer;
        buildings.forEach(function (building) {
            if (building.isCreated === true) {
                keepouts = building.keepouts;
                keepoutContainer = container.selectAll('path.EZModelKeepout')
                    .data(keepouts, function (d) {
                        return d.id;
                    });
                // enter
                keepoutContainer
                    .enter()
                    .append('path')
                    .raise()
                    .attr('class', 'EZModelKeepout')
                    .attr('id', function (d) {
                        return self.name + '_keepout_' + d.id;
                    })
                    .attr('d', function (d) {
                        return d.path.lineData;
                    })
                    .attr('transform', function (d) {
                        var offsetx = d.parent.path.center.x;
                        var offsety = d.parent.path.center.y;
                        return 'translate(' + offsetx + ', ' + offsety + ')';
                    })
                    .attr('pointer-events', 'none')
                    .attr('stroke', ez3dScene.layoutRules.defaultColors.keepoutColors.autocad.stroke)
                    .attr('stroke-width', strokeScale(self.zoomValue) * ez3dScene.layoutRules.defaultColors.keepoutColors.autocad.strokeWidth)
                    .attr('fill', ez3dScene.layoutRules.defaultColors.keepoutColors.autocad.fill)
                    .attr('fill-opacity', ez3dScene.layoutRules.defaultColors.keepoutColors.autocad.fillOpacity);
    
                // update
                keepoutContainer
                    .attr('d', function (d) {
                        return d.path.lineData;
                    })
                    .attr('stroke-width', strokeScale(self.zoomValue) * ez3dScene.layoutRules.defaultColors.keepoutColors.autocad.strokeWidth);
            }
        });
    };
    
        /**
     * [svgAutocadOptions description]
     * @type {[type]}
     */
    EZViewport.prototype.svgAutoCADOptions = {
        grid: true,
        zoom: true,
        coord: 'scene',
        limits: function() {
            return ez3dScene.utils.calculateProjectRange(ez3dScene);
        },
        zoomed: function zoomed() {
            var that = ez3dViewport.svgElements.svgAutoCAD;
            if (d3.event.sourceEvent &&
                (d3.event.sourceEvent.type === 'mousemove' ||
                d3.event.sourceEvent.type === 'wheel')) {
                that.needsDraw = true;
            }
            if (d3.select(this).attr('width') !== null && d3.select(this).attr('width') !== '0') {
                that.transform = d3.zoomTransform(this);
                that.getCanvasLimits(that.transform);
    
                that.elementsGroup.attr('transform', d3.event.transform);
                that.limitsGroup.attr('transform', d3.event.transform);
                if (that.options && that.options.grid) {
                    if (ez3dScene.layoutRules.scenePreferences.snapToGrid === false ||
                        ez3dScene.context !== 'pathEditor') {
                        that.gX.call(that.xAxis.scale(d3.event.transform.rescaleX(that.x)));
                        that.gY.call(that.yAxis.scale(d3.event.transform.rescaleY(that.y)));
                    }
                }
            }
        },
        zoomEnd: function (svgElement) {
            if (d3.select(svgElement).attr('width') !== null && d3.select(svgElement).attr('width') !== '0') {
                var that = ez3dViewport.svgElements.svgAutoCAD;
                if (that.needsDraw === true) {
                    that.draw();
                    that.needsDraw = false;
                }
            }
        },
        fitRange: function(duration) {
            duration = duration || 250;
            var that = ez3dViewport.svgElements.svgAutoCAD;
            this.fitRangeToContainer(that.options.limits(), duration);
            this.needsDraw = true;
            if (this.transform) {
                this.gX.call(this.xAxis.scale(this.transform.rescaleX(this.x)));
                this.gY.call(this.yAxis.scale(this.transform.rescaleY(this.y)));
            }
        },
        draw: function() {
            var that = ez3dViewport.svgElements.svgAutoCAD;
            this.drawProjectAutoCAD();
            if (!that.isReady) {
                ee.emitEvent('containerReady');
                that.isReady = true;
            }
        }
    };
    
        EZsvgContainer.prototype.drawMaps = function(svgContainer, container, scene, deferCallback) {
        var enterMaps = function(selection) {
            selection
                .attr('class', 'ez3d-map-group')
                .attr('id', function(d) {
                    return d.id;
                })
                .append('rect')
                .attr('class', 'ez3d-map')
                .attr('x', function(d) {
                    return d.x;
                })
                .attr('y', function(d) {
                    return d.y;
                })
                .attr('width', function(d) {
                    return d.w;
                })
                .attr('height', function(d) {
                    return d.h;
                });
    
            selection
                .each(function(d) {
                    if (d3.select('#texture_' + d.id).node() === null) {
                        svgContainer.addProjectTextureDef(svgContainer.svgDefs, d.id);
                    }
                    d3.select('#texture_' + d.id).select('image').attr('href', d.texture);
                });
        };
    
        var updateMaps = function(selection) {
            selection
                .select('rect.ez3d-map')
                .style('stroke', function(d) {
                    if (ez3dScene.layoutRules.scenePreferences.activeMapper) return 'yellow';
                    return 'none';
                })
                .style('stroke-width', function(d) {
                    if (ez3dScene.layoutRules.scenePreferences.activeMapper) return 0.2;
                    return 'none';
                })
                .style('fill', function(d) {
                    if (ez3dScene.layoutRules.scenePreferences.activeMapper) return 'none';
                    return 'url(#texture_' + d.id + ')';
                });
        };
    
        if (ez3dScene.layoutRules.scenePreferences.enableSnapshotMap) {
            // Start d3 join
            var update = container.selectAll('g.ez3d-map-group')
                .data(scene.maps, function(d) {
                    return d.id;
                });
    
            var enter = update.enter()
                .append('g')
                .call(enterMaps);
    
            update.merge(enter)
                .call(updateMaps);
    
            update.exit()
                .each(function(d) {
                    d3.select('#texture_' + d.id).remove();
                })
                .remove();
        }
    
        if (deferCallback) {
            deferCallback(null);
        }
    };
    
        /**
     * [svgMapOptions description]
     * @type {[type]}
     */
    EZViewport.prototype.svgMapOptions = {
        zoom: true,
        coord: 'world',
        rasterMap: true,
        limits: function() {
            return ez3dScene.utils.calculateProjectRange(ez3dScene);
        },
        zoomed: function zoomed() {
            var that = ez3dViewport.svgElements.svgMap;
            if (d3.select(this).attr('width') !== null && d3.select(this).attr('width') !== '0') {
                that.transform = d3.zoomTransform(this);
                that.getCanvasLimits(that.transform);
    
                that.elementsGroup.attr('transform', d3.event.transform);
                that.limitsGroup.attr('transform', d3.event.transform);
                if (that.options && that.options.grid) {
                    if (ez3dScene.layoutRules.scenePreferences.snapToGrid === false ||
                        ez3dScene.context !== 'pathEditor') {
                        that.gX.call(that.xAxis.scale(d3.event.transform.rescaleX(that.x)));
                        that.gY.call(that.yAxis.scale(d3.event.transform.rescaleY(that.y)));
                    }
                }
            }
        },
        zoomEnd: function (svgElement) {
            // console.log('zoomEnd');
            if (d3.select(svgElement).attr('width') !== null && d3.select(svgElement).attr('width') !== '0') {
                var that = ez3dViewport.svgElements.svgMap;
                if (that.needsDraw === true) {
                    console.project('needsDraw, so: drawing again');
                    that.draw();
                    that.needsDraw = false;
                }
                // ee.emitEvent('zoomEnd');
            }
        },
        draw: function() {
            //
        }
    };
    
        /**
     * Get areas in limits and draw them with d3.
     * @param {array} buildingsData     - Array of buildings with the areas to draw.
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.filterAndDrawAreas = function (buildingsData, svgContainer, deferCallback) {
        const queue = d3.queue();
        let areasCounter = 0;
    
        // Filter visible areas in limits
        buildingsData.forEach((building) => {
            let areasData = [];
            building.roofs.forEach((roof) => {
                areasData.push(...roof.areas
                    .filter((area) => {
                        if (!area.style.visible) return false;
                        return area.path.isInLimits(svgContainer.limits);
                    }));
            });
            // Draw areas
            queue.defer(svgContainer.drawAreas, areasData,
                true, building.id, undefined, svgContainer);
            areasCounter += areasData.length;
        });
    
        queue
            .awaitAll(function(error) {
                if (error) throw error;
                deferCallback(null, areasCounter);
            });
    };
    
        /**
     * Get subareas in limits and draw their dilatation lines with d3.
     * @param {array} buildingsData     - Array of buildings with dilatation lines to draw.
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.filterAndDrawDilatationLines = function (buildingsData, svgContainer, deferCallback) {
        const queue = d3.queue();
    
        // Filter visible subareas in limits
        buildingsData.forEach((building) => {
            building.getEnabledSubareas(building)
                .filter((subarea) => {
                    if (!subarea.style.visible) return false;
                    if (!subarea.path.isInLimits(svgContainer.limits)) return false;
                    return true;
                })
                .forEach((subarea) => {
                    const dilatationLinesData = (subarea.system.dilatationLines.enabled && subarea.system.dilatationLines.draw)
                        ? svgContainer.parseDilatationLines(subarea.system.dilatationLines.draw)
                        : [];
    
                    // Draw dilatation lines
                    queue.defer(svgContainer.drawDilatationLines, dilatationLinesData, subarea.system, svgContainer);
                });
        });
    
        queue
            .awaitAll(function(error) {
                if (error) throw error;
                deferCallback(null);
            });
    };
    
        /**
     * Get keepouts in limits and draw them with d3.
     * @param {array} buildingsData     - Array of buildings with the keepouts to draw.
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.filterAndDrawKeepouts = function (buildingsData, svgContainer, deferCallback) {
        const queue = d3.queue();
        let keepoutsCounter = 0;
    
        // Filter visible keepouts in limits
        buildingsData.forEach((building) => {
            const keepoutsData = building.keepouts
                .filter((keepout) => {
                    if (!keepout.style.visible) return false;
                    return keepout.path.isInLimits(svgContainer.limits);
                });
            // Draw keepouts
            queue.defer(svgContainer.drawKeepouts, keepoutsData, building.id, false, svgContainer);
            keepoutsCounter += keepoutsData.length;
        });
    
        queue
            .awaitAll(function(error) {
                if (error) throw error;
                deferCallback(null, keepoutsCounter);
            });
    };
    
        /**
     * Get subareas in limits from enabled areas and draw them with d3.
     * @param {array} buildingsData     - Array of buildings with subareas to draw.
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.filterAndDrawSubareas = function (buildingsData, svgContainer, deferCallback) {
        const queue = d3.queue();
        let subareasCounter = 0;
    
        buildingsData.forEach((building) => {
            const subareasData = [];
            // Filter visible areas in limits
            building.roofs.forEach((roof) => {
                roof.areas
                    .filter((area) => {
                        if (!area.style.visible) return false;
                        return area.path.isInLimits(svgContainer.limits);
                    })
                    .forEach((area) => {
                        // Filter visible subareas in limits
                        area.subareas
                            .filter((subarea) => {
                                if (!subarea.style.visible) return false;
                                return subarea.path.isInLimits(svgContainer.limits);
                            })
                            .forEach((subarea) => subareasData.push(subarea));
                    });
            });
            // Draw subareas
            queue.defer(svgContainer.drawSubareas, subareasData, true, building.id, false, svgContainer);
            subareasCounter += subareasData.length;
        });
    
        queue
            .awaitAll(function(error) {
                if (error) throw error;
                deferCallback(null, subareasCounter);
            });
    };
    
        /**
     * Get subareas in limits from enabled areas and draw their system with d3.
     * @param {array} buildingsData     - Array of buildings with systems to draw.
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.filterAndDrawSystems = function (buildingsData, svgContainer, deferCallback) {
        const queue = d3.queue();
    
        // Filter visible subareas in limits
        buildingsData.forEach((building) => {
            const subareasData = building.getEnabledSubareas(building)
                .filter((subarea) => {
                    if (!subarea.style.visible) return false;
                    return subarea.path.isInLimits(svgContainer.limits);
                });
            // Draw systems
            queue.defer(svgContainer.drawSystems, subareasData, false, svgContainer);
        });
    
        queue
            .awaitAll(function(error, results) {
                if (error) throw error;
                let drawnModules = 0;
                if (results.length > 0) {
                    drawnModules = results.reduce((accumulator, currentValue) => {
                        return accumulator + currentValue;
                    });
                }
                deferCallback(null, drawnModules);
            });
    };
    
        EZsvgContainer.prototype.drawSnapGrid = function() {
        /**
         * [gridDataF description]
         * @param  {[type]} limits [description]
         * @return {[type]}        [description]
         */
        function gridDataF(limits) {
            var gridSize = parseFloat(ez3dScene.layoutRules.scenePreferences.gridSize);
    
            var startXPos = (Math.round(limits[0] / ez3dScene.layoutRules.scenePreferences.gridSize)) * ez3dScene.layoutRules.scenePreferences.gridSize - ez3dScene.layoutRules.scenePreferences.gridSize;
            var startYPos = (Math.round(limits[1] / ez3dScene.layoutRules.scenePreferences.gridSize)) * ez3dScene.layoutRules.scenePreferences.gridSize - ez3dScene.layoutRules.scenePreferences.gridSize;
    
            startXPos += ez3dScene.layoutRules.scenePreferences.gridOffsetX;
            startYPos += ez3dScene.layoutRules.scenePreferences.gridOffsetY;
    
            var rows = Math.round(Math.abs(limits[3] - limits[1]) / gridSize) + 2;
            var cols = Math.round(Math.abs(limits[2] - limits[0]) / gridSize) + 2;
    
            var data = [];
            var ypos = startYPos;
            for (var row = 0; row < rows; row++) {
                data.push([limits[0], ypos]);
                data.push([limits[2], ypos]);
                data.push(null);
                ypos += gridSize;
            }
            var xpos = startXPos;
            for (var column = 0; column < cols; column++) {
                data.push([xpos, limits[1]]);
                data.push([xpos, limits[3]]);
                data.push(null);
                xpos += gridSize;
            }
    
            return data;
        }
    
        var gridData = gridDataF(this.limits);
    
        d3.select('#svgProject-gridGroup').attr('display', 'none');
    
        var grid = d3.select('#ez3d-path-editor-grid');
    
        grid.selectAll('*').remove();
    
        var line = d3.line()
            .defined(function(d) {
                return d;
            })
            .curve(d3.curveLinear);
    
        grid.append('path')
            .datum(gridData)
            .attr('d', line)
            .classed('ez3d-snap-grid-line', true);
    };
    
    EZsvgContainer.prototype.removeSnapGrid = function() {
        d3.select('#ez3d-path-editor-grid').selectAll('*').remove();
        d3.select('#svgProject-gridGroup').attr('display', 'inherit');
    };
    
        EZsvgContainer.prototype.drawProjectPromise = function() {
        const self = this;
        const promise = new Promise((resolve, reject) => {
            if (ez3dScene.layoutRules.scenePreferences.debugContainers) {
                console.project('▓▒░▒▓ draw project promise');
            }
            let debugOpTime = Date.now();
            const queue = d3.queue();
    
            // Filter visible trees
            const treesData = ez3dScene.trees.filter((tree) => tree.style.visible);
            // Get buildings
            let buildingsData = ez3dScene.buildings;
    
            // Draw all buildings (even the ones that are being edited) in layer 1
            queue.defer(self.drawBuildings, buildingsData, true, d3.select('#' + self.name + '-L1'), self);
    
            // Filter buildings to draw their components (areas, subareas, keepouts, ...)
            buildingsData = buildingsData.filter((building) => building.style.visible && building.path.isInLimits(self.limits));
    
            queue
                // Draw invisible buildings group in other layers to apply transformations
                .defer(self.drawBuildings, buildingsData, false, d3.select('#' + self.name + '-L2'), self)
                .defer(self.drawBuildings, buildingsData, false, d3.select('#' + self.name + '-L3'), self)
                .defer(self.drawBuildings, buildingsData, false, d3.select('#' + self.name + '-L4'), self)
                // Areas in layer 2
                .defer(self.filterAndDrawAreas, buildingsData, self)
                // Subareas, system and DL in layer 3
                .defer(self.filterAndDrawSubareas, buildingsData, self)
                .defer(self.filterAndDrawSystems, buildingsData, self)
                .defer(self.filterAndDrawDilatationLines, buildingsData, self)
                // Keepouts in layer 4
                .defer(self.filterAndDrawKeepouts, buildingsData, self)
                // Trees in layer 5
                .defer(self.drawTrees, treesData, d3.select('#' + self.name + '-L5'), self);
            // Moving object in layer 6
            self.drawMovingObject(queue, self);
    
            queue
                .awaitAll(function(error, results) {
                    if (error) throw error;
                    setTimeout(function () {
                        debugOpTime = Date.now() - debugOpTime;
                        resolve({status: 'FINISHED', args: debugOpTime,
                            buildingsPainted: results[0],
                            areasPainted: results[4],
                            subareasPainted: results[5],
                            modulesPainted: results[6],
                            keepoutsPainted: results[8]
                        });
                    }, 0);
                });
        });
    
        return promise;
    };
    
        /**
     * [svgProjectOptions description]
     * @type {[type]}
     */
    EZViewport.prototype.svgProjectOptions = {
        grid: true,
        zoom: true,
        coord: 'scene',
        limits: function() {
            console.project('getLimits');
            return ez3dScene.utils.calculateProjectRange(ez3dScene);
        },
        zoomed: function zoomed() {
            console.project('zoomed');
            var that = ez3dViewport.svgElements.svgProject;
            if (d3.event.sourceEvent &&
                (d3.event.sourceEvent.type === 'mousemove' ||
                d3.event.sourceEvent.type === 'wheel')) {
                that.needsDraw = true;
            }
            if (d3.select(this).attr('width') !== null && d3.select(this).attr('width') !== '0') {
                if (!isNaN(d3.zoomTransform(this).k) && !isNaN(d3.zoomTransform(this).x) && !isNaN(d3.zoomTransform(this).y)) {
                    that.transform = d3.zoomTransform(this);
                    that.getCanvasLimits(that.transform);
                }
    
                // sync player to this container
                if ((ez3dScene.layoutRules.scenePreferences.activeMapper === true ||
                    ez3dScene.layoutRules.scenePreferences.activeRenderer === true) &&
                    ez3dScene.layoutRules.scenePreferences.enablePlayer && that.ez3dViewport.mode !== 6) {
                    if (that.ez3dViewport.ez3dPlayer && that.ez3dViewport.ez3dPlayer.getMode() === '2d') {
                        that.ez3dViewport.ez3dPlayer.fitToBounds(that.center, that.widthInMeters);
                    }
                }
    
                // draw new path editor
                if (that.editorGroup && ez3dScene.activeEditor) {
                    // that.editorGroup.attr('transform', d3.event.transform);
                    that.editorGroup.attr('transform', that.transform);
                    that.editors.forEach(function(editor) {
                        if (ez3dViewport[editor].poll(that)) {
                            ez3dViewport[editor].zoomed(that);
                        }
                    });
                }
    
                that.elementsGroup.attr('transform', d3.event.transform);
                that.limitsGroup.attr('transform', d3.event.transform);
                if (that.options && that.options.grid) {
                    if (ez3dScene.layoutRules.scenePreferences.snapToGrid === false ||
                        ez3dScene.context !== 'pathEditor') {
                        that.gX.call(that.xAxis.scale(d3.event.transform.rescaleX(that.x)));
                        that.gY.call(that.yAxis.scale(d3.event.transform.rescaleY(that.y)));
                    }
                }
            }
        },
        zoomEnd: function (svgElement) {
            const that = ez3dViewport.svgElements.svgProject;
            console.project('zoomEnd');
            if (d3.select(svgElement).attr('width') !== null && d3.select(svgElement).attr('width') !== '0') {
                if (that.hasTransformChanged() && that.needsDraw === true) {
                    console.project('needsDraw, so: drawing again');
                    that.draw();
                    that.needsDraw = false;
                }
            }
            that.initialZoomValue = that.transform;
        },
        fitRange: function(duration) {
            console.project('fitRange');
            duration = duration || 250;
            if (ez3dScene.active.constructor === EZModelTree) {
                ez3dViewport.svgElements.svgProject.flyTo(ez3dScene.active.position, duration);
                console.project('flyTo');
                this.needsDraw = true;
            } else {
                if (ez3dScene.mode !== 'interactiveMove' || !ez3dScene.active.path.isInLimits(this.limits)) {
                    const center = (ez3dScene.active && ez3dScene.active.path) ? ez3dScene.active.path.center : ez3dScene.projectCenter;
                    ez3dViewport.svgElements.svgProject.flyTo(center, duration);
                    console.project('flyTo');
                    this.needsDraw = true;
                } else {
                    const {svgProject} = ez3dViewport.svgElements;
                    svgProject.getCanvasLimits(svgProject.transform);
                    if (svgProject.ez3dViewport.ez3dPlayer) {
                        svgProject.ez3dViewport.ez3dPlayer.fitToBounds(svgProject.center, svgProject.widthInMeters);
                    }
                }
            }
    
            if (ez3dScene.active.customPath) {
                let coords = ez3dScene.active.customPath.center;
                // Center svgProject when creating a custom building shape (customPath.center doesn't exist yet)
                if (!coords) {
                    const defaultShapeValues = ez3dScene.active.customPath.buildingShapeValues[ez3dScene.layoutRules.scenePreferences.defaultBuildingShape];
                    coords = {
                        x: defaultShapeValues.mainHorizontal / 2,
                        y: -defaultShapeValues.mainVertical / 2
                    };
                }
                this.flyTo(coords, duration);
                this.needsDraw = true;
            }
            if (this.transform) {
                this.gX.call(this.xAxis.scale(this.transform.rescaleX(this.x)));
                this.gY.call(this.yAxis.scale(this.transform.rescaleY(this.y)));
            }
        },
        draw: function() {
            ee.emitEvent('show_svgProject_spinner');
            var that = ez3dViewport.svgElements.svgProject;
            // Resize overlap layer for moving objects
            if (ez3dScene.mode === 'interactiveMove') {
                d3.select('#svgProject-L6.ez3d-moving-objects-overlap').select('rect')
                    .attr('x', that.limits[0])
                    .attr('y', that.limits[1])
                    .attr('width', that.limits[2] - that.limits[0])
                    .attr('height', that.limits[3] - that.limits[1])
                    .attr('fill', 'transparent');
            }
            that.drawProjectPromise()
                .then((result) => {
                    console.project('drawn project in ' + (result.args / 1000) + '  sec  (' +
                    result.buildingsPainted + ' buildings, ' +
                    result.areasPainted + ' areas, ' +
                    result.subareasPainted + ' subareas, ' +
                    result.modulesPainted + ' modules, ' +
                    result.keepoutsPainted + ' keepouts)'
                    );
                    that.editors.forEach(function(editor) {
                        if (ez3dViewport[editor].poll(that)) {
                            ez3dViewport[editor].draw(that);
                        }
                    });
                    if (ez3dScene.context === 'pathEditor' && ez3dScene.layoutRules.scenePreferences.snapToGrid) {
                        that.drawSnapGrid();
                    } else {
                        that.removeSnapGrid();
                    }
                    ee.emitEvent('hide_svgProject_spinner');
                });
            if (!that.isReady) {
                ee.emitEvent('containerReady');
                that.isReady = true;
            }
        },
        voronoiGuideLines: function() {
            var self = this;
            var scene = ez3dScene;
            var preferences = scene.layoutRules.scenePreferences;
            if (scene.context === 'pathEditor') {
                self.guideLines = self.calculateGuideLines();
                self.basicGuideLines = self.calculateGuideLines();
            } else if (scene.context === 'roof') {
                self.guideLines = self.calculateBorderGuideLines();
            }
    
            if (self.guideLines && self.guideLines.length > 0) {
                // add existing building edges to guideLines
                if (preferences.snapToGuides === true) {
                    scene.buildings.forEach(function(building) {
                        var isInLimits = ez3dScene.utils.buildingIsInLimits(building, self.limits);
                        // console.project(building.name, isInLimits);
                        // debugger
                        if (building.id !== scene.active.id && isInLimits === true) {
                            building.path.vertices.forEach(function(vertex) {
                                self.guideLines.push([vertex.x + building.path.center.x, vertex.y + building.path.center.y]);
                            });
                            self.guideLines.push([building.path.vertices[0].x + building.path.center.x, building.path.vertices[0].y + building.path.center.y]);
                            self.guideLines.push(null);
                        }
                    });
                }
    
                // generate path from guidelines and draw guidelines
                self.guideLinesPath = self.drawGuideLines(self.guideLines);
            }
        },
        voronoiSamples: function() {
            var self = this;
    
            var snapToLines = ez3dScene.layoutRules.scenePreferences.snapToLines;
            var snapToVertices = ez3dScene.layoutRules.scenePreferences.snapToVertices;
            if (self.snapFastToggle === true) {
                snapToLines = !ez3dScene.layoutRules.scenePreferences.snapToLines;
                snapToVertices = !ez3dScene.layoutRules.scenePreferences.snapToVertices;
            }
            if (snapToLines === true) {
                d3.select('#snap-to-lines')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
                d3.select('#snap-to-vertices')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.disabledbuttons);
            } else {
                d3.select('#snap-to-lines')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.second)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.disabledbuttons);
                d3.select('#snap-to-vertices')
                    .style('color', ez3dScene.layoutRules.defaultColors.cssColors.white)
                    .style('background-color', ez3dScene.layoutRules.defaultColors.cssColors.first);
            }
    
            self.sampledPoints = [];
            if (self.guideLines.length > 0) {
                // sample points from lines to scale precision
                if (snapToLines === true) {
                    self.sampledPoints = ez3dScene.utils.pathPrecisionSample(self.guideLinesPath.node(), self.precision);
                }
            }
    
            // add existing buildings vertices and roofpoints to samplepoints
            if (snapToVertices === true) {
                ez3dScene.buildings.forEach(function(building) {
                    building.path.vertices.forEach(function(vertex) {
                        if (building.id === ez3dScene.active.id && ez3dScene.active.isCreated === false) {
                            self.sampledPoints.push([vertex.x, vertex.y]);
                        } else {
                            self.sampledPoints.push([vertex.x + building.path.center.x, vertex.y + building.path.center.y]);
                        }
                    });
                    building.roofs.forEach(function(roof) {
                        if (building.id !== ez3dScene.active.id) {
                            roof.roofPoints.forEach(function(vertex) {
                                self.sampledPoints.push([vertex.x + building.path.center.x, vertex.y + building.path.center.y]);
                            });
                        }
                    });
                });
            }
    
            if (self.guideLines.length > 0) {
                // add intersections to final sampledPoints
                if (self.guideLinesIntersections) {
                    self.guideLinesIntersections.forEach(function(point) {
                        self.sampledPoints.push([point.x, point.y]);
                    });
                }
            }
        },
        editors: ['pathEditor2', 'roofEditor']
    };
    
        /**
     * Check if area is in limits and draw it with d3.
     * @param {array} buildingsData     - Array of buildings with the area to draw.
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.filterAndDrawSimpleArea = function (buildingsData, svgContainer, deferCallback) {
        const queue = d3.queue();
        let areasCounter = 0;
    
        // Filter visible areas in limits
        buildingsData.forEach((building) => {
            building.roofs.forEach((roof) => {
                const areasData = roof.areas
                    .filter((area) => {
                        return area.style.visible;
                    });
                // Draw areas
                queue.defer(svgContainer.drawAreas, areasData,
                    false, building.id, undefined, svgContainer);
                areasCounter += areasData.length;
            });
        });
    
        queue
            .awaitAll(function(error) {
                if (error) throw error;
                deferCallback(null, areasCounter);
            });
    };
    
        /**
     * Check if subarea is in limits and draw it with d3.
     * @param {array} buildingsData     - Array of buildings with subarea to draw.
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.filterAndDrawSimpleSubarea = function (buildingsData, svgContainer, deferCallback) {
        const queue = d3.queue();
        let subareasCounter = 0;
    
        // Filter visible subareas in limits
        buildingsData.forEach((building) => {
            const subareasData = building.getEnabledSubareas(building)
                .filter((subarea) => {
                    return subarea.style.visible;
                });
            // Draw subareas
            queue.defer(svgContainer.drawSubareas, subareasData, false, building.id, false, svgContainer);
            subareasCounter += subareasData.length;
        });
    
        queue
            .awaitAll(function(error) {
                if (error) throw error;
                deferCallback(null, subareasCounter);
            });
    };
    
        /**
     * Get subareas in limits from enabled areas and draw their system with d3.
     * @param {array} buildingsData     - Array of buildings with system to draw.
     * @param {boolean} svgContainer    - Svg container.
     * @param {function} deferCallback  - Defer callback function.
     */
    EZsvgContainer.prototype.filterAndDrawSimpleSystem = function (buildingsData, svgContainer, deferCallback) {
        const queue = d3.queue();
    
        // Filter visible subareas in limits
        buildingsData.forEach((building) => {
            const subareasData = building.getEnabledSubareas(building)
                .filter((subarea) => {
                    return subarea.style.visible;
                });
            // Draw systems
            queue.defer(svgContainer.drawSystems, subareasData, false, svgContainer);
        });
    
        queue
            .awaitAll(function(error, results) {
                if (error) throw error;
                let drawnModules = 0;
                if (results.length > 0) {
                    drawnModules = results.reduce((accumulator, currentValue) => {
                        return accumulator + currentValue;
                    });
                }
                deferCallback(null, drawnModules);
            });
    };
    
        EZsvgContainer.prototype.drawSimplePromise = function() {
        var self = this;
        var promise = new Promise((resolve, reject) => {
            if (ez3dScene.layoutRules.scenePreferences.debugContainers) {
                console.simple('▓▒░▒▓ draw simple promise');
            }
            let debugOpTime = Date.now();
    
            const buildingsData = ez3dScene.buildings.filter((building) => {
                return building.style.visible;
            });
    
            d3.queue()
                // Draw invisible buildings group in other layers to apply transformations
                .defer(self.drawBuildings, buildingsData, false, d3.select('#' + self.name + '-L2'), self)
                .defer(self.drawBuildings, buildingsData, false, d3.select('#' + self.name + '-L3'), self)
                // Areas in layer 2
                .defer(self.filterAndDrawSimpleArea, buildingsData, self)
                // Subareas, system and DL in layer 3
                .defer(self.filterAndDrawSimpleSubarea, buildingsData, self)
                .defer(self.filterAndDrawSimpleSystem, buildingsData, self)
                .defer(self.filterAndDrawFlattenAndSimpleDilatationLines, self)
                .awaitAll(function(error, results) {
                    if (error) throw error;
                    setTimeout(function () {
                        debugOpTime = Date.now() - debugOpTime;
                        resolve({status: 'FINISHED', args: debugOpTime,
                            areasPainted: results[2],
                            subareasPainted: results[3],
                            modulesPainted: results[4]
                        });
                    }, 0);
                });
        });
    
        return promise;
    };
    
        /**
     * [svgSimpleOptions description]
     * @type {[type]}
     */
    EZViewport.prototype.svgSimpleOptions = {
        grid: true,
        zoom: true,
        coord: 'area',
        limits: function() {
            console.simple('getLimits');
            return ez3dScene.utils.calculateAreaRange(
                ez3dScene.activeBuilding.roofs[0].areas[0]);
        },
        zoomed: function zoomed() {
            console.simple('zoomed');
            var that = ez3dViewport.svgElements.svgSimple;
            if (d3.event.sourceEvent &&
                (d3.event.sourceEvent.type === 'mousemove' ||
                d3.event.sourceEvent.type === 'wheel')) {
                that.needsDraw = true;
            }
            if (d3.select(this).attr('width') !== null && d3.select(this).attr('width') !== '0') {
                that.transform = d3.zoomTransform(this);
                if (!isNaN(d3.zoomTransform(this).k) && !isNaN(d3.zoomTransform(this).x) && !isNaN(d3.zoomTransform(this).y)) {
                    that.transform = d3.zoomTransform(this);
                    that.getCanvasLimits(that.transform);
                }
    
                that.elementsGroup.attr('transform', d3.event.transform);
                that.limitsGroup.attr('transform', d3.event.transform);
                if (that.options && that.options.grid) {
                    if (ez3dScene.layoutRules.scenePreferences.snapToGrid === false || ez3dScene.context !== 'pathEditor') {
                        that.gX.call(that.xAxis.scale(d3.event.transform.rescaleX(that.x)));
                        that.gY.call(that.yAxis.scale(d3.event.transform.rescaleY(that.y)));
                    }
                }
            }
        },
        zoomEnd: function (svgElement) {
            console.simple('zoomEnd');
            if (d3.select(svgElement).attr('width') !== null && d3.select(svgElement).attr('width') !== '0') {
                var that = ez3dViewport.svgElements.svgSimple;
                if (that.needsDraw === true) {
                    console.simple('needsDraw, so: drawing again');
                    that.draw();
                    that.needsDraw = false;
                    ee.addOnceListener('launch_updateGrid', () =>{
                        ee.emitEvent('simple_updateGrid'); // update grid after zoomend simple mode
                    });
                }
            }
        },
        fitRange: function(duration) {
            console.simple('simple fitRange');
            duration = duration || 250;
            var that = ez3dViewport.svgElements.svgSimple;
            if (ez3dScene.lastOperatorExecuted && ez3dScene.lastOperatorExecuted !== 'editSystem') {
                this.fitRangeToContainer(that.options.limits(), duration);
            }
            this.needsDraw = true;
            if (this.transform) {
                this.gX.call(this.xAxis.scale(this.transform.rescaleX(this.x)));
                this.gY.call(this.yAxis.scale(this.transform.rescaleY(this.y)));
            }
        },
        draw: function() {
            console.log('simple draw');
            ee.emitEvent('show_svgSimple_spinner');
            var that = ez3dViewport.svgElements.svgSimple;
            that.drawSimplePromise()
                .then((result) => {
                    console.simple('drawn project in ' + (result.args / 1000) + '  sec  (' + result.modulesPainted + ' modules)');
                    ee.emitEvent('hide_svgSimple_spinner');
                    ee.emitEvent('launch_updateGrid'); // To update grid after zoomend
                });
            if (!that.isReady) {
                ee.emitEvent('containerReady');
                that.isReady = true;
            }
        }
    };
    
        /**
     * method to draw a location in cartesian coord system (scene or area coord system)
     * @param  {EZModelLocation} loc - the location to be drawed
     * @param  {d3Element} container - the container where the location group should be drawed
     * @param  {boolean} flatten - if the point to be drawed is the flatten one or not
     * @param  {boolean} rotatedData - if the point to be drawed is in rotated space or not
     * @return {d3Element} - container where the location was drawed
     */
    EZsvgContainer.prototype.drawLocation = function(loc, container, flatten, rotatedData) {
        let point;
        if (flatten === true) {
            point = [([loc.xF, loc.yF])];
        } else {
            point = [([loc.x, loc.y])];
        }
    
        if (rotatedData) {
            point = [([loc.w, loc.h])];
        }
    
        let offsetX = 0;
        let offsetY = 0;
    
        if (!flatten || flatten === false) {
            if (loc.coordSystem === 'scene') {
                // project center, building center, tree, or scene location
            } else if (loc.coordSystem === 'building') {
                var building = loc.getAncestor('EZModelBuilding');
                offsetX = building.path.center.x;
                offsetY = building.path.center.y;
            }
        }
    
        const EZModelLocationGroup = container.append('g')
            .attr('class', 'EZModelLocation')
            .attr('id', this.name + '-EZModelLocation_' + loc.id);
    
        EZModelLocationGroup.selectAll('circle')
            .data(point).enter().append('circle')
            .attr('cx', function(d) {
                return d[0] + offsetX;
            })
            .attr('cy', function(d) {
                return d[1] + offsetY;
            })
            .attr('r', 0.3)
            .append('title')
            .text(function(d) {
                return EZModelUtils.formatDecimalComma(d[0]) + ',\n' + EZModelUtils.formatDecimalComma(d[1]);
            });
    
        return EZModelLocationGroup;
    };
    
    /**
     * method to draw a path in any cartesian coord system (scene or area coord system)
     * @param  {EZModelPath} path     - the path to be drawed
     * @param  {d3Element} container - the container where the path group should be appended
     * @param  {boolean} flatten   - if the path sould be drawn flattened or not
     * @return {d3Element}          the pathgroup created
     */
    EZsvgContainer.prototype.drawPath = function(path, container, flatten) {
        if (flatten === undefined) {
            flatten = false;
        }
    
        let offsetX = 0;
        let offsetY = 0;
    
        var points = [];
        var point = {};
        if (path.constructor.name === 'EZModelPath') {
            if (flatten === false) {
                if (path.coordSystem === 'scene') {
                    //
                } else if (path.coordSystem === 'building') {
                    var building = path.getAncestor('EZModelBuilding');
                    if (building && building.isEditing === false) {
                        offsetX = building.path.center.x;
                        offsetY = building.path.center.y;
                    }
                }
            }
            path.vertices.forEach(function(location) {
                if (flatten === true) {
                    point = {
                        x: location.xF,
                        y: location.yF
                    };
                } else {
                    point = {
                        x: location.x,
                        y: location.y
                    };
                }
                points.push(point);
            });
        } else {
            path.forEach(function(location) {
                point = {
                    x: location.X || location.x || 0,
                    y: location.Y || location.y || 0
                };
                points.push(point);
            });
        }
        var lineData = points;
        lineData.push(lineData[0]);
    
        var lineFunction = d3.line()
            .x(function(d) {
                return d.x + offsetX;
            })
            .y(function(d) {
                return d.y + offsetY;
            });
    
        var pathGroup = container.append('g');
        pathGroup.append('path')
            .attr('d', lineFunction(lineData));
    
        return pathGroup;
    };
    
    /**
    * Method to parse dilatation lines coords.
    * @param {object} dataset dilatation lines coords.
    *
    * @return {object} lines.
    */
    EZsvgContainer.prototype.parseDilatationLines = function(dataset) {
        const lines = [];
    
        if (dataset.cols.length > 0) {
            dataset.cols.forEach(function(col, i) {
                for (var dlIndex = 1; dlIndex < col.length; dlIndex += 2) {
                    const line = [
                        [col[dlIndex].x, col[dlIndex].y],
                        [col[dlIndex + 1].x, col[dlIndex + 1].y],
                        'vertical',
                        col[0],
                        'dilatationLine-col-' + i,
                    ];
                    lines.push(line);
                }
            });
        }
    
        if (dataset.rows.length > 0) {
            dataset.rows.forEach(function(row, i) {
                for (var dlIndex = 1; dlIndex < row.length; dlIndex += 2) {
                    const line = [
                        [row[dlIndex].x, row[dlIndex].y],
                        [row[dlIndex + 1].x, row[dlIndex + 1].y],
                        'horizontal',
                        row[0],
                        'dilatationLine-row-' + i,
                    ];
                    lines.push(line);
                }
            });
        }
    
        return lines;
    };
    
    /**
     * method to manage location dragging started
     */
    EZsvgContainer.prototype.moduleDragStartedNew = function() {
        var subareaId = this.parentElement.parentElement.parentElement.id.split('_')[2];
    
        if (ez3dScene.projectType === 'simple') {
            subareaId = ez3dScene.activeBuilding.roofs[0].areas[0].subareas[0].id;
        }
    
        var svg = this.parentElement;
        while (svg.constructor.name !== 'SVGSVGElement') {
            svg = svg.parentElement;
        }
        var containerId = svg.parentElement.id.split('-')[1];
        var svgContainer = ez3dViewport.svgElements[containerId];
        if (ez3dScene.active.id === subareaId) {
            if (ez3dScene.mode === 'interactiveMove' && svgContainer.name === 'svgArea') {
                this.move = {
                    x: 0,
                    y: 0
                };
                this.mouse = {
                    x: d3.event.x,
                    y: d3.event.y
                };
            } else {
                svg = this.parentElement;
                while (svg.constructor.name !== 'SVGSVGElement') {
                    svg = svg.parentElement;
                }
                containerId = svg.parentElement.id.split('-')[1];
                ez3dScene.tempSvgContainer = ez3dViewport.svgElements[containerId];
    
                ez3dScene.tempModulesList = [];
                ez3dScene.tempModulesList.push(this.id.split('_')[2]);
    
                ez3dScene.tempModulesType = this.__data__.type;
            }
        }
    };
    
    /**
     * method to manage location dragging while dragging event is still in progress
     */
    EZsvgContainer.prototype.moduleDraggedNew = function() {
        if (ez3dScene.layoutRules.scenePreferences.readOnly !== true) {
            var subareaId = this.parentElement.parentElement.parentElement.id.split('_')[2];
    
            if (ez3dScene.projectType === 'simple') {
                subareaId = ez3dScene.activeBuilding.roofs[0].areas[0].subareas[0].id;
            }
    
            var svg = this.parentElement;
            while (svg.constructor.name !== 'SVGSVGElement') {
                svg = svg.parentElement;
            }
            var containerId = svg.parentElement.id.split('-')[1];
            var svgContainer = ez3dViewport.svgElements[containerId];
    
            if (ez3dScene.active.id === subareaId) {
                if (ez3dScene.mode === 'interactiveMove' && svgContainer.name === 'svgArea') {
                    var limits = ez3dScene.active.system.gridLimits;
    
                    this.move.x += (d3.event.x - this.mouse.x);
                    if (this.move.x < -limits.left) {
                        this.move.x = -limits.left;
                    } else if (this.move.x > limits.right) {
                        this.move.x = limits.right;
                    }
    
                    this.move.y += (d3.event.y - this.mouse.y);
                    if (this.move.y < -limits.up) {
                        this.move.y = -limits.up;
                    } else if (this.move.y > limits.down) {
                        this.move.y = limits.down;
                    }
    
                    var translation = 'translate(' + this.move.x + ', ' + this.move.y + ')';
                    d3.select(this.parentElement)
                        .attr('transform', translation);
                } else {
                    var element = document.elementFromPoint(d3.event.sourceEvent.clientX, d3.event.sourceEvent.clientY);
                    if (element) {
                        var elementId = element.id.split('_')[2];
                        if (elementId && ez3dScene.tempModulesList.indexOf(elementId) === -1) {
                            ez3dScene.tempModulesList.push(elementId);
                        }
    
                        var type = 'module';
                        if (ez3dScene.tempModulesType === 'module') {
                            type = 'empty';
                        }
    
                        d3.select(element)
                            .classed('paint', true);
    
                        if (d3.select(element).classed('ez3d-module')) {
                            d3.select(element)
                                .call(styleModules, svgContainer, type, ez3dScene.active.system.model.colour);
                        }
                    }
                }
            }
        }
    };
    
    /**
     * method to manage location dragging end
     */
    EZsvgContainer.prototype.moduleDragEndedNew = function() {
        if (ez3dScene.layoutRules.scenePreferences.readOnly !== true) {
            var subareaId = this.parentElement.parentElement.parentElement.id.split('_')[2];
    
            if (ez3dScene.projectType === 'simple') {
                subareaId = ez3dScene.activeBuilding.roofs[0].areas[0].subareas[0].id;
            }
    
            var svg = this.parentElement;
            while (svg.constructor.name !== 'SVGSVGElement') {
                svg = svg.parentElement;
            }
            var containerId = svg.parentElement.id.split('-')[1];
            var svgContainer = ez3dViewport.svgElements[containerId];
    
            if (ez3dScene.mode === 'interactiveMove' &&
                ez3dScene.active.constructor.name === 'EZModelSubarea' &&
                ez3dScene.active.id === subareaId && svgContainer.name === 'svgArea') {
                // console.log('drag system end');
                ee.emitEvent('EZModelSubarea_moveSystemListener', [{
                    args: [this.move, undefined, undefined],
                    updateRender: {draw2d: true, draw3d: ez3dScene.active, updatePanels: true, fitRange: false},
                    progress: true
                }]);
                d3.select(this.parentElement)
                    .attr('transform', null);
                delete this.move;
                delete this.mouse;
            } else {
                if ((ez3dScene.active.constructor.name !== 'EZModelSubarea' || ez3dScene.active.id !== subareaId)) {
                    if (ez3dScene.projectType === 'simple') {
                        ee.addOnceListener('setActive_Finished', function () {
                            ee.emitEvent('openSimpleSubareaPanel');
                        });
                    }
                    ee.emitEvent('EZModelScene_setActiveListener', [{args: subareaId, updateRender: false}]);
                } else if (this.__data__.type !== 'collision') {
                    var type = (ez3dScene.tempModulesType === 'empty');
                    var mode = 'one';
                    var indexList = [];
                    var args = {};
                    ez3dScene.tempModulesList.forEach(function (id) {
                        var index = [
                            Number(id.split(':')[0]),
                            Number(id.split(':')[1])
                        ];
                        // Shortcuts
                        if (ez3dScene.layoutRules.scenePreferences.userData.OS === 'MacOS') {
                            args = svgContainer.clickMacEvent(type, mode, index, indexList, id);
                        } else {
                            args = svgContainer.clickEvent(type, mode, index, indexList, id);
                        }
                    });
                    if (ez3dScene.tempModulesList.length > 1) {
                        ee.emitEvent('EZModelSubarea_editSystemListener', [{
                            args: [args.type, args.mode, args.indexList],
                            updateRender: {draw2d: true, draw3d: ez3dScene.active, updatePanels: true, fitRange: false}
                        }]);
                    }
                }
                delete ez3dScene.tempModulesList;
                delete ez3dScene.tempModulesType;
                delete ez3dScene.tempSvgContainer;
            }
        }
    };
    
    EZsvgContainer.prototype.drawRotatedSpace = function() {
        var container = d3.select('#' + this.name + '-elementsGroup');
    
        container.selectAll('.path-label').remove();
    
        // draw rotatedPath
        var rotatedPath = this.drawPath(
            ez3dScene.active.system.rotatedData.rotatedPath,
            container);
    
        rotatedPath.select('path')
            .attr('fill', 'white')
            .attr('fill-opacity', 0.8);
    
        // draw boundingBox
        var boundingBox = this.drawPath(
            ez3dScene.active.system.rotatedData.boundingBox,
            container);
    
        boundingBox.select('path')
            .attr('fill', 'none')
            .attr('stroke-width', 0.03)
            .attr('stroke', 'purple');
    
        // finishingPoint and finishingPointRotated
        var finishingPointRotated = this.drawLocation(
            ez3dScene.active.system.rotatedData.finishingPoint,
            container, undefined, true
        );
    
        finishingPointRotated.select('circle')
            .attr('fill', 'red')
            .attr('r', 0.1);
    
        var finishingPoint = this.drawLocation(
            ez3dScene.active.system.rotatedData.finishingPoint,
            container
        );
    
        finishingPoint.select('circle')
            .attr('fill', 'none')
            .attr('stroke', 'red')
            .attr('stroke-width', 0.05)
            .attr('r', 0.2);
    
        // startingPoint and startingPointRotated
        var startingPointRotated = this.drawLocation(
            ez3dScene.active.system.rotatedData.startingPoint,
            container, undefined, true
        );
    
        startingPointRotated.select('circle')
            .attr('fill', 'green')
            .attr('r', 0.1);
    
        var startingPoint = this.drawLocation(
            ez3dScene.active.system.rotatedData.startingPoint,
            container
        );
    
        startingPoint.select('circle')
            .attr('fill', 'none')
            .attr('stroke', 'green')
            .attr('stroke-width', 0.05)
            .attr('r', 0.2);
    
        // pivotingPoint and pivotingPointRotated
        var pivotingPointRotated = this.drawLocation(
            ez3dScene.active.system.rotatedData.pivotingPoint,
            container, undefined, true
        );
    
        pivotingPointRotated.select('circle')
            .attr('fill', 'blue')
            .attr('r', 0.1);
    
        var pivotingPoint = this.drawLocation(
            ez3dScene.active.system.rotatedData.pivotingPoint,
            container
        );
    
        pivotingPoint.select('circle')
            .attr('fill', 'none')
            .attr('stroke', 'blue')
            .attr('stroke-width', 0.05)
            .attr('r', 0.2);
    
        // gridOffsetRotated
        var gridOffsetRotated = this.drawLocation(
            {
                x: ez3dScene.active.system.grid.offset.x + ez3dScene.active.system.grid.boundingBox[0].x,
                y: ez3dScene.active.system.grid.offset.y + ez3dScene.active.system.grid.boundingBox[0].y
            },
            container, undefined, true
        );
    
        gridOffsetRotated.select('circle')
            .attr('fill', 'orange')
            .attr('r', 0.05);
    };
    
    EZsvgContainer.prototype.drawRotatedSpaceInRotatedTime = function(rotatedGroup) {
        var svgContainer = d3.select('#' + this.name + '-elementsGroup');
        svgContainer.selectAll('.EZModelArea').remove();
    
        rotatedGroup
            .selectAll('*').remove();
    
        // draw rotatedPath
        var rotatedPath = this.drawPath(
            ez3dScene.active.system.rotatedPath,
            svgContainer);
    
        rotatedPath.select('path')
            .attr('fill', 'white')
            .attr('fill-opacity', 0.8);
    
        // draw boundingBox
        var boundingBox = this.drawPath(
            ez3dScene.active.system.boundingBox,
            svgContainer);
    
        boundingBox.select('path')
            .attr('fill', 'none')
            .attr('stroke-width', 0.03)
            .attr('stroke', 'purple');
    
        // gridOffsetRotated
        var gridOffsetRotated = this.drawLocation(
            {
                x: ez3dScene.active.system.grid.offset.x + ez3dScene.active.system.boundingBox[0].x,
                y: ez3dScene.active.system.grid.offset.y + ez3dScene.active.system.boundingBox[0].y
            },
            rotatedGroup
        );
    
        gridOffsetRotated.select('circle')
            .attr('class', 'gridOffsetRotated')
            .attr('fill', 'orange')
            .attr('r', 0.05);
    
        // startingPoint
        var startingPoint = this.drawLocation(
            ez3dScene.active.system.startingPoint,
            rotatedGroup
            // , undefined, true
        );
    
        startingPoint.select('circle')
            .attr('class', 'startingPoint')
            .attr('fill', 'green')
            .attr('r', 0.1);
    
        // pivotingPoint and pivotingPointRotated
        var pivotingPointRotated = this.drawLocation(
            ez3dScene.active.system.pivotingPoint,
            rotatedGroup
        );
    
        pivotingPointRotated.select('circle')
            .attr('class', 'pivotingPointRotated')
            .attr('fill', 'yellow')
            .attr('r', 0.1);
    
        var pivotingPoint = this.drawLocation(
            ez3dScene.active.system.pivotingPoint,
            rotatedGroup, undefined, true
        );
    
        pivotingPoint.select('circle')
            .attr('class', 'pivotingPoint')
            .attr('fill', 'none')
            .attr('stroke', 'yellow')
            .attr('stroke-width', 0.05)
            .attr('r', 0.2);
    
        if (ez3dScene.active.system.activePoint) {
            var activePoint = this.drawLocation(
                ez3dScene.active.system.activePoint,
                rotatedGroup
            );
            activePoint.select('circle')
                .attr('class', 'activePoint')
                .attr('fill', 'none')
                .attr('stroke', 'cyan')
                .attr('stroke-width', 0.05)
                .attr('r', 0.2);
        }
    };
    
    EZsvgContainer.prototype.drawFinishingPointInRotatedTime = function(container) {
        var svgContainer = d3.select('#' + this.name + '-elementsGroup');
    
        svgContainer.selectAll('.EZModelArea').remove();
    
        // finishingPoint and finishingPointRotated
        var finishingPointRotated = this.drawLocation(
            ez3dScene.active.system.finishingPoint,
            container
        );
    
        finishingPointRotated.select('circle')
            .attr('class', 'finishingPointRotated')
            .attr('fill', 'red')
            .attr('r', 0.1);
    };
    
    EZsvgContainer.prototype.drawRow = function(row, container, offset) {
        var self = this;
        var offset = (offset) ? offset : {x: 0, y: 0};
        row.modules.forEach(function(module) {
            if (module.w) {
                container.append('rect')
                    .attr('x', module.x + offset.x)
                    .attr('y', module.y - (module.h || 0.5) + offset.y)
                    .attr('width', module.w || 1)
                    .attr('height', module.h || 0.5)
                    .attr('fill-opacity', function(d) {
                        var value = 0.8;
                        if (module.type === 'empty') {
                            value = 0.2;
                        }
                        return value;
                    })
                    .attr('fill', 'blue')
                    .attr('stroke', 'white')
                    .attr('stroke-width', 0.02);
            } else {
                var path = self.drawPath(module.box, container);
                path
                    .attr('fill-opacity', function(d) {
                        var value = 0.8;
                        if (module.type === 'empty') {
                            value = 0.2;
                        }
                        return value;
                    })
                    .attr('fill', 'blue')
                    .attr('stroke', 'white')
                    .attr('stroke-width', 0.02);
            }
        });
    };
    
    /**
     * Function to select/deselect module or modules.
     * @param {boolean} type    Has been activated/deactivated.
     * @param {string} mode     Clicked mode ("one", "row", "col", etc)
     * @param {array} i         Index of module ([row, col])
     * @param {array} indexList Index of module ([row, col])
     * @param {string} id       Module row and col ("row:col")
     * @return {object}         Type, mode and indexList
     */
    EZsvgContainer.prototype.clickEvent = function(type, mode, i, indexList, id) {
        if (ez3dScene.layoutRules.scenePreferences.readOnly !== true) {
            var index = i;
            if (d3.event.sourceEvent.shiftKey) {
                if (d3.event.sourceEvent.ctrlKey) {
                    // Activate/Deactivate all modules
                    mode = undefined;
                    index = undefined;
                } else {
                    // Activate/Deactivate row with SHIFT
                    mode = 'row';
                    index = Number(id.split(':')[0]);
                }
            } else if (d3.event.sourceEvent.ctrlKey) {
                // Activate/Deactivate col with CTRL
                mode = 'col';
                index = Number(id.split(':')[1]);
            }
    
            // Activate or deactivate module or modules
            if (ez3dScene.tempModulesList.length > 1) {
                indexList.push(index);
            } else {
                ee.emitEvent('EZModelSubarea_editSystemListener', [{
                    args: [type, mode, index],
                    updateRender: {draw2d: true, draw3d: ez3dScene.active, updatePanels: true, fitRange: false}
                }]);
            }
            return {type: type, mode: mode, indexList: indexList};
        }
    };
    
    /**
     * Function to select/deselect module or modules.
     * @param {boolean} type    Has been activated/deactivated.
     * @param {string} mode     Clicked mode ("one", "row", "col", etc)
     * @param {array} i         Index of module ([row, col])
     * @param {array} indexList Index of module ([row, col])
     * @param {string} id       Module row and col ("row:col")
     * @return {object}         Type, mode and indexList
     */
    EZsvgContainer.prototype.clickMacEvent = function(type, mode, i, indexList, id) {
        if (ez3dScene.layoutRules.scenePreferences.readOnly !== true) {
            // Check Command key pressed in MacOS and disable Ctrl key
            var index = i;
            if (d3.event.sourceEvent.shiftKey) {
                if (d3.event.sourceEvent.metaKey) {
                    // console.log('Activar/Desactivar todos los módulos');
                    mode = undefined;
                    index = undefined;
                } else {
                    // console.log('Activar/Desactivar hilera con SHIFT');
                    mode = 'row';
                    index = Number(id.split(':')[0]);
                }
            } else if (d3.event.sourceEvent.ctrlKey) {
                // muestra el contexPanel
            } else if (d3.event.sourceEvent.metaKey) {
                // console.log('Activar/Desactivar hilera con COMMAND');
                mode = 'col';
                index = Number(id.split(':')[1]);
            }
    
            // Activate or deactivate module or modules
            if (d3.event.sourceEvent.ctrlKey === false) {
                if (ez3dScene.tempModulesList.length > 1) {
                    indexList.push(index);
                } else {
                    ee.emitEvent('EZModelSubarea_editSystemListener', [{
                        args: [type, mode, index],
                        updateRender: {draw2d: true, draw3d: ez3dScene.active, updatePanels: true, fitRange: false}
                    }]);
                }
            }
            return {type: type, mode: mode, indexList: indexList};
        }
    };
    
    /**
     * Function to select row.
     * @param {object} module   Hovered module properties.
     */
    EZsvgContainer.prototype.shiftMouseOverEventNew = function(module) {
        // Select row
        var self = this;
        const row = module.id.split(/[_:]/)[2];
        d3.select(module.parentElement).selectAll('rect.ez3d-module')
            .filter((d) => d.id.split(/[_:]/)[1] === row && d.type !== 'collision')
            .each(function(d) {
                d3.select(this)
                    .call(styleModules, self, d.type + 'Hover', ez3dScene.active.system.model.colour);
            });
    };
    
    /**
     * Function to select column.
     * @param {object} module   Hovered module properties.
     */
    EZsvgContainer.prototype.ctrlCmdMouseOverEventNew = function(module) {
        // Select column
        const self = this;
        const col = module.id.split(/[_:]/)[3];
        d3.select(module.parentElement).selectAll('rect.ez3d-module')
            .filter((d) => d.id.split(/[_:]/)[2] === col && d.type !== 'collision')
            .each(function(d) {
                d3.select(this)
                    .call(styleModules, self, d.type + 'Hover', ez3dScene.active.system.model.colour);
            });
    };
    
        EZsvgContainer.prototype.removeVoronoiDiagram = function() {
        // console.log('remove voronoi Function');
        var voronoiGroup = d3.select('#ez3d-path-editor-voronoi-group');
        voronoiGroup.selectAll('*').remove();
    };
    
    EZsvgContainer.prototype.calculateGuideLines = function() {
        var self = this;
        this.voronoiLimits = this.limits;
    
        var parseObjectArrayToArray = function(array) {
            var newArray = [];
            array.forEach(function(object) {
                newArray.push([
                    Number(object.x),
                    Number(object.y)
                ]);
            });
            newArray = _.uniqWith(newArray, _.isEqual);
            return newArray;
        }
    
        var parseArrayToArrayOfObjects = function(array) {
            var newArray = [];
            array.forEach(function(element) {
                newArray.push({
                    x: element[0],
                    y: element[1]
                });
            });
            return newArray;
        }
    
        // calculate guideLines
        var guideLines = [];
        if (ez3dScene.tempPath.vertices.length > 1) {
            guideLines = ez3dScene.utils.createGuideLinesCollectionNew(ez3dScene.tempPath.vertices, this.voronoiLimits);
        }
    
        // guideLinePoints is used to calculate intersections, to do this, null elements are removed.
        var guideLinePoints = [];
        guideLines.forEach(function(point) {
            if (point !== null) {
                guideLinePoints.push(point);
            }
        });
    
        self.guideLinesIntersections = [];
        if (ez3dScene.buildings.length === 0) {
            self.totalIntersections = ez3dScene.utils.bentleyOttmannIntersection(guideLinePoints);
        }
        // self.GuidesAndEdgesIntersections = self.totalIntersections;
    
        // calculate intersections
        var buildingIntersections = {};
        ez3dScene.buildings.forEach(function(building) {
            if (building.isCreated === true) {
                var isInLimits = ez3dScene.utils.buildingIsInLimits(building, self.limits)
                // console.log(building.name, isInLimits);
                if (building.isEditing === false && isInLimits) {
                    var center = building.path.center;
                    var offsetX = center.x;
                    var offsetY = center.y;
                    var vertices = building.path.vertices;
                    var point1;
                    var point2;
    
                    var setPar = [];
                    var setImpar = [];
                    var setUltimo = [];
    
                    var intersectionsA = [];
                    var intersectionsB = [];
                    var intersectionsC = [];
    
                    for (var i = 0; i < vertices.length; i++) {
                        point1 = [vertices[i].x + offsetX, vertices[i].y + offsetY];
    
                        if ((i === vertices.length - 1) && vertices.length % 2 === 1) {
                            setUltimo.push(point1);
                        } else {
                            setImpar.push(point1);
                        }
    
                        if (i < vertices.length - 1) {
                            point2 = [vertices[i + 1].x + offsetX, vertices[i + 1].y + offsetY];
                        } else {
                            point2 = [vertices[0].x + offsetX, vertices[0].y + offsetY];
                        }
    
                        if ((i === vertices.length - 1) && vertices.length % 2 === 1) {
                            setUltimo.push(point2);
                        } else {
                            setPar.push(point2);
                        }
                    }
    
                    intersectionsA = ez3dScene.utils.bentleyOttmannIntersection(guideLinePoints.concat(setPar));
                    intersectionsB = ez3dScene.utils.bentleyOttmannIntersection(guideLinePoints.concat(setImpar));
                    intersectionsC = ez3dScene.utils.bentleyOttmannIntersection(guideLinePoints.concat(setUltimo));
    
                    buildingIntersections[building.name] = parseObjectArrayToArray(
                        _.union(self.totalIntersections, intersectionsA, intersectionsB, intersectionsC)
                    );
                }
            }
        });
    
        self.buildingIntersections = [];
        Object.keys(buildingIntersections).forEach(function(Key) {
            buildingIntersections[Key].forEach(function(point) {
                self.buildingIntersections.push(point);
            })
        });
        self.buildingIntersections = parseArrayToArrayOfObjects(_.uniqWith(self.buildingIntersections, _.isEqual));
    
        // self.guideLinesIntersections = self.totalIntersections;
        self.guideLinesIntersections = self.buildingIntersections;
    
        return guideLines;
    };
    
    EZsvgContainer.prototype.calculateBorderGuideLines = function() {
        var guideLines = [];
        var vertices = ez3dScene.active.path.vertices;
        var point1;
        var point2;
    
        var offsetX = 0;
        var offsetY = 0;
        if (ez3dScene.active.path.coordSystem === 'building') {
            var center = ez3dScene.active.path.center;
            offsetX = center.x;
            offsetY = center.y;
        }
    
        for (var i = 0; i < vertices.length; i++) {
            point1 = [vertices[i].x + offsetX, vertices[i].y + offsetY];
            if (i < vertices.length - 1) {
                point2 = [vertices[i + 1].x + offsetX, vertices[i + 1].y + offsetY];
            } else {
                point2 = [vertices[0].x + offsetX, vertices[0].y + offsetY];
            }
            guideLines.push(point1);
            guideLines.push(point2);
            guideLines.push(null);
        }
    
        this.voronoiLimits = this.limits;
    
        var guideLinePoints = [];
        guideLines.forEach(function(point) {
            if (point !== null) {
                guideLinePoints.push(point);
            }
        });
        this.guideLinesIntersections = ez3dScene.utils.bentleyOttmannIntersection(guideLinePoints);
    
        return guideLines;
    };
    
    EZsvgContainer.prototype.drawGuideLines = function(guideLines) {
        var voronoiGroup = d3.select('#ez3d-path-editor-voronoi-group');
    
        var strokeScale = d3.scalePow()
            .exponent(0.1)
            .domain([10, 25, 200])
            .range([0.5, 0.2, 0.05]);
    
        var dasharrayScale = d3.scalePow()
            .exponent(0.1)
            .domain([10, 25, 200])
            .range([0.7, 0.5, 0.06]);
    
        var line = d3.line()
            .defined(function(d) {
                return d;
            })
            .curve(d3.curveLinear);
    
        var guideLinesPath = voronoiGroup.append('path')
            .datum(guideLines)
            .attr('class', 'ez3d-line')
            .classed('guidelines', true)
            .attr('d', line)
            .attr('stroke-width', function() {
                var value = strokeScale(ez3dViewport.svgElements.svgProject.zoomValue) * 0.35;
                return value;
            })
            .attr('stroke-dasharray', function() {
                var value = dasharrayScale(ez3dViewport.svgElements.svgProject.zoomValue);
                return value;
            })
            .style('pointer-events', 'none');
    
        return guideLinesPath;
    };
    
    EZsvgContainer.prototype.drawVoronoiDiagram = function() {
        // console.log('draw voronoi Function');
        var self = this;
        var voronoiGroup = d3.select('#ez3d-path-editor-voronoi-group');
        voronoiGroup.selectAll('*').remove();
        self.precision = ez3dScene.utils.voronoiPrecisionScale(ez3dViewport.svgElements.svgProject.zoomValue);
    
        self.voronoiGuideLines();
        self.voronoiSamples();
    
        if ((self.guideLines && self.guideLines.length > 0) ||
            ez3dScene.layoutRules.scenePreferences.snapToVertices === true) {
            // draw guidelines and voronoi polygons
            var x = d3.scalePow()
                .exponent(3);
    
            var voronoi = d3.voronoi()
                .extent([[self.voronoiLimits[0], self.voronoiLimits[1]], [self.voronoiLimits[2], self.voronoiLimits[3]]]);
    
            var polygon = voronoiGroup.append('g')
                .attr('class', 'ez3d-polygons');
    
            var dataset = voronoi.polygons(self.sampledPoints);
    
            var polygons = polygon.selectAll('g')
                .data(dataset)
                .enter().append('g')
                .classed('ez3d-polygon', true);
    
            polygons.append('path')
                .attr('d', function(d) {
                    return d ? 'M' + d.join('L') + 'Z' : null;
                })
                .on('click', function() {
                    if (d3.select('#ez3d-context-panel-container').style('display') === 'flex') {
                        d3.select('#ez3d-context-panel-container').style('display', 'none');
                    } else if (ez3dScene.context === 'pathEditor' && (!ez3dScene.tempPath || !ez3dScene.tempPath.locked)) {
                        if (ez3dScene.tempPath.estimatedPoint === true) {
                            ez3dScene.tempPath.removeEstimatedFourthPoint();
                        }
                        var point = {x: this.parentElement.children[1].cx.baseVal.value, y: this.parentElement.children[1].cy.baseVal.value};
                        var oldPoint = {x: point.x, y: point.y};
                        point = ez3dScene.utils.translateSphericalByCartesianOffset(ez3dScene.projectCenter, point);
    
                        ee.addOnceListener('addLocation_Finished', function() {
                            ez3dScene.tempPath.updateIndexes();
                            var index = self.edgeClick ? self.edgeClick : ez3dScene.tempPath.vertices.length - 1;
                            ez3dScene.tempPath.setActiveVertex(index);
                            ez3dScene.tempPath.calculateEstimatedFourthPoint();
                            if (self.edgeClick) {
                                delete self.edgeClick;
                            }
                        });
    
                        ee.emitEvent('EZModelScene_addLocationListener', [{
                            args: [point, self.edgeClick, undefined, oldPoint],
                            undo: true,
                            updateRender: {draw2d: true, draw3d: false, updatePanels: false, fitRange: false},
                            notifications: false
                        }]);
                    }
                })
                .on('mouseover', function() {
                    ez3dViewport.svgElements.svgProject.voronoi = {
                        x: this.parentElement.children[1].cx.baseVal.value,
                        y: this.parentElement.children[1].cy.baseVal.value
                    };
                })
                .style('stroke-width', function() {
                    var value = ez3dScene.utils.voronoiStrokeScale(ez3dViewport.svgElements.svgProject.zoomValue);
                    return value;
                });
    
            var circleRadiusScale = d3.scalePow().exponent(0.2)
                .domain([0.25, 75, 200])
                .range([0.2, 0.035, 1 / 75]);
    
            polygons.append('circle')
                .attr('r', function() {
                    var value = circleRadiusScale(ez3dViewport.svgElements.svgProject.zoomValue) * 3;
                    return value;
                })
                .attr('cx', function(d) {
                    return d ? d.data[0] : null;
                })
                .attr('cy', function(d) {
                    return d ? d.data[1] : null;
                })
                .style('pointer-events', 'none')
                .style('fill', 'red')
                .style('stroke', 'white')
                .style('stroke-width', function() {
                    var value = circleRadiusScale(ez3dViewport.svgElements.svgProject.zoomValue);
                    return value;
                });
        }
    
        ee.emitEvent('voronoi_Finished');
    };
    
    EZsvgContainer.prototype.updateVoronoi = function(forceUpdate) {
        var self = this;
        if (ez3dScene.context === 'pathEditor') {
            // console.log('update voronoi Function');
            if (ez3dScene.layoutRules.scenePreferences.snapToGuides === true &&
                (
                    (ez3dScene.tempPath && ez3dScene.tempPath.vertices.length > 1) ||
                    ez3dScene.tempPath && ez3dScene.layoutRules.scenePreferences.snapToVertices === true
                )
            ) {
                // only recalc voronoi if viewport limits changed or if forceUpdate
                if ((_.difference(self.voronoiLimits, self.limits).length !== 0) || forceUpdate) {
                    var redrawVoronoi = _.debounce(function() {
                        ez3dViewport.ez3dGuiManager.notificationManager(['calculatingGuidelines', {style: 'warning', spiner: true, event: 'voronoi_Finished'}]);
                        self.drawVoronoiDiagram();
                    }, 500);
                    redrawVoronoi();
                }
            }
        }
    };
    
        /**
     * method to draw the SLD scene in viewport
     */
    
    EZsvgContainer.prototype.drawProjectSLD = function(/*json info prob here*/) {
        const setOfData = this._testDataCreation02();
        this.drawSLDScene(setOfData);
    };
    
    EZsvgContainer.prototype._testDataCreation01 = function() {
        let arrayOfTypes = [
            'Grid', 'Service Panel', 'AC Disconnect',
            'Interconnect', 'Inverter', 'Disconnect',
            'Combiner', 'Modules...'];
        let arrayOfCaptions = ['An optional caption here', '', 'Sunny Tripower 2400TL-US', '3-circuit ....'];
        // Snippet for a datum for our tests
    
        const column = 3;
        const testData = new Array(column);
        for (let row = 0; row < column; row++) {
            let numOfCells = Math.floor(Math.random() * 5) + 1;
            let typeForColumn = _.sample(arrayOfTypes); // === arrayOfTypes[Math.floor(Math.random() * arrayOfTypes.length)];
            testData[row] = new Array(numOfCells);
            for (let cell = 0; cell < numOfCells; cell++) {
                //Example of placeholder data
                const datum =
                {
                    'type': 'TypeName',
                    'caption': 'The caption',
                    'input': 4,
                    'output': 5,
                    'children': [],
                    'height': 10,
                    'width': 20,
                    'color': 'blue'
                };
                datum.type = typeForColumn;
                datum.caption = _.sample(arrayOfCaptions);
                datum.input = _.random(1, 12);
                datum.output = _.random(1, 4);
                testData[row][cell] = datum;
            }
        }
        console.log(testData);
        return testData;
    };
    
    EZsvgContainer.prototype._testDataCreation02 = function() {
        const maxColumns = 7;
        const cellsPerColumn = [4, 4, 4, 2, 2, 1, 1]; // 'right to left' order
        let totalCells = 0;
        for (const value of cellsPerColumn) {
            totalCells += value;
        }
        // ///////////////////////////// //
        // //// Generate Test Nodes //// //
        // ///////////////////////////// //
        const arrayOfTypes = ['Grid', 'Interconnect', 'Inverter', 'Disconnect',
        'Combiner', 'Combiner', 'Modules...'];
        arrayOfTypes.reverse(); // change to 'right to left' order
        const arrayOfCaptions = ['An optional caption here', '',
        'Sunny Tripower 2400TL-US', '3-circuit ....'];
        const diagramData = new Array(maxColumns);
        // Populate the final array with the nodes
        for (let column = 0, cellID = 0; column < maxColumns; column++) {
            diagramData[column] = new Array(cellsPerColumn);
            for (let row = 0; row < cellsPerColumn[column]; row++, cellID++) {
                const sldnode = new SLDNode(cellID, row, column, arrayOfTypes[column], _.sample(arrayOfCaptions));
                //sldnode.parent = linksToParents[cellID];
                //sldnode.children = linksToChildren[cellID];
                diagramData[column][row] = sldnode; // Save in the array of data the fabricated datum
            }
        }
    
        // ///////////////////////////// //
        // //// Generate Test Links //// //
        // ///////////////////////////// //
        const linksToParents = [];
        const linksToChildren = [new Array(totalCells)];
        let thelink;
        let parentRow;
        let kids;
        for (let cellID = 0, column = 0; column < maxColumns; column++) {
            for (let row = 0; row < cellsPerColumn[column]; row++, cellID++) {
                switch (column) {
                    case 0:
                        // Children links
                        linksToChildren[cellID] = null;
    
                        // Parent links
                        thelink = new SLDLink(diagramData[column + 1][row], 2);
                        linksToParents.push(thelink);
                        break;
                    case 1:
                        // Children links
                        linksToChildren[cellID] = []; // Important!!
                        thelink = new SLDLink(diagramData[column - 1][row], 2);
                        linksToChildren[cellID].push(thelink);
    
                        // Parent links
                        thelink = new SLDLink(diagramData[column + 1][row]);
                        linksToParents.push(thelink);
                        break;
                    case 2:
                        // Children links
                        linksToChildren[cellID] = []; // Important!!
                        thelink = new SLDLink(diagramData[column - 1][row]);
                        linksToChildren[cellID].push(thelink);
    
                        // Parent links
                        parentRow = (row < 2) ? 0 : 1;
                        //thelink = new SLDLink({x: column + 1, y: parentRow});
                        thelink = new SLDLink(diagramData[column + 1][parentRow]);
                        linksToParents.push(thelink);
                        break;
                    case 3:
                        // Children links
                        linksToChildren[cellID] = []; // Important!!
                        kids = (row < 1) ? 0 : 2;
                        //thelink = new SLDLink({x: column - 1, y: kids});
                        thelink = new SLDLink(diagramData[column - 1][kids]);
                        linksToChildren[cellID].push(thelink);
                        //thelink = new SLDLink({x: column - 1, y: ++kids});
                        thelink = new SLDLink(diagramData[column - 1][++kids]);
                        linksToChildren[cellID].push(thelink);
    
                        // Parent links
                        //thelink = new SLDLink({x: column + 1, y: row}, 2);
                        thelink = new SLDLink(diagramData[column + 1][row], 2);
                        linksToParents.push(thelink);
                        break;
                    case 4:
                        // Children links
                        linksToChildren[cellID] = []; // Important!!
                        //thelink = new SLDLink({x: column - 1, y: row}, 2);
                        thelink = new SLDLink(diagramData[column - 1][row], 2);
                        linksToChildren[cellID].push(thelink);
    
                        // Parent links
                        //thelink = new SLDLink({x: column + 1, y: 0});
                        thelink = new SLDLink(diagramData[column + 1][0]);
                        linksToParents.push(thelink);
                        break;
                    case 5:
                        // Children links
                        linksToChildren[cellID] = []; // Important!!
                        // thelink = new SLDLink({x: column - 1, y: 0});
                        thelink = new SLDLink(diagramData[column - 1][0]);
                        linksToChildren[cellID].push(thelink);
                        // thelink = new SLDLink({x: column - 1, y: 1});
                        thelink = new SLDLink(diagramData[column - 1][1]);
                        linksToChildren[cellID].push(thelink);
    
                        // Parent links
                        //thelink = new SLDLink({x: column + 1, y: 0});
                        thelink = new SLDLink(diagramData[column + 1][0]);
                        linksToParents.push(thelink);
                        break;
                    case 6:
                        // Children links
                        linksToChildren[cellID] = []; // Important!!
                        //thelink = new SLDLink({x: column - 1, y: 0});
                        thelink = new SLDLink(diagramData[column - 1][0]);
                        linksToChildren[cellID].push(thelink);
    
                        // Parent links
                        linksToParents.push(null); // before was linksToParents[6] = {}
                        break;
                }
                diagramData[column][row].parent = linksToParents[cellID];
                diagramData[column][row].children = linksToChildren[cellID];
            }
        }
    
        console.log(diagramData);
        return diagramData;
    };
    
        EZsvgContainer.prototype.drawSLDScene = function (setOfData) {
        // Create tooltip container
        const sldDivContainer = d3.select('#' + this.parentId); // === #ez3d-viewport-right-content
    
        const tooltipNode = sldDivContainer
          .append('div')
            .attr('id', 'tooltip')
            .style('position', 'fixed')
            .style('background-color', 'lightgray')
            .style('pointer-events', 'none')
            .style('display', 'none');
    
        // Create the 'bigger picture' layout in DOM
    
        // Lets bind data (without joining) to the root selection of our diagram:
        const sldRoot = d3.select('#' + this.name + '-elementsGroup')
          .append('g')
            .attr('id', 'SLD')
            .datum(setOfData);
    
        const sldColumn = sldRoot
          .selectAll('g[class^=SLD-column-group-]')   // search for a 'g' with a class that starts with...
          .data(setOfData)
          .enter()
          .append('g')
            .attr('class', (d, i) => 'SLD-column-group-' + i)
            .attr('data-column-index', (d, i) => i); // make parent index available from DOM
    
        const sldNode = sldColumn
          .selectAll('g.SLD-element')
          .data((d) => d)
          .enter()
          .append('g') // let's group each sldElement for layout purpuses
            .attr('class', 'SLD-element')
            .each((d, i, nodes) => {
                const j = +nodes[i].parentNode.getAttribute('data-column-index'); // get column-index from DOM and convert it to number
                const nodeGroup = d3.select(nodes[i]);
    
                // lines
                nodeGroup.call(this.drawLinksToParent.bind(this), sldRoot, d, 0.02, 'black');
    
                // rect
                const rectElement = nodeGroup.append('rect').style('pointer-events', 'visible');
                this.drawSLDElement(rectElement, this.indexToSVGCoord(j), this.indexToSVGCoord(i), d.width, d.height, 0, 0);
                this.styleSLDElement(rectElement, '#000000', 0.05, 'none');     // JFY, ezzing green color: #7aad37
    
                // node name text
                const textElement = nodeGroup.append('text')
                  .call(this.setNameSLDElement, d.type, this.indexToSVGCoord(j, {offset: d.width / 2}), this.indexToSVGCoord(i, {offset: d.height / 2}), '0.15em', 'middle', 'central');
    
                // node captions
                const captionElement = nodeGroup.append('text')
                  .call(this.setCaptionSLDElement, d.caption, this.indexToSVGCoord(j, {offset: d.width / 2}), this.indexToSVGCoord(i, {offset: d.height + 2}), '0.10em', 'middle', 'ideographic');
                  //.call(this.WrapSVGText, d.width, d.caption);
    
                // bind tooltip
                nodeGroup.call(this.showSLDTooltip, tooltipNode, d.type, 8, 16);
            });
    };
    
    /**
     * How to draw each element in the SLD diagram
     */
    EZsvgContainer.prototype.drawSLDElement = function(selection, xPos, yPos, width, height, xRadius, yRadius) {
        selection
            .attr('x', xPos)
            .attr('y', yPos)
            .attr('width', width)
            .attr('height', height)
            .attr('rx', xRadius)
            .attr('ry', yRadius);
    };
    
    /**
     * Style the current element
     * 
     * @param {selection} selection - D3.js selection
     * @param {import("d3").Color} strokeColor - svg styling: stroke color
     * @param {number} strokeWidth - svg styling: the stroke width
     * @param {import("d3").Color} fillColor - svg styling: the fill color
     * @returns {undefined}
     */
    EZsvgContainer.prototype.styleSLDElement = function(selection, strokeColor, strokeWidth, fillColor) {
        strokeWidth = +strokeWidth + '%';
        strokeColor = strokeColor || 'none';
        fillColor = fillColor || 'none';
        selection
            .style('fill', fillColor)
            .style('stroke', strokeColor)
            .style('stroke-width', strokeWidth);
    };
    
    EZsvgContainer.prototype.setNameSLDElement = function(selection, text, xPos, yPos, fontSize, anchor, vertAlign) {
        selection
            .style('font-size', fontSize)
            .attr('alignment-baseline', vertAlign)
            .attr('text-anchor', anchor)
            .attr('x', xPos)
            .attr('y', yPos)
            .text(text);
    };
    
    EZsvgContainer.prototype.setCaptionSLDElement = function(selection, text, xPos, yPos, fontSize, anchor, vertAlign) {
        selection
            .style('font-size', fontSize)
            .attr('text-anchor', anchor)
            .attr('alignment-baseline', vertAlign)
            .attr('x', xPos)
            .attr('y', yPos)
            .text(text);
    };
    
    EZsvgContainer.prototype.showSLDTooltip = function(selection, tooltipNode, textTip, xOffset, yOffset) {
        selection
            .on('mousedown', () => {
                return tooltipNode.style('display', 'none');
            })
            .on('mouseenter', (d, i, nodes) => {
                return tooltipNode
                    .text(textTip)
                    .style('display', 'inline');
            }).on('mousemove', () => {
                return tooltipNode
                    .style('left', (d3.event.pageX + xOffset) + 'px')
                    .style('top', (d3.event.pageY + yOffset) + 'px');
            }).on('mouseleave', () => {
                return tooltipNode.style('display', 'none');
            });
    };
    
    EZsvgContainer.prototype.drawLinksToParent = function(selection, appendTo, datum, strokeWidth, strokeColor, rightToLeft = true) {
        strokeWidth = +strokeWidth + '%';
        if (datum.parentLinks === null) return;
        const divisions = datum.height / (datum.parentLinks.length);
        let hello = selection
            .selectAll('polyline')
            .data((d, i, nodes) => {
                return d.parentLinks; // TODO: check if custom data function is required
            }).enter()
            .append('polyline')
            .style('stroke-width', strokeWidth)
            .style('stroke', strokeColor)
            .style('fill', 'none')
            .each((d, i, nodes) => {
                const nodeGroup = d3.select(nodes[i]);
                const heightRedress = (divisions / 2) + (divisions * i);
                nodeGroup.attr('points', () => {
                    const svgX0 = (rightToLeft) ? this.indexToSVGCoord(d.source.x, {offset: datum.width}) : this.indexToSVGCoord(d.source.x);
                    const svgX1 = (rightToLeft) ? this.indexToSVGCoord(d.target.x) : this.indexToSVGCoord(d.target.x, {offset: datum.width});
                    const svgY0 = this.indexToSVGCoord(d.source.y, {offset: heightRedress});
                    const svgY1 = this.indexToSVGCoord(d.target.y, {offset: heightRedress});
                    return this.calcMidPointStep(svgX0, svgX1, svgY0, svgY1);
                });
                console.log(nodes[i]);
            });
    };
    /**
     * Convert any array index in the data array to SVG coordinates in a that axis
     *
     * @param {Number} index  This is the column index
     * @param {object} options  {gap, offset} gap is the spacing left in that axis, offset is to align the position in that axis (both optional)
     *
     * @returns {Number} The position in that axis
     */
    EZsvgContainer.prototype.indexToSVGCoord = function(index, {gap = 20, offset = 0} = {}) {
        return (index * gap) + (offset);
    };
    
    EZsvgContainer.prototype.calcMidPointStep = function(xSrc, xTrgt, ySrc, yTrgt) {
        const xMidPoint = (xSrc + xTrgt) / 2;
        if (yTrgt === ySrc) {
            return [xSrc, ySrc] + ' ' + [xTrgt, yTrgt];
        }
        return [xSrc, ySrc] + ' ' + [xMidPoint, ySrc] + ' ' + [xMidPoint, yTrgt] + ' ' + [xTrgt, yTrgt];
    };
    
    
    EZsvgContainer.prototype.wrapSVGText = function(text, maxWidth, content) {
        // text is like my selection in other functions 
        // expected to be called with d3.selection.call()
        debugger;
        text.each(function (d, i, nodes) {
            debugger;
            var text = d3.select(nodes[i]);
            var words = content.split(/\s+/).reverse();
            var word;
            var line = [];
            var lineNumber = 0;
            var lineHeight = 1; // ems
            var x = text.attr("x");
            var y = text.attr("y");
            var dy = 0; //parseFloat(text.attr("dy")),
            var tspan = text.text(null)
              .append("tspan")
                .attr("x", x)
                .attr("y", y)
                .attr("dy", dy + "em");
            while (word = words.pop()) {
                line.push(word);
                tspan.text(line.join(''));
                if (tspan.node().getComputedTextLength() > width) {
                    line.pop();
                    tspan.text(line.join(" "));
                    line = [word];
                    tspan = text.append("tspan")
                                .attr("x", x)
                                .attr("y", y)
                                .attr("dy", ++lineNumber * lineHeight + dy + "em")
                                .text(word);
                }
            }
        });
    };
    
        /**
     * SLDLink is an implementation of a link in the SLD multigraph. Also known 
     * as edges in graph theory, 'link' nomenclature is chosen over
     * 'edge' to avoid confusion with any 3D model related stuff.
     */
    class SLDLink {
        /**
         * Class constructor for SLDLink
         *
         * @param {object} srcIndexes  The first node in the pair forming the link {x,y}
         * @param {object} trgtIndexes  The last node in the pair forming the link {x,y}
         * @param {Number} count  The number of times the link should be represented
         *
         */
        constructor(srcIndexes, trgtIndexes, count = 1) {
            this.source = {
                x: srcIndexes.x,
                y: srcIndexes.y,
            };
    
            this.target = {
                x: trgtIndexes.x,
                y: trgtIndexes.y,
            };
    
            this.count = count;
        }
    }
    
        /**
     * SLDNode is an implementation of a node in the SLD multigraph. Also
     * known as vertex in graph theory, 'node' nomenclature is chosen over
     * 'vertex' to avoid confusion with any 3D model related stuff.
     */
    class SLDNode {
        /**
         *
         * Class constructor for SLDNode
         *
         * @param {Number} id  [description]
         * @param {Numer} row  [description]
         * @param {Numer} column  [description]
         * @param {String} type  Component type that's being represented
         * @param {String} caption  The caption at underneath the SVG element
         * @param {Number} count  The number of components represented by this SLDNode
         * @param {Array} parentLinks  Array containing the {source:XX,target:YY} pairs to the parent
         * @param {Array} childrenLinks  Array containing the {source:XX,target:YY} pairs to the children
         * @param {SLDNode} parent  The parent object
         * @param {Array} children  The array of references to children SLDNodes
         * @param {Number} height  The height of the SVG image
         * @param {Number} width  The width of the SVG image
         * @param {String} color  The color in string format of the SVG
         */
        constructor(id, row, column, type, caption = 'Example caption...',
            count = 1,
            parentLinks = null,
            childrenLinks = null,
            parent = null,
            children = null,
            height = 8,
            width = 15,
            color = 'black') {
            this.id = id;
            this.row = row;
            this.column = column;
            this.type = type;
            this.caption = caption;
            this.count = count;
            this.height = height;
            this.width = width;
            this.color = color;
            // SLDLinks:
            this.parentLinks = parentLinks;
            this.childrenLinks = childrenLinks;
            // SLDNodes:
            this.parent = parent;
            this.children = children;
        }
        /**
         * Get the type of the SLDNode
         */
        get type() {
            return this._type;
        }
        /**
         * Set the type of the SLDNode
         *
         * @param {String} value  String with the type of the SLDNode
         */
        set type(value) {
            this._type = value;
        }
    
        /**
         * Get array with parentLinks
         */
        get parentLinks() {
            return this._parentLinks;
        }
    
        /**
         * Converts a SLDLink to an array of source-target pairs that can be repeated (multigraph)
         *
         * @param {SLDLink} link  The SLDLink to attach as parent
         */
        set parentLinks(link) {
            if (link instanceof SLDLink) {
                this._parentLinks = [];
                for (let i = 0; i < link.count; i++) {
                    this._parentLinks.push({source: link.source, target: link.target});
                }
            } else {
                this._parentLinks = null;
            }
        }
    }
    
        /**
     * [svgSLDOptions description]
     * @type {[type]}
     */
    EZViewport.prototype.svgSLDOptions = {
        grid: false,
        zoom: true,
        coord: 'scene',
        limits: function() {
            return ez3dScene.utils.calculateProjectRange(ez3dScene);
        },
        zoomed: function zoomed() {
            var that = ez3dViewport.svgElements.svgSLD;
            if (d3.event.sourceEvent &&
                (d3.event.sourceEvent.type === 'mousemove' ||
                d3.event.sourceEvent.type === 'wheel')) {
                that.needsDraw = true;
            }
            if (d3.select(this).attr('width') !== null && d3.select(this).attr('width') !== '0') {
                that.transform = d3.zoomTransform(this);
                that.getCanvasLimits(that.transform);
    
                that.elementsGroup.attr('transform', d3.event.transform);
                that.limitsGroup.attr('transform', d3.event.transform);
                if (that.options && that.options.grid) {
                    if (that.ez3dViewport.ez3dScene.layoutRules.scenePreferences.snapToGrid === false ||
                        that.ez3dViewport.ez3dScene.context !== 'pathEditor') {
                        that.gX.call(that.xAxis.scale(d3.event.transform.rescaleX(that.x)));
                        that.gY.call(that.yAxis.scale(d3.event.transform.rescaleY(that.y)));
                    }
                }
            }
        },
        zoomEnd: function (svgElement) {
            if (d3.select(svgElement).attr('width') !== null && d3.select(svgElement).attr('width') !== '0') {
                var that = ez3dViewport.svgElements.svgSLD;
                if (that.needsDraw === true) {
                    that.draw();
                    that.needsDraw = false;
                }
            }
        },
        draw: function() {
            const svgSLD = ez3dViewport.svgElements.svgSLD;
            //console.log(svgSLD);
            if (svgSLD.svg.select('#SLD').node() === null) {
                this.drawProjectSLD();
            }
            if (!svgSLD.isReady) {
                ee.emitEvent('containerReady');
                svgSLD.isReady = true;
            }
        }
    };
    
        /**
     * EZsvgContainer
     * @class
     * @classdesc D3 SVG canvas class with grid, responsive and zoom capabilities
     *
     * This *object* has some important attributes to reach main elements:
     *
     * * this.sceneGroup: The main svg group to store the project drawing
     * * this.svg: the svg element inside the container
     * * this.info: a div element inside the container to put debugger info
     * * this.svgContainer: d3 selection of the main div element of the container
     *
     * @param {EZViewport} ez3dViewport - description
     * @param {string} name     - the name of the element
     * @param {string} parentId - the ID of the parent where the container will be created
     * @param {object} options  - collection of some different rules to create the svg
     *
     * @param {boolean} options.grid - if the svg should show a grid with labels
     * @param {boolean} options.zoom - if the svg should have zoom behaviour
     * @param {string} options.coord - the coordinate system the svg will be using
     */
    function EZsvgContainer(ez3dViewport, name, parentId, options) {
        var self = this;
        // console.log('create ' + self.name);
    
        self.ez3dViewport = ez3dViewport;
        self.options = options;
        self.parentId = parentId;
        self.name = name;
        self.split = false;
        self.draggingLocation = false;
        self.snapFastToggle = false;
    
        self.svgContainer = self.appendSvgContainer();
        self.container = document.getElementById('svgContainer-' + self.name);
        self.svg = this.svgContainer
            .append('svg')
            .attr('id', name);
    
        if (ez3dScene.layoutRules.scenePreferences.showCoordinates === true) {
            self.appendInfoDiv();
        }
    
        self.appendMainGroups();
        self.appendDefs();
        self.appendEditorGroups();
        ez3dViewport.ez3dGuiManager.generateSpinner(self);
        self.resize();
    
        if (self.options && self.options.grid) {
            self.appendGrid();
        }
    
        if (self.options && self.options.fitRange) {
            self.fitRange = self.options.fitRange;
        }
    
        // @todo remove this when new workflow is complete
        if (self.options && self.options.voronoiGuideLines) {
            self.voronoiGuideLines = self.options.voronoiGuideLines;
            self.voronoiSamples = self.options.voronoiSamples;
        }
        //
    
        if (self.options && self.options.draw) {
            self.draw = self.options.draw;
        }
    
        if (self.options && self.options.editors) {
            self.editors = self.options.editors;
        }
    
        // lodash debounce for zoomend function
        var lazyzoom = _.debounce(function(svgElement) {
            self.options.zoomEnd(svgElement);
        }, 500);
    
        // zoom
        if (self.options && self.options.zoom) {
            self.zoom = d3.zoom()
                .scaleExtent([0.25, 200])
                .on('zoom', self.options.zoomed)
                .on('end', function() {
                    self.transform = d3.event.transform;
                    lazyzoom(this);
                });
            self.svg.call(self.zoom);
        }
    
        // fit range before first drawing
        self.fitRangeToContainer(self.options.limits(), 0);
        self.needsDraw = true;
    
        // todo crea listeners
        if (self.options.listeners) {
            self.options.listeners();
        }
        // @todo sustituir por svgElements si se puede
        self.ez3dViewport.svgContainersList.push(self);
    }
    
    /**
     * remove all elements inside the svg container
     */
    EZsvgContainer.prototype.clear = function() {
        // console.log('clear ' + self.name);
        // clear all except mainGroup groups
        this.svgContainer.selectAll('svg > *')
            .filter(function() {
                return !d3.select(this).classed('mainGroup');
            })
            .remove();
    
        // remove buildings
        this.svgContainer.selectAll('svg .EZModelBuilding').remove();
    };
    
    /**
     * update size of the svg element and redraw grid if needed
     */
    EZsvgContainer.prototype.resize = function() {
        // console.log('resize ' + this.name);
        this.width = ez3dScene.utils.getElementOuterWidth(this.svgContainer.node());
        this.height = ez3dScene.utils.getElementOuterHeight(this.svgContainer.node());
    
        this.svg
            .attr('width', this.width)
            .attr('height', this.height);
    };
    
    /**
     * clear the svg, resize the container and fitRange
     */
    EZsvgContainer.prototype.redraw = function(fitRange = false) {
        var self = this;
        if (ez3dScene.layoutRules.scenePreferences.debugContainers) {
            console.log('░▒▓▒░ redraw ' + self.name + ' fitRange: ' + fitRange);
        }
    
        self.clear();
        self.resize();
        // rasterMap
        if (self.options && self.options.rasterMap === true) {
            self.addRasterMap();
        }
        // grid
        if (self.options && self.options.grid) {
            if (self.options.rasterMap === true) {
                self.addSphericalGrid();
            } else if (ez3dScene.layoutRules.scenePreferences.snapToGrid === false || (ez3dScene.context !== 'pathEditor' && ez3dScene.context !== 'location')) {
                self.addGrid();
            }
        }
        self.elementsGroup.raise();
        self.editorGroup.raise();
        if (fitRange) {
            this.launchFitRange();
        }
    };
    
    EZsvgContainer.prototype.launchFitRange = function () {
        if (this.width !== 0 && this.height !== 0) {
            if (this.fitRange) {
                if (this.limits === undefined) {
                    this.needsDraw = true;
                    this.limits = this.options.limits();
                    this.fitRangeToContainer(self.limits, 0);
                } else {
                    this.fitRange();
                }
            }
        } else {
            ee.emitEvent('show_' + self.name + '_spinner');
        }
    };
    
    EZsvgContainer.prototype.callZoom = function(scale, x, y) {
        this.svg.transition()
            .call(this.zoom.transform, d3.zoomIdentity
                .scale(scale)
                .translate(-x, -y)
            );
    };
    
    
    EZsvgContainer.prototype.getCanvasLimits = function(transform) {
        // console.log('get canvas limit ' +  this.name);
        if (!transform) {
            transform = this.transform;
        }
    
        var coordinates = [
            this.width / 2,
            this.height / 2
        ];
    
        var point = {
            x: (coordinates[0], coordinates[0] - transform.x) / transform.k,
            y: (coordinates[1], coordinates[1] - transform.y) / transform.k
        };
    
        // width in meters for this zoom value
        this.widthInMeters = ((point.x - (0, 0 - transform.x) / transform.k) * 2);
        this.heightInMeters = ((point.y - (0, 0 - transform.y) / transform.k) * 2);
    
        var minX = point.x - (this.widthInMeters / 2);
        var maxX = point.x + (this.widthInMeters / 2);
        var minY = point.y - (this.heightInMeters / 2);
        var maxY = point.y + (this.heightInMeters / 2);
    
        this.limits = [minX, minY, maxX, maxY];
        this.center = point;
        this.zoomValue = transform.k;
        // console.log(transform.k, this.widthInMeters, this.widthInMeters * transform.k);
    
        return this.limits;
    };
    
    /*
     * make the svg half height of the parent element
     */
    EZsvgContainer.prototype.splitView = function() {
        if (ez3dScene.projectType !== 'simple') {
            this.svgContainer
                .style('height', '50%');
    
            d3.select('#playerContainer')
                .style('height', '50%');
    
            this.split = true;
            this.redraw(true);
        }
    };
    
    /*
     * make the svg full height of the parent element
     */
    EZsvgContainer.prototype.collapseView = function() {
        if (ez3dScene.projectType !== 'simple') {
            this.svgContainer
                .style('height', '100%');
    
            d3.select('#playerContainer')
                .style('height', '100%');
    
            this.split = false;
            this.redraw(true);
        }
    };
    
    EZsvgContainer.prototype.getScale = function() {
        this.scale = this.width / (this.limits[2] - this.limits[0]);
        return this.scale;
    };
    
    /*
     * Function to check if transform object has changed (check for translation or zoom).
     */
    EZsvgContainer.prototype.hasTransformChanged = function() {
        return EZModelUtils.parseTransformValues(this.initialZoomValue) !== EZModelUtils.parseTransformValues(this.transform);
    };
    
    /**
     * if zoom is active, fit view to range
     * @param  {array} range    - range = [ minX, maxX, minY, maxY]
     * @param  {float} duration - duration of the transition
     * @param  {boolean} limits - limits @todo
     */
    EZsvgContainer.prototype.fitRangeToContainer = function(range, duration, limits) {
        var self = this;
    
        // console.log('fitRangeToContainer ' + self.name);
        if (!duration) {
            duration = 0;
        }
    
        // projectRange returns [minX, minY, maxX, maxY];
        // range returns [minX, maxX, minY, maxY];
        if (range && self.options.coord === 'scene') {
            if (!self.projectRange) {
                self.projectRange = ez3dScene.utils.calculateProjectRange(ez3dScene);
            }
            if (range[0] === self.projectRange[0] &&
                range[1] === self.projectRange[1] &&
                range[2] === self.projectRange[2] &&
                range[3] === self.projectRange[3]) {
                range = [
                    self.projectRange[0] + 50, self.projectRange[2] - 50,
                    self.projectRange[1] + 50, self.projectRange[3] - 50
                ];
            }
        }
    
        if (range && limits) {
            // debugger
            range = [
                range[0], range[2],
                range[1], range[3]
            ];
        }
    
        if (self.projection) {
            // debugger
        }
    
        if (range && !self.projection) {
            var scale;
            var rangeWidth = range[1] - range[0];
            var rangeHeight;
            if (self.options.coord === 'scene') {
                rangeHeight = range[3] - range[2];
            } else if (self.options.coord === 'area') {
                rangeHeight = range[2] - range[3];
            }
    
            rangeHeight = Math.abs(rangeHeight);
    
            if ((self.width / rangeWidth) > (self.height / rangeHeight)) {
                scale = (self.height / rangeHeight) * 0.9;
            } else {
                scale = (self.width / rangeWidth) * 0.9;
            }
    
            if (range !== self.projectRange) {
                scale *= 0.8;
            }
    
            if (limits) {
                scale = (self.height / rangeHeight);
            }
    
            self.scale = scale;
    
            var offsetX = (self.width - (rangeWidth * scale)) / scale * 0.5;
            var offsetY = (self.height - (rangeHeight * scale)) / scale * 0.5;
    
            self.svg.transition()
                .duration(duration)
                .call(self.zoom.transform, d3.zoomIdentity
                    .scale(scale)
                    .translate(-range[0], -range[2])
                    .translate(offsetX, offsetY)
                );
        }
    
        if (range === undefined) {
            console.warn('fitRangeToContainer cancelled. Range undefined');
        }
    };
    
    EZsvgContainer.prototype.fitRangeToContainerWithoutMargin = function(range) {
        var self = this;
        if (range && !self.projection) {
            var scale;
            var rangeWidth = range[1] - range[0];
            var rangeHeight;
            if (self.options.coord === 'scene') {
                rangeHeight = range[3] - range[2];
            } else if (self.options.coord === 'area') {
                rangeHeight = range[2] - range[3];
            }
    
            rangeHeight = Math.abs(rangeHeight);
    
            if ((self.width / rangeWidth) > (self.height / rangeHeight)) {
                scale = (self.height / rangeHeight) * 1;
            } else {
                scale = (self.width / rangeWidth) * 1;
            }
    
            self.scale = scale;
    
            var offsetX = (self.width - (rangeWidth * scale)) / scale * 0.5;
            var offsetY = (self.height - (rangeHeight * scale)) / scale * 0.5;
    
            self.svg.transition()
                .duration(0)
                .call(self.zoom.transform, d3.zoomIdentity
                    .scale(scale)
                    .translate(-range[0], -range[2])
                    .translate(offsetX, offsetY)
                );
        }
    };
    
    
    /**
     * destroy remove all elements inside parent element
     */
    EZsvgContainer.prototype.destroy = function() {
        // console.log('destroy ' +  this.name);
    
        while (this.svgContainer.node().firstChild) {
            this.svgContainer.node().removeChild(this.svgContainer.node().firstChild);
        }
        var parent = document.getElementById(this.parentId);
        parent.removeChild(this.svgContainer.node());
        delete this.svgContainer.node();
    };
    
    EZsvgContainer.prototype.getLocation = function(coordinates, transform) {
        var returnValue = undefined;
        if (this.options.coord === 'scene') {
            var point;
            var oldPoint;
            // var transform = d3.zoomTransform(this);
            point = {
                x: (coordinates[0], coordinates[0] - transform.x) / transform.k,
                y: (coordinates[1], coordinates[1] - transform.y) / transform.k
            };
            if (ez3dScene.layoutRules.scenePreferences.snapToGrid === true) {
                point.x = (Math.round(point.x / ez3dScene.layoutRules.scenePreferences.gridSize)) *
                ez3dScene.layoutRules.scenePreferences.gridSize;
                point.y = (Math.round(point.y / ez3dScene.layoutRules.scenePreferences.gridSize)) *
                ez3dScene.layoutRules.scenePreferences.gridSize;
            }
            var newPos2 = ez3dScene.utils.convertCartesianToSpherical(point, ez3dScene.projectCenter);
            oldPoint = {x: point.x, y: point.y};
            point = newPos2;
    
            returnValue = [point, oldPoint];
        } else {
            console.warn('getLocation method is only available for scene coords svgContainers');
        }
        return returnValue;
    };
    
    /**
     * Translate container (without applying any zoom) with x and y coords
     * @param  {object} coords      - x and y movement coords
     */
    EZsvgContainer.prototype.flyTo = function(coords, duration) {
        if (!duration) {
            duration = 250;
        }
        if (!this.center) {
            this.center = {
                x: (this.limits[0] + this.limits[2]) / 2,
                y: (this.limits[1] + this.limits[3]) / 2
            };
        }
        var deltaX = coords.x - this.center.x;
        var deltaY = coords.y - this.center.y;
        // console.log('center fly from...', this.center.x, this.center.y);
        // console.log('to...', coords.x, coords.y);
        // console.log('deltas...', deltaX, deltaY);
    
        var newLimits = [
            this.limits[0] + deltaX,
            this.limits[1] + deltaY,
            this.limits[2] + deltaX,
            this.limits[3] + deltaY
        ];
        this.fitRangeToContainer(newLimits, duration, true);
    };
    
    /**
     * Translate container (without applying any zoom) with container limits
     * @param  {array} containerLimits      - Limits of the container to fly to
     */
    EZsvgContainer.prototype.flyToRangeContainer = function(containerLimits, duration) {
        var newXCentre = (containerLimits[0] + containerLimits[2]) / 2;
        var newYCentre = (containerLimits[1] + containerLimits[3]) / 2;
        this.flyTo({x: newXCentre, y: newYCentre}, duration);
    };
    
    EZViewport.prototype.pathEditor2 = {
        poll: function (svgContainer) {
            var value = false;
            if (ez3dScene.context === 'pathEditor') {
                value = true;
                if (!this.isActive(svgContainer)) {
                    this.create(svgContainer);
                }
            } else if (this.isActive(svgContainer)) {
                this.destroy(svgContainer);
            }
            // console.pathEditor('pathEditor poll ' + value);
            return value;
        },
        isActive: function (svgContainer) {
            return ez3dScene.activeEditor === 'pathEditor2';
            // return d3.select('#' + svgContainer.name + '-editorGroup').node() !== null;
        },
        create: function (svgContainer) {
            console.pathEditor('pathEditor create');
            ez3dScene.activeEditor = 'pathEditor2';
            svgContainer.editorGroup.attr('transform', svgContainer.transform);
            d3.select('#ez3d-path-editor-overlap')
                .lower()
                .append('rect')
                .attr('fill', 'transparent')
                .on('click', this.click);
            // Create containers for custom building and keepout shapes
            if (ez3dScene.layoutRules.scenePreferences.enableBuildingShapes) {
                d3.select('.ez3d-path-editor-elements').append('g').classed('ez3d-buildingShape-editor', true);
                d3.select('.ez3d-path-editor-elements').append('g').classed('ez3d-keepoutShape-editor', true);
            }
            // @todo check if it's needed
            ee.emitEvent('tempPath_ready');
            ee.emitEvent('updatePanels');
        },
        destroy: function (svgContainer) {
            console.pathEditor('pathEditor destroy');
            d3.select('#svgProject-editorGroup').selectAll('*').selectAll('*').remove();
            delete ez3dScene.activeEditor;
            ee.emitEvent('updatePanels');
        },
        draw: function (svgContainer) {
            d3.select('#ez3d-path-editor-overlap')
                .select('rect')
                .attr('x', svgContainer.limits[0])
                .attr('y', svgContainer.limits[1])
                .attr('width', svgContainer.limits[2] - svgContainer.limits[0])
                .attr('height', svgContainer.limits[3] - svgContainer.limits[1]);
    
            // @todo change names for divs in editorGroups
            // to add svgContainer name to ids
            // and change css rules also
            // Draw building shape path
            if (ez3dScene.active.customPath && ez3dScene.active.customPath.vertices.length) {
                svgContainer.drawInteractivePath(
                    ez3dScene.active.customPath,
                    d3.select('#ez3d-path-editor-elements .ez3d-buildingShape-editor')
                );
            } else {
                d3.select('#ez3d-path-editor-elements .ez3d-buildingShape-editor').selectAll('*').remove();
            }
            // Draw keepout building shape path
            if (ez3dScene.active.customKeepoutPath && ez3dScene.active.customKeepoutPath.vertices.length) {
                svgContainer.drawInteractivePath(
                    ez3dScene.active.customKeepoutPath,
                    d3.select('#ez3d-path-editor-elements .ez3d-keepoutShape-editor')
                );
            } else {
                d3.select('#ez3d-path-editor-elements .ez3d-keepoutShape-editor').selectAll('*').remove();
            }
            // Draw temp path
            svgContainer.drawInteractivePath(
                ez3dScene.tempPath,
                d3.select('#ez3d-path-editor-elements')
            );
    
            if (ez3dScene.layoutRules.scenePreferences.snapToGuides === true &&
                ((ez3dScene.tempPath && ez3dScene.tempPath.vertices.length > 1) ||
                    ez3dScene.tempPath && ez3dScene.layoutRules.scenePreferences.snapToVertices === true
                )) {
                ez3dViewport.ez3dGuiManager.notificationManager(['calculatingGuidelines', {
                    style: 'warning',
                    spiner: true,
                    event: 'voronoi_Finished'
                }]);
                svgContainer.drawVoronoiDiagram();
            }
    
    
            console.pathEditor('pathEditor drawing');
        },
        click: function () {
            if (d3.event.defaultPrevented) return;
            console.clickOn('click on pathEditor2');
    
            var that = ez3dViewport.svgElements.svgProject;
            if (!ez3dScene.active.customPath && !ez3dScene.active.customKeepoutPath && (!ez3dScene.tempPath || !ez3dScene.tempPath.locked)) {
                if (d3.select('#ez3d-context-panel-container').style('display') === 'flex') {
                    d3.select('#ez3d-context-panel-container').style('display', 'none');
                } else if (ez3dScene.layoutRules.scenePreferences.snapToGuides === false ||
                    ez3dScene.locations.length < 2) {
                    var points = that.getLocation(that.clickCoords || d3.mouse(this), d3.zoomTransform(this));
                    var point = points[0];
                    var oldPoint = points[1];
                    var checkMaxDistance = false;
                    if (ez3dScene.buildings.length === 0 && ez3dScene.locations.length === 0) {
                        checkMaxDistance = true;
                    }
    
                    ee.addOnceListener('addLocation_Finished', function () {
                        if (that.edgeClick) {
                            delete that.edgeClick;
                        }
                    });
    
                    ee.emitEvent('EZModelScene_addLocationListener', [{
                        args: [point, that.edgeClick, undefined, oldPoint, checkMaxDistance],
                        undo: true,
                        updateRender: {
                            draw2d: true,
                            draw3d: false,
                            updatePanels: false,
                            fitRange: false
                        },
                        notifications: false
                    }]);
                }
            }
        },
        voronoiGuideLines: function (svgContainer) {
            console.pathEditor('----> voronoiGuideLines');
            return svgContainer.voronoiGuideLines();
        },
        voronoiSamples: function () {
            console.pathEditor('----> voronoiSamples');
            return svgContainer.voronoiSamples();
        },
        zoomed: function (svgContainer) {
            d3.select('#ez3d-path-editor-overlap').select('rect')
                .attr('x', svgContainer.limits[0])
                .attr('y', svgContainer.limits[1])
                .attr('width', svgContainer.limits[2] - svgContainer.limits[0])
                .attr('height', svgContainer.limits[3] - svgContainer.limits[1]);
        }
    };
    
        /**
     * Change d3 edges styles when focusing and unfocusing panel inputs.
     * @param {array} args          - [targetElement (edge name), isKeepout (if edge is from keepout)].
     * @param {boolean} isFocusing  - If the panel input is being focused.
     */
    EZsvgContainer.prototype.hoverBuildingEdgesOnInputFocus = function(args, isFocusing) {
        const containerId = (args[1]) ? '.ez3d-keepoutShape-editor' : '.ez3d-buildingShape-editor';
        const customPath = ez3dScene.active.customPath.buildingShapeValues[ez3dScene.active.customPath.buildingShapeType];
    
        let edgesElements;
        if (ez3dScene.active.customPath.buildingShapeType === 'step') {
            edgesElements = d3.selectAll(containerId + ' .ez3d-edgeGroup')
                .filter((node) => node.index === args[0]);
        } else {
            const edgesArray = customPath.edgesPosition[args[0]];
            edgesElements = d3.selectAll(containerId + ' .ez3d-edgeGroup')
                .filter((node) => edgesArray.includes(node.index));
        }
    
        // Edges lines
        edgesElements.select('line')
            .transition()
            .ease(d3.easeLinear)
            .style('stroke', () => (isFocusing) ? 'blue' : null);
    
        // Label
        edgesElements.select('.path-label rect')
            .transition()
            .ease(d3.easeLinear)
            .style('fill', () => (isFocusing) ? 'blue' : null);
    };
    
    EZsvgContainer.prototype.drawInteractivePath = function(data, container, options) {
        if (!data && ez3dScene.tempPath) {
            data = ez3dScene.tempPath.updateCalculations();
        } else if (data) {
            data = data.updateCalculations();
        }
        if (!container) {
            container = d3.select('#ez3d-path-editor-elements');
        }
        if (data && data.vertices.length > 0) {
            if (data.vertices.length > 1) {
                this.drawInteractiveEdges(data, container, this, options);
            } else {
                d3.selectAll('g.ez3d-edgeGroup').remove();
                d3.selectAll('#ez3d-path-editor-voronoi-group').selectAll('*').remove();
            }
            this.drawInteractiveLocations(data, container, this, options);
        } else {
            d3.selectAll('#ez3d-path-editor-elements > :not(.ez3d-buildingShape-editor):not(.ez3d-keepoutShape-editor)').selectAll('*').remove();
        }
        d3.selectAll('.ez3d-path-editor-elements').raise();
    };
    
    EZsvgContainer.prototype.drawInteractiveEdges = function(data, container, svgContainer, options) {
        var strokeScale = d3.scalePow()
            .exponent(0.1)
            .domain([10, 25, 200])
            .range([1, 0.1, 0.02]);
    
        var getEdgeTitleText = function(d) {
            var index = d.index;
            var value = '';
            if (data.edgeAzimuths[index]) {
                value += '\nazimuth: ' + parseInt(data.edgeAzimuths[index], 10);
            }
            if (data.wallFacing[index]) {
                value += '\nfacing: ' + data.wallFacing[index];
            }
            return value;
        };
    
        var interactiveEdgeRightClick = function() {
            d3.event.preventDefault();
            if (ez3dScene.layoutRules.scenePreferences.snapToGuides) {
                var index = Number(d3.select(this.parentElement).attr('index'));
                var ob = data.vertices[index];
                var contextPanelData = {
                    functionOnCreation: 'contextPanelEdge',
                    elementData: {
                        id: ob.id,
                        element: this,
                        object: ob
                    },
                };
                var mouse = d3.mouse(d3.select('#ez3d-main-container').node());
                ez3dViewport.ez3dGuiManager.generateContextPanel(mouse, contextPanelData, svgContainer.name);
            }
        };
    
        var interactiveEdgeClick = function() {
            var index = this.__data__.index;
            var modifierKey;
            if (ez3dScene.layoutRules.scenePreferences.userData.OS === 'MacOS') {
                modifierKey = d3.event.metaKey;
            } else {
                modifierKey = d3.event.ctrlKey;
            }
            // Click + Shift to select edge
            if (d3.event.shiftKey === true && modifierKey === false) {
                d3.event.stopImmediatePropagation();
                data.vertices.forEach(function(loc) {
                    if (loc.index === index) {
                        if (loc.selectedEdge) {
                            loc.selectedEdge = !loc.selectedEdge;
                        } else {
                            loc.selectedEdge = true;
                        }
                        d3.select(svgContainer)
                            .classed('ez3d-selected-edge', loc.selectedEdge);
                    }
                });
                svgContainer.drawInteractiveEdges();
                svgContainer.updateVoronoi(true);
            // Only click
            } else if (d3.event.shiftKey === false && modifierKey === false) {
                d3.select('#ez3d-path-editor-elements').attr('display', 'none');
                var elementBelow = document.elementFromPoint(d3.event.clientX, d3.event.clientY);
                svgContainer.clickCoords = d3.mouse(this);
                svgContainer.edgeClick = index;
                ee.addOnceListener('addLocation_Finished', function() {
                    delete svgContainer.edgeClick;
                    delete svgContainer.clickCoords;
                    d3.select('#ez3d-path-editor-elements').attr('display', 'inherit');
                });
                d3.select(elementBelow).dispatch('click');
            }
        };
    
        var estimatedEdge = function(index) {
            var value = false;
            if (index === data.vertices.length - 1 && data.vertices.length > 2) {
                value = true;
            } else if (svgContainer.estimatedPoint === true) {
                if (index === data.vertices.length - 2) {
                    value = true;
                }
            }
            return value;
        };
    
        var updateEdges;
    
        var enterEdges = function(selection) {
            selection
                .attr('index', function(d) {
                    return d.index;
                })
                .lower()
                .classed('ez3d-edgeGroup', true)
                .attr('id', function(d) {
                    return 'interactiveEdge_' + d.id;
                })
                .filter(function() {
                    if (options && options.locked) {
                        return !options.locked;
                    }
                    return !data.locked;
                })
                .on('mouseover', function() {
                    d3.select(this).select('g.path-label')
                        .transition()
                        .style('opacity', '0')
                        .on('end', function() {
                            d3.select(this)
                                .remove();
                        });
                })
                .on('mouseout', function(d) {
                    svgContainer.drawLabel(d3.select(this), data, d.index);
                });
    
            selection
                .append('line')
                .classed('ez3d-edge', true);
        };
    
        updateEdges = function(selection) {
            selection
                .classed('edge-group-locked', function() {
                    if (options && options.locked) {
                        return options.locked;
                    }
                    return data.locked;
                })
                .selectAll('line')
                .classed('ez3d-selected-edge', function(d) {
                    return d.selectedEdge;
                })
                .classed('ez3d-estimatedEdge', function(d) {
                    return estimatedEdge(d.index);
                })
                .attr('x1', function(d) {
                    return d.x;
                })
                .attr('y1', function(d) {
                    return d.y;
                })
                .attr('x2', function(d) {
                    var value = 0;
                    if (data.edgeCenters[d.index]) {
                        value = data.edgeCenters[d.index].x - d.x;
                    }
                    return d.x + (value * 2);
                })
                .attr('y2', function(d) {
                    var value = 0;
                    if (data.edgeCenters[d.index]) {
                        value = data.edgeCenters[d.index].y - d.y;
                    }
                    return d.y + (value * 2);
                })
                .style('stroke-width', function() {
                    var value = strokeScale(svgContainer.zoomValue);
                    return value;
                })
                .each(function(d) {
                    if (d.index !== undefined) {
                        if (!options || options && options.labels) {
                            if (!(data.vertices.length === 2 && d.index === 1)) {
                                var labelContainer = d3.select(this.parentElement);
                                svgContainer.drawLabel(labelContainer, data, d.index);
                            }
                        }
                    }
                })
                .filter(function() {
                    if (options && options.locked) {
                        return !options.locked;
                    }
                    return !data.locked;
                })
                .on('click', interactiveEdgeClick)
                .on('contextmenu', interactiveEdgeRightClick)
                .append('title')
                .text(getEdgeTitleText);
        };
    
        // start d3 join
        var update = container.selectAll('g.ez3d-edgeGroup')
            .data(data.vertices, function(d) {
                return d.id;
            });
    
        var enter = update.enter()
            .append('g')
            .call(enterEdges);
    
        update.merge(enter)
            .call(updateEdges);
    
        update.exit().remove();
    };
    
    EZsvgContainer.prototype.drawInteractiveLocations = function(data, container, svgContainer, options) {
        var self = this;
        var circleRadiusScale = d3.scalePow().exponent(0.2)
            .domain([0.25, 75, 200])
            .range([0.2, 0.035, 1 / 75]);
    
        var updateCircles;
    
        var interactiveLocRightClick = function() {
            d3.event.preventDefault();
            var ob = ez3dScene.findById(this.id.split('_')[1]);
            var contextPanelData = {
                functionOnCreation: 'contextPanelLocation',
                elementData: {
                    id: ob.id,
                    object: ob,
                },
            };
            var mouse = d3.mouse(d3.select('#ez3d-main-container').node());
            ez3dViewport.ez3dGuiManager.generateContextPanel(mouse, contextPanelData, svgContainer.name);
        };
    
        var getLocationCircleFill = function(d) {
            var bias = ez3dScene.layoutRules.scenePreferences.angleBias;
            var angle = data.cornerAngles[d.index];
            var color = '#790';
            if (d.index === 0) {
                color = '#0af';
            }
    
            if (ez3dScene.active.regular === false) {
                if (Math.abs(Math.abs(angle) - 90) >= bias) {
                    color = '#f00';
                    if (d.index === 0) {
                        color = '#a7f';
                    }
                }
            }
            if (d.estimated === true) {
                color = '#f60';
            }
            return color;
        };
    
        var getLocationTooltip = function(d) {
            const x = EZModelUtils.formatDecimalComma(d.x);
            const y = EZModelUtils.formatDecimalComma(d.y);
            let value = 'x: ' + x + '   y: ' + y;
            if (d.index !== undefined) {
                value += '\nindex: ' + d.index;
            }
            const angle = (data.cornerAngles.length > 0)
                ? EZModelUtils.formatDecimalComma(data.cornerAngles[d.index]) : '';
            if (!isNaN(angle)) {
                value += '\nangle: ' + angle;
            }
            return value;
        };
    
        var setLocationTextSizes = function() {
            var bbox = d3.select(this.parentElement).select('circle').node().getBBox();
    
            d3.select(this)
                .attr('x', bbox.x + (bbox.width / 2))
                .attr('y', bbox.y + (bbox.height / 2))
                .attr('font-size', circleRadiusScale(ez3dViewport.svgElements.svgProject.zoomValue) * 4);
        };
    
        var interactiveLocDragStarted = function() {
            svgContainer.deleteAction = false;
            svgContainer.draggingLocation = true;
            var ob = ez3dScene.findById(this.id.split('_')[1]);
            if (data.estimatedPoint === true && !ob.estimated) {
                data.removeEstimatedFourthPoint();
                ez3dViewport.svgElements.svgProject.drawInteractivePath(data);
            }
            // hide context panel if visible
            if (d3.select('#ez3d-context-panel-container').style('display') === 'flex') {
                d3.select('#ez3d-context-panel-container').style('display', 'none');
            }
            var e = d3.event.sourceEvent;
            // select
            if (e.shiftKey === true) {
                if (!ob.active) {
                    if (d3.select(this).classed('ez3d-active-location') === false) {
                        d3.select(this).raise()
                            .select('circle')
                            .classed('ez3d-selected-location', function(d) {
                                var value = !d.selected;
                                return value;
                            });
                        ob.selected = !ob.selected;
                    }
                }
            // activate
            } else if (e.shiftKey === false) {
                d3.select(this.parentElement).selectAll('circle')
                    .classed('ez3d-active-location ', false);
    
                d3.select(this).raise()
                    .select('circle')
                    .classed('ez3d-active-location ', true);
    
                data.vertices.forEach(function(loc) {
                    loc.active = false;
                });
                ob.active = true;
                ob.selected = false;
            }
            d3.select(this.parentElement)
                .style('pointer-events', 'none');
            d3.select(this).select('circle')
                .style('fill-opacity', 0);
            if (ez3dScene.layoutRules.scenePreferences.snapToGuides) {
                d3.select(this)
                    .style('fill-opacity', 0);
            }
            this.origin = {x: 0, y: 0};
            if (d3.select(this).attr('transform')) {
                this.origin = {
                    x: Number(d3.select(this).attr('transform').split('(')[1].split(',')[0]),
                    y: Number(d3.select(this).attr('transform').split('(')[1].split(',')[1].split(')')[0])
                };
            }
            this.move = {x: 0 + this.origin.x, y: 0 + this.origin.y};
            this.originalPosition = {
                x: d3.select(this).select('circle').attr('cx'),
                y: d3.select(this).select('circle').attr('cy')
            };
        };
    
        var interactiveLocDragged = function() {
            // console.log('interactiveLocDragged');
            // var self = this;
            this.move.x += d3.event.dx;
            this.move.y += d3.event.dy;
    
            // store index of dragged element
            var index;
            d3.select(this).select('circle')
                .each(function(d) {
                    index = d.index;
                });
    
            // data.calculateCornersAngles();
    
            var circleRadiusScale2 = d3.scalePow().exponent(0.5)
                .domain([0.25, 200])
                .range([0.2, 1 / 50]);
    
            if (ez3dScene.layoutRules.scenePreferences.snapToGuides === true && data.vertices.length === 2) {
                d3.select('#ez3d-path-editor-voronoi-group').selectAll('*').remove();
                if (ez3dScene.layoutRules.scenePreferences.showOldSystemProject) {
                    svgContainer.voronoiGuideLines();
                } else if (ez3dScene.activeEditor) {
                    ez3dViewport[ez3dScene.activeEditor].voronoiGuideLines(svgContainer);
                }
            }
    
            var calculateOffsets = function() {
                var position = [];
                if (ez3dScene.layoutRules.scenePreferences.snapToGrid === false) {
                    if (ez3dScene.layoutRules.scenePreferences.snapToGuides === true && data.vertices.length > 2) {
                        position[0] = svgContainer.voronoi.x;
                        position[1] = svgContainer.voronoi.y;
                    } else {
                        position[0] = d3.event.x;
                        position[1] = d3.event.y;
                    }
                } else {
                    position[0] = (Math.round(d3.event.x / ez3dScene.layoutRules.scenePreferences.gridSize)) * ez3dScene.layoutRules.scenePreferences.gridSize;
                    position[1] = (Math.round(d3.event.y / ez3dScene.layoutRules.scenePreferences.gridSize)) * ez3dScene.layoutRules.scenePreferences.gridSize;
                }
                var offset = [position[0] - data.vertices[index].x,
                    position[1] - data.vertices[index].y];
    
                return offset;
            };
    
            var offset = calculateOffsets();
    
            d3.select(this.parentElement).selectAll('circle')
                .filter(function(d) {
                    return (d.selected === true || d.active === true);
                })
                .attr('cx', function(d) {
                    return d.x + offset[0];
                })
                .attr('cy', function(d) {
                    return d.y + offset[1];
                })
                .each(function(d) {
                    d.moveByCartesianCoords({
                        x: offset[0],
                        y: offset[1]
                    });
    
                    d3.select(this.parentElement).select('text')
                        .attr('display', 'none');
                    d3.select('#label_' + d.index)
                        .attr('display', 'none');
                })
                .style('fill', getLocationCircleFill)
                .classed('estimatedLocation', function(d) {
                    return d.estimated;
                })
                .select('title')
                .filter(function(d) {
                    if (options && !options.labels) {
                        return false;
                    }
                    return (d.selected === true || d.active === true);
                })
                .text(getLocationTooltip);
    
            // update dragged text
            d3.select(this).select('text')
                .each(setLocationTextSizes);
    
            // draw corner angles
            var cornerAngles = [];
            for (var i = 0; i <= data.vertices.length - 1; i++) {
                cornerAngles.push(i);
            }
            if (data.vertices.length > 5) {
                index -= 2;
                if (index < 0) {
                    index += data.vertices.length;
                }
                var first = cornerAngles.splice(index, cornerAngles.length - index);
                var last = cornerAngles.splice(0, index);
                cornerAngles = first.concat(last);
                cornerAngles = cornerAngles.splice(0, 5);
            }
    
            var vertex;
            var vertices = [];
            cornerAngles.forEach(function(vertexIndex) {
                vertex = {
                    x: data.vertices[vertexIndex].x,
                    y: data.vertices[vertexIndex].y
                };
                vertices.push(vertex);
            });
    
            var calculatedAngles = ez3dScene.utils.calculateCornersAngles(vertices);
    
            var draggedElementFilter = function(d) {
                var value = false;
                if (cornerAngles.indexOf(d.index) !== -1) {
                    value = true;
                }
                if (cornerAngles.length === 5) {
                    if (cornerAngles.indexOf(d.index) === 0 || cornerAngles.indexOf(d.index) === 4) {
                        value = false;
                    }
                }
                if (cornerAngles.length === 4) {
                    if (cornerAngles.indexOf(d.index) === 4) {
                        value = false;
                    }
                }
                return value;
            };
    
            // update text
            d3.select(this.parentElement).selectAll('text')
                .filter(draggedElementFilter)
                .text(function(d) {
                    var value = d.index;
                    if (data.vertices.length > 2) {
                        value = parseInt(Math.abs(calculatedAngles[cornerAngles.indexOf(d.index)]), 0) + '°';
                    }
                    return value;
                });
    
            // update all circles
            d3.select(this.parentElement).selectAll('circle')
                .filter(draggedElementFilter)
                .style('fill', getLocationCircleFill);
    
            // draw pathData
            if (data.vertices.length > 1) {
                data = data.updateCalculations();
                svgContainer.drawInteractiveEdges(data, d3.select(this.parentElement), svgContainer);
            }
        };
    
        var interactiveLocDragEnded = function() {
            // console.log('interactiveLocDragEnded');
            var locations = [];
    
            this.totalOffset = {
                x: d3.select(this).select('circle').attr('cx') - this.originalPosition.x,
                y: d3.select(this).select('circle').attr('cy') - this.originalPosition.y
            };
    
            if (ez3dScene.layoutRules.scenePreferences.snapToGuides) {
                d3.select(this)
                    .style('opacity', 1);
            }
    
            d3.select(this.parentElement)
                .style('pointer-events', 'inherit');
    
            var ob = ez3dScene.findById(this.id.split('_')[1]);
            if (ob !== {}) {
                if (this.move && (this.move.x !== 0 || this.move.y !== 0)) {
                    data.vertices.forEach(function(loc) {
                        if (loc.selected === true || loc.active === true) {
                            locations.push(loc.id);
                        }
                    });
    
                    ee.addOnceListener('moveByCartesianCoords_Finished', function() {
                        // delete svgElement.lastPosition;
                        svgContainer.getCanvasLimits();
                        if (data.vertices.length === 3) {
                            data.calculateEstimatedFourthPoint();
                        }
                        ez3dViewport.svgElements.svgProject.updateVoronoi(true);
                        svgContainer.drawInteractivePath(data, container);
                    });
    
                    ee.emitEvent('EZModelScene_moveByCartesianCoordsListener', [{
                        args: [locations, {x: 0, y: 0}, this.totalOffset],
                        undo: true,
                        updateRender: false,
                        notifications: false
                    }]);
                }
            }
    
            var active = false;
            data.vertices.forEach(function(loc) {
                if (loc.active) {
                    active = true;
                }
            });
            if (active === false) {
                ob.active = true;
                ob.selected = false;
            }
    
            d3.select(this)
                .style('fill-opacity', 1)
                .select('circle')
                .style('fill-opacity', 1);
            d3.select(this).select('text')
                .attr('display', 'inherit');
    
            svgContainer.draggingLocation = false;
            ee.emitEvent('updateRender',
                [{draw2d: true, draw3d: false, updatePanels: false, fitRange: false}]);
        };
    
        var enterCircles = function(selection) {
            selection
                .append('circle')
                .classed('ez3d-location', true);
    
            if (!options || options && options.labels) {
                selection
                    .append('text')
                    .attr('text-anchor', 'middle')
                    .attr('dominant-baseline', 'middle')
                    .style('pointer-events', 'none')
                    .attr('fill', 'white');
            }
        };
    
        updateCircles = function(selection) {
            selection
                .classed('ez3d-locationGroup-locked', function() {
                    if (options && options.locked) {
                        return options.locked;
                    }
                    return data.locked;
                });
    
            selection.selectAll('circle')
                .classed('ez3d-selected-location', function(d) {
                    return d.selected === true && d.active === false;
                })
                .classed('ez3d-active-location', function(d) {
                    return d.active === true;
                })
                .classed('estimatedLocation', function(d) {
                    return d.estimated === true;
                })
                .attr('cx', function(d) {
                    return d.x;
                })
                .attr('cy', function(d) {
                    return d.y;
                })
                .attr('r', function(d) {
                    var value = circleRadiusScale(svgContainer.zoomValue) * 4;
                    return value;
                })
                .attr('stroke-width', function(d) {
                    var value = 0;
                    if (d.active === true) {
                        value = (circleRadiusScale(svgContainer.zoomValue) * 4) / 3;
                    } else if (d.selected === true) {
                        value = (circleRadiusScale(svgContainer.zoomValue) * 4) / 2;
                    }
                    return value;
                })
                .attr('fill', getLocationCircleFill)
                .append('title')
                .text(getLocationTooltip);
    
            selection.selectAll('text')
                .text(function(d) {
                    return d.index + 1;
                })
                .each(setLocationTextSizes);
    
            selection
                .filter(function() {
                    if (options && options.locked) {
                        return !options.locked;
                    }
                    return !data.locked;
                })
                .on('contextmenu', interactiveLocRightClick)
                .call(d3.drag()
                    .on('start', interactiveLocDragStarted)
                    .on('drag', interactiveLocDragged)
                    .on('end', interactiveLocDragEnded));
    
            selection
                .filter(function() {
                    if (options && options.locked) {
                        return options.locked;
                    }
                    return data.locked;
                })
                .on('contextmenu', null)
                .call(d3.drag()
                    .on('start', null)
                    .on('drag', null)
                    .on('end', null));
        };
    
        // start d3 join
        var update = container.selectAll('g.ez3d-locationGroup')
            .data(data.vertices, function(d) {
                return d.id;
            });
    
        var enter = update.enter()
            .append('g')
            .classed('ez3d-locationGroup', true)
            .attr('id', function(d) {
                return 'interactiveLoc_' + d.id;
            })
            .call(enterCircles);
    
        update.merge(enter)
            .call(updateCircles);
    
        update.exit().remove();
    };
    
        EZViewport.prototype.roofEditor = {
        poll: function (svgContainer) {
            var value = false;
            if (ez3dScene.context === 'roof') {
                value = true;
                if (!this.isActive(svgContainer)) {
                    this.create(svgContainer);
                }
            } else if (this.isActive(svgContainer)) {
                this.destroy(svgContainer);
            }
            return value;
        },
        isActive: function (svgContainer) {
            return ez3dScene.activeEditor === 'roofEditor';
        },
        create: function (svgContainer) {
            console.pathEditor('roofEditor create');
            ez3dScene.activeEditor = 'roofEditor';
            svgContainer.editorGroup.attr('transform', svgContainer.transform);
            d3.select('#ez3d-path-editor-overlap')
                .lower()
                .append('rect')
                .attr('fill', 'transparent')
                .on('click', this.click);
            ee.emitEvent('updatePanels');
    
            d3.select('#ez3d-path-editor-elements')
                .append('g')
                .attr('id', 'roofEditor-main');
        },
        destroy: function (svgContainer) {
            console.pathEditor('roofEditor destroy');
            d3.select('#svgProject-editorGroup').selectAll('*').selectAll('*').remove();
            delete ez3dScene.activeEditor;
            d3.selectAll('[id^=roofPattern_').remove();
            ee.emitEvent('updatePanels');
        },
        draw: function (svgContainer) {
            d3.select('#ez3d-path-editor-overlap')
                .select('rect')
                .attr('x', svgContainer.limits[0])
                .attr('y', svgContainer.limits[1])
                .attr('width', svgContainer.limits[2] - svgContainer.limits[0])
                .attr('height', svgContainer.limits[3] - svgContainer.limits[1]);
    
            svgContainer.drawRoofEditorPromise()
                .then(() => {
                    console.pathEditor('roofEditor drawing');
                });
        },
        click: function () {
            //
        },
        voronoiGuideLines: function (svgContainer) {
            console.pathEditor('----> voronoiGuideLines');
            return svgContainer.voronoiGuideLines();
        },
        voronoiSamples: function () {
            console.pathEditor('----> voronoiSamples');
            return svgContainer.voronoiSamples();
        },
        zoomed: function (svgContainer) {
            d3.select('#ez3d-path-editor-overlap').select('rect')
                .attr('x', svgContainer.limits[0])
                .attr('y', svgContainer.limits[1])
                .attr('width', svgContainer.limits[2] - svgContainer.limits[0])
                .attr('height', svgContainer.limits[3] - svgContainer.limits[1]);
        }
    };
    
        EZsvgContainer.prototype.addRoofPoints = function(container, building) {
        var self = this;
    
        const colors = ez3dScene.layoutRules
            .defaultColors.roofColors.roofPoints;
    
        const strokeScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.2, 1 / 50]);
    
        const roof = ez3dScene.active;
        let points = roof.roofPoints;
        if (roof.topPoint) {
            points = points.concat(roof.topPoint);
        }
    
        const roofPoints = container
            .selectAll('g.EZModelLocation')
            .data(points, function(d) {
                return d.id;
            });
    
        const locGroup = roofPoints
            .enter()
            .append('g')
            .attr('class', 'EZModelLocation')
            .attr('id', function(d) {
                return self.name + '-roofPoints-EZModelBuilding_' + d.id;
            });
    
        locGroup
            .filter(function() {
                const {type} = roof;
                return !['pent', 'pergola', 'pitched'].includes(type);
            })
            .append('circle')
            .classed('roofPoints', true)
            .classed('locked', false)
            .attr('id', function(d) {
                return self.name + '-roofPoints-EZModelBuilding_' + d.id;
            })
            .attr('cx', function(d) {
                return d.x;
            })
            .attr('cy', function(d) {
                return d.y;
            })
            .attr('r', strokeScale(self.zoomValue) *
                colors.standard.radius)
            .attr('fill', function(d) {
                if (d.parent.roofPoints.includes(d)) {
                    return colors.standard.fill;
                }
                return colors.symmetry.fill;
            })
            .attr('stroke', function(d) {
                if (d.parent.roofPoints.includes(d)) {
                    return colors.standard.stroke;
                }
                return colors.symmetry.stroke;
            })
            .attr('stroke-width', strokeScale(self.zoomValue) *
                colors.standard.strokeWidth)
            .append('title')
            .text(function(d) {
                const index = d.parent.roofPoints.indexOf(d);
                let value = '';
                if (index !== -1) value += 'index: ' + index;
                if (d.z) value += '\nheight: ' + EZModelUtils.formatDecimalComma(d.z, 0);
                return value;
            });
    
        locGroup
            .classed('locked', false)
            .call(d3.drag()
                .on('start', self.roofPointsDragStarted)
                .on('drag', self.roofPointsDragged)
                .on('end', self.roofPointsDragEnded));
    };
    
    // -----------------------------------------------------------------------------
    // interactions
    EZsvgContainer.prototype.roofPointsDragStarted = function() {
        d3.select(this).raise().select('circle').classed('active ', true);
        d3.select('#roofEditor-main_building_' + ez3dScene.activeBuilding.id)
            .selectAll('g.ez3d-roofArea').attr('display', 'none');
    };
    
    EZsvgContainer.prototype.roofPointsDragged = function() {
        const {aproxPointToLine, collide2, pairWithNext} = EZModelUtils;
        const roof = ez3dScene.active;
    
        const thisId = this.id.split('_')[1];
        const thisIndex = roof.roofPoints
            .findIndex((point) => (point.id === thisId));
        const isTopPoint = (thisIndex === -1);
    
        let newPos = {x: d3.event.x, y: d3.event.y};
        const pointArea = (isTopPoint)
            ? roof.path.vertices
            : roof.roofPointsAreas[thisIndex];
    
        let isOutlineType = roof.isOutlineRoofType();
        if (roof.type === 'mansard') {
            isOutlineType = (roof.roofPointsSymmetry ||
                roof.roofPointsSymmetry === undefined);
        }
        if (isOutlineType && !isTopPoint) {
            let line = [pointArea[(roof.type === 'mansard') ? 2 : 3]];
            if (roof.type === 'gambrel' && thisIndex % 2 === 1) {
                line = [pointArea[1]];
            }
            newPos = aproxPointToLine(newPos, line, pointArea[0]);
        } else {
            const isInsidePath = collide2(newPos, pointArea, true);
            if (!isInsidePath) {
                pointArea
                    .map(pairWithNext)
                    .forEach(([point, next]) => {
                        const line = [point];
                        newPos = aproxPointToLine(newPos, line, next, true);
                    });
            }
        }
    
        // Roof Points
        const prefix = '#svgProject-roofPoints-EZModelBuilding_';
        let roofPoints = roof.roofPoints.map((point, index) => {
            if (index === thisIndex) {
                const ut = d3.select(this).selectAll('circle');
                return {circle: ut, x: newPos.x, y: newPos.y};
            }
            const ut = d3.select(prefix + point.id).selectAll('circle');
            return {circle: ut, x: point.x, y: point.y};
        });
    
        if (roofPoints.length > 1) {
            roofPoints = roof
                .calculatePointsSymmetry(roofPoints, thisIndex, newPos);
        }
    
        // Edge Lines
        const svgProject = ez3dViewport.svgElements.svgProject;
    
        const pointsLines = roof.calculatePointsLines(thisIndex, newPos);
        svgProject.drawPointsLines(pointsLines, roof.parent.id, svgProject);
    
        const edgeLines = roof.calculateEdgeLines(roofPoints);
        svgProject.drawRidgeLines(edgeLines, roof.parent.id, svgProject);
    
        // Redraw
        roofPoints.forEach((point) => {
            point.circle.attr('cx', point.x).attr('cy', point.y);
        });
        if (isTopPoint) {
            d3.select(this).selectAll('circle')
                .attr('cx', newPos.x)
                .attr('cy', newPos.y);
        }
    };
    
    /**
     *
     *  * Method to show a widget notification when drag end point
     */
    EZsvgContainer.prototype.roofPointsDragEnded = function() {
        const roof = ez3dScene.active;
        if (roof.needsUpdate === true) {
            ez3dViewport.svgElements.svgProject.roofPointsResolveDrag(this);
        } else {
            ee.addOnceListener('widget_roofDragPoints_eventOk', () => {
                ez3dViewport.svgElements.svgProject.roofPointsResolveDrag(this);
                ee.removeEvent('widget_roofDragPoints_eventCancel');
            });
    
            ee.addOnceListener('widget_roofDragPoints_eventCancel', () => {
                ee.emitEvent('updateRender',
                    [{draw2d: true, draw3d: roof, updatePanels: true, fitRange: false}]);
                ee.removeEvent('widget_roofDragPoints_eventOk');
            });
    
            ee.emitEvent('widget_notification', [{
                'name': 'roofPointsDragEnded',
                'title': 'alertRoofChangeTitle',
                'content': 'alertRoofChangeContent',
                'eventOk': 'widget_roofDragPoints_eventOk',
                'eventCancel': 'widget_roofDragPoints_eventCancel'
            }]);
        }
        d3.select('#roofEditor-main_building_' + roof.parent.id)
            .selectAll('g.ez3d-roofArea').attr('display', 'inherit');
    };
    
    /**
     *
     *  * Method to set new locations to roof point
     *
     * @param {object} svgElement this is a json data
     * @param {string} svgElement.id svg element id
     * @param {object} svgElement.lastPosition svg element last position x first point
     */
    EZsvgContainer.prototype.roofPointsResolveDrag = function(svgElement) {
        const roof = ez3dScene.active;
        const convert = EZModelUtils.convertCartesianToSpherical;
    
        // Update Roof Points
        const prefix = '#svgProject-roofPoints-EZModelBuilding_';
        let points = roof.roofPoints;
        if (roof.topPoint) points = points.concat(roof.topPoint);
        points.forEach((point) => {
            const ut = d3.select(prefix + point.id).selectAll('circle');
            const cartesianCoords = {
                x: Number(ut.attr('cx')),
                y: Number(ut.attr('cy'))
            };
            const sphericalCoords = convert(cartesianCoords, point);
            // Update
            point.x = cartesianCoords.x;
            point.y = cartesianCoords.y;
            point.lat = sphericalCoords.lat;
            point.lng = sphericalCoords.lng;
        });
    
        // Clean svgElement
        d3.select(svgElement).classed('active', false);
        d3.select(svgElement).selectAll('text')
            .attr('opacity', 0).attr('font-size', '0');
    
        roof.needsUpdate = true;
        roof.roofSoftUpdate(function() {
            ee.emitEvent('updateRender',
                [{draw2d: true, draw3d: false, updatePanels: true, fitRange: false}]);
        });
    };
    
    EZsvgContainer.prototype.drawInclinationLabel = function(container, areasData, activeBuilding) {
        let self = this;
        const labelScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.75, 0.1]);
    
        const enterLabels = function(selection) {
            selection
                .attr('pointer-events', 'none')
                .attr('class', 'inclination-label')
                .attr('id', function(d) {
                    return 'inclination-label_' + d.id;
                })
                .attr('transform', function(d) {
                    return 'translate(' + (d.path.center.x - activeBuilding.path.center.x) + ', ' + (d.path.center.y - activeBuilding.path.center.y) + ')';
                })
                .append('text')
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'middle')
                .attr('fill-opacity', 1)
                .attr('fill', 'black')
                .attr('stroke', 'none');
        };
    
        const updateLabels = function(selection) {
            selection.selectAll('text')
                .text(function(d) {
                    return EZModelUtils.formatDecimalComma(d.inclination, 0) + '°';
                })
                .attr('font-size', labelScale(self.zoomValue));
        };
    
        // Start d3 join
        const update = container.selectAll('g.inclination-label')
            .data(areasData, function(d) {
                return d.id;
            });
    
        const enter = update.enter()
            .append('g')
            .call(enterLabels);
    
        update.merge(enter)
            .call(updateLabels);
    
        update.exit().remove();
    
        // deferCallback(null);
    };
    
    EZsvgContainer.prototype.drawInteractiveRoofEdges = function(data, buildingId, svgContainer, deferCallback) {
        const colors = ez3dScene.layoutRules
            .defaultColors.roofColors.roofEdges;
    
        // Stroke Scale
        const strokeScale = d3.scalePow()
            .exponent(0.1)
            .domain([10, 200])
            .range([1, 0.02]);
    
        // Container
        let container = d3.select('#roofEditor-main_building_' + buildingId);
        if (container.select('#roofEdgesGroup').node() === null) {
            container = container.append('g').attr('id', 'roofEdgesGroup');
        } else {
            container = container.select('#roofEdgesGroup');
        }
    
        const interactiveEdgeRightClick = function(d) {
            d3.event.preventDefault();
            const type = d.parent.wallType[d.index];
            if (svgContainer.name === 'svgProject' && type !== 'default') {
                var contextPanelData = {
                    functionOnCreation: 'contextPanelRoofEdges',
                    elementData: {
                        element: d,
                        path: this
                    }
                };
                var mouse = d3.mouse(d3.select('#ez3d-main-container').node());
                self.ez3dViewport.ez3dGuiManager
                    .generateContextPanel(mouse, contextPanelData, svgContainer.name);
            }
        };
    
        const enterEdges = function(selection) {
            selection
                .attr('index', function(d) {
                    return d.index;
                })
                .lower()
                .classed('ez3d-edgeGroup', true)
                .attr('id', function(d) {
                    return 'interactiveEdge_' + d.id;
                });
    
            selection
                .append('line')
                .classed('ez3d-edge', true);
        };
    
        const updateEdges = function(selection) {
            selection
                .selectAll('line')
                .raise()
                .attr('x1', (d) => d.x)
                .attr('y1', (d) => d.y)
                .attr('x2', (d) => {
                    var value = 0;
                    if (data.edgeCenters[d.index]) {
                        value = data.edgeCenters[d.index].x - d.x;
                    }
                    return d.x + (value * 2);
                })
                .attr('y2', (d) => {
                    var value = 0;
                    if (data.edgeCenters[d.index]) {
                        value = data.edgeCenters[d.index].y - d.y;
                    }
                    return d.y + (value * 2);
                })
                .style('stroke', function(d) {
                    return colors[d.parent.wallType[d.index]].stroke;
                })
                .style('stroke-width', strokeScale(svgContainer.zoomValue) *
                    colors.standard.strokeWidth)
                .on('click', interactiveEdgeRightClick)
                .on('contextmenu', interactiveEdgeRightClick)
                .on('mouseover', function() {
                    d3.select(this)
                        .style('stroke-width', strokeScale(svgContainer.zoomValue) *
                            colors.hover.strokeWidth);
                })
                .on('mouseout', function() {
                    d3.select(this)
                        .style('stroke-width', strokeScale(svgContainer.zoomValue) *
                            colors.standard.strokeWidth);
                });
        };
    
        // start d3 join
        var update = container.selectAll('g.ez3d-edgeGroup')
            .data(data.vertices, function(d) {
                return d.id;
            });
    
        var enter = update.enter()
            .append('g')
            .call(enterEdges);
    
        update.merge(enter)
            .call(updateEdges);
    
        update.exit().remove();
    
        deferCallback(null);
    };
    
    EZsvgContainer.prototype.drawRidgeLines = function (dataset, buildingId, svgContainer, deferCallback) {
        const colors = ez3dScene.layoutRules
            .defaultColors.roofColors.roofRidge;
    
        // Stroke Scale
        const strokeScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.2, 1 / 50]);
    
        // Container
        let container = d3.select('#roofEditor-main_building_' + buildingId);
        if (container.select('#ridgeLinesGroup').node() === null) {
            container = container.append('g').attr('id', 'ridgeLinesGroup');
        } else {
            container = container.select('#ridgeLinesGroup');
        }
        const enterRidgeLines = function(selection) {
            selection
                .classed('ez3d-ridge-lines', true);
        };
        const updateRidgeLines = function(selection) {
            selection
                .attr('x1', (d) => d.x1)
                .attr('y1', (d) => d.y1)
                .attr('x2', (d) => d.x2)
                .attr('y2', (d) => d.y2)
                .attr('stroke-width', strokeScale(svgContainer.zoomValue) *
                    colors.standard.strokeWidth)
                .attr('stroke', colors.standard.stroke);
        };
        const update = container.selectAll('line.ez3d-ridge-lines')
            .data(dataset);
    
        const enter = update.enter()
            .append('line')
            .call(enterRidgeLines);
    
        update
            .merge(enter)
            .call(updateRidgeLines);
    
        update.exit().remove();
        if (deferCallback) deferCallback(null);
    };
    
    EZsvgContainer.prototype.drawPointsLines = function (dataset, buildingId, svgContainer, deferCallback) {
        const colors = ez3dScene.layoutRules
            .defaultColors.roofColors.roofRidge;
        const strokeScale = d3.scalePow().exponent(0.5)
            .domain([0.25, 200])
            .range([0.2, 1 / 50]);
    
        let container = d3.select('#roofEditor-main_building_' + buildingId);
        if (container.select('#pointLinesGroup').node() === null) {
            container = container.append('g').attr('id', 'pointLinesGroup');
        } else {
            container = container.select('#pointLinesGroup');
        }
        const enterPointsLines = function(selection) {
            selection
                .classed('ez3d-point-lines', true);
        };
        const updatePointsLines = function(selection) {
            selection
                .attr('x1', (d) => d.x1)
                .attr('y1', (d) => d.y1)
                .attr('x2', (d) => d.x2)
                .attr('y2', (d) => d.y2)
                .attr('stroke-width', strokeScale(svgContainer.zoomValue) *
                    colors.symmetry.strokeWidth)
                .attr('stroke-dasharray', colors.symmetry.strokeDasharray)
                .attr('stroke', colors.symmetry.stroke);
        };
        const update = container.selectAll('line.ez3d-point-lines')
            .data(dataset);
    
        const enter = update.enter()
            .append('line')
            .call(enterPointsLines);
    
        update
            .merge(enter)
            .call(updatePointsLines);
    
        update.exit().remove();
        if (deferCallback) deferCallback(null);
    };
    
    EZsvgContainer.prototype.drawRoofAreas = function(areasData, buildingId, svgContainer, deferCallback) {
        let container = d3.select('#roofEditor-main_building_' + buildingId);
    
        if (container.select('#roofAreasGroup').node() === null) {
            container = container.append('g').attr('id', 'roofAreasGroup');
        } else {
            container = container.select('#roofAreasGroup');
        }
    
        var enterAreas = function(selection) {
            selection
                .attr('class', 'ez3d-roofArea')
                .attr('id', function(d) {
                    return svgContainer.name + '_roofArea_' + d.id;
                });
    
            const changeInclination = function(d) {
                d3.event.preventDefault();
                const roof = ez3dScene.activeRoof;
                const face = roof.geometry.find((geo) => (geo.link === d.id));
                // WIP ROOF EDITING : getRoofAreaContextPanelContent
                if (face.wallIndex === undefined) {
                    console.warn('WIP: Edit Linked Roof');
                    return;
                }
                if (roof.path.wallType[face.wallIndex] === 'default') {
                    console.warn('WIP: Edit Flat Surface');
                    return;
                }
                if (svgContainer.name === 'svgProject' &&
                    this.parentElement.className.baseVal === 'area-shape-group') {
                    var contextPanelData = {
                        functionOnCreation: 'contextPanelRoofArea',
                        elementData: {
                            element: d,
                            path: this
                        }
                    };
                    var mouse = d3.mouse(d3.select('#ez3d-main-container').node());
                    self.ez3dViewport.ez3dGuiManager.generateContextPanel(mouse, contextPanelData, svgContainer.name);
                }
            };
    
            selection
                .append('g')
                .attr('class', 'area-shape-group')
                .append('path')
                .attr('class', 'area-shape')
                .on('mouseover', function() {
                    d3.select(this)
                        .call(styleAreas, svgContainer, 'hoverRoofEditor');
                })
                .on('mouseout', function() {
                    d3.select(this)
                        .call(styleAreas, svgContainer, 'roofEditor');
                })
                .on('click', changeInclination)
                .on('contextmenu', changeInclination);
        };
    
        var updateAreas = function(selection) {
            selection
                .select('path.area-shape')
                .attr('d', function(d) {
                    return (svgContainer.options.coord === 'scene'
                        ? d.path.lineData : d.path.lineDataFlatten);
                })
                .call(styleAreas, svgContainer, 'roofEditor');
        };
    
        // Start d3 join
        var update = container.selectAll('g.ez3d-roofArea')
            .data(areasData, function(d) {
                return d.id;
            });
    
        var enter = update.enter()
            .append('g')
            .call(enterAreas);
    
        update.merge(enter)
            .call(updateAreas);
    
        update.exit().remove();
    
        deferCallback(null);
    };
    
    EZsvgContainer.prototype.drawRoofEditorPromise = function() {
        const self = this;
        const promise = new Promise((resolve, reject) => {
            console.project('▓▒░▒▓ draw roof editor promise');
            let debugOpTime = Date.now();
            const activeBuilding = ez3dScene.activeBuilding;
    
            const drawAngleLabels = function() {
                ez3dScene.active.areas.forEach(function(area) {
                    self.addroofPattern(self.svgDefs, area);
                    const areaContainer = d3.select('#svgProject_roofArea_' + area.id)
                        .select('.area-shape-group');
                    if (Math.abs(area.inclination) >= 1) {
                        self.drawInclinationLabel(areaContainer, [area], activeBuilding);
                    }
                });
            };
    
            const queue = d3.queue()
                .defer(self.drawBuildings, [activeBuilding], false, d3.select('#roofEditor-main'), self);
    
            d3.selectAll('g.ez3d-edgeGroup').remove();
            // Draw Roof Areas
            queue
                .defer(self.drawRoofAreas, ez3dScene.active.areas, activeBuilding.id, self);
            // Draw Interactive Roof Edges
            queue
                .defer(self.drawInteractiveRoofEdges, ez3dScene.active.path, activeBuilding.id, self);
            // Draw Points Lines
            const pointsLines = ez3dScene.active.calculatePointsLines();
            queue
                .defer(self.drawPointsLines, pointsLines, activeBuilding.id, self);
            // Draw Ridge Lines
            const edgeLines = ez3dScene.active.calculateEdgeLines(ez3dScene.active.roofPoints);
            queue
                .defer(self.drawRidgeLines, edgeLines, activeBuilding.id, self);
            // Draw Roof Points
            queue
                .defer(self.drawRoofPoints, ez3dScene.active.areas, activeBuilding.id, self)
                .awaitAll(function(error, results) {
                    if (error) throw error;
                    drawAngleLabels();
                    setTimeout(function () {
                        debugOpTime = Date.now() - debugOpTime;
                        resolve({status: 'FINISHED', args: debugOpTime,
                            areasPainted: results[0]
                        });
                    }, 0);
                });
        });
    
        return promise;
    };
    
    EZsvgContainer.prototype.drawRoofPoints = function(areasData, buildingId, svgContainer, deferCallback) {
        let container = d3.select('#roofEditor-main_building_' + buildingId);
        if (container.select('#roofPointsGroup').node() === null) {
            container = container.append('g').attr('id', 'roofPointsGroup');
        } else {
            container = container.select('#roofPointsGroup');
        }
    
        container.selectAll('g.EZModelLocation').remove();
        container.selectAll('.ridgeLine').remove();
        container.raise();
        svgContainer.addRoofPoints(container, ez3dScene.activeBuilding);
    
        deferCallback(null);
    };
    
    // CHECK THIS: require ./EZ-SvgContainer-options/**/*.js
    function Mapper(playerContainer, anchor, mainLatLng, providerId, width, height, googleApiKey, bingApiKey) {
        this.wMult = this.hMult = 2;
    
        this.googleApiKey = googleApiKey;
        this.bingApiKey = bingApiKey;
    
        this.providers = loadProviders(this.bingApiKey, this.googleApiKey);
    
        this.provider = providerId;
    
        if (this.provider === 1 && !this.googleApiKey) {
            this.provider = 5;
        }
        if (this.provider === 2 && !this.bingApiKey) {
            this.provider = 5;
        }
    
    
        this.width = width * this.wMult;
        this.height = height * this.hMult;
        if (this.width === 0 || this.height === 0) {
            this.width = this.height = 400;
        }
    
        this.mapsAnchor = anchor;
        this.playerContainer = playerContainer;
    
        this.mainLatLng = mainLatLng;
        this.newCenter = {x: 0, y:0};
        this.newWidth = 100;
        this.newHeight = 100;
        this.metersPerDegree = 111320;
        this.metersPerPixelZoom0 = 156543.03;
    
        this.mapContainer = document.createElement('div');
        this.mapContainer.style = `visibility:hidden;width:${this.width}px; height:${this.height}px; position:fixed; left:${this.width * 10}px; top:${this.height * 10}px;`;
    
        this.playerContainer.appendChild(this.mapContainer);
        this.mapContainer.id = 'mapContainer';
    
    
        this.mymap = L.map(this.mapContainer, {
            attributionControl: true,
            drawControl: true,
            center: [this.mainLatLng.lat, this.mainLatLng.lng],
            zoom: 10
        });
    
    
        const targetNode = document.querySelector('.leaflet-control-attribution');
        const config = {attributes: true, childList: true, subtree: true};
        const callback = (mutationsList, observer) => {
            if (this.provider !== 1) {
                for (var mutation of mutationsList) {
                    const noLink = mutation.target.innerHTML.replace(/Leaflet/g, '');
                    ee.emitEvent('Mapper.AttributionChange', [noLink]);
                }
            }
        };
        const observer = new MutationObserver(callback);
        observer.observe(targetNode, config);
    
    
    
        this.providers.forEach((prov) => {
            prov.anchor = new THREE.Object3D();
            this.mapsAnchor.add(prov.anchor);
            if (prov.layer) {
                prov.layer.on('tileload', (e) => {
                    const cosLat = Math.cos(EZModelUtils.degToRad(this.mainLatLng.lat));
                    const tileSizeMeters = this.metersPerPixelZoom0 * 256 / Math.pow(2, e.coords.z) * cosLat;
                    let tileLatLng = {};
                    const tileX = e.coords.x;
                    const tileY = e.coords.y;
                    const tileZ = e.coords.z;
                    tileLatLng.lng = EZModelUtils.tile2lng(tileX, tileZ);
                    tileLatLng.lat = EZModelUtils.tile2lat(tileY, tileZ);
                    let tileXY = {};
                    tileXY.y = (-tileLatLng.lat + this.mainLatLng.lat) * this.metersPerDegree;
                    tileXY.x = (tileLatLng.lng - this.mainLatLng.lng) * this.metersPerDegree * cosLat;
    
                    const positionX = tileXY.x + tileSizeMeters / 2;
                    const positionY = tileXY.y + tileSizeMeters / 2;
    
                    let image = document.createElement('img');
    
                    // const plane = getPlaneGeometry(image, tileSizeMeters);
    
                    const texture = new THREE.Texture(image);
                    texture.encoding = THREE.GammaEncoding;
    
                    const mat = new THREE.MeshStandardMaterial({
                        map: texture,
                        metalness: 0,
                        roughness: 1,
                    });
                    mat.shadowSide = THREE.FrontSide;
    
                    const planeGeo = new THREE.PlaneGeometry(tileSizeMeters, tileSizeMeters);
                    const plane = new THREE.Mesh(planeGeo, mat);
                    plane.receiveShadow = true;
                    
                    plane.position.x = positionX;// tileXY.x;
                    plane.position.z = positionY;// tileXY.y;
                    plane.rotation.x = -Math.PI / 2;
    
                    plane.name = `x${e.coords.x}y${e.coords.y}z${e.coords.z}`;
    
                    
    
                    image.crossOrigin = 'anonymous';
                    image.onload = () => {
                        texture.needsUpdate = true;
                        prov.anchor.add(plane);
                        plane.updateMatrixWorld();
                        ee.emitEvent('Mapper.TileLoaded');
                    };
                    if (prov.id === 1) {
                        image.src = e.tile.children[0].src;
                    } else {
                        image.src = e.tile.src;
                    }
                });
    
    
                prov.layer.on('tileunload', (e) => {
                    for(let i = 0; i<prov.anchor.children.length; i++){
                        let obj = prov.anchor.children[i];
                        if (obj.name === `x${e.coords.x}y${e.coords.y}z${e.coords.z}`) {
                            prov.anchor.remove(obj);
                            obj.material.dispose();
                            obj.geometry.dispose();
                            obj = undefined;
                            ee.emitEvent('Mapper.TileLoaded');
                            break;
                            
                        }
                    }
                });
    
    
                prov.layer.on('load', (e) => {
                    ee.emitEvent('Mapper.AllTilesLoaded');
                });
            }
        });
    
    
        if (this.provider !== 0) {
            this.providers[this.provider].layer.addTo(this.mymap);
        }
        if(this.googleApiKey){
            this.providers[1].layer.addTo(this.mymap);
        }
    
    
        this.setProvider(this.provider);
    
        ee.addListener('Renderer.Resize', (e) => {
            if (e.width !== 0 && e.height !== 0) {
                this.width = e.width * this.wMult;
                this.height = e.height * this.hMult;
                this.mapContainer.style.width = this.width + 'px';
                this.mapContainer.style.height = this.height + 'px';
                this.mymap.invalidateSize();
            }
        });
    }
    
    Mapper.prototype.getProvider = function() {
        return this.provider;
    };
    
    Mapper.prototype.getProviders = function() {
        return this.providers;
    };
    
    Mapper.prototype._setProvider = function (providerId) {
    
        if (this.provider !== 0) {
            this.mymap.removeLayer(this.providers[this.provider].layer);
        }
        this.provider = providerId;
        if (this.provider !== 0) {
            this.mymap.addLayer(this.providers[this.provider].layer);
        }
    
        ee.emitEvent('Mapper.AttributionLogo', [this.providers[this.provider].logo]);
        ee.emitEvent('Mapper.AttributionChange', [this.providers[this.provider].attribution]);
        setTimeout(()=>{
            this.changeDimensions(this.newWidth, this.newHeight, this.newCenter);
        }, 1000);
    };
    
    Mapper.prototype.setProvider = function(id) {
        this.providers.forEach((provider) => {
            provider.anchor.visible = provider.id === id;
        });
        ee.emitEvent('Mapper.TileLoaded');
    
        if (id === 1) {
            if (this.providers[1].layer._mutant) {
                EZModelUtils.mutantEvent(this.providers[1].layer._mutant);
                this._setProvider(id);
            } else {
                const timeIntervalHandler = setInterval(() => {
                    if (this.providers[1].layer._mutant) {
                        clearInterval(timeIntervalHandler);
                        EZModelUtils.mutantEvent(this.providers[1].layer._mutant);
                        this._setProvider(id);
                    }
                }, 100);
            }
        } else {
            this._setProvider(id);
        }
    }
    
    Mapper.prototype.setMainLatLng = function (latlng) {
        this.mainLatLng = latlng;
    };
    
    
    
    
    Mapper.prototype.changeDimensions = function(newWidth, newHeight, newCenter) {
        this.newWidth = newWidth;
        this.newHeight = newHeight;
        this.newCenter = newCenter;
    
        const centerLatLng = EZModelUtils.calculateDelta(this.mainLatLng, newCenter, this.metersPerDegree);
    
        const ne = EZModelUtils.calculateDelta(centerLatLng, {x: newWidth / 2, y: newHeight / 2}, this.metersPerDegree);
        const sw = EZModelUtils.calculateDelta(centerLatLng, {x: -newWidth / 2, y: -newHeight / 2}, this.metersPerDegree);
    
        if (newWidth !== 0 && newHeight !== 0) {
            this.mymap.fitBounds([[sw.lat, sw.lng], [ne.lat, ne.lng]]/*, {padding:[1000,1000] }*/);
        }
    };
    
    Mapper.prototype.setVisible = function (visible) {
        this.mapsAnchor.visible = visible;
            if (visible) {
            this.enableProvider();
        } else {
            this.disableProvider();
        }
    };
    
    Mapper.prototype.disableProvider = function () {
        this.disabledProvider = this.provider;
        this.setProvider(0); //white Map
    };
    
    Mapper.prototype.enableProvider = function () {
        if (this.disabledProvider){
            this.setProvider(this.disabledProvider);
            delete this.disabledProvider;
        }
     };
    
    
    
    function loadProviders(bingApiKey, googleApiKey){
    
        const providers = [
            {
                name: 'White Map',
                label: 'white',
                logo: '',
                attribution: '',
                id: 0,
                url: '',
                type: 'xyz',
                subdomains: []
            },
            {
                name: 'Google Hybrid',
                label: 'googleHybrid',
                logo: 'https://maps.gstatic.com/mapfiles/api-3/images/google_white5.png',
                attribution: '© Google',
                id: 1,
                type: 'xyz',
                subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
            },
            {
                name: 'Bing',
                label: 'bing',
                logo: 'https://img-prod-cms-rt-microsoft-com.akamaized.net/cms/api/am/imageFileData/RW9wQg?ver=4da4',
                attribution: '© Microsoft',
                id: 2,
                type: 'quadkey',
                subdomains: ['t0', 't1', 't2', 't3'],
            },
            {
                name: 'Digital Globe',
                label: 'digitalGlobe',
                logo: '',
                id: 3,
                url: '',
                type: 'xyz',
                subdomains: []
            },
            {
                name: 'Carto',
                label: 'carto',
                logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/13/CARTO-logo.svg/75px-CARTO-logo.svg.png',
                attribution: '© Carto',
                id: 4,
                type: 'xyz',
                subdomains: ['a', 'b', 'c', 'd'],
                layer: L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png', {
                    minZoom: 0,
                    maxZoom: 30,
                    maxNativeZoom: 22,
                    subdomains: ['a', 'b', 'c', 'd']
                })
            },
            {
                name: 'Open Street Maps',
                label: 'openStreetMaps',
                logo: 'https://wiki.openstreetmap.org/w/images/thumb/a/a9/OSM_fi_flag.svg/75px-OSM_fi_flag.svg.png',
                attribution: '© Open Street Maps',
                id: 5,
                type: 'xyz',
                subdomains: ['a', 'b', 'c'],
                layer: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    minZoom: 0,
                    maxZoom: 30,
                    maxNativeZoom: 19,
                    subdomains: ['a', 'b', 'c']
                })
            }
        ];
    
        if (bingApiKey) {
            providers[2].layer = L.tileLayer.bing({
                minZoom: 0,
                maxZoom: 30,
                maxNativeZoom: 20,
                imagerySet: 'AerialWithLabels',
                bingMapsKey: `${bingApiKey}`
            });
        }
    
        if (googleApiKey) {
            providers[1].layer = L.gridLayer.googleMutant({
                type: 'satellite',
                minZoom : 0,
                maxZoom: 50,
                maxNativeZoom: 22
            });
    
        }
    
        return providers;
    };
    
    
    /**
     *
     * @param {Object}  modelArea
     * @param {Object}  roof
     * @param {boolean} isSubarea
     */
    Renderer.prototype._drawArea = function(modelArea, roof) {
        
        const modulesAnchor = new THREE.Object3D();
        modulesAnchor.userData.type = 'modulesAnchor';
    
        modulesAnchor.position.x = modelArea.axis[1].x;
        modulesAnchor.position.y = -modelArea.axis[1].y;
        modulesAnchor.position.z = modelArea.axis[1].z;
    
        modulesAnchor.rotation.order = 'ZXY';
        modulesAnchor.rotation.z = EZModelUtils.degToRad(modelArea.angle);
        modulesAnchor.rotation.x = EZModelUtils.degToRad(modelArea.inclination);
    
        modulesAnchor.translateX(modelArea.flattenOrigin.x - modelArea.axis[1].x);
        let translation = modelArea.axis[1].y - modelArea.flattenOrigin.y;
        translation /= (Math.sin(EZModelUtils.degToRad(90 - modelArea.inclination)));
        modulesAnchor.translateY(translation);
    
    
        roof.add(modulesAnchor);
        modulesAnchor.updateMatrixWorld();
        this.needsUpdate = true;
    
        if (modelArea.subareas) {
            const areaAzimuth = modelArea.angle;
            modelArea.subareas
                .filter(({isEditing, disabled}) => !isEditing && !disabled)
                .forEach((subarea) => {
                    // this.drawSubarea(subarea, area);
                    this.drawModules(subarea, modulesAnchor, areaAzimuth);
                });
        }
    };
    
    /**
     * Draws area in 3D World
     * @param  {EZModelArea} modelArea - Area Model
     */
    Renderer.prototype.drawArea = function (modelArea) {
        const building = modelArea.getAncestor('EZModelBuilding');
        this.drawBuilding(building);
    };
    
    /**
     * Draws area in 3D World by ID
     * @param  {string} id - Area ID
     */
    Renderer.prototype.drawAreaById = function (id) {
        const modelArea = ez3dScene.findById(id);
        this.drawArea(modelArea);
    };
    
    /**
     * Removes a area from 3d World
     * @param  {EZModelArea} modelArea - Area Model
     */
    Renderer.prototype.removeArea = function (modelArea) {
        const building = modelArea.getAncestor('EZModelBuilding');
        this.removeBuilding(building);
    };
    
    /**
     * Removes a area from 3d World using ID
     * @param  {string} id - Area ID
     */
    Renderer.prototype.removeAreaById = function (id) {
        console.log("sin uso por el momento");
    };
    /**
     * Updates Area in 3D world
     * @param  {EZModelArea} modelArea - Area to be updated
     */
    Renderer.prototype.updateArea = function (modelArea) {
        const building = modelArea.getAncestor('EZModelBuilding');
        this.updateBuilding(building);
    };
    
    /**
     * Updates a area in 3d World using ID
     * @param  {string} id - Area ID
     */
    Renderer.prototype.updateAreaById = function (id) {
        console.log("sin uso por el momento");
    };
    /**
     *
     * @param {EZModelBuilding}  modelBuilding
     * @param {THREE.Object3D}  container
     */
    Renderer.prototype._drawBuilding = function(modelBuilding, container) {
        var building = new THREE.Object3D();
    
        building.userData.type = 'Building';
        building.name = modelBuilding.id;
    
        building.position.x = modelBuilding.path.center.x;
        building.position.y = 0;
        building.position.z = modelBuilding.path.center.y;
    
        building.rotation.x = Math.PI / 2;
    
        var shape = new THREE.Shape();
        var hole = new THREE.Path();
        var firstPoint;
    
        var vertices = [];
        modelBuilding.path.vertices.forEach((vertex, index) => {
            if (index === 0) {
                shape.moveTo(vertex.x, vertex.y);
                hole.moveTo(vertex.x, vertex.y);
                firstPoint = {
                    x: vertex.x,
                    y: vertex.y
                };
            } else {
                shape.lineTo(vertex.x, vertex.y);
                hole.lineTo(vertex.x, vertex.y);
            }
            vertices.push(vertex);
        });
        vertices.push(firstPoint);
    
        shape.holes = hole;
    
        var wallHeight = modelBuilding.height + modelBuilding.ridge.minRoof;
        var wallWidth = 0.001;
    
        building.position.y = modelBuilding.height;
    
        const matOptions = {
            metalness: 0,
            roughness: 1,
        };
    
        modelBuilding.path.edgeDimensions.forEach((obj, index) => {
            var wallGeo = new THREE.BoxGeometry(obj, wallWidth, wallHeight);
    
            matOptions.color = (modelBuilding.color) ? modelBuilding.color
                : ez3dScene.layoutRules.defaultColors.buildingColors.path.standard.fill;
    
            var wallMat = new THREE.MeshStandardMaterial(matOptions);
            wallMat.shadowSide = THREE.FrontSide;
            const wall = new THREE.Mesh(wallGeo, wallMat);
    
            wall.type = 'facade';
            if (modelBuilding.roofs && modelBuilding.roofs[0] &&
                modelBuilding.roofs[0].type === 'pergola') {
                wall.visible = false;
            }
            // Shadows
            wall.receiveShadow = true;
            wall.castShadow = true;
            // Rotation
            wall.rotation.z = EZModelUtils.degToRad(modelBuilding.path.edgeAzimuths[index] - 90);
            // Position
            wall.position.x = (vertices[index].x + vertices[index + 1].x) / 2;
            wall.position.y = (vertices[index].y + vertices[index + 1].y) / 2;
            wall.position.z = modelBuilding.height - (wallHeight / 2);
            wall.translateY(wallWidth / 2);
            // Update
            this.needsUpdate = true;
            building.add(wall);
            wall.updateMatrixWorld();
        });
    
        container.add(building);
        building.updateMatrixWorld();
        this.needsUpdate = true;
    
        if (modelBuilding.roofs) {
            modelBuilding.roofs.forEach((modelRoof) => {
                if (!modelRoof.isEditing && modelRoof.isCreated) {
                    this._drawRoof(modelRoof, building);
                }
            });
        }
    
        if (modelBuilding.keepouts) {
            modelBuilding.keepouts.forEach((modelKeepout) => {
                if (!modelKeepout.isEditing && modelKeepout.isCreated &&
                    !modelKeepout.invisible && modelKeepout.height > 0 &&
                    modelBuilding.roofs[0].isEditing === false) {
                    this._drawKeepout(modelKeepout, building);
                }
            });
        }
    };
    
    /**
     * Gets Building form 3D World
     * @param  {string} id - Building ID
     * @return {THREE.Object3D} building if exist or {null} if not
     */
    Renderer.prototype.getBuildingById = function (id) {
        if (!this.buildings) return null;
        for (let i = 0; i < this.buildings.children.length; i++) {
            if (this.buildings.children[i].name === id) {
                return this.buildings.children[i];
            }
        }
        return null;
    };
    
    /**
     * Draws building in 3D World
     * @param  {EZModelBuilding} modelBuilding - Building Model
     */
    Renderer.prototype.drawBuilding = function (modelBuilding) {
        this._drawBuilding(modelBuilding, this.buildings);
    };
    
    /**
     * Draws building in 3D World by ID
     * @param  {string} id - Building ID
     */
    Renderer.prototype.drawBuildingById = function (id) {
        const modelBuilding = ez3dScene.findById(id);
        this.drawBuilding(modelBuilding);
    };
    
    /**
     * Removes a building from 3d World
     * @param  {THREE.Object3D} building - building to be removed
     */
    Renderer.prototype._removeBuilding = function (building) {
        if (!this.buildings) return;
    
        this.buildings.remove(building);
        this.buildings.needsUpdate = true;
        this.needsUpdate = true;
    };
    
    /**
     * Removes a building from 3d World
     * @param  {EZModelBuilding} modelBuilding - Building Model
     */
    Renderer.prototype.removeBuilding = function (modelBuilding) {
        const building = this.getBuildingById(modelBuilding.id);
        this._removeBuilding(building);
    };
    
    /**
     * Removes a building from 3d World using ID
     * @param  {string} id - Building ID
     */
    Renderer.prototype.removeBuildingById = function (id) {
        const building = this.getBuildingById(id);
        this._removeBuilding(building);
    };
    /**
     * Updates Building in 3D world
     * @param  {EZModelBuilding} modelBuilding - Building to be updated
     */
    Renderer.prototype.updateBuilding = function (modelBuilding) {
        this.removeBuilding(modelBuilding);
        this.drawBuilding(modelBuilding);
    };
    
    /**
     * Updates a building in 3d World using ID
     * @param  {string} id - Building ID
     */
    Renderer.prototype.updateBuildingById = function (id) {
        this.removeBuildingById(id);
        this.drawBuildingById(id);
    };
    
    /* eslint-disable */
    function Keepout(modelKeepout, currentBuilding) {
        THREE.Object3D.call(this);
    
        // var vertices = [];
        var customColor = ez3dScene.layoutRules.defaultColors.keepoutColors.path.standard.fill;
        if (modelKeepout.color) {
            customColor = modelKeepout.color;
        }
        switch (modelKeepout.type) {
            case 'vertical':
                var vector2d = [];
                var minZ = _.min( modelKeepout.path.vertices, function(vertex){
                    return vertex.z;
                }).z;
    
                modelKeepout.path.vertices.forEach(function(vertex) {
                    vector2d.push(new THREE.Vector2(vertex.x, -vertex.y));
                });
    
                var shape = new THREE.Shape(vector2d);
                var extrudeSettings = {depth: minZ, bevelEnabled: false,
                    bevelSegments: 2, steps: 2,
                    bevelSize: 1, bevelThickness: 1};
    
                var geo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    
                var mat = new THREE.MeshStandardMaterial({
                    color: customColor,
                    metalness: 0,
                    roughness: 1
                });
                mat.shadowSide = THREE.FrontSide;
    
                var keepoutMesh = new THREE.Mesh(geo, mat);
    
                this.add(keepoutMesh);
                this.rotation.x = Math.PI;
    
                keepoutMesh.castShadow = true;
                keepoutMesh.receiveShadow = true;
                break;
            case 'inclined':
                var multiareas = modelKeepout.multiAreas;
                multiareas.forEach(function (area) {
                    var keepoutsAnchor = new THREE.Object3D();
                    keepoutsAnchor.type = 'keepoutsAnchor';
                    keepoutsAnchor.position.x = area.axis[1].x;
                    keepoutsAnchor.position.y = -area.axis[1].y;
                    keepoutsAnchor.position.z = area.axis[1].z;
    
                    keepoutsAnchor.rotation.order = 'ZXY';
                    keepoutsAnchor.rotation.z = EZModelUtils.degToRad(area.angle);
                    keepoutsAnchor.rotation.x = EZModelUtils.degToRad(area.inclination);
    
                    keepoutsAnchor.translateX(area.flattenOrigin.x - area.axis[1].x);
                    keepoutsAnchor.translateY(area.axis[1].y - area.flattenOrigin.y);
    
                    keepoutsAnchor.updateMatrixWorld();
    
                    var roof = currentBuilding.getObjectByName( area.parent.id );
    
                    roof.add(keepoutsAnchor);
                    keepoutsAnchor.updateMatrixWorld();
    
                    var path = area.keepouts[modelKeepout.id][0];
    
                    var vector2d = [];
                    path.vertices.forEach(function(vertex) {
                        vector2d.push(new THREE.Vector2(vertex.xF, -vertex.yF));
                    });
                    var shape = new THREE.Shape(vector2d);
                    var extrudeSettings = {depth: modelKeepout.height, bevelEnabled: false,
                        bevelSegments: 2, steps: 2,
                        bevelSize: 1, bevelThickness: 1};
    
                    var geo = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    
                    var mat = new THREE.MeshStandardMaterial({
                        color: customColor,
                        metalness: 0,
                        roughness: 1
                    });
                    mat.shadowSide = THREE.FrontSide;
    
                    var keepoutMesh = new THREE.Mesh(geo, mat);
    
                    keepoutsAnchor.add(keepoutMesh);
                    keepoutMesh.updateMatrixWorld();
    
                    keepoutMesh.castShadow = true;
                    keepoutMesh.receiveShadow = true;
                });
    
                break;
        }
    }
    
    Keepout.prototype = Object.create(THREE.Object3D.prototype);
    
    /**
     * Draws keepout in 3D
     * @param  {EZModelKeepout} modelKeepout - keepout model
     * @param  {THREE.Object3D} building - 3D building 
     */
    Renderer.prototype._drawKeepout = function (modelKeepout, building) {
        const keepout = new Keepout(modelKeepout, building);
        keepout.userData.type = 'Keepout';
        keepout.userData.modelId = modelKeepout.id;
        building.add(keepout);
        keepout.updateMatrixWorld();
        this.needsUpdate = true;
    };
    
    /**
     * Draws keepout in 3D World
     * @param  {EZModelKeepout} modelKeepout - Keepout Model
     */
    Renderer.prototype.drawKeepout = function (modelKeepout) {
        const building = modelKeepout.getAncestor('EZModelBuilding');
        this.drawBuilding(building);
    };
    
    /**
     * Draws keepout in 3D World by ID
     * @param  {string} id - Keepout ID
     */
    Renderer.prototype.drawKeepoutById = function (id) {
        const modelKeepout = ez3dScene.findById(id);
        this.drawKeepout(modelKeepout);
    };
    
    /**
     * Removes a keepout from 3d World
     * @param  {EZModelKeepout} modelKeepout - Keepout Model
     */
    Renderer.prototype.removeKeepout = function (modelKeepout) {
        const building = modelKeepout.getAncestor('EZModelBuilding');
        this.removeBuilding(building);
    };
    
    /**
     * Removes a keepout from 3d World using ID
     * @param  {string} id - Keepout ID
     */
    Renderer.prototype.removeKeepoutById = function (id) {
        console.log("sin uso por el momento");
    };
    /**
     * Updates Keepout in 3D world
     * @param  {EZModelKeepout} modelKeepout - Keepout to be updated
     */
    Renderer.prototype.updateKeepout = function (modelKeepout) {
        const building = modelKeepout.getAncestor('EZModelBuilding');
        this.updateBuilding(building);
    };
    
    /**
     * Updates a keepout in 3d World using ID
     * @param  {string} id - Keepout ID
     */
    Renderer.prototype.updateKeepoutById = function (id) {
        console.log("sin uso por el momento");
    };
    
    Renderer.prototype.initMapSnapshot = function() {
        if (ee.getListeners('Renderer.mapSnapshot.mapReady').length < 1) {
            ee.addOnceListener('Renderer.mapSnapshot.mapReady', this.generateMapSnapshot.bind(this));
        }
    };
    
    Renderer.prototype.generateMapSnapshot = function() {
        const map = new THREE.Object3D();
        map.name = 'snapshotMap';
        this.mapSnapshot = map;
        this.scene.add(this.mapSnapshot);
    
        if (ez3dScene.maps && ez3dScene.layoutRules.scenePreferences.enableSnapshotMap) {
            ez3dScene.maps.forEach((obj) => {
                this.drawMap(obj);
            });
            this.updateMapTextures();
        }
    
        this.mapSnapshot.updateMatrixWorld();
    };
    
    Renderer.prototype.drawMap = function(modelMap) {
        const mat = new THREE.MeshStandardMaterial();
        mat.shadowSide = THREE.FrontSide;
        const mesh = new THREE.Mesh(
            new THREE.PlaneGeometry(modelMap.w, modelMap.h),
            mat
        );
    
        const loader = new THREE.TextureLoader();
        loader.load(modelMap.texture, (texture) => {
            texture.encoding = THREE.GammaEncoding;
            mesh.material.color.setHex(0xffffff);
            mesh.material.map = texture;
            mesh.receiveShadow = true;
            mesh.castShadow = false;
            mesh.material.needsUpdate = true;
        });
    
        mesh.userData.type = 'MapSnapshotTile';
        mesh.userData.modelId = modelMap.id;
        mesh.position.x = modelMap.x + modelMap.w / 2;
        mesh.position.z = modelMap.y + modelMap.h / 2;
        mesh.position.y = 0.01;
        mesh.rotation.x = -Math.PI / 2;
    
        this.mapSnapshot.add(mesh);
    
        this.mapSnapshot.updateMatrixWorld();
    
        this.updateMapTextures();
        this.needsUpdate = true;
    };
    
    Renderer.prototype.updateMapTextures = function() {
        if (this.lastModelScene) {
            this.lastModelScene.maps.forEach((modelMap) => {
                this.mapSnapshot.traverse((o) => {
                    if (o.type === 'MapSnapshotTile' && o.modelId === modelMap.id) {
                        const loader = new THREE.TextureLoader();
                        loader.load(modelMap.texture, (texture) => {
                            const mesh = o;
                            texture.encoding = THREE.GammaEncoding;
                            mesh.material.color.setHex(0xffffff);
                            mesh.material.map = texture;
                            mesh.material.needsUpdate = true;
                            this.needsUpdate = true;
                        });
                    }
                });
            });
        }
    };
    /**
     * Removes map's snapshot
     */
    Renderer.prototype.removeMapSnapshot = function () {
        this.scene.remove(this.mapSnapshot);
        this.needsUpdate = true;
        delete this.mapSnapshot;
    };
    
    Renderer.prototype._drawRoof = function(modelRoof, building) {
        var roof = new THREE.Object3D();
        roof.rotation.x = Math.PI;
        roof.name = modelRoof.id;
        roof.userData.type = 'roof';
        building.add(roof);
        roof.updateMatrixWorld();
    
        var customColor;
        if (modelRoof.parent.color) {
            customColor = modelRoof.parent.color;
        } else {
            customColor = ez3dScene.layoutRules.defaultColors
                .buildingColors.path.roofContext.fill;
        }
    
        const matOptions = {
            // color: 0xA0522D,
            color: customColor,
            metalness: 0,
            roughness: 1,
            // reflectivity: 0,
            // xwireframe: true,
        };
    
        const minRoof = modelRoof.parent.ridge.minRoof;
        modelRoof.geometry.forEach((polygon) => {
            let geo;
            let vertices = polygon.vertices.map((vertex) => {
                return (new THREE.Vector3(vertex.x, -vertex.y, vertex.z));
            });
            if (polygon.type === 'area') {
                geo = new THREE.Geometry();
                geo.vertices = vertices;
                const triangles = THREE.ShapeUtils.triangulateShape(vertices, []);
                for (var i = 0; i < triangles.length; i++) {
                    geo.faces.push(new THREE.Face3(
                        triangles[i][0], triangles[i][1], triangles[i][2]
                    ));
                }
                geo = new THREE.BufferGeometry().fromGeometry(geo);
                geo.computeVertexNormals();
                geo.computeFaceNormals();
            } else if (['facade', 'ramp'].includes(polygon.type)) {
                vertices = polygon.vertices.map((vertex) => {
                    let height = vertex.z;
                    if (height === undefined) {
                        height = (modelRoof.index === 1) ? minRoof
                            : modelRoof.baseHeight[0];
                    }
                    return new THREE.Vector3(vertex.x, -vertex.y, height);
                });
                vertices = EZModelUtils.clearDuplicates(vertices);
                // convexGeometry needs at least 4 points
                if (vertices.length < 3) return;
                if (vertices.length < 4) vertices.push(vertices[0]);
                geo = new THREE.ConvexBufferGeometry(vertices);
            } else {
                return;
            }
            if (geo) {
                geo.computeBoundingBox();
                const face = new THREE.Mesh(geo);
                face.type = polygon.type;
                face.material = (polygon.type === 'area')
                    ? new THREE.MeshStandardMaterial(matOptions)
                    : building.children[0].material;
                face.material.shadowSide = THREE.FrontSide;
                face.castShadow = true;
                face.receiveShadow = true;
                // face.visible = false;
                if (polygon.type === 'area') {
                    this.setUVMap(face.geometry, modelRoof.path.vertices);
                }
                roof.add(face);
                face.updateMatrixWorld();
                this.needsUpdate = true;
            }
        });
    
        if (modelRoof.areas) {
            modelRoof.areas
                .filter(({isEditing, disabled}) => !isEditing && !disabled)
                .forEach((area) => {
                    this._drawArea(area, roof);
                });
        }
    
        if (modelRoof.parent.texture) {
            this.loadRoofTexture(roof, modelRoof);
        }
    };
    
    Renderer.prototype.loadRoofTexture = function (roofMesh, modelRoof) {
        const loader = new THREE.TextureLoader();
        loader.load(modelRoof.parent.texture, (texture) => {
            texture.encoding = THREE.GammaEncoding;
            if (roofMesh.material) {
                roofMesh.material.color.setHex(0xffffff);
                roofMesh.material.map = texture;
                roofMesh.material.needsUpdate = true;
                this.setUVMap(roofMesh.geometry);
            } else {
                roofMesh.children.forEach((face) => {
                    if (face.type === 'area') {
                        face.material.color.setHex(0xffffff);
                        face.material.map = texture;
                        face.material.needsUpdate = true;
                        const roofTypes = ['pent', 'pitched', 'flat'];
                        if (roofTypes.includes(modelRoof.type)) {
                            this.setUVMap(face.geometry);
                        }
                    }
                });
            }
            this.needsUpdate = true;
        });
    };
    
    Renderer.prototype.updateRoofTexture = function(roof) {
        if (!roof.parent.texture) return;
        this.buildings.children.forEach((building) => {
            building.children.forEach((o)=> {
                if (o.userData.type === 'roof' && o.name === roof.id) {
                    this.loadRoofTexture(o, roof);
                }
            });
        });
        this.needsUpdate = true;
    };
    
    
    Renderer.prototype.removeRoofTexture = function(roof) {
        this.scene.traverse((o) => {
            if (o.type === 'roof' && o.modelId === roof.id) {
                var roofMesh = o;
                var customColor = this.lastModelScene.layoutRules.defaultColors.buildingColors.path.roofContext.fill;
                if (roof.parent.color) {
                    customColor = roof.parent.color;
                }
                if (roofMesh.material) {
                    roofMesh.material.color.set(customColor);
                    roofMesh.material.map = null;
                    roofMesh.material.needsUpdate = true;
                } else {
                    roofMesh.children.forEach((face) => {
                        if (face.type === 'area') {
                            face.material.color.set(customColor);
                            face.material.map = null;
                            face.material.needsUpdate = true;
                        }
                    });
                }
            }
        });
        this.needsUpdate = true;
    };
    
    
    /**
     * Draws roof in 3D World
     * @param  {EZModelRoof} modelRoof - Roof Model
     */
    Renderer.prototype.drawRoof = function (modelRoof) {
        const building = modelRoof.getAncestor('EZModelBuilding');
        this.drawBuilding(building);
    };
    
    /**
     * Draws roof in 3D World by ID
     * @param  {string} id - Roof ID
     */
    Renderer.prototype.drawRoofById = function (id) {
        const modelRoof = ez3dScene.findById(id);
        this.drawRoof(modelRoof);
    };
    
    /**
     * Removes a roof from 3d World
     * @param  {EZModelRoof} modelRoof - Roof Model
     */
    Renderer.prototype.removeRoof = function (modelRoof) {
        const building = modelRoof.getAncestor('EZModelBuilding');
        this.removeBuilding(building);
    };
    
    /**
     * Removes a roof from 3d World using ID
     * @param  {string} id - Roof ID
     */
    Renderer.prototype.removeRoofById = function (id) {
        console.log("sin uso por el momento");
    };
    /**
     * Updates Roof in 3D world
     * @param  {EZModelRoof} modelRoof - Roof to be updated
     */
    Renderer.prototype.updateRoof = function (modelRoof) {
        const building = modelRoof.getAncestor('EZModelBuilding');
        this.updateBuilding(building);
    };
    
    /**
     * Updates a roof in 3d World using ID
     * @param  {string} id - Roof ID
     */
    Renderer.prototype.updateRoofById = function (id) {
        console.log("sin uso por el momento");
    };
    
    /**
     *
     * @param {EZObject} modelScene
     */
    Renderer.prototype.drawScene = function(modelScene) {
        var self = this;
        this.lastModelScene = modelScene;
        const newCenter = {
            lat: modelScene.projectCenter.lat,
            lng: modelScene.projectCenter.lng
        };
        this.center = newCenter;
        ee.emitEvent('Renderer.NewProject');
    
        if (this.mainScene) {
            function closure(s) {
                var oldScene = s;
                self.scene.remove(s);
                setTimeout(function() {
                    var startTime = performance.now();
                    self.deepDelete(oldScene);
                    var endTime = performance.now() - startTime;
                }, 1000);
            }
            closure(this.mainScene);
        }
    
        const mainScene = new THREE.Object3D();
        this.mainScene = mainScene;
    
        mainScene.position.x = 0;
        mainScene.position.z = 0;
        this.scene.add(mainScene);
        mainScene.updateMatrixWorld();
        if (modelScene.buildings) {
            this.buildings = new THREE.Object3D();
            mainScene.add(this.buildings);
            modelScene.buildings.forEach((obj) => {
                if (!obj.isEditing && obj.isCreated && obj.style.visible) {
                    this._drawBuilding(obj, this.buildings);
                }
            });
        }
        if (modelScene.trees) {
            this.trees = new THREE.Object3D();
            mainScene.add(this.trees);
            modelScene.trees.forEach((obj) => {
                if (!obj.isEditing /* && obj.isCreated*/) {
                    this._drawTree(obj, this.trees);
                }
            });
        }
    };
    
    
    /**
     *
     */
    Renderer.prototype.removeCurrentScene = function() {
        if (!this.mainScene) {
            return;
        }
    
        this.scene.remove(this.mainScene);
        this.mainScene = null;
        this.buildings = null;
        this.trees = null;
    };
    
    /**
     * Draws Subarea in Area
     * @param  {EZModelSubarea} modelSubarea - subarea model
     * @param  {THREE.Object3D} area - Area in 3d World
     */
    Renderer.prototype._drawSubarea = function(modelSubarea, area) {
        const vertices = [];
        modelSubarea.path.vertices.forEach((vertex, index) => {
            if (modelSubarea.path.edgeDimensions[index] > 0.001) {
                vertices.push(new THREE.Vector3(vertex.x, -vertex.y, vertex.z));
            }
        });
        vertices.reverse();
        const geo = new THREE.Geometry();
        geo.vertices = vertices;
        const triangles = THREE.ShapeUtils.triangulateShape(vertices, []);
        for (var i = 0; i < triangles.length; i++) {
            geo.faces.push(new THREE.Face3(
                triangles[i][0], triangles[i][1], triangles[i][2]
            ));
        }
        // -------------------------------------------------------------------------
        const subarea = new THREE.Mesh(geo);
        subarea.userData.type = 'Subarea';
        subarea.name = subarea.id;
        const matOptions = {
            color: 0x00FFFF,
            metalness: 0,
            roughness: 1,
        };
        subarea.material = new THREE.MeshStandardMaterial(matOptions);
        subarea.material.shadowSide = THREE.FrontSide;
        // subarea.castShadow = true;
        // subarea.receiveShadow = true;
        subarea.visible = false;
    
        area.add(subarea);
        area.updateMatrixWorld();
        this.needsUpdate = true;
        // -------------------------------------------------------------------------
        //
    };
    
    /**
     * Draws subarea in 3D World
     * @param  {EZModelSubarea} modelSubarea - Subarea Model
     */
    Renderer.prototype.drawSubarea = function (modelSubarea) {
        const building = modelSubarea.getAncestor('EZModelBuilding');
        this.drawBuilding(building);
    };
    
    /**
     * Draws subarea in 3D World by ID
     * @param  {string} id - Subarea ID
     */
    Renderer.prototype.drawSubareaById = function (id) {
        const modelSubarea = ez3dScene.findById(id);
        this.drawSubarea(modelSubarea);
    };
    
    /**
     * Removes a subarea from 3d World
     * @param  {EZModelSubarea} modelSubarea - Subarea Model
     */
    Renderer.prototype.removeSubarea = function (modelSubarea) {
        const building = modelSubarea.getAncestor('EZModelBuilding');
        this.removeBuilding(building);
    };
    
    /**
     * Removes a subarea from 3d World using ID
     * @param  {string} id - Subarea ID
     */
    Renderer.prototype.removeSubareaById = function (id) {
        console.log("sin uso por el momento");
    };
    /**
     * Updates Subarea in 3D world
     * @param  {EZModelSubarea} modelSubarea - Subarea to be updated
     */
    Renderer.prototype.updateSubarea = function (modelSubarea) {
        const building = modelSubarea.getAncestor('EZModelBuilding');
        this.updateBuilding(building);
    };
    
    /**
     * Updates a subarea in 3d World using ID
     * @param  {string} id - Subarea ID
     */
    Renderer.prototype.updateSubareaById = function (id) {
        console.log("sin uso por el momento");
    };
    
    /**
     *
     * @param   {number}        color
     * @param   {string}        type
     * @return {THREE.Mesh}    module mesh
     */
    Renderer.prototype.createModuleMesh = function(color, type) {
        var canvas = document.createElement('canvas');
        if (type === 'landscape') {
            canvas.width = 100;
            canvas.height = 60;
        } else {
            canvas.width = 60;
            canvas.height = 100;
        }
        var ctx = canvas.getContext('2d');
    
        ctx.fillStyle = color;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'white';
        for (var i = 10; i < canvas.width; i += 10) {
            ctx.beginPath();
            ctx.moveTo(i, 0);
            ctx.lineTo(i, canvas.height);
            ctx.stroke();
        }
        for (var j = 10; j < canvas.height; j += 10) {
            ctx.beginPath();
            ctx.moveTo(0, j);
            ctx.lineTo(canvas.width, j);
            ctx.stroke();
        }
        ctx.strokeStyle = 'black';
        ctx.lineWidth = 3;
        ctx.strokeRect(0, 0, canvas.width, canvas.height);
    
        var texture = new THREE.Texture(canvas);
        texture.encoding = THREE.GammaEncoding;
        texture.minFilter = THREE.LinearFilter;
        texture.needsUpdate = true;
        const mat = new THREE.MeshStandardMaterial(
            {
                map: texture,
                metalness: 0,
                roughness: 1
            }
        );
        mat.shadowSide = THREE.FrontSide;
        var module = new THREE.Mesh(
            new THREE.BoxGeometry(1, 1, 1),
            mat
            
        );
        if (type === 'landscape') {
            this.landscapeModuleMeshes[color] = module;
        } else {
            this.portraitModuleMeshes[color] = module;
        }
        module.castShadow = module.receiveShadow = true;
        return module;
    };
    
    /**
     *
     * @param {Object}  subareaModel
     * @param {Object}  modulesAnchor
     * @param {number}  areaAzimuth
     */
    Renderer.prototype.drawModules = function(subareaModel, modulesAnchor, areaAzimuth) {
        var self = this;
        if (subareaModel.system) {
            var azimuth = subareaModel.system.azimuth;
    
            var systemModel = subareaModel.system;
            var moduleColor = (systemModel.model.colour) ? systemModel.model.colour : systemModel.color;
    
            if (systemModel.grid && systemModel.grid.rows) {
                var moduleWidth = systemModel.model.width;
                var moduleHeight = systemModel.model.height;
                var moduleLength = systemModel.model.length;
    
                var moduleOpposite = (systemModel.__data.placement === 'landscape') ?
                    moduleWidth : moduleLength;
                moduleOpposite = ez3dScene.utils.getOppositeH(
                    systemModel.__data.inclination, moduleOpposite);
                var positionZ = 0;
    
                systemModel.grid.rows.forEach(function(row, rowIndex) {
                    if (systemModel.__data.sails && systemModel.__data.sails.enabled) {
                        positionZ = moduleOpposite;
                        positionZ *= (rowIndex % systemModel.__data.sails.levels);
                    }
                    row.modules.forEach(function(module) {
                        if (module && module.type === 'module' && !module.isEditing) {
                            var mesh;
                            // placement & module size
                            var placement = false;
                            if (Array.isArray(row.placement) && row.placement[module.index] === 'landscape') {
                                placement = true;
                            } else if (row.placement === 'landscape') {
                                placement = true;
                            }
                            if (placement) {
                                if (self.landscapeModuleMeshes[moduleColor]) {
                                    mesh = self.landscapeModuleMeshes[moduleColor].clone();
                                } else {
                                    mesh = self.createModuleMesh(moduleColor, 'landscape');
                                }
                                mesh.scale.x = moduleLength;
                                mesh.scale.y = moduleWidth;
                                mesh.position.x = moduleLength / 2;
                                mesh.position.y = moduleWidth / 2;
                            } else {
                                if (self.portraitModuleMeshes[moduleColor]) {
                                    mesh = self.portraitModuleMeshes[moduleColor].clone();
                                } else {
                                    mesh = self.createModuleMesh(moduleColor, 'portrait');
                                }
                                mesh.scale.x = moduleWidth;
                                mesh.scale.y = moduleLength;
                                mesh.position.x = moduleWidth / 2;
                                mesh.position.y = moduleLength / 2;
                            }
                            mesh.scale.z = moduleHeight;
                            var moduleOriginPoint = new THREE.Object3D();
    
                            modulesAnchor.add(moduleOriginPoint);
    
                            mesh.position.z = moduleHeight / 2;
                            moduleOriginPoint.position.x = module.x;
                            moduleOriginPoint.position.y = -module.y;
                            moduleOriginPoint.position.z = positionZ;
    
                            moduleOriginPoint.rotation.order = 'ZXY';
                            moduleOriginPoint.rotation.z = EZModelUtils.degToRad((180 - areaAzimuth) - azimuth);
    
    
                            if (row.structure === 'Standard') {
                                moduleOriginPoint.rotation.x = EZModelUtils.degToRad(row.inclination);
                            } else {
                                moduleOriginPoint.translateY(mesh.scale.y);
                                mesh.position.y -= mesh.scale.y;
                                moduleOriginPoint.rotation.x = -EZModelUtils.degToRad(row.inclination);
                            }
                            //
                            moduleOriginPoint.castShadow = true;
                            moduleOriginPoint.receiveShadow = true;
                            moduleOriginPoint.add(mesh);
                            moduleOriginPoint.updateMatrixWorld();
                        }
                    });
                });
            }
        }
        this.needsUpdate = true;
    };
    
    
    /**
     *
     * @param {boolean}    state
     */
    Renderer.prototype.setModulesVisibility = function(state) {
        var self = this;
        this.mainScene.traverse(function(obj) {
            if (obj.userData.type === 'modulesAnchor') {
                obj.visible = state;
                self.needsUpdate = true;
            }
        });
    };
    
    /**
     * Draws system in 3D World
     * @param  {EZModelSystem} modelSystem - System Model
     */
    Renderer.prototype.drawSystem = function (modelSystem) {
        const building = modelSystem.getAncestor('EZModelBuilding');
        this.drawBuilding(building);
    };
    
    /**
     * Draws system in 3D World by ID
     * @param  {string} id - System ID
     */
    Renderer.prototype.drawSystemById = function (id) {
        const modelSystem = ez3dScene.findById(id);
        this.drawSystem(modelSystem);
    };
    
    /**
     * Removes a system from 3d World
     * @param  {EZModelSystem} modelSystem - System Model
     */
    Renderer.prototype.removeSystem = function (modelSystem) {
        const building = modelSystem.getAncestor('EZModelBuilding');
        this.removeBuilding(building);
    };
    
    /**
     * Removes a system from 3d World using ID
     * @param  {string} id - System ID
     */
    Renderer.prototype.removeSystemById = function (id) {
        console.log("sin uso por el momento");
    };
    /**
     * Updates System in 3D world
     * @param  {EZModelSystem} modelSystem - System to be updated
     */
    Renderer.prototype.updateSystem = function (modelSystem) {
        const building = modelSystem.getAncestor('EZModelBuilding');
        this.updateBuilding(building);
    };
    
    /**
     * Updates a system in 3d World using ID
     * @param  {string} id - System ID
     */
    Renderer.prototype.updateSystemById = function (id) {
        console.log("sin uso por el momento");
    };
    
    /**
     *
     * @param {Object}  modelTree
     * @param {Object}  container
     */
    Renderer.prototype._drawTree = function(modelTree, container) {
        var tree = new THREE.Object3D();
        var color = d3.scaleLinear().domain([1, 4, 7, 10, 12])
            .interpolate(d3.interpolateHcl)
            .range([d3.rgb('#ffffff'), d3.rgb('#6DC552'), d3.rgb('#C7ED29'), d3.rgb('#AA5518'), d3.rgb('#ffffff')]);
    
        var color2 = d3.scaleLinear().domain([1, 4, 7, 10, 12])
            .interpolate(d3.interpolateHcl)
            .range([d3.rgb('#164400'), d3.rgb('#339900'), d3.rgb('#7BC10B'), d3.rgb('#656D09'), d3.rgb('#164400')]);
    
        var month = ez3dViewport.ez3dPlayer.getDateTime();
        month = month.getMonth() + 1;
    
        var higherColor = new THREE.Color(color(month));
        var lowerColor = new THREE.Color(color2(month));
    
        // TRUNK
        const mat = new THREE.MeshStandardMaterial({
            color: 0xffffff,
            map: this.barkTexture.clone()
        });
        mat.shadowSide = THREE.FrontSide;
        var trunk = new THREE.Mesh(
            new THREE.CylinderGeometry(
                modelTree.trunkRadius,
                modelTree.trunkRadius,
                modelTree.trunkHeight,
                8, 1),
            mat
        );
        trunk.material.map.needsUpdate = true;
        trunk.position.y = modelTree.trunkHeight / 2;
        trunk.receiveShadow = true;
        trunk.castShadow = true;
    
        // CROWN
        var crown = new THREE.Mesh(
            new THREE.CylinderGeometry(
                0,
                modelTree.trunkRadius,
                modelTree.crownTopHeight - modelTree.trunkHeight,
                8, 4),
            new THREE.MeshStandardMaterial({
                color: higherColor,
                map: this.crownTexture.clone(),
                vertexColors: THREE.VertexColors,
            })
        );
    
        crown.material.map.needsUpdate = true;
        crown.position.y = modelTree.trunkHeight +
            (modelTree.crownTopHeight - modelTree.trunkHeight) / 2;
        crown.receiveShadow = true;
        crown.castShadow = true;
    
        crown.geometry.vertices.forEach(function (vertex, index) {
            if (index > 0 && index <= 8) {
                // Higher Crown
                vertex.x = modelTree.crownHigherRadius *
                    crown.geometry.vertices[index + 24].x / modelTree.trunkRadius;
                vertex.y = modelTree.crownHigherHeight - crown.position.y;
                vertex.z = modelTree.crownHigherRadius *
                    crown.geometry.vertices[index + 24].z / modelTree.trunkRadius;
            } else if (index > 8 && index <= 16) {
                // Middle Crown
                vertex.x = modelTree.crownMiddleRadius *
                    crown.geometry.vertices[index + 16].x / modelTree.trunkRadius;
                vertex.y = modelTree.crownMiddleHeight - crown.position.y;
                vertex.z = modelTree.crownMiddleRadius *
                    crown.geometry.vertices[index + 16].z / modelTree.trunkRadius;
            } else if (index > 16 && index <= 24) {
                // Lower Crown
                vertex.x = modelTree.crownLowerRadius *
                    crown.geometry.vertices[index + 8].x / modelTree.trunkRadius;
                vertex.y = modelTree.crownLowerHeight - crown.position.y;
                vertex.z = modelTree.crownLowerRadius *
                    crown.geometry.vertices[index + 8].z / modelTree.trunkRadius;
            }
        });
    
        crown.geometry.faces.forEach(function (face, index) {
            if (index % 7 === 0) {
                face.vertexColors.push(new THREE.Color(0xffffff));
                face.vertexColors.push(new THREE.Color(0xffffff));
                face.vertexColors.push(new THREE.Color(0xffffff));
            } else if (index % 7 === 1) {
                face.vertexColors.push(new THREE.Color(0xffffff));
                face.vertexColors.push(lowerColor);
                face.vertexColors.push(new THREE.Color(0xffffff));
            } else if (index % 7 === 2) {
                face.vertexColors.push(lowerColor);
                face.vertexColors.push(lowerColor);
                face.vertexColors.push(new THREE.Color(0xffffff));
            } else {
                face.vertexColors.push(lowerColor);
                face.vertexColors.push(lowerColor);
                face.vertexColors.push(lowerColor);
            }
        });
    
        // TREE
        trunk.name = 'Trunk';
        tree.add(trunk);
        crown.name = 'Crown';
        tree.add(crown);
    
        tree.position.x = modelTree.position.x;
        tree.position.z = modelTree.position.y;
    
        tree.userData.type = 'Tree';
        tree.name = modelTree.id;
    
        container.add(tree);
        tree.updateMatrixWorld();
        this.needsUpdate = true;
    };
    
    Renderer.prototype.drawTrees = function(modelScene) {
        var self = this;
        this.lastModelScene = modelScene;
        if (this.mainScene && this.mainScene.trees) {
            function closure(s) {
                var oldScene = s;
                self.scene.remove(s);
                setTimeout(function() {
                    self.deepDelete(oldScene);
                }, 1000);
            }
            closure(this.mainScene.trees);
        }
    
        if (modelScene.trees) {
            var trees = new THREE.Object3D();
            mainScene.add(trees);
            modelScene.trees.forEach(function (obj) {
                if (!obj.isEditing /* && obj.isCreated*/) {
                    self._drawTree(obj, trees);
                }
            });
        }
    };
    
    Renderer.prototype.drawTree = function (modelTree) {
        this._drawTree(modelTree, this.trees);
    };
    
    Renderer.prototype.drawTreeById = function (id) {
        const modelTree = ez3dScene.findById(id);
        this.drawTree(modelTree);
    };
    
    
    Renderer.prototype.getTreeById = function (id) {
        if (!this.trees) return null;
        for (let i = 0; i < this.trees.children.length; i++) {
            if (this.trees.children[i].name === id) {
                return this.trees.children[i];
            }
        }
        return null;
    };
    
    Renderer.prototype.removeTree = function (tree) {
        if (!this.trees) return;
    
        this.trees.remove(tree);
        this.trees.needsUpdate = true;
        this.needsUpdate = true;
    };
    
    Renderer.prototype.removeTreeById = function (id) {
        const tree = this.getTreeById(id);
        this.removeTree(tree);
    };
    
    Renderer.prototype.updateTree = function (modelTree) {
        this.removeTreeById(modelTree.id);
        this.drawTree(modelTree);
    };
    
    Renderer.prototype.updateTreeById = function (id) {
        this.removeTreeById(id);
        this.drawTreeById(id);
    };
    
    /**
     * @class Renderer
     *
     * @param {HTMLElement}     container
     * @param {number}          zoom
     * @param {Object}          center      - Initial center in degrees
     * @param {number}          center.lat
     * @param {number}          center.lng
     */
    function Renderer(container, zoom, center) {
        var self = this;
        var loader = new THREE.ObjectLoader();
        this.landscapeModuleMesh = new THREE.Mesh(
            new THREE.BoxGeometry(1, 1, 1),
            new THREE.MeshStandardMaterial({color: 0x0000ff,
                metalness: 0,
                roughness: 1
            })
        );
        this.timezone = {};
        this.drawTextures = true;
        this.portraitModuleMesh = this.landscapeModuleMesh.clone();
        this.landscapeModuleMeshes = {};
        this.portraitModuleMeshes = {};
    
        var textureLoader = new THREE.TextureLoader();
        this.barkTexture = textureLoader.load(EZModelUtils.getBase64Image('barkTexture'));
        this.barkTexture.encoding = THREE.GammaEncoding;
        this.crownTexture = textureLoader.load(EZModelUtils.getBase64Image('crownTexture'));
        this.crownTexture.encoding = THREE.GammaEncoding;
    
        this.zoom = zoom;
        this.centerLatLng = center;
        this.centerMeters = {x: 0, y: 0};
        this.mode = '2d';
        this.viewport = null;
    
        this.inMouseEvent = false;
        this.needsUpdate = false;
    
        var scene = new THREE.Scene();
        scene.autoUpdate = false;
        this.scene = scene;
    
        var renderer = new (THREE.WebGLRenderer)({
            preserveDrawingBuffer: true,
            antialias: true,
            alpha: true
        });
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.outputEncoding = THREE.GammaEncoding;
    
        renderer.domElement.style.zIndex = 1;
        renderer.domElement.style.position = 'absolute';
        renderer.domElement.style.top = '0px';
    
        container.append(renderer.domElement);
        this.domElement = renderer.domElement;
    
    
        var cameras = {
            orthographic: new THREE.OrthographicCamera(0, 0, 0, 0, -4096, 4096),
            perspective: new THREE.PerspectiveCamera(45, 0, 0.5, 524288)
        };
        this.cameras = cameras;
    
        cameras.orthographic.rotation.x = THREE.Math.degToRad(-90);
    
        cameras.perspective.position.x = 0;
        cameras.perspective.position.y = -cameras.orthographic.position.y;
        cameras.perspective.position.z = 200;
    
        var perspectiveSphere = new THREE.Object3D();
        var perspectiveAnchor = new THREE.Object3D();
        perspectiveAnchor.name = 'perspectiveAnchor';
        this.perspectiveAnchor = perspectiveAnchor;
        perspectiveAnchor.rotation.order = 'YXZ';
        this.perspectiveSphere = perspectiveSphere;
        perspectiveSphere.rotation.order = 'YXZ';
    
        var sunAnchor = new THREE.Gyroscope();
        sunAnchor.name = 'sunAnchor';
        this.sunAnchor = sunAnchor;
        sunAnchor.rotation.order = 'YXZ';
        sunAnchor.rotation.x = -cameras.orthographic.rotation.x;
        sunAnchor.position.y = -cameras.orthographic.position.y;
    
        scene.add(perspectiveAnchor);
        cameras.orthographic.add(sunAnchor);
        sunAnchor.updateMatrixWorld();
    
        perspectiveSphere.add(cameras.perspective);
        perspectiveAnchor.add(perspectiveSphere);
    
        perspectiveSphere.rotation.x = -Math.PI / 4;
        scene.add(cameras.orthographic);
    
        cameras.orthographic.updateMatrixWorld();
    
        this.cameras = cameras;
        _resize();
    
        var hemiLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 0.6);
        hemiLight.color.setHSL(0.6, 1, 0.6);
        hemiLight.groundColor.setHSL(0.095, 1, 0.75);
        hemiLight.position.set(0, 500, 0);
        scene.add(hemiLight);
        hemiLight.updateMatrixWorld();
    
        var light = new THREE.DirectionalLight(0xffffff, 1);
        light.color.setHSL(0.1, 1, 0.95);
        light.castShadow = true;
    
        var cameraSize = 150;
        light.position.set(0, 0, 450);
    
        light.shadow.mapSize.width = 8192;
        light.shadow.mapSize.height = 8192;
        light.shadow.camera.near = 1;
        light.shadow.camera.far = 1000;
        light.shadow.camera.top = cameraSize;
        light.shadow.camera.bottom = -cameraSize;
        light.shadow.camera.left = -cameraSize;
        light.shadow.camera.right = cameraSize;
    
        light.shadow.bias = -0.0001;
    
        textureLoader.load(EZModelUtils.getBase64Image('lensflare0'), function (textureFlare0) {
            textureFlare0.encoding = THREE.GammaEncoding;
            textureLoader.load(EZModelUtils.getBase64Image('lensflare2'), function (textureFlare2) {
                textureFlare2.encoding = THREE.GammaEncoding;
                textureLoader.load(EZModelUtils.getBase64Image('hexangle'), function (textureHexangle) {
                    textureHexangle.encoding = THREE.GammaEncoding;
                    const h = 0.14;
                    const s = 0.9;
                    const l = 0.5;
    
    
    
                    const flareColor = new THREE.Color(0xffffff);
                    flareColor.setHSL(h, s, l + 0.5);
    
                    const lensFlare = new THREE.Lensflare();
                    lensFlare.addElement( new THREE.LensflareElement( textureFlare0, 1500, 0 ) );
                    // lensFlare.addElement( new THREE.LensflareElement( textureFlare2, 512, 0.0 ) );
                    // lensFlare.addElement( new THREE.LensflareElement( textureFlare2, 512, 0.0 ) );
                    lensFlare.addElement( new THREE.LensflareElement( textureFlare2, 512, 0.0 ) );
                    lensFlare.addElement( new THREE.LensflareElement( textureHexangle, 60, 0.6 ) );
                    lensFlare.addElement( new THREE.LensflareElement( textureHexangle, 70, 0.7 ) );
                    lensFlare.addElement( new THREE.LensflareElement( textureHexangle, 120, 0.9 ) );
                    lensFlare.addElement( new THREE.LensflareElement( textureHexangle, 70, 1 ) );
    
                    
                    lensFlare.customUpdateCallback = function (object) {
                        var f = 0;
                        var fl = object.lensFlares.length;
                        var flare;
                        var vecX = -object.positionScreen.x * 2;
                        var vecY = -object.positionScreen.y * 2;
                        for (f = 0; f < fl; f++) {
                            flare = object.lensFlares[f];
                            flare.x = object.positionScreen.x + vecX * flare.distance;
                            flare.y = object.positionScreen.y + vecY * flare.distance;
                            flare.rotation = 0;
                        }
    
                        object.lensFlares[2].y += 0.025;
                        object.lensFlares[3].rotation = object.positionScreen.x * 0.5 + 0.79;
                    };
    
                    lensFlare.position.z = 45000;
    
                    sunAnchor.add(lensFlare);
                    lensFlare.updateMatrixWorld();
                });
            });
        });
    
    
    
        loader.parse(this.skyBoxJSON, function(loadedScene) {
            loadedScene.traverse((object) => {
                if (object.name === 'skyBox' && object instanceof THREE.Mesh) {
                    const scale = 50000;
                    object.scale.x = scale;
                    object.scale.y = scale;
                    object.scale.z = scale;
                    
                    object.material = new THREE.MeshBasicMaterial({
                        map: object.material.map
                    });
                    object.material.map.encoding = THREE.GammaEncoding;
    
                    object.rotateX(-Math.PI / 2);
                    object.translateZ(-500);
                    object.material.fog = false;
                    
                    cameras.orthographic.add(object);
                    object.updateMatrixWorld();
                }
            });
        });
    
        sunAnchor.add(light);
        light.target = sunAnchor;
        light.updateMatrixWorld();
        sunAnchor.rotation.x = -Math.PI / 32;
        sunAnchor.updateMatrixWorld();
    
        this.needsUpdate = true;
    
        var bigGround = new THREE.Mesh(
            new THREE.CubeGeometry(50000, 50000, 0.1, 8, 8),
            new THREE.MeshLambertMaterial({
                // color: 0x93862c,
                color: 0xd3d3d3,
                side: THREE.DoubleSide
            })
        );
        bigGround.name = 'bigGround';
        bigGround.receiveShadow = true;
        bigGround.castShadow = true;
    
        this.bigGround = bigGround;
    
        this.bigGround.position.x = 0;
        this.bigGround.position.y = -this.perspectiveAnchor.position.y - 0.3;
        this.bigGround.position.z = 0;
    
        this.bigGround.rotation.x = -Math.PI / 2;
        this.scene.add(this.bigGround);
        this.bigGround.updateMatrixWorld();
        this.perspectiveRotationX = this.perspectiveSphere.rotation.x;
        this.perspectiveRotationY = this.perspectiveAnchor.rotation.y;
        this.rotatePerspective(0, 0);
    
        // Save default cams and anchors positions and rotations to reset in the future
        this.defaultCamsAndAnchors = {
            perspectiveCam: {
                position: this.cameras.perspective.position.clone(),
                rotation: this.cameras.perspective.rotation.clone(),
            },
            orthographicCam: {
                position: this.cameras.orthographic.position.clone(),
                rotation: this.cameras.orthographic.rotation.clone()
            },
            perspectiveSphere: {
                position: this.perspectiveSphere.position.clone(),
                rotation: this.perspectiveSphere.rotation.clone()
            },
            perspectiveAnchor: {
                position: this.perspectiveAnchor.position.clone(),
                rotation: this.perspectiveAnchor.rotation.clone()
            }
        };
    
        function _resize() {
            var rect = renderer.domElement.parentNode.getBoundingClientRect();
    
            var viewport = {
                width: rect.width,
                height: rect.height,
                top: rect.top,
                left: rect.left
            };
    
            renderer.setSize(viewport.width, viewport.height);
    
    
            cameras.perspective.aspect = viewport.width / viewport.height;
            cameras.perspective.updateProjectionMatrix();
    
            cameras.orthographic.left = viewport.width / -2;
            cameras.orthographic.right = viewport.width / 2;
            cameras.orthographic.top = viewport.height / 2;
            cameras.orthographic.bottom = viewport.height / -2;
            cameras.orthographic.updateProjectionMatrix();
    
            self.viewport = viewport;
    
            self.needsUpdate = true;
    
            ee.emitEvent('Renderer.Resize', [viewport]);
        }
    
        this.ro = new ResizeObserver(_resize);
        this.ro.observe(container);
    
        this.render = function () {
            requestAnimationFrame(this.render.bind(this));
    
            if (!this.needsUpdate) {
                return;
            }
    
            switch (this.mode) {
                case '2d':
                    renderer.render(scene, cameras.orthographic);
                    this.needsUpdate = false;
                    break;
                case '3d':
                    renderer.render(scene, cameras.perspective);
                    this.needsUpdate = false;
                    break;
                case 'showcase':
                    renderer.render(scene, cameras.perspective);
                    this.needsUpdate = false;
                    break;
            }
        };
    
        this.mainScene = new THREE.Object3D();
        this.buildings = new THREE.Object3D();
        this.mainScene.add(this.buildings);
        this.trees = new THREE.Object3D();
        this.mainScene.add(this.trees);
    
        this.scene.add(this.mainScene);
    
        this.render();
        this.setZoom(zoom);
        this.mapperAnchor = new THREE.Object3D();
        this.mapperAnchor.name = 'mapperAnchor';
    
        scene.add(this.mapperAnchor);
        this.mapperAnchor.updateMatrixWorld();
    
        ee.addListener('setActive_Finished', function(attr) {
            if (ez3dScene.layoutRules.scenePreferences.activeRenderer === true && attr[2]) {
                const modelId = attr[2].id;
                const building = self.getBuildingById(modelId);
                if (building) {
                    perspectiveAnchor.position.x = building.position.x;
                    perspectiveAnchor.position.y = building.position.y;
                    perspectiveAnchor.position.z = building.position.z;
                    perspectiveAnchor.updateMatrixWorld();
                    bigGround.position.y = -perspectiveAnchor.position.y - 0.3;
                    bigGround.updateMatrixWorld();
                    self.needsUpdate = true;
                }
            }
        });
    }
    
    /**
     * [resetCamsAndAnchors description]
     */
    Renderer.prototype.resetCamsAndAnchors = function() {
        const {
            perspectiveCam,
            orthographicCam,
            perspectiveSphere,
            perspectiveAnchor
        } = this.defaultCamsAndAnchors;
        // cameras.perspective
        this.cameras.perspective.position.set(
            perspectiveCam.position.x,
            perspectiveCam.position.y,
            perspectiveCam.position.z);
        this.cameras.perspective.rotation.set(
            perspectiveCam.rotation.x,
            perspectiveCam.rotation.y,
            perspectiveCam.rotation.z);
        // cameras.orthographic
        this.cameras.orthographic.position.set(
            orthographicCam.position.x,
            orthographicCam.position.y,
            orthographicCam.position.z);
        this.cameras.orthographic.rotation.set(
            orthographicCam.rotation.x,
            orthographicCam.rotation.y,
            orthographicCam.rotation.z);
        // perspectiveSphere
        this.perspectiveSphere.position.set(
            perspectiveSphere.position.x,
            perspectiveSphere.position.y,
            perspectiveSphere.position.z);
        this.perspectiveSphere.rotation.set(
            perspectiveSphere.rotation.x,
            perspectiveSphere.rotation.y,
            perspectiveSphere.rotation.z);
        // perspectiveAnchor
        this.perspectiveAnchor.position.set(
            perspectiveAnchor.position.x,
            perspectiveAnchor.position.y,
            perspectiveAnchor.position.z);
        this.perspectiveAnchor.rotation.set(
            perspectiveAnchor.rotation.x,
            perspectiveAnchor.rotation.y,
            perspectiveAnchor.rotation.z);
    
        this.cameras.perspective.updateMatrixWorld();
        this.cameras.orthographic.updateMatrixWorld();
        this.perspectiveSphere.updateMatrixWorld();
        this.perspectiveAnchor.updateMatrixWorld();
    
        this.needsUpdate = true;
    };
    
    /**
     * [setVisible description]
     */
    Renderer.prototype.setVisible = function(visible) {
        this.isVisible = visible;
        if (this.mainScene) {
            this.mainScene.visible = visible;
        }
        this.needsUpdate = true;
    };
    
    /**
     * [translatePerspective description]
     *
     * @param  {Object} center [description]
     */
    Renderer.prototype.translatePerspective = function(center) {
        this.perspectiveAnchor.translateX(-center.x);
        this.perspectiveAnchor.translateZ(-center.y);
        this.perspectiveAnchor.updateMatrixWorld();
    
        this.cameras.orthographic.translateX(-center.x);
        this.cameras.orthographic.translateZ(-center.y);
        this.cameras.orthographic.updateMatrixWorld();
    
        this.needsUpdate = true;
    };
    
    /**
     *
     * @param {number} rotationX
     * @param {number} rotationZ
     */
    Renderer.prototype.rotatePerspective = function(rotationX, rotationZ) {
        var prevRotation = this.perspectiveSphere.rotation.x;
        this.perspectiveSphere.rotation.x = this.perspectiveRotationX + rotationX;
        this.perspectiveSphere.updateMatrixWorld();
        this.perspectiveAnchor.rotation.y = this.perspectiveRotationY + rotationZ;
        if (this.perspectiveSphere.rotation.x <= -Math.PI / 2) {
            this.perspectiveSphere.rotation.x = -Math.PI / 2;
        }
        var perspectiveCamPosition = new THREE.Vector3().setFromMatrixPosition(this.cameras.perspective.matrixWorld);
        if (perspectiveCamPosition.y <= 0) {
            this.perspectiveSphere.rotation.x = prevRotation;
        }
    
        this.perspectiveAnchor.updateMatrixWorld();
        this.needsUpdate = true;
    };
    
    
    /**
     *
     * @param {number} zoom
     */
    Renderer.prototype.setZoom = function(zoom) {
        this.zoom = zoom;
        if (this.mode === '2d') {
            this.cameras.orthographic.zoom = zoom;
            this.cameras.orthographic.updateProjectionMatrix();
        }
        if (this.mode === '3d' || this.mode === 'showcase') {
            var prevPosition = this.cameras.perspective.position.z;
            this.cameras.perspective.position.z = 10000 / zoom;
            this.cameras.perspective.updateMatrixWorld();
            var perspectiveCamPosition = new THREE.Vector3().setFromMatrixPosition(this.cameras.perspective.matrixWorld);
            if (perspectiveCamPosition.y <= 0) {
                this.cameras.perspective.position.z = prevPosition;
                this.cameras.perspective.updateMatrixWorld();
            }
        }
        this.needsUpdate = true;
    };
    
    /**
     *
     * @param {Object}  position
     * @param {number}  position.x
     * @param {number}  position.y
     */
    Renderer.prototype.setCenter = function(position) {
        var prevCenter = {
            x: this.centerMeters.x,
            y: this.centerMeters.y
        };
    
        this.cameras.orthographic.position.x = position.x;
        this.cameras.orthographic.position.z = position.y;
        this.perspectiveAnchor.position.x = position.x;
        this.perspectiveAnchor.position.z = position.y;
        this.perspectiveAnchor.updateMatrixWorld();
        this.cameras.orthographic.updateMatrixWorld();
    
        this.needsUpdate = true;
    };
    
    /**
     *
     * @param {Object}  position
     * @param {number}  position.x
     * @param {number}  position.y
     */
    Renderer.prototype.setCenterAxis = function(position) {
        var camRotation = this.cameras.orthographic.rotation.x;
        this.cameras.orthographic.rotation.set(0, 0, 0);
        this.cameras.orthographic.rotation.y = this.perspectiveAnchor.rotation.y;
        this.cameras.orthographic.translateX(position.x);
        this.cameras.orthographic.rotation.x = camRotation;
    
        this.cameras.orthographic.updateMatrixWorld();
        this.needsUpdate = true;
    };
    
    /**
     *
     */
    Renderer.prototype.set2d = function() {
        ee.emitEvent('Renderer.Resize', [this.viewport]);
        this.mode = '2d';
        this.needsUpdate = true;
        console.log('set 2d');
    };
    
    /**
     *
     */
    Renderer.prototype.set3d = function() {
        ee.emitEvent('Renderer.Resize', [this.viewport]);
        this.mode = '3d';
        this.needsUpdate = true;
        console.log('set 3d');
    };
    
    /**
     *
     */
    Renderer.prototype.setShowcase = function() {
        this.mode = 'showcase';
        this.needsUpdate = true;
        console.log('set showcase');
    };
    
    /**
     *
     * @param {Date}                datetime
     * @param {Object}              degrees
     * @param {number}              degrees.lat
     * @param {number}              degrees.lng
     */
    Renderer.prototype.setDateTime = function(datetime, degrees) {
        const browserTimeZoneOffset = datetime.getTimezoneOffset();
        const offsetDatetime = new Date(datetime.getTime() - this.timezone.offset * 1000 - browserTimeZoneOffset * 60 * 1000);
        var obj = SunCalc.getPosition(offsetDatetime, degrees.lat, degrees.lng);
        this.sunAnchor.rotation.order = 'YXZ';
        this.sunAnchor.rotation.y = -obj.azimuth;
        this.sunAnchor.rotation.x = -obj.altitude;
        this.sunAnchor.updateMatrixWorld();
        this.needsUpdate = true;
    };
    
    
    /**
     *
     * @param {Object}  obj
     */
    Renderer.prototype.deepDelete = function(obj) {
        function remove(o) {
            if (o.geometry) {
                o.geometry.dispose();
            }
            if (o.material) {
                o.material.dispose();
            }
            o = null;
        }
    
        for (var i = obj.children.length - 1; i > -1; i--) {
            var item = obj.children[i];
            this.deepDelete(item);
            obj.remove(item);
            remove(item);
        }
    };
    
    /**
     *
     */
    Renderer.prototype.lookToNorth = function() {
        this.rotatePerspective(0, -this.perspectiveRotationY);
    };
    
    
    
    Renderer.prototype.updateTexture = function() {
        if (this.lastModelScene) {
            this.lastModelScene.active.roofs.forEach((obj) => {
                this.updateRoofTexture(obj);
            });
        }
    };
    
    Renderer.prototype.updateTextures = function() {
        if (this.lastModelScene) {
            this.lastModelScene.buildings.forEach((building) => {
                building.roofs.forEach((roof) => {
                    this.updateRoofTexture(roof);
                });
            });
        }
    };
    
    Renderer.prototype.removeTexture = function() {
        this.lastModelScene.active.roofs.forEach((roof) => {
            this.removeRoofTexture(roof);
        });
    };
    
    Renderer.prototype.removeTextures = function() {
        var self = this;
        self.lastModelScene.buildings.forEach((building) => {
            building.roofs.forEach((roof) => {
                this.removeRoofTexture(roof);
            });
        });
    };
    
    Renderer.prototype.setUVMap = function(geo, path) {
        let max;
        let min;
        if (path) {
            const mapX = path.map((vertex) => vertex.x);
            const mapY = path.map((vertex) => vertex.y);
            max = {x: _.max(mapX), y: _.max(mapY)};
            min = {x: _.min(mapX), y: _.min(mapY)};
        } else {
            geo.computeBoundingBox();
            max = geo.boundingBox.max;
            min = geo.boundingBox.min;
        }
    
        var offset = new THREE.Vector2(0 - min.x, 0 - min.y);
        var range = new THREE.Vector2(max.x - min.x, max.y - min.y);
    
        var vertices = geo.attributes.position.array;
        var uv = new Float32Array(geo.attributes.position.count * 2);
    
        var uvPosition = 0;
        for (var i = 0; i < vertices.length; i += 3) {
            var vertex = {x: vertices[i], y: vertices[i + 1]};
            var u = (vertex.x + offset.x) / range.x;
            var v = (vertex.y + offset.y) / range.y;
            uv[uvPosition] = u; uv[uvPosition + 1] = v;
            uvPosition += 2;
        }
        // console.log('uv',uv);
        geo.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
        geo.uvsNeedUpdate = true;
    };
    
    /**
     * @param {array} geoMatrix
     * @param {THREE.Texture} texture
     */
    Renderer.prototype.texturedPolygon = function(geoMatrix, texture) {
        let vertices = [];
        geoMatrix.forEach(function(obj) {
            vertices.push(new THREE.Vector3(obj.x, -obj.y, obj.z));
        });
    
        let geo = new THREE.Geometry();
        geo.vertices = vertices;
        const triangles = THREE.ShapeUtils.triangulateShape(vertices, []);
        for (let i = 0; i < triangles.length; i++) {
            geo.faces.push(new THREE.Face3(triangles[i][0], triangles[i][1], triangles[i][2]));
        }
        let bufferGeo = new THREE.BufferGeometry();
        bufferGeo.fromGeometry(geo);
        this.setUVMap(bufferGeo);
    
    
        const mat = new THREE.MeshStandardMaterial({
            color: 0x000000,
            metalness: 0,
            roughness: 1,
            map: texture
        });
    
        mat.shadowSide = THREE.FrontSide;
    
        const polygon = new THREE.Mesh(bufferGeo, mat);
    
        return polygon;
    };
    
    /* eslint-disable */
    Renderer.prototype.skyBoxJSON = {
        "metadata":{
            "type":"Object",
            "generator":"io_three",
            "sourceFile":"SkyBox.blend",
            "version":4.4
        },
        "object":{
            "type":"Scene",
            "children":[{
                "name":"skyBox",
                "uuid":"15D9782A-CB65-3FD5-8AA0-035A0E517EDE",
                "matrix":[-1,0,0,0,0,0,1,0,0,1,0,0,0,0,0,1],
                "visible":true,
                "type":"Mesh",
                "material":"3082B3E8-1661-32C2-92CF-F87CF7E9E316",
                "castShadow":true,
                "receiveShadow":true,
                "geometry":"61AA7CA8-89D8-345D-8556-3D4871424200"
            }],
            "matrix":[1,0,0,0,0,1,0,0,0,0,1,0,0,0,0,1],
            "uuid":"62F76249-9A69-4F37-A9FA-EB577640D647"
        },
        "animations":[{
            "tracks":[],
            "fps":24,
            "name":"default"
        }],
        "materials":[{
            "emissive":0,
            "vertexColors":0,
            "map":"63CCEB68-2D71-3AC9-A63E-07B41824E535",
            "depthWrite":true,
            "name":"SkyBox",
            "type":"MeshLambertMaterial",
            "depthTest":true,
            "color":10724259,
            "uuid":"3082B3E8-1661-32C2-92CF-F87CF7E9E316",
            "blending":"NormalBlending"
        }],
        "geometries":[{
            "data":{
                "metadata":{
                    "generator":"io_three",
                    "normals":257,
                    "version":3,
                    "faces":256,
                    "vertices":257,
                    "uvs":1
                },
                "faces":[41,2,10,9,1,0,1,2,3,0,1,2,3,41,3,11,10,2,4,5,1,0,4,5,1,0,41,4,12,11,3,6,7,5,4,6,7,5,4,41,5,13,12,4,8,9,7,6,8,9,7,6,41,6,14,13,5,10,11,9,8,10,11,9,8,41,7,15,14,6,12,13,11,10,12,13,11,10,41,8,0,1,9,14,15,3,2,14,15,3,2,41,9,17,16,8,2,16,17,14,2,16,17,14,41,10,18,17,9,1,18,16,2,1,18,16,2,41,11,19,18,10,5,19,18,1,5,19,18,1,41,12,20,19,11,7,20,19,5,7,20,19,5,41,13,21,20,12,9,21,20,7,9,21,20,7,41,14,22,21,13,11,22,21,9,11,22,21,9,41,15,23,22,14,13,23,22,11,13,23,22,11,41,21,29,28,20,21,24,25,20,21,24,25,20,41,22,30,29,21,22,26,24,21,22,26,24,21,41,23,31,30,22,23,27,26,22,23,27,26,22,41,17,25,24,16,16,28,29,17,16,28,29,17,41,18,26,25,17,18,30,28,16,18,30,28,16,41,19,27,26,18,19,31,30,18,19,31,30,18,41,20,28,27,19,20,25,31,19,20,25,31,19,41,26,34,33,25,30,32,33,28,30,32,33,28,41,27,35,34,26,31,34,32,30,31,34,32,30,41,28,36,35,27,25,35,34,31,25,35,34,31,41,29,37,36,28,24,36,35,25,24,36,35,25,41,30,38,37,29,26,37,36,24,26,37,36,24,41,31,39,38,30,27,38,37,26,27,38,37,26,41,25,33,32,24,28,33,39,29,28,33,39,29,41,38,46,45,37,37,40,41,36,37,40,41,36,41,39,47,46,38,38,42,40,37,38,42,40,37,41,33,41,40,32,33,43,44,39,33,43,44,39,41,34,42,41,33,32,45,43,33,32,45,43,33,41,35,43,42,34,34,46,45,32,34,46,45,32,41,36,44,43,35,35,47,46,34,35,47,46,34,41,37,45,44,36,36,41,47,35,36,41,47,35,41,42,50,49,41,45,48,49,43,45,48,49,43,41,43,51,50,42,46,50,48,45,46,50,48,45,41,44,52,51,43,47,51,50,46,47,51,50,46,41,45,53,52,44,41,52,51,47,41,52,51,47,41,46,54,53,45,40,53,52,41,40,53,52,41,41,47,55,54,46,42,54,53,40,42,54,53,40,41,41,49,48,40,43,49,55,44,43,49,55,44,41,54,62,61,53,53,56,57,52,53,56,57,52,41,55,63,62,54,54,58,56,53,54,58,56,53,41,49,57,56,48,49,59,60,55,49,59,60,55,41,50,58,57,49,48,61,59,49,48,61,59,49,41,51,59,58,50,50,62,61,48,50,62,61,48,41,52,60,59,51,51,63,62,50,51,63,62,50,41,53,61,60,52,52,57,63,51,52,57,63,51,41,59,67,66,58,64,65,66,67,62,64,65,61,41,60,68,67,59,68,69,65,64,63,66,64,62,41,61,69,68,60,70,71,69,68,57,67,66,63,41,62,70,69,61,72,73,71,70,56,68,67,57,41,63,71,70,62,74,75,73,72,58,69,68,56,41,57,65,64,56,76,77,78,79,59,70,71,60,41,58,66,65,57,67,66,77,76,61,65,70,59,41,71,79,78,70,75,80,81,73,69,72,73,68,41,65,73,72,64,77,82,83,78,70,74,75,71,41,66,74,73,65,66,84,82,77,65,76,74,70,41,67,75,74,66,65,85,84,66,64,77,76,65,41,68,76,75,67,69,86,85,65,66,78,77,64,41,69,77,76,68,71,87,86,69,67,79,78,66,41,70,78,77,69,73,81,87,71,68,73,79,67,41,75,83,82,74,85,88,89,84,77,80,81,76,41,76,84,83,75,86,90,88,85,78,82,80,77,41,77,85,84,76,87,91,90,86,79,83,82,78,41,78,86,85,77,81,92,91,87,73,84,83,79,41,79,87,86,78,80,93,92,81,72,85,84,73,41,73,81,80,72,82,94,95,83,74,86,87,75,41,74,82,81,73,84,89,94,82,76,81,86,74,41,87,95,94,86,93,96,97,92,85,88,89,84,41,81,89,88,80,94,98,99,95,86,90,91,87,41,82,90,89,81,89,100,98,94,81,92,90,86,41,83,91,90,82,88,101,100,89,80,93,92,81,41,84,92,91,83,90,102,101,88,82,94,93,80,41,85,93,92,84,91,103,102,90,83,95,94,82,41,86,94,93,85,92,97,103,91,84,89,95,83,41,92,100,99,91,102,104,105,101,94,96,97,93,41,93,101,100,92,103,106,104,102,95,98,96,94,41,94,102,101,93,97,107,106,103,89,99,98,95,41,95,103,102,94,96,108,107,97,88,100,99,89,41,89,97,96,88,98,109,110,99,90,101,102,91,41,90,98,97,89,100,111,109,98,92,103,101,90,41,91,99,98,90,101,105,111,100,93,97,103,92,41,97,105,104,96,109,112,113,110,101,104,105,102,41,98,106,105,97,111,114,112,109,103,106,104,101,41,99,107,106,98,105,115,114,111,97,107,106,103,41,100,108,107,99,104,116,115,105,96,108,107,97,41,101,109,108,100,106,117,116,104,98,109,108,96,41,102,110,109,101,107,118,117,106,99,110,109,98,41,103,111,110,102,108,119,118,107,100,111,110,99,41,109,117,116,108,117,120,121,116,109,112,113,108,41,110,118,117,109,118,122,120,117,110,114,112,109,41,111,119,118,110,119,123,122,118,111,115,114,110,41,105,113,112,104,112,124,125,113,104,116,117,105,41,106,114,113,105,114,126,124,112,106,118,116,104,41,107,115,114,106,115,127,126,114,107,119,118,106,41,108,116,115,107,116,121,127,115,108,113,119,107,41,113,121,120,112,124,128,129,125,116,120,121,117,41,114,122,121,113,126,130,128,124,118,122,120,116,41,115,123,122,114,127,131,130,126,119,123,122,118,41,116,124,123,115,121,132,131,127,113,124,123,119,41,117,125,124,116,120,133,132,121,112,125,124,113,41,118,126,125,117,122,134,133,120,114,126,125,112,41,119,127,126,118,123,135,134,122,115,127,126,114,41,125,133,132,124,133,136,137,132,125,128,129,124,41,126,134,133,125,134,138,136,133,126,130,128,125,41,127,135,134,126,135,139,138,134,127,131,130,126,41,121,129,128,120,128,140,141,129,120,132,133,121,41,122,130,129,121,130,142,140,128,122,134,132,120,41,123,131,130,122,131,143,142,130,123,135,134,122,41,124,132,131,123,132,137,143,131,124,129,135,123,41,130,138,137,129,142,144,145,140,134,136,137,132,41,131,139,138,130,143,146,144,142,135,138,136,134,41,132,140,139,131,137,147,146,143,129,139,138,135,41,133,141,140,132,136,148,147,137,128,140,139,129,41,134,142,141,133,138,149,148,136,130,141,140,128,41,135,143,142,134,139,150,149,138,131,142,141,130,41,129,137,136,128,140,145,151,141,132,137,143,133,41,142,150,149,141,149,152,153,148,141,144,145,140,41,143,151,150,142,150,154,152,149,142,146,144,141,41,137,145,144,136,145,155,156,151,137,147,148,143,41,138,146,145,137,144,157,155,145,136,149,147,137,41,139,147,146,138,146,158,157,144,138,150,149,136,41,140,148,147,139,147,159,158,146,139,151,150,138,41,141,149,148,140,148,153,159,147,140,145,151,139,41,146,154,153,145,157,160,161,155,149,152,153,147,41,147,155,154,146,158,162,160,157,150,154,152,149,41,148,156,155,147,159,163,162,158,151,155,154,150,41,149,157,156,148,153,164,163,159,145,156,155,151,41,150,158,157,149,152,165,164,153,144,157,156,145,41,151,159,158,150,154,166,165,152,146,158,157,144,41,145,153,152,144,155,161,167,156,147,153,159,148,41,158,166,165,157,165,168,169,164,157,160,161,156,41,159,167,166,158,166,170,168,165,158,162,160,157,41,153,161,160,152,161,171,172,167,153,163,164,159,41,154,162,161,153,160,173,171,161,152,165,163,153,41,155,163,162,154,162,174,173,160,154,166,165,152,41,156,164,163,155,163,175,174,162,155,167,166,154,41,157,165,164,156,164,169,175,163,156,161,167,155,41,163,171,170,162,174,176,177,173,166,168,169,165,41,164,172,171,163,175,178,176,174,167,170,168,166,41,165,173,172,164,169,179,178,175,161,171,170,167,41,166,174,173,165,168,180,179,169,160,172,171,161,41,167,175,174,166,170,181,180,168,162,173,172,160,41,161,169,168,160,171,182,183,172,163,174,175,164,41,162,170,169,161,173,177,182,171,165,169,174,163,41,175,183,182,174,181,184,185,180,173,176,177,172,41,169,177,176,168,182,186,187,183,174,178,179,175,41,170,178,177,169,177,188,186,182,169,180,178,174,41,171,179,178,170,176,189,188,177,168,181,180,169,41,172,180,179,171,178,190,189,176,170,182,181,168,41,173,181,180,172,179,191,190,178,171,183,182,170,41,174,182,181,173,180,185,191,179,172,177,183,171,41,180,188,187,179,190,192,193,189,182,184,185,181,41,181,189,188,180,191,194,192,190,183,186,184,182,41,182,190,189,181,185,195,194,191,177,187,186,183,41,183,191,190,182,184,196,195,185,176,188,187,177,41,177,185,184,176,186,197,198,187,178,189,190,179,41,178,186,185,177,188,199,197,186,180,191,189,178,41,179,187,186,178,189,193,199,188,181,185,191,180,41,185,193,192,184,197,200,201,198,189,192,193,190,41,186,194,193,185,199,202,200,197,191,194,192,189,41,187,195,194,186,193,203,202,199,185,195,194,191,41,188,196,195,187,192,204,203,193,184,196,195,185,41,189,197,196,188,194,205,204,192,186,197,196,184,41,190,198,197,189,195,206,205,194,187,198,197,186,41,191,199,198,190,196,207,206,195,188,199,198,187,41,196,204,203,195,204,208,209,203,196,200,201,195,41,197,205,204,196,205,210,208,204,197,202,200,196,41,198,206,205,197,206,211,210,205,198,203,202,197,41,199,207,206,198,207,212,211,206,199,204,203,198,41,193,201,200,192,200,213,214,201,192,205,206,193,41,194,202,201,193,202,215,213,200,194,207,205,192,41,195,203,202,194,203,209,215,202,195,201,207,194,41,201,210,209,200,213,216,217,214,205,208,209,206,41,202,211,210,201,215,218,216,213,207,210,208,205,41,203,212,211,202,209,219,218,215,201,211,210,207,41,204,213,212,203,208,220,219,209,200,212,211,201,41,205,214,213,204,210,221,220,208,202,213,212,200,41,206,215,214,205,211,222,221,210,203,214,213,202,41,207,216,215,206,212,223,222,211,204,215,214,203,41,214,222,221,213,221,224,225,220,213,216,217,212,41,215,223,222,214,222,226,224,221,214,218,216,213,41,216,224,223,215,223,227,226,222,215,219,218,214,41,210,218,217,209,216,228,229,217,208,220,221,209,41,211,219,218,210,218,230,228,216,210,222,220,208,41,212,220,219,211,219,231,230,218,211,223,222,210,41,213,221,220,212,220,225,231,219,212,217,223,211,41,218,226,225,217,228,232,233,229,220,224,225,221,41,219,227,226,218,230,234,232,228,222,226,224,220,41,220,228,227,219,231,235,234,230,223,227,226,222,41,221,229,228,220,225,236,235,231,217,228,227,223,41,222,230,229,221,224,237,236,225,216,229,228,217,41,223,231,230,222,226,238,237,224,218,230,229,216,41,224,232,231,223,227,239,238,226,219,231,230,218,41,230,238,237,229,237,240,241,236,229,232,233,228,41,231,239,238,230,238,242,240,237,230,234,232,229,41,232,240,239,231,239,243,242,238,231,235,234,230,41,226,234,233,225,232,244,245,233,224,236,237,225,41,227,235,234,226,234,246,244,232,226,238,236,224,41,228,236,235,227,235,247,246,234,227,239,238,226,41,229,237,236,228,236,241,247,235,228,233,239,227,41,235,243,242,234,246,248,249,244,238,240,241,236,41,236,244,243,235,247,250,248,246,239,242,240,238,41,237,245,244,236,241,251,250,247,233,243,242,239,41,238,246,245,237,240,252,251,241,232,244,243,233,41,239,247,246,238,242,253,252,240,234,245,244,232,41,240,248,247,239,243,254,253,242,235,246,245,234,41,234,242,241,233,244,249,255,245,236,241,247,237,41,247,255,254,246,253,256,257,252,245,248,249,244,41,248,256,255,247,254,258,256,253,246,250,248,245,41,242,250,249,241,249,259,260,255,241,251,252,247,41,243,251,250,242,248,261,259,249,240,253,251,241,41,244,252,251,243,250,262,261,248,242,254,253,240,41,245,253,252,244,251,263,262,250,243,255,254,242,41,246,254,253,245,252,257,263,251,244,249,255,243,40,0,8,208,15,14,264,15,14,256,40,8,16,208,14,17,264,14,17,256,40,16,24,208,17,29,264,17,29,256,40,24,32,208,29,264,264,29,39,256,40,32,40,208,264,264,264,39,44,256,40,40,48,208,264,264,264,44,55,256,40,48,56,208,264,79,264,55,60,256,40,56,64,208,79,78,264,60,71,256,40,64,72,208,78,83,264,71,75,256,40,72,80,208,83,95,264,75,87,256,40,80,88,208,95,99,264,87,91,256,40,88,96,208,99,110,264,91,102,256,40,96,104,208,110,113,264,102,105,256,40,104,112,208,113,125,264,105,117,256,40,112,120,208,125,129,264,117,121,256,40,120,128,208,129,141,264,121,133,256,40,128,136,208,141,151,264,133,143,256,40,136,144,208,151,156,264,143,148,256,40,144,152,208,156,167,264,148,159,256,40,152,160,208,167,172,264,159,164,256,40,160,168,208,172,183,264,164,175,256,40,168,176,208,183,187,264,175,179,256,40,176,184,208,187,198,264,179,190,256,40,184,192,208,198,201,264,190,193,256,40,192,200,208,201,214,264,193,206,256,40,200,209,208,214,217,264,206,209,256,40,209,217,208,217,229,264,209,221,256,40,217,225,208,229,233,264,221,225,256,40,225,233,208,233,245,264,225,237,256,40,233,241,208,245,255,264,237,247,256,40,241,249,208,255,260,264,247,252,256,41,252,3,2,251,262,4,0,261,254,4,0,253,41,253,4,3,252,263,6,4,262,255,6,4,254,41,254,5,4,253,257,8,6,263,249,8,6,255,41,255,6,5,254,256,10,8,257,248,10,8,249,41,256,7,6,255,258,12,10,256,250,12,10,248,40,249,0,208,260,15,264,252,15,256,41,0,249,250,1,15,260,259,3,15,252,251,3,41,251,2,1,250,261,0,3,259,253,0,3,251],
                "name":"SkyBoxGeometry.1",
                "normals":[0.56,0,-0.829,0.549,-0.109,-0.829,0.38,-0.076,-0.922,0.388,0,-0.922,0.71,0,-0.704,0.696,-0.139,-0.704,0.833,0,-0.553,0.817,-0.163,-0.553,0.925,0,-0.381,0.907,-0.18,-0.381,0.981,0,-0.194,0.962,-0.191,-0.194,0.995,0,-0.098,0.976,-0.194,-0.098,0.197,-0.039,-0.98,0.201,0,-0.98,0.358,-0.148,-0.922,0.186,-0.077,-0.98,0.517,-0.214,-0.829,0.656,-0.272,-0.704,0.77,-0.319,-0.553,0.854,-0.354,-0.381,0.906,-0.375,-0.194,0.919,-0.381,-0.098,0.769,-0.514,-0.381,0.693,-0.463,-0.553,0.816,-0.545,-0.194,0.827,-0.553,-0.098,0.322,-0.215,-0.922,0.167,-0.112,-0.98,0.465,-0.311,-0.829,0.59,-0.395,-0.704,0.396,-0.396,-0.829,0.274,-0.274,-0.922,0.502,-0.502,-0.704,0.589,-0.589,-0.553,0.654,-0.654,-0.381,0.694,-0.694,-0.194,0.704,-0.704,-0.098,0.142,-0.142,-0.98,0.545,-0.816,-0.194,0.514,-0.769,-0.381,0.553,-0.827,-0.098,0.215,-0.322,-0.922,0.112,-0.167,-0.98,0.311,-0.465,-0.829,0.395,-0.59,-0.704,0.463,-0.693,-0.553,0.214,-0.517,-0.829,0.148,-0.358,-0.922,0.272,-0.656,-0.704,0.319,-0.77,-0.553,0.354,-0.854,-0.381,0.375,-0.906,-0.194,0.381,-0.919,-0.098,0.077,-0.186,-0.98,0.191,-0.962,-0.194,0.18,-0.907,-0.381,0.194,-0.976,-0.098,0.076,-0.38,-0.922,0.039,-0.197,-0.98,0.109,-0.549,-0.829,0.139,-0.696,-0.704,0.163,-0.817,-0.553,0,-0.71,-0.704,0,-0.56,-0.829,0,-0.833,-0.553,0,-0.925,-0.381,0,-0.981,-0.194,0,-0.995,-0.098,0,-0.388,-0.922,0,-0.201,-0.98,-0.194,-0.976,-0.098,-0.191,-0.962,-0.194,-0.076,-0.38,-0.922,-0.039,-0.197,-0.98,-0.109,-0.549,-0.829,-0.139,-0.696,-0.704,-0.163,-0.817,-0.553,-0.18,-0.907,-0.381,-0.272,-0.656,-0.704,-0.214,-0.517,-0.829,-0.319,-0.77,-0.553,-0.354,-0.854,-0.381,-0.375,-0.906,-0.194,-0.381,-0.919,-0.098,-0.148,-0.358,-0.922,-0.077,-0.186,-0.98,-0.553,-0.827,-0.098,-0.545,-0.816,-0.194,-0.215,-0.322,-0.922,-0.112,-0.167,-0.98,-0.311,-0.465,-0.829,-0.395,-0.59,-0.704,-0.463,-0.693,-0.553,-0.514,-0.769,-0.381,-0.589,-0.589,-0.553,-0.502,-0.502,-0.704,-0.654,-0.654,-0.381,-0.694,-0.694,-0.194,-0.704,-0.704,-0.098,-0.274,-0.274,-0.922,-0.142,-0.142,-0.98,-0.396,-0.396,-0.829,-0.322,-0.215,-0.922,-0.167,-0.112,-0.98,-0.465,-0.311,-0.829,-0.59,-0.395,-0.704,-0.693,-0.463,-0.553,-0.769,-0.514,-0.381,-0.816,-0.545,-0.194,-0.827,-0.553,-0.098,-0.854,-0.354,-0.381,-0.77,-0.319,-0.553,-0.906,-0.375,-0.194,-0.919,-0.381,-0.098,-0.358,-0.148,-0.922,-0.186,-0.077,-0.98,-0.517,-0.214,-0.829,-0.656,-0.272,-0.704,-0.38,-0.076,-0.922,-0.197,-0.039,-0.98,-0.549,-0.109,-0.829,-0.696,-0.139,-0.704,-0.817,-0.163,-0.553,-0.907,-0.18,-0.381,-0.962,-0.191,-0.194,-0.976,-0.194,-0.098,-0.925,0,-0.381,-0.833,0,-0.553,-0.981,0,-0.194,-0.995,0,-0.098,-0.388,0,-0.922,-0.201,0,-0.98,-0.56,0,-0.829,-0.71,0,-0.704,-0.549,0.109,-0.829,-0.38,0.076,-0.922,-0.696,0.139,-0.704,-0.817,0.163,-0.553,-0.907,0.18,-0.381,-0.962,0.191,-0.194,-0.976,0.194,-0.098,-0.197,0.039,-0.98,-0.906,0.375,-0.194,-0.854,0.354,-0.381,-0.919,0.381,-0.098,-0.358,0.148,-0.922,-0.186,0.077,-0.98,-0.517,0.214,-0.829,-0.656,0.272,-0.704,-0.77,0.319,-0.553,-0.465,0.311,-0.829,-0.322,0.215,-0.922,-0.59,0.395,-0.704,-0.693,0.463,-0.553,-0.769,0.514,-0.381,-0.816,0.545,-0.194,-0.827,0.553,-0.098,-0.167,0.112,-0.98,-0.694,0.694,-0.194,-0.654,0.654,-0.381,-0.704,0.704,-0.098,-0.274,0.274,-0.922,-0.142,0.142,-0.98,-0.396,0.396,-0.829,-0.502,0.502,-0.704,-0.589,0.589,-0.553,-0.395,0.59,-0.704,-0.311,0.465,-0.829,-0.463,0.693,-0.553,-0.514,0.769,-0.381,-0.545,0.816,-0.194,-0.553,0.827,-0.098,-0.215,0.322,-0.922,-0.112,0.167,-0.98,-0.381,0.919,-0.098,-0.375,0.906,-0.194,-0.148,0.358,-0.922,-0.077,0.186,-0.98,-0.214,0.517,-0.829,-0.272,0.656,-0.704,-0.319,0.77,-0.553,-0.354,0.854,-0.381,-0.163,0.817,-0.553,-0.139,0.696,-0.704,-0.18,0.907,-0.381,-0.191,0.962,-0.194,-0.194,0.976,-0.098,-0.076,0.38,-0.922,-0.039,0.197,-0.98,-0.109,0.549,-0.829,0,0.388,-0.922,0,0.201,-0.98,0,0.56,-0.829,0,0.71,-0.704,0,0.833,-0.553,0,0.925,-0.381,0,0.981,-0.194,0,0.995,-0.098,0.163,0.817,-0.553,0.139,0.696,-0.704,0.18,0.907,-0.381,0.191,0.962,-0.194,0.194,0.976,-0.098,0.076,0.38,-0.922,0.039,0.197,-0.98,0.109,0.549,-0.829,0.148,0.358,-0.922,0.077,0.186,-0.98,0.214,0.517,-0.829,0.272,0.656,-0.704,0.319,0.77,-0.553,0.354,0.854,-0.381,0.375,0.906,-0.194,0.381,0.919,-0.098,0.514,0.769,-0.381,0.463,0.693,-0.553,0.545,0.816,-0.194,0.553,0.827,-0.098,0.215,0.322,-0.922,0.112,0.167,-0.98,0.311,0.465,-0.829,0.395,0.59,-0.704,0.274,0.274,-0.922,0.142,0.142,-0.98,0.396,0.396,-0.829,0.502,0.502,-0.704,0.589,0.589,-0.553,0.654,0.654,-0.381,0.694,0.694,-0.194,0.704,0.704,-0.098,0.769,0.514,-0.381,0.693,0.463,-0.553,0.816,0.545,-0.194,0.827,0.553,-0.098,0.322,0.215,-0.922,0.167,0.112,-0.98,0.465,0.311,-0.829,0.59,0.395,-0.704,0.517,0.214,-0.829,0.358,0.148,-0.922,0.656,0.272,-0.704,0.77,0.319,-0.553,0.854,0.354,-0.381,0.906,0.375,-0.194,0.919,0.381,-0.098,0.186,0.077,-0.98,0.962,0.191,-0.194,0.907,0.18,-0.381,0.976,0.194,-0.098,0.38,0.076,-0.922,0.197,0.039,-0.98,0.549,0.109,-0.829,0.696,0.139,-0.704,0.817,0.163,-0.553,0,0,-1],
                "vertices":[-0.195,0,0.981,-0.383,0,0.924,-0.556,0,0.831,-0.707,0,0.707,-0.831,0,0.556,-0.924,0,0.383,-0.981,0,0.195,-1,0,0,-0.191,0.038,0.981,-0.375,0.075,0.924,-0.545,0.108,0.831,-0.694,0.138,0.707,-0.815,0.162,0.556,-0.906,0.18,0.383,-0.962,0.191,0.195,-0.981,0.195,0,-0.18,0.075,0.981,-0.354,0.146,0.924,-0.513,0.213,0.831,-0.653,0.271,0.707,-0.768,0.318,0.556,-0.854,0.354,0.383,-0.906,0.375,0.195,-0.924,0.383,0,-0.162,0.108,0.981,-0.318,0.213,0.924,-0.462,0.309,0.831,-0.588,0.393,0.707,-0.691,0.462,0.556,-0.768,0.513,0.383,-0.815,0.545,0.195,-0.831,0.556,0,-0.138,0.138,0.981,-0.271,0.271,0.924,-0.393,0.393,0.831,-0.5,0.5,0.707,-0.588,0.588,0.556,-0.653,0.653,0.383,-0.694,0.694,0.195,-0.707,0.707,0,-0.108,0.162,0.981,-0.213,0.318,0.924,-0.309,0.462,0.831,-0.393,0.588,0.707,-0.462,0.691,0.556,-0.513,0.768,0.383,-0.545,0.815,0.195,-0.556,0.831,0,-0.075,0.18,0.981,-0.146,0.354,0.924,-0.213,0.513,0.831,-0.271,0.653,0.707,-0.318,0.768,0.556,-0.354,0.854,0.383,-0.375,0.906,0.195,-0.383,0.924,0,-0.038,0.191,0.981,-0.075,0.375,0.924,-0.108,0.545,0.831,-0.138,0.694,0.707,-0.162,0.815,0.556,-0.18,0.906,0.383,-0.191,0.962,0.195,-0.195,0.981,0,0,0.195,0.981,0,0.383,0.924,0,0.556,0.831,0,0.707,0.707,0,0.831,0.556,0,0.924,0.383,0,0.981,0.195,0,1,0,0.038,0.191,0.981,0.075,0.375,0.924,0.108,0.545,0.831,0.138,0.694,0.707,0.162,0.815,0.556,0.18,0.906,0.383,0.191,0.962,0.195,0.195,0.981,0,0.075,0.18,0.981,0.146,0.354,0.924,0.213,0.513,0.831,0.271,0.653,0.707,0.318,0.768,0.556,0.354,0.854,0.383,0.375,0.906,0.195,0.383,0.924,0,0.108,0.162,0.981,0.213,0.318,0.924,0.309,0.462,0.831,0.393,0.588,0.707,0.462,0.691,0.556,0.513,0.768,0.383,0.545,0.815,0.195,0.556,0.831,0,0.138,0.138,0.981,0.271,0.271,0.924,0.393,0.393,0.831,0.5,0.5,0.707,0.588,0.588,0.556,0.653,0.653,0.383,0.694,0.694,0.195,0.707,0.707,0,0.162,0.108,0.981,0.318,0.213,0.924,0.462,0.309,0.831,0.588,0.393,0.707,0.691,0.462,0.556,0.768,0.513,0.383,0.815,0.545,0.195,0.831,0.556,0,0.18,0.075,0.981,0.354,0.146,0.924,0.513,0.213,0.831,0.653,0.271,0.707,0.768,0.318,0.556,0.854,0.354,0.383,0.906,0.375,0.195,0.924,0.383,0,0.191,0.038,0.981,0.375,0.075,0.924,0.545,0.108,0.831,0.694,0.138,0.707,0.815,0.162,0.556,0.906,0.18,0.383,0.962,0.191,0.195,0.981,0.195,0,0.195,-0,0.981,0.383,0,0.924,0.556,0,0.831,0.707,-0,0.707,0.831,-0,0.556,0.924,0,0.383,0.981,-0,0.195,1,-0,0,0.191,-0.038,0.981,0.375,-0.075,0.924,0.545,-0.108,0.831,0.694,-0.138,0.707,0.815,-0.162,0.556,0.906,-0.18,0.383,0.962,-0.191,0.195,0.981,-0.195,0,0.18,-0.075,0.981,0.354,-0.146,0.924,0.513,-0.213,0.831,0.653,-0.271,0.707,0.768,-0.318,0.556,0.854,-0.354,0.383,0.906,-0.375,0.195,0.924,-0.383,0,0.162,-0.108,0.981,0.318,-0.213,0.924,0.462,-0.309,0.831,0.588,-0.393,0.707,0.691,-0.462,0.556,0.768,-0.513,0.383,0.815,-0.545,0.195,0.831,-0.556,0,0.138,-0.138,0.981,0.271,-0.271,0.924,0.393,-0.393,0.831,0.5,-0.5,0.707,0.588,-0.588,0.556,0.653,-0.653,0.383,0.694,-0.694,0.195,0.707,-0.707,0,0.108,-0.162,0.981,0.213,-0.318,0.924,0.309,-0.462,0.831,0.393,-0.588,0.707,0.462,-0.691,0.556,0.513,-0.768,0.383,0.545,-0.815,0.195,0.556,-0.831,0,0.075,-0.18,0.981,0.146,-0.354,0.924,0.213,-0.513,0.831,0.271,-0.653,0.707,0.318,-0.768,0.556,0.354,-0.854,0.383,0.375,-0.906,0.195,0.383,-0.924,0,0.038,-0.191,0.981,0.075,-0.375,0.924,0.108,-0.545,0.831,0.138,-0.694,0.707,0.162,-0.815,0.556,0.18,-0.906,0.383,0.191,-0.962,0.195,0.195,-0.981,0,0,-0.195,0.981,0,-0.383,0.924,0,-0.556,0.831,0,-0.707,0.707,0,-0.831,0.556,0,-0.924,0.383,0,-0.981,0.195,0,-1,0,-0.038,-0.191,0.981,-0.075,-0.375,0.924,-0.108,-0.545,0.831,-0.138,-0.694,0.707,-0.162,-0.815,0.556,-0.18,-0.906,0.383,-0.191,-0.962,0.195,-0.195,-0.981,0,0,-0,1,-0.075,-0.18,0.981,-0.146,-0.354,0.924,-0.213,-0.513,0.831,-0.271,-0.653,0.707,-0.318,-0.768,0.556,-0.354,-0.854,0.383,-0.375,-0.906,0.195,-0.383,-0.924,0,-0.108,-0.162,0.981,-0.213,-0.318,0.924,-0.309,-0.462,0.831,-0.393,-0.588,0.707,-0.462,-0.691,0.556,-0.513,-0.768,0.383,-0.545,-0.815,0.195,-0.556,-0.831,0,-0.138,-0.138,0.981,-0.271,-0.271,0.924,-0.393,-0.393,0.831,-0.5,-0.5,0.707,-0.588,-0.588,0.556,-0.653,-0.653,0.383,-0.694,-0.694,0.195,-0.707,-0.707,0,-0.162,-0.108,0.981,-0.318,-0.213,0.924,-0.462,-0.309,0.831,-0.588,-0.393,0.707,-0.691,-0.462,0.556,-0.768,-0.513,0.383,-0.815,-0.545,0.195,-0.831,-0.556,0,-0.18,-0.075,0.981,-0.354,-0.146,0.924,-0.513,-0.213,0.831,-0.653,-0.271,0.707,-0.768,-0.318,0.556,-0.854,-0.354,0.383,-0.906,-0.375,0.195,-0.924,-0.383,0,-0.191,-0.038,0.981,-0.375,-0.075,0.924,-0.545,-0.108,0.831,-0.694,-0.138,0.707,-0.815,-0.162,0.556,-0.906,-0.18,0.383,-0.962,-0.191,0.195,-0.981,-0.195,0],
                "uvs":[[0.219,0.832,0.187,0.832,0.187,0.924,0.219,0.924,0.219,0.707,0.188,0.707,0.219,0.556,0.188,0.556,0.219,0.383,0.188,0.383,0.219,0.195,0.188,0.195,0.219,0,0.188,0,0.187,0.981,0.219,0.981,0.156,0.924,0.156,0.981,0.156,0.832,0.156,0.707,0.156,0.556,0.156,0.383,0.156,0.195,0.156,0,0.125,0.383,0.125,0.556,0.125,0.195,0.125,0,0.125,0.924,0.125,0.981,0.125,0.832,0.125,0.707,0.094,0.832,0.094,0.924,0.094,0.707,0.094,0.556,0.094,0.383,0.094,0.195,0.094,0,0.094,0.981,0.062,0.195,0.062,0.383,0.062,0,0.062,0.924,0.062,0.981,0.062,0.832,0.062,0.707,0.062,0.556,0.031,0.832,0.031,0.924,0.031,0.707,0.031,0.556,0.031,0.383,0.031,0.195,0.031,0,0.031,0.981,-0,0.195,-0,0.383,-0,0,-0,0.924,-0,0.981,-0,0.832,-0,0.707,-0,0.556,1,0.707,0.969,0.707,0.969,0.832,1,0.832,1,0.556,0.969,0.556,1,0.383,0.969,0.383,1,0.195,0.969,0.195,1,0,0.969,0,1,0.924,0.969,0.924,0.969,0.981,1,0.981,0.937,0,0.937,0.195,0.937,0.924,0.937,0.981,0.937,0.832,0.937,0.707,0.937,0.556,0.937,0.383,0.906,0.707,0.906,0.832,0.906,0.556,0.906,0.383,0.906,0.195,0.906,0,0.906,0.924,0.906,0.981,0.875,0,0.875,0.195,0.875,0.924,0.875,0.981,0.875,0.832,0.875,0.707,0.875,0.556,0.875,0.383,0.844,0.556,0.844,0.707,0.844,0.383,0.844,0.195,0.844,0,0.844,0.924,0.844,0.981,0.844,0.832,0.812,0.924,0.812,0.981,0.812,0.832,0.812,0.707,0.812,0.556,0.812,0.383,0.812,0.195,0.812,0,0.781,0.383,0.781,0.556,0.781,0.195,0.781,0,0.781,0.924,0.781,0.981,0.781,0.832,0.781,0.707,0.75,0.924,0.75,0.981,0.75,0.832,0.75,0.707,0.75,0.556,0.75,0.383,0.75,0.195,0.75,0,0.719,0.383,0.719,0.556,0.719,0.195,0.719,0,0.719,0.924,0.719,0.981,0.719,0.832,0.719,0.707,0.688,0.832,0.688,0.924,0.688,0.707,0.688,0.556,0.688,0.383,0.688,0.195,0.688,0,0.688,0.981,0.656,0.195,0.656,0.383,0.656,0,0.656,0.924,0.656,0.981,0.656,0.832,0.656,0.707,0.656,0.556,0.625,0.832,0.625,0.924,0.625,0.707,0.625,0.556,0.625,0.383,0.625,0.195,0.625,0,0.625,0.981,0.594,0.195,0.594,0.383,0.594,0,0.594,0.924,0.594,0.981,0.594,0.832,0.594,0.707,0.594,0.556,0.563,0.707,0.563,0.832,0.562,0.556,0.563,0.383,0.562,0.195,0.562,0,0.563,0.924,0.563,0.981,0.531,0,0.531,0.195,0.531,0.924,0.531,0.981,0.531,0.832,0.531,0.707,0.531,0.556,0.531,0.383,0.5,0.556,0.5,0.707,0.5,0.383,0.5,0.195,0.5,0,0.5,0.924,0.5,0.981,0.5,0.832,0.469,0.924,0.469,0.981,0.469,0.832,0.469,0.707,0.469,0.556,0.469,0.383,0.469,0.195,0.469,0,0.438,0.556,0.438,0.707,0.438,0.383,0.438,0.195,0.438,0,0.438,0.924,0.438,0.981,0.438,0.832,0.406,0.924,0.406,0.981,0.406,0.832,0.406,0.707,0.406,0.556,0.406,0.383,0.406,0.195,0.406,0,0.375,0.383,0.375,0.556,0.375,0.195,0.375,0,0.375,0.924,0.375,0.981,0.375,0.832,0.375,0.707,0.344,0.924,0.344,0.981,0.344,0.832,0.344,0.707,0.344,0.556,0.344,0.383,0.344,0.195,0.344,0,0.313,0.383,0.312,0.556,0.312,0.195,0.312,0,0.313,0.924,0.313,0.981,0.313,0.832,0.312,0.707,0.281,0.832,0.281,0.924,0.281,0.707,0.281,0.556,0.281,0.383,0.281,0.195,0.281,0,0.281,0.981,0.25,0.195,0.25,0.383,0.25,0,0.25,0.924,0.25,0.981,0.25,0.832,0.25,0.707,0.25,0.556,0.99,0.995]]
            },
            "type":"Geometry",
            "uuid":"61AA7CA8-89D8-345D-8556-3D4871424200"
        }],
        "textures":[{
            "wrap":[1000,1000],
            "repeat":[1,1],
            "uuid":"63CCEB68-2D71-3AC9-A63E-07B41824E535",
            "name":"SkyBox.Texture",
            "minFilter":1008,
            "anisotropy":1,
            "mapping":300,
            "image":"86747549-0804-3C68-AE6E-71D4C294E379",
            "magFilter":1006
        }],
        "images":[{
            //"url":"skyBlue.jpg",
            "url":"data:image/jpeg;base64,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",
            "uuid":"86747549-0804-3C68-AE6E-71D4C294E379",
            "name":"skyBlue.jpg"
        }]
    };
    
    
    /**
     *
     */
    class EZPlayer {
        /**
         * [constructor description]
         *
         * @param  {[type]} container  [description]
         * @param  {[type]} zoom       [description]
         * @param  {[type]} providerId [description]
         * @param  {[type]} center     [description]
         * @param  {[type]} options    [description]
         *
         * @return {[type]}            [description]
         */
        constructor(container, zoom, providerId, center, options) {
            this._tzUrl = 'https://crm.ezzing.com/api/irradiation/coords-to-tz';
            // Coordinates To TimeZone endpoint courtesy of Carlos López :-)))
    
            this._centerMeters = {x: 0, y: 0};
            this._activeRenderer = true;
            this._activeMapper = true;
            this._widthMeters = 0;
            this._heightMeters = 0;
            this._isPanningOrRotating = false;
            this._activePlayer = true;
            this._googleApiKey;
            this._bingApiKey;
            this._zoomRatio = 1;
            this._rotationRatio = 1;
            this._translationRatio = 1;
            this._sunSpeed = 1;
            this._lastDelta = 0;
            this._sunPlaying = false;
            this._currentScene;
            this._originPoint;
            this._objects = [];
            this._currentButton;
    
            this._container = container;
            this._zoom = zoom;
            this._providerId = providerId;
            this._centerLatLng = center;
            this._centerProjected = L.Projection.Mercator.project(this._centerLatLng);
    
            this._temporalCenterInMeters = this._centerMeters;
            this._temporalWidth = this._widthMeters;
    
    
            this._mapper;
            this._renderer = new Renderer(this._container, this._zoom, this._centerLatLng);
    
            this._container.style.height = '100%';
            this._container.style.position = 'relative';
    
            // Container
            const attributionContainer = document.createElement('div');
            attributionContainer.classList.add('ez3d-attribution-container');
            this._container.append(attributionContainer);
            // Logo
            const logo = document.createElement('div');
            logo.classList.add('ez3d-attribution-logo');
            attributionContainer.append(logo);
            // Attributions
            const attrib = document.createElement('div');
            attrib.classList.add('ez3d-attribution');
            attributionContainer.append(attrib);
    
    
            ee.addListener('Mapper.AttributionChange', (e) => {
                attrib.innerHTML = e;
            });
            ee.addListener('Mapper.AttributionLogo', (e) => {
                logo.innerHTML = `<img src="${e}" />`;
                // const evento = e;
                // logo.style.backgroundImage = "url("+evento+")";
            });
    
            if (options && options.googleApiKey) {
                this._googleApiKey = options.googleApiKey;
            }
    
            if (options && options.bingApiKey) {
                this._bingApiKey = options.bingApiKey;
            }
    
            if (options && options.zoomRatio) {
                this._zoomRatio = options.zoomRatio;
            }
    
            if (options && options.rotationRatio) {
                this._rotationRatio = options.rotationRatio;
            }
    
            if (options && options.translationRatio) {
                this._translationRatio = options.translationRatio;
            }
    
            if (options && options.hasMapper === false) {
                //
            } else {
                this._mapper = new Mapper(this._container, this._renderer.mapperAnchor, this._centerLatLng, this._providerId, this._renderer.viewport.width, this._renderer.viewport.height, this._googleApiKey, this._bingApiKey);
                this._setDimensions();
            }
    
            ee.addListener('Mapper.TileLoaded', () => {
                if (this._activePlayer) {
                    this._renderer.needsUpdate = true;
                }
            });
    
            ee.addListener('Renderer.Resize', () => {
                if (this._activePlayer) {
                    this._setDimensions();
                    if (this._mapper) {
                        this._mapper.changeDimensions(this._widthMeters, this._heightMeters, this._centerMeters);
                    }
                }
            });
    
    
            this._datetime = new Date();
            this.changeLatLng(this._centerLatLng);
    
            this._container.addEventListener('contextmenu', (e) => {
                e.preventDefault();
            }, false);
    
            this._container.addEventListener('mousedown', (e) => {
                if (this._activePlayer) {
                    this._renderer.inMouseEvent = true;
                    this._currentButton = e.button;
                    this._isPanningOrRotating = true;
                    this._originPoint = {
                        mouse: {
                            x: e.layerX,
                            y: e.layerY
                        },
                        cam: {
                            x: this._renderer.cameras.orthographic.position.x,
                            y: this._renderer.cameras.orthographic.position.z
                        },
                        element: {
                            width: container.clientWidth,
                            height: container.clientHeight,
                        }
                    };
                    this._renderer.perspectiveRotationX = this._renderer.perspectiveSphere.rotation.x;
                    this._renderer.perspectiveRotationY = this._renderer.perspectiveAnchor.rotation.y;
                }
                e.preventDefault();
                e.stopPropagation();
            });
    
            this._container.addEventListener('mousemove', (e) => (this._debouncedMouseMove(e)));
    
            d3.select('#playerContainer').select('canvas').on('wheel', () => {
                this._mouseWheel(d3.event);
                d3.event.preventDefault();
            });
    
            this._container.addEventListener('mouseup', (e) => {
                if (this._activePlayer) {
                    this._renderer.inMouseEvent = false;
                    this._isPanningOrRotating = false;
                    if (this._mapper) {
                        var worldPerspective = new THREE.Vector3().setFromMatrixPosition(this._renderer.cameras.perspective.matrixWorld);
                        var camHeight = worldPerspective.y;
                        var bounds = 600;
                        this._mapper.changeDimensions(bounds, bounds, this._centerMeters);
                    }
                }
                e.stopPropagation();
            });
    
            // Texture management
    
            ee.addListener('getTexture_Finished', () => {
                this._renderer.updateTexture();
            });
    
            ee.addListener('getTextures_Finished', () => {
                this._renderer.updateTextures();
            });
    
            ee.addListener('removeTexture_Finished', () => {
                this._renderer.removeTexture();
            });
    
            ee.addListener('removeTextures_Finished', () => {
                this._renderer.removeTextures();
            });
    
            ee.emitEvent('Player.Instanced');
    
        }
    
        _mouseWheel(e) {
            if (this._activePlayer && this._renderer.mode) {
                const inc = e.wheelDelta / 12 * this._zoomRatio;
                let newZoom;
                if (inc > 0) {
                    newZoom = this.getZoom() * (1 + inc * 0.01);
                } else {
                    newZoom = this.getZoom() * (1 - (-inc * 0.01));
                }
    
                if (newZoom < 30) {
                    newZoom = 30;
                }
                this.setZoom(newZoom);
                if (this._renderer.mode === '3d' || this._renderer.mode === 'showcase') {
                    if (this._mapper) {
                        const bounds = 600;
                        this._mapper.changeDimensions(bounds, bounds, this._centerMeters);
                    }
                }
            }
            e.stopPropagation();
        }
    
        /**
         * debouncedMouseMove.
         * @param {event} e     Mouse event.
         */
        _debouncedMouseMove(e) {
            if (this._activePlayer) {
                if (this._isPanningOrRotating) {
                    let delta;
                    let newCenter = '';
                    if (this._renderer.mode === '2d') {
                        delta = {
                            x: (e.layerX - this._originPoint.mouse.x) * 1 / this._zoom,
                            y: (e.layerY - this._originPoint.mouse.y) * 1 / this._zoom
                        };
                        newCenter = {
                            x: (this._originPoint.cam.x - delta.x),
                            y: (this._originPoint.cam.y - delta.y)
                        };
                        this._centerMeters = newCenter;
                        this._renderer.setCenter(this._centerMeters);
                    }
                    if (this._renderer.mode === '3d' || this._renderer.mode === 'showcase') {
                        if ((e.shiftKey && this._currentButton === 0) || (!e.shiftKey && this._currentButton === 2)) {
                            // Translate: Shift + left click || Right click
                            const rendererZoom = this._renderer.zoom;
                            const rotationZ = this._renderer.perspectiveRotationY;
                            const sinZ = Math.sin(rotationZ);
                            const cosZ = Math.cos(rotationZ);
                            const northDelta = {
                                x: e.layerX - this._originPoint.mouse.x,
                                y: e.layerY - this._originPoint.mouse.y
                            };
                            const translatedDelta = {
                                x: (northDelta.x * cosZ + northDelta.y * sinZ),
                                y: (northDelta.y * cosZ - northDelta.x * sinZ)
                            };
                            delta = {
                                x: translatedDelta.x * 30 / rendererZoom * this._translationRatio,
                                y: translatedDelta.y * 30 / rendererZoom * this._translationRatio
                            };
                            newCenter = {
                                x: (this._originPoint.cam.x - delta.x),
                                y: (this._originPoint.cam.y - delta.y)
                            };
    
                            this._renderer.setCenter(newCenter);
                            this._centerMeters = newCenter;
                        }
                        else if (this._currentButton === 0) {
                            // Rotate: left click
                            delta = {
                                x: e.layerX - this._originPoint.mouse.x,
                                y: e.layerY - this._originPoint.mouse.y
                            };
                            const newRotationX = -delta.y / this._originPoint.element.height * Math.PI / 2;
                            const newRotationZ = -delta.x / this._originPoint.element.width * Math.PI * 2;
                            this._renderer.rotatePerspective(newRotationX, newRotationZ * this._rotationRatio);
                        }
                    }
                }
            }
            e.stopPropagation();
        }
    
        /**
         *
         * @param {boolean}         state       - new active status
         * @private
         */
        activateRenderer(state) {
            this._activeRenderer = state;
            this._renderer.setVisible(state);
        }
    
        /**
         *
         * @param {boolean}         state   - new active status
         * @private
         */
        activateMapper(state) {
            this._activeMapper = state;
            if (!this._mapper && state) {
                this._mapper = new Mapper(this._renderer.mapperAnchor, this._centerLatLng, this._providerId, this._renderer.viewport.width, this._renderer.viewport.height, this._googleApiKey, this._bingApiKey);
                this._setDimensions();
            }
    
            if (this._mapper) {
                this._mapper.setVisible(state);
            }
        }
    
    
        /**
         *
         * @param {boolean}     state       - new active status
         * @private
         */
        activatePlayer(state) {
            this._activePlayer = state;
            if (state === true) {
                this._renderer.domElement.style.visibility = 'visible';
                this.fitToBounds(this._temporalCenterInMeters, this._temporalWidth);
                if (this._currentScene) {
                    this.draw(this._currentScene);
                }
            } else {
                this._renderer.domElement.style.visibility = 'hidden';
            }
        }
    
        /**
         *
         * @private
         */
        _setDimensions() {
            if (this._activePlayer) {
                this._widthMeters = this._renderer.viewport.width / this._zoom;
                this._heightMeters = this._renderer.viewport.height / this._zoom;
            }
        }
    
        /**
         * @return {float} zoom        - Current zoom
         * @private
         */
        getZoom() {
            if (this._activePlayer) {
                return this._zoom;
            }
        }
    
        /**
         *
         * @param {float} z
         * @private
         */
        setZoom(z) {
            if (this._activePlayer) {
                const zoom = (z < 30) ? 30 : z;
    
                this._zoom = zoom;
                this._renderer.setZoom(zoom);
                this._setDimensions();
                if (this._mapper) {
                    const bounds = 600;
                    this._mapper.changeDimensions(bounds, bounds, this._centerMeters);
                }
                this._renderer.needsUpdate = true;
            }
        }
    
        /**
         *
         * @return {Array} - providers array
         * @private
         */
        getProviders() {
            if (this._mapper) {
                return this._mapper.getProviders();
            }
            return [];
        }
    
        /**
         *
         * @param {int} providerId
         * @private
         */
        setProvider(id) {
            this._providerId = id;
            if (this._mapper) {
                this._mapper.setProvider(this._providerId);
            }
        }
    
        /**
         *
         * @return {int} - providerId
         * @private
         */
        getProvider() {
            return this._mapper.getProvider();
        }
    
        /**
         *
         * @param {{x:float, y:float}} locationInMeters
         * @private
         */
        setCenter(locationInMeters) {
            if (this._activePlayer) {
                this._centerMeters = locationInMeters;
                this._renderer.setCenter(locationInMeters);
                if (this._mapper) {
                    this._mapper.changeDimensions(this._widthMeters, this._heightMeters, this._centerMeters);
                }
            }
        }
    
        /**
         *
         * @param {x,y} locationInMeters
         * @private
         */
        flyTo(locationInMeters) {
            if (this._activePlayer) {
                this.setCenter(locationInMeters);
            }
        }
    
        /**
         *
         * @param {lat:Number, lng:Number}  latlng
         * @private
         */
        flyToLatLng(latlng) {
            if (this._activePlayer) {
                const projected = L.Projection.Mercator.project(latlng);
                const meters = {};
                meters.x = projected.x - this._centerProjected.x;
                meters.y = -projected.y + this._centerProjected.y;
                this.flyTo(meters);
            }
        }
    
    
        /**
         *
         * @param {int} index
         * @private
         */
        gotoBuilding(index) {
            if (this._activePlayer) {
                this.flyTo(this._objects[index].position);
            }
        }
    
        /**
         *
         * @private
         */
        set2d() {
            if (this._activePlayer) {
                this._setDimensions();
                this._renderer.set2d();
                if (this._mapper) {
                    this._mapper.changeDimensions(this._widthMeters, this._heightMeters, this._centerMeters);
                }
            }
        }
    
        /**
         *
         * @private
         */
        set3d() {
            if (this._activePlayer) {
                this._renderer.set3d();
                if (this._mapper) {
                    const bounds = 600;
                    this._mapper.changeDimensions(bounds, bounds, this._centerMeters);
                }
            }
        }
    
        /**
         *
         * @private
         */
        setShowcase() {
            if (this._activePlayer) {
                this._renderer.setShowcase();
                this.setZoom(this.getZoom() + 100);
                this._setDimensions();
    
                if (this._mapper) {
                    setTimeout(() => {
                        const worldPerspective = new THREE.Vector3().setFromMatrixPosition(this._renderer.cameras.perspective.matrixWorld);
                        const camHeight = worldPerspective.y;
                        const bounds = camHeight * 10;
                        this._mapper.changeDimensions(bounds, bounds, this._centerMeters);
                    }, 5000);
                }
            }
        }
    
        /**
         *
         * @param {string}  datetimeIsoString
         * @private
         */
        setDateTime(datetimeIsoString) {
            if (this._activePlayer) {
                this._datetime = new Date(datetimeIsoString);
                this._renderer.setDateTime(this._datetime, this._centerLatLng);
            }
        }
    
        /**
         *
         * @return {Date}  datetime
         * @private
         */
        _publishDateTime() {
            if (this._activePlayer) {
                ee.emitEvent('Player.DateChanged', [this._datetime]);
                return this._datetime;
            }
        }
    
        /**
         *
         * @return {Date}  datetime
         * @private
         */
        getDateTime() {
            if (this._activePlayer) {
                return this._datetime;
            }
        }
    
        /**
         *
         * @private
         */
        _sunUpdate() {
            const sunUp = () =>{
                if (this._activePlayer) {
                    const delta = performance.now() - this._lastDelta;
                    this._datetime.setMilliseconds(this._datetime.getMilliseconds() + delta * this._sunSpeed);
                    this._renderer.setDateTime(this._datetime, this._centerLatLng);
                    this._publishDateTime();
                    this._lastDelta = performance.now();
                    if (this._sunPlaying) {
                        window.requestAnimationFrame(sunUp);
                    }
                }
            };
            sunUp();
        }
    
        /**
         *
         * @param {int} speed  - multiplicator
         * @private
         */
        playSun(speed) {
            if (this._activePlayer) {
                this._sunSpeed = speed;
                this._lastDelta = performance.now();
                this._sunPlaying = true;
                this._sunUpdate();
            }
        }
    
        /**
         *
         * @private
         */
        stopSun() {
            if (this._activePlayer) {
                this._sunPlaying = false;
            }
        }
    
        /**
         *
         * @param {EZObject} obj
         * @private
         */
    
        drawTrees(obj) {
            if (this._activePlayer) {
                if (obj instanceof EZModelScene) {
                    this._centerMeters = {x: 0, y: 0};
                    this._centerLatLng = {
                        lat: obj.projectCenter.lat,
                        lng: obj.projectCenter.lng
                    };
    
                    if (this._renderer) {
                        this._currentScene = obj;
                        this._centerProjected = L.Projection.Mercator.project(this._centerLatLng);
                        this._renderer.centerMeters = this._centerMeters;
                        this._renderer.drawTrees(obj);
                    }
                }
            }
        }
    
        /**
         *
         * @param {EZObject} obj
         * @private
         */
        draw(obj) {
            if (this._activePlayer) {
                if (obj instanceof EZModelScene) {
                    this._centerMeters = {x: 0, y: 0};
                    this._centerLatLng = {
                        lat: obj.projectCenter.lat,
                        lng: obj.projectCenter.lng
                    };
    
                    if (this._renderer) {
                        this._currentScene = obj;
                        this._centerProjected = L.Projection.Mercator.project(this._centerLatLng);
                        this._renderer.centerMeters = this._centerMeters;
                        this._renderer.drawScene(obj);
                    }
                }
            }
        }
    
        /**
         *
         * @param {{x: float, y: float}} centerInMeters
         * @param {float} width
         * @private
         */
        fitToBounds(centerInMeters, width) {
            if (this._activePlayer) {
                const zoomRatio = this._renderer.viewport.width / width;
                const newZoom = zoomRatio;
                this._zoom = newZoom;
                this._renderer.setZoom(this._zoom);
                this._setDimensions();
                this._centerMeters = centerInMeters;
                this._renderer.setCenter(this._centerMeters);
                if (this._mapper) {
                    this._mapper.changeDimensions(this._widthMeters, this._heightMeters, this._centerMeters);
                }
            } else {
                this._temporalCenterInMeters = centerInMeters;
                this._temporalWidth = width;
            }
        }
    
        /**
         *
         * @private
         */
        getSnapShot() {
            this._renderer.domElement.toBlob((blob) => {
                saveAs(blob, 'snapshot.png');
            });
        }
    
        /**
         *
         * @return {string}   renderer.mode
         * @private
         */
        getMode() {
            return this._renderer.mode;
        }
    
        /**
         *
         * @param {Object}  object
         * @private
         */
        _killObject(object) {
            for (let property in object) {
                if (object.hasOwnProperty(property)) {
                    delete object[property];
                }
                object = null;
            }
        }
    
        /**
         *
         * @private
         */
        kill() {
            this._renderer.ro.disconnect();
    
            ee.removeAllListeners();
            this._container.innerHTML = '';
            this._killObject(this._mapper);
            this._killObject(this._renderer);
            this._killObject(this);
            console.log('Player is null');
        }
    
    
        /**
         *
         * @param {boolean} state
         * @private
         */
        setModulesVisibility(state) {
            this._renderer.setModulesVisibility(state);
        }
    
        /**
         *
         * @param {lat:Number, lng:Number}  latlng
         * @private
         */
        changeLatLng(latlng) {
            const url = this._tzUrl + '/' + latlng.lat + '/' + latlng.lng;
    
            fetch(url)
                .then((response) => {
                    return response.json();
                })
                .then((resp) => {
                    const timezone = resp;
                    const year = new Date().getFullYear();
                    let month = 0;
                    if (latlng.lat >= 0) {
                        month = 5;
                    } else {
                        month = 11;
                    }
                    const date = new Date(Date.UTC(year, month, 20, 12));
    
                    this._datetime = date;
                    this._renderer.timezone = timezone;
                    this._renderer.setDateTime(this._datetime, latlng);
                    this._publishDateTime();
                });
    
            this.setCenter({x: 0, y: 0});
    
            this._renderer.needsUpdate = true;
    
            this._centerMeters = {x: 0, y: 0};
            this._mapper.setMainLatLng(latlng);
            this._mapper.changeDimensions(this._widthMeters, this._heightMeters, this._centerMeters);
        }
    
    
        lookToNorth() {
            this._renderer.lookToNorth();
        }
    
    
        resetCamsAndAnchors() {
            this._renderer.resetCamsAndAnchors();
        }
    
        setMovementRatios(zRatio, tRatio, rRatio) {
            this._zoomRatio = zRatio;
            this._translationRatio = tRatio;
            this._rotationRatio = rRatio;
        }
    
        getMovementRatios() {
            return {
                zoomRatio: this._zoomRatio,
                translationRatio: this._translationRatio,
                rotationRatio: this._rotationRatio
            }
        }
    
        updateRenderer() {
            this._renderer.needsUpdate = true;
        }
    
        /**
         * Removes map's snapshot
         */
        removeMapSnapshot() {
            this._renderer.removeMapSnapshot();
        }
    
        /**
         * Runs renderer initMapSnapshot
         */
        initMapSnapshot() {
            this._renderer.initMapSnapshot();
        }
    
        /** ** ** ** ** ** **
         *
         * Building functions
         *
         ** ** ** ** ** ** **/
    
        /**
         * Draws building in renderer by ID
         * @param  {string} buildingId - ID
         */
        drawBuildingById(buildingId) {
            this._renderer.drawBuildingById(buildingId);
        }
    
        /**
         * Draws building in renderer
         * @param  {EZModelBuilding} building - building
         */
        drawBuilding(building) {
            this._renderer.drawBuilding(building);
        }
    
        /**
         * Updates building in renderer by ID
         * @param  {string} buildingId - ID
         */
        updateBuildingById(buildingId) {
            this._renderer.updateBuildingById(buildingId);
        }
    
        /**
         * Updates building in renderer
         * @param  {EZModelBuilding} building - building
         */
        updateBuilding(building) {
            this._renderer.updateBuilding(building);
        }
    
        /**
         * Removes building of renderer by ID
         * @param  {string} buildingId - ID
         */
        removeBuildingById(buildingId) {
            this._renderer.removeBuildingById(buildingId);
        }
    
        /**
         * Removes building of renderer by ID
         * @param  {EZModelBuilding} building - building
         */
        removeBuilding(building) {
            this._renderer.removeBuilding(building);
        }
    
        /** ** ** ** ** ** **
         *
         * Tree functions
         *
         ** ** ** ** ** ** **/
    
        /**
         * Draws Tree in renderer
         * @param  {EZModelTree} tree - tree
         */
        drawTree(tree) {
            this._renderer.drawTree(tree);
        }
    
        /**
         * Draws Tree in renderer by ID
         * @param  {string} treeId - ID
         */
        drawTreeById(treeId) {
            this._renderer.drawTreeById(treeId);
        }
    
        /**
         * Updates Tree
         * @param  {EZModelTree} tree - tree
         */
        updateTree(tree) {
            this._renderer.updateTree(tree);
        }
    
        /**
         * Updates Tree in renderer by ID
         * @param  {string} treeId - ID
         */
        updateTreeById(treeId) {
            this._renderer.updateTreeById(treeId);
        }
    
        /**
         * Removes tree of renderer
         * @param  {EZModelTree} tree - tree
         */
        removeTree(tree) {
            this._renderer.removeTree(tree);
        }
    
        /**
         * Removes tree of renderer by ID
         * @param  {string} treeId - ID
         */
        removeTreeById(treeId) {
            this._renderer.removeTreeById(treeId);
        }
    
        /** ** ** ** ** ** **
         *
         * Roof functions
         *
         ** ** ** ** ** ** **/
    
        /**
         * Draws Roof in renderer
         * @param  {EZModelRoof} roof - roof
         */
        drawRoof(roof) {
            this._renderer.drawRoof(roof);
        }
    
        /**
         * Draws Roof in renderer by ID
         * @param  {string} roofId - ID
         */
        drawRoofById(roofId) {
            this._renderer.drawRoofById(roofId);
        }
    
        /**
         * Updates Roof in renderer
         * @param  {EZModelRoof} roof - roof
         */
        updateRoof(roof) {
            this._renderer.updateRoof(roof);
        }
    
        /**
         * Updates Roof in renderer by ID
         * @param  {string} roofId - ID
         */
        updateRoofById(roofId) {
            this._renderer.updateRoofById(roofId);
        }
    
        /**
         * Removes roof of renderer
         * @param  {EZModelRoof} roof - roof
         */
        removeRoof(roof) {
            this._renderer.removeRoof(roof);
        }
    
        /**
         * Removes roof of renderer by ID
         * @param  {string} roofId - ID
         */
        removeRoofById(roofId) {
            this._renderer.removeRoofById(roofId);
        }
    
        /** ** ** ** ** ** **
         *
         * Area functions
         *
         ** ** ** ** ** ** **/
    
        /**
         * Draws Area in renderer
         * @param  {EZModelArea} area - area
         */
        drawArea(area) {
            this._renderer.drawArea(area);
        }
    
        /**
         * Draws Area in renderer by ID
         * @param  {string} areaId - ID
         */
        drawAreaById(areaId) {
            this._renderer.drawAreaById(areaId);
        }
    
        /**
         * Updates Area in renderer
         * @param  {EZModelArea} area - area
         */
        updateArea(area) {
            this._renderer.updateArea(area);
        }
    
        /**
         * Updates Area in renderer by ID
         * @param  {string} areaId - ID
         */
        updateAreaById(areaId) {
            this._renderer.updateAreaById(areaId);
        }
    
        /**
         * Removes area of renderer
         * @param  {EZModelArea} area - area
         */
        removeArea(area) {
            this._renderer.removeArea(area);
        }
    
        /**
         * Removes area of renderer by ID
         * @param  {string} areaId - ID
         */
        removeAreaById(areaId) {
            this._renderer.removeAreaById(areaId);
        }
    
        /** ** ** ** ** ** **
         *
         * Subarea functions
         *
         ** ** ** ** ** ** **/
    
        /**
         * Draws Subarea in renderer
         * @param  {EZModelSubarea} subarea - subarea
         */
        drawSubarea(subarea) {
            this._renderer.drawSubarea(subarea);
        }
    
        /**
         * Draws Subarea in renderer by ID
         * @param  {string} subareaId - ID
         */
        drawSubareaById(subareaId) {
            this._renderer.drawSubareaById(subareaId);
        }
    
        /**
         * Updates Subarea in renderer
         * @param  {EZModelSubarea} subarea - subarea
         */
        updateSubarea(subarea) {
            this._renderer.updateSubarea(subarea);
        }
    
        /**
         * Updates Subarea in renderer by ID
         * @param  {string} subareaId - ID
         */
        updateSubareaById(subareaId) {
            this._renderer.updateSubareaById(subareaId);
        }
    
        /**
         * Removes subarea of renderer
         * @param  {EZModelSubarea} subarea - subarea
         */
        removeSubarea(subarea) {
            this._renderer.removeSubarea(subarea);
        }
    
        /**
         * Removes subarea of renderer by ID
         * @param  {string} subareaId - ID
         */
        removeSubareaById(subareaId) {
            this._renderer.removeSubareaById(subareaId);
        }
    
        /** ** ** ** ** ** **
         *
         * System functions
         *
         ** ** ** ** ** ** **/
    
        /**
         * Draws System in renderer
         * @param  {EZModelSystem} system - system
         */
        drawSystem(system) {
            this._renderer.drawSystem(system);
        }
    
        /**
         * Draws System in renderer by ID
         * @param  {string} systemId - ID
         */
        drawSystemById(systemId) {
            this._renderer.drawSystemById(systemId);
        }
    
        /**
         * Updates System in renderer
         * @param  {EZModelSystem} system - system
         */
        updateSystem(system) {
            this._renderer.updateSystem(system);
        }
    
        /**
         * Updates System in renderer by ID
         * @param  {string} systemId - ID
         */
        updateSystemById(systemId) {
            this._renderer.updateSystemById(systemId);
        }
    
        /**
         * Removes system of renderer
         * @param  {EZModelSystem} system - system
         */
        removeSystem(system) {
            this._renderer.removeSystem(system);
        }
    
        /**
         * Removes system of renderer by ID
         * @param  {string} systemId - ID
         */
        removeSystemById(systemId) {
            this._renderer.removeSystemById(systemId);
        }
    
        /** ** ** ** ** ** **
         *
         * Keepout functions
         *
         ** ** ** ** ** ** **/
    
        /**
         * Draws Keepout in renderer
         * @param  {EZModelKeepout} keepout - keepout
         */
        drawKeepout(keepout) {
            this._renderer.drawKeepout(keepout);
        }
    
        /**
         * Draws Keepout in renderer by ID
         * @param  {string} keepoutId - ID
         */
        drawKeepoutById(keepoutId) {
            this._renderer.drawKeepoutById(keepoutId);
        }
    
        /**
         * Updates Keepout in renderer
         * @param  {EZModelKeepout} keepout - keepout
         */
        updateKeepout(keepout) {
            this._renderer.updateKeepout(keepout);
        }
    
        /**
         * Updates Keepout in renderer by ID
         * @param  {string} keepoutId - ID
         */
        updateKeepoutById(keepoutId) {
            this._renderer.updateKeepoutById(keepoutId);
        }
    
        /**
         * Removes keepout of renderer
         * @param  {EZModelKeepout} keepout - keepout
         */
        removeKeepout(keepout) {
            this._renderer.removeKeepout(keepout);
        }
    
        /**
         * Removes keepout of renderer by ID
         * @param  {string} keepoutId - ID
         */
        removeKeepoutById(keepoutId) {
            this._renderer.removeKeepoutById(keepoutId);
        }
    }
    

    this.layoutInstance = layoutInstance;

    this.EZGuiManager = EZGuiManager;
    this.EZsvgContainer = EZsvgContainer;
    this.EZPlayer = EZPlayer;

    this.lockedInterface = false;

    this.svgContainersList = [];
    this.EZsvgContainerTimeout = false;
    this.svgElements = {};

    ez3dScene = scene;

    // this.mode = ez3dScene.layoutRules.scenePreferences.viewportMode || 3;
    this.resizeListener();
}

/**
 * main listeners for the EZViewport Class
 *
 *  * activeUpdated           => update the GUI and run updateContainers()
 *  * updateRenderer          => update the whole set of containers
 *  * onUpdateRender      => update the sceneGroup inside each container
 *
 */
EZViewport.prototype.listeners = function() {
    var self = this;
    // updateRender Trigger
    ee.addOnceListener('updateRender', function(attr) {
        ee.addOnceListener('modeReady', function() {
            ee.addListener('updateRender', function(attr2) {
                self.onUpdateRender(attr2);
            });
            console.log('updateRender (first)', attr);
            ee.emitEvent('updateRender', [{
                draw2d: true,
                draw3d: true,
                updatePanels: true,
                fitRange: true
            }]);
        });
    });

    ee.addListener('saveWaitingFinishOperation', function(attr, val) {
        // TODO: check if 'attr' or 'val' are ever distinct from undefined
        if (attr !== undefined || val !== undefined) {
            console.warn('saveWaitingFinishOperation', attr, val);
        } else {
            console.log('saveWaitingFinishOperation', attr, val);
        }
        self.ez3dGuiManager.notificationManager(['saveWaitingFinishOperation', {style: 'warning', event: 'close-saving-notification', spiner: false}]);
    });

    ee.addListener('lockInterface', function() {
        self.lockedInterface = true;
        ee.emitEvent('sendLockInterface', [self.lockedInterface]);
    });

    ee.addListener('unlockInterface', function() {
        self.lockedInterface = false;
        ee.emitEvent('sendLockInterface', [self.lockedInterface]);
    });

    ee.addListener('updateViewportMode', function() {
        self.resetMode();
    });

    ee.addListener('Mapper.AllTilesLoaded', function() {
        // console.log('AllTilesLoaded');
    });

    ee.addListener('changePlayerLatLng', function(val) {
        if (ez3dViewport.ez3dPlayer) {
            ez3dViewport.ez3dPlayer.changeLatLng(val);
        }
    });
};

EZViewport.prototype.resetMode = function() {
    var self = this;
    self.removeAllContainers();

    ee.addOnceListener('modeReady', function() {
        ee.emitEvent('updateRender',
            [{draw2d: true, draw3d: true, updatePanels: true, fitRange: true}]);
        // self.ez3dGuiManager.toggleViewportRight(false);

        // // remove widget export
        if (self.ez3dGuiManager.widgets.indexOf('ez3d-widget-export') !== -1 && mode !== 8) {
            self.ez3dGuiManager.removeWidget('ez3d-widget-export');
        }
    });

    var mode = ez3dScene.layoutRules.scenePreferences.viewportMode;

    if (ez3dScene.projectType === 'simple') {
        // simple mode
        mode = 6;
    }

    switch (mode) {
        case 'invisible':
        case 0:
            // hide interface
            self.setMode0();
            break;
        case 1:
            // showcase mode
            self.setMode1();
            break;
        case 2:
            // scene and open street maps
            self.setMode2();
            break;
        case 3:
            // hide interface
            // self.setMode3();
            break;
        case 4:
            // scene and streetview
            self.setMode4();
            break;
        case 5:
            // scene and json editor
            self.setMode5();
            break;
        case 6:
            // simple mode
            self.setMode6();
            break;
        case 7:
            // scene and documentation
            self.setMode7();
            break;
        case 8:
            // scene and autocad
            self.setMode8();
            break;
        case 9:
            // simple mode invisible
            self.setMode9();
            break;
        case 10:
            // SLD
            self.setMode10();
            break;
        case 11:
            // scene read only
            self.setMode11();
            break;
        default:
            break;
    }
};

/**
 * method to run once the model is loaded inside ez3dScene
 * <br><br>
 *
 * this method instantiate each {@link EZsvgContainer}
 * acording to the drawing mode selected
 * <br><br>
 *
 * @param  {EZModelScene} ez3dScene - the main scene model
 *
 * @name loadProject
 */
EZViewport.prototype.loadProject = function(ez3dScene) {
    this.removeAllContainers();
    ez3dScene.customDateTime();

    const self = this;
    ee.addOnceListener('modeReady', function() {
        if (self.svgElements.svgProject) {
            self.svgElements.svgProject.fitRangeToContainer(
                self.svgElements.svgProject.projectRange, 0);
        }
        d3.select('#ez3d-loading-spinner-fullscreen')
            .classed('hide-spinner', true);
        ee.emitEvent('viewportReady');
    });

    // mode 0 - invisible interface
    // mode 1 - showcase mode
    // mode 2 - scene and open street maps
    // mode 3 - hide interface - DEPRECATED
    // mode 4 - scene and streetview
    // mode 5 - scene and json editor
    // mode 6 - simple mode
    // mode 7 - scene and documentation
    // mode 8 - autocad
    // mode 9 -simple mode invisible
    // mode 10 -SLD
    // mode 11 -read only

    let mode = ez3dScene.layoutRules.scenePreferences.viewportMode;

    const invisible = (mode === 0 || mode === 3 || mode === 9);
    if (!invisible) {
        this.ez3dGuiManager = new this.EZGuiManager(this);
    }
    if (ez3dScene.projectType === 'simple' && !invisible) {
        mode = 6; // simple mode
    }

    switch (mode) {
        case 'invisible':
        case 0:
            // hide interface
            this.setMode0();
            break;
        case 1:
            // showcase mode
            this.setMode1();
            break;
        case 2:
            // scene and open street maps
            this.setMode2();
            break;
        case 3:
            // hide interface
            // self.setMode3();
            break;
        case 4:
            // scene and streetview
            this.setMode4();
            break;
        case 5:
            // scene and json editor
            this.setMode5();
            break;
        case 6:
            // simple mode
            this.setMode6();
            break;
        case 7:
            // scene and documentation
            this.setMode7();
            break;
        case 8:
            // scene and autocad
            this.setMode8();
            break;
        case 9:
            // simple mode invisible
            this.setMode9();
            break;
        case 10:
            // SLD
            this.setMode10();
            break;
        case 11:
            // scene read only
            this.setMode11();
            break;
        default:
            break;
    }

    if ((ez3dScene.layoutRules.scenePreferences.enablePlayer === true && mode !== 6) && !invisible) {
        this.createPlayer();
    } else {
        this.ez3dPlayer = null;
    }

    this.listeners();
};

EZViewport.prototype.createPlayer = function() {
    const loadScript = (url) => {
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = false;
        script.onload = function() {
            // console.log('loaded', url);
        };
        script.src = url;
        document.getElementsByTagName('head')[0].appendChild(script);
    };

    if (layoutRules.scenePreferences.googleApiKey && !window.google) {
        loadScript(`https://maps.googleapis.com/maps/api/js?key=${layoutRules.scenePreferences.googleApiKey}&libraries=places`);
    }

    if (layoutRules.scenePreferences.bingApiKey) {
        loadScript(`https://www.bing.com/api/maps/mapcontrol?key=${layoutRules.scenePreferences.bingApiKey}`);
    }

    this.ez3dPlayer = new this.EZPlayer(
        document.getElementById('playerContainer'),
        21,
        1,
        {
            lat: ez3dScene.projectCenter.lat,
            lng: ez3dScene.projectCenter.lng
        },
        {
            hasMapper: ez3dScene.layoutRules.scenePreferences.activeMapper,
            googleApiKey: layoutRules.scenePreferences.googleApiKey,
            bingApiKey: layoutRules.scenePreferences.bingApiKey,
            zoomRatio: layoutRules.scenePreferences.zoomRatio,
            translationRatio: layoutRules.scenePreferences.translationRatio,
            rotationRatio: layoutRules.scenePreferences.rotationRatio
        }
    );
    ee.emitEvent('playerReady');
};

EZViewport.prototype.destroyPlayer = function() {
    this.ez3dPlayer.kill();
    this.ez3dPlayer = null;
};


/**
 * onUpdateRender function to redraw all containers
 * @param  {object} args - {draw2d, draw3d, updatePanels, fitRange}
 * @param {boolean} args.draw2d - redraw 2D layer
 * @param {object} args.draw3d - redraw 3D layer, it can be: true(all 3D objects),
 * string (ID of object to be updated) or object (object to be updated)
 * @param {boolean} args.updatePanels - redraw panels
 * @param {boolean} args.fitRange - fitRange
 */
EZViewport.prototype.onUpdateRender = function(args) {
    // console.log('onUpdateRender args: ' + args);
    args = (args) ? args : {};
    const {draw2d = false, draw3d = false,
        updatePanels = false, fitRange = false} = args;
    var self = this;

    // const draw3dToPrint = (typeof (draw3d) === 'object') ? draw3d.id : draw3d;
    // console.warn('onUpdateRender:\n  draw2d = ' + draw2d + '\n  draw3d = ' + draw3dToPrint +
    //     '\n  updatePanels = ' + updatePanels + '\n  fitRange = ' + fitRange);

    // update svgContainers
    self.svgContainersList.forEach(function(container) {
        if (draw2d && draw3d) {
            container.redraw();
        }
        if (fitRange) {
            container.launchFitRange();
        }
        if (draw2d && container.draw) {
            container.draw();
        }
    });

    // update editors
    if (draw2d) {
        if (ez3dScene.context === 'pathEditor' &&
            self.snapToGuides === true &&
            (
                (ez3dScene.tempPath && ez3dScene.tempPath.vertices.length > 1) ||
                ez3dScene.tempPath && ez3dScene.layoutRules.scenePreferences.snapToVertices === true
            )) {
            self.ez3dGuiManager.notificationManager(['calculatingGuidelines',
                {style: 'warning', spiner: true, event: 'voronoi_Finished'}]);
            self.svgElements.svgProject.drawVoronoiDiagram();
        }
    }
    // update buildingIndex
    if (updatePanels && ez3dScene.layoutRules.scenePreferences.viewportMode !== 3 && ez3dScene.projectType !== 'simple') {
        self.ez3dGuiManager.calculateBuildingIndexSize();
        self.ez3dGuiManager.generateBuildingIndex(ez3dScene);
    }

    // update player
    // @todo choose between draw 3d w/o camera movement
    if (draw3d &&
        ez3dScene.layoutRules.scenePreferences.enablePlayer === true &&
        ez3dScene.layoutRules.scenePreferences.viewportMode !== 6 &&
        self.ez3dPlayer !== null &&
        ez3dScene.layoutRules.scenePreferences.activeRenderer === true
    ) {
        if (typeof (draw3d) === 'object' && draw3d.constructor.name.includes('EZModel')) {
            this.update3DObject(draw3d);
        } else if (typeof (draw3d) === 'string') {
            this.update3DObjectById(draw3d);
        } else if (draw3d === true) {
            self.ez3dPlayer.draw(ez3dScene);
        }
        // self.ez3dPlayer.activateRenderer(ez3dScene.layoutRules.scenePreferences.activeRenderer);
        // self.ez3dPlayer.activateMapper(ez3dScene.layoutRules.scenePreferences.activeMapper);
    }
    // update panels
    if (updatePanels) {
        ee.emitEvent('updatePanels');
    }
};


/**
 * method to delete a contanier and remove from the container list
 * @param  {string} name - the name of the container
 */
EZViewport.prototype.removeContainer = function(name) {
    var self = this;
    self.svgContainersList.forEach(function(container, i) {
        if (container.name === name) {
            self.svgContainersList.splice(i, 1);
        }
    });
    self.svgElements[name].destroy();
    self.svgElements[name].ez3dViewport = undefined;

    delete self.svgElements[name];
};

/**
 * method to remove all containers
 */
EZViewport.prototype.removeAllContainers = function() {
    var self = this;
    self.svgContainersList.forEach(function(container) {
        self.svgElements[container.name].destroy();
        self.svgElements[container.name].ez3dViewport = undefined;

        delete self.svgElements[container.name];
    });

    d3.select('#ez3d-viewport-right-content').selectAll('*').remove();

    self.svgContainersList = [];
};

EZViewport.prototype.resizeWindow = function() {
    // TODO remove this timeout
    clearTimeout(self.EZsvgContainerTimeout);
    self.EZsvgContainerTimeout = setTimeout(function() {
        ee.emitEvent('updateRender',
            [{draw2d: true, draw3d: true, updatePanels: false, fitRange: true}]);
    }, 250);
};

/**
 * method to run any time the window is resized
 * to redraw all the svgContainers
 */
EZViewport.prototype.resizeListener = function() {
    var self = this;
    window.addEventListener('resize', self.resizeWindow);
};

EZViewport.prototype.svgProjectFactory = function(container) {
    this.svgElements.svgProject = new this.EZsvgContainer(this, 'svgProject', container, this.svgProjectOptions);
    ee.emitEvent('show_svgProject_spinner');
    this.svgElements.svgProject.hideAreaContainer = true;

    return this.svgElements.svgProject;
};

EZViewport.prototype.svgMapFactory = function(container) {
    this.svgElements.svgMap = new this.EZsvgContainer(this, 'svgMap', container, this.svgMapOptions);

    return this.svgElements.svgMap;
};

EZViewport.prototype.svgAutoCADFactory = function(container) {
    this.svgElements.svgAutoCAD = new this.EZsvgContainer(this, 'svgAutoCAD', container, this.svgAutoCADOptions);

    return this.svgElements.svgAutoCAD;
};

EZViewport.prototype.svgSLDFactory = function(container) {
    this.svgElements.svgSLD = new this.EZsvgContainer(this, 'svgSLD', container, this.svgSLDOptions);

    return this.svgElements.svgSLD;
};

EZViewport.prototype.svgSimpleFactory = function(container) {
    this.svgElements.svgSimple = new this.EZsvgContainer(this, 'svgSimple', container, this.svgSimpleOptions);
    ee.emitEvent('show_svgSimple_spinner');

    return this.svgElements.svgSimple;
};

/**
 * method to draw the mode 0 "Hide mode"
 */
EZViewport.prototype.setMode0 = function() {
    ee.emitEvent('modeReady');
};

EZViewport.prototype.setMode1 = function() {
    var self = this;

    self.ez3dGuiManager.hideElementsByShowCase();
    ee.addOnceListener('playerReady', function() {
        ez3dViewport.ez3dPlayer.setShowcase();
        ez3dViewport.ez3dPlayer.draw(ez3dScene);
        setTimeout(function () {
            ee.emitEvent('modeReady');
        }, 0);
    });
};

/**
 * method to draw the mode 2
 */
EZViewport.prototype.setMode2 = function() {
    this.svgProjectFactory('ez3d-canvas');
    this.svgMapFactory('ez3d-viewport-right-content');
    ee.addOnceListener('containerReady', function() {
        ee.emitEvent('modeReady');
    });
};

/**
 * method to draw the mode 3 "Hide mode"
 */
EZViewport.prototype.setMode3 = function() {
    ee.emitEvent('modeReady');
};

/**
 * method to draw the mode 4
 */
EZViewport.prototype.setMode4 = function() {
    this.svgProjectFactory('ez3d-canvas');

    d3.select('#ez3d-viewport-right-content')
        .append('div')
        .attr('id', 'street-view');

    if (window.google !== undefined) {
        this.setStreetView(ez3dScene.projectCenter.lat, ez3dScene.projectCenter.lng);
    }

    ee.addOnceListener('containerReady', function() {
        ee.emitEvent('modeReady');
    });
};

EZViewport.prototype.setStreetView = function(lat, lng) {
    this.panorama = new google.maps.StreetViewPanorama(
        document.getElementById('street-view'),
        {
            position: {lat: lat, lng: lng},
            pov: {heading: 165, pitch: 0},
            zoom: 1
        }
    );
};

/**
 * method to draw the mode 5
 */
EZViewport.prototype.setMode5 = function() {
    var self = this;
    self.svgProjectFactory('ez3d-canvas');

    var jsonEditorContainer = d3.select('#ez3d-viewport-right-content')
        .append('div')
        .attr('id', 'jsoneditor')
        .style('background-color', 'white');

    var jsonEditorOptions = [
        'defaultButtons',
        'defaultColors',
        'defaultLanguages',
        'defaultModelValues',
        'defaultModules',
        'defaultProviders',
        // 'modulesListByTypeOfSystem'
        'scenePreferences'
    ];

    var elements = {
        buttonsPanel: {
            type: 'buttonsPanelWidget',
            elements: {
                select: {
                    type: 'select',
                    id: 'jsonEditorFileSelector',
                    value: jsonEditorOptions[1],
                    optionsLabels: jsonEditorOptions,
                },
                reload: {
                    type: 'button',
                    content: 'reload',
                    classed: 'ez3d-square-button ez3d-square-button-green',
                    functionOnClick: ['jsonEditorFunction', 'reload']
                },
                save: {
                    type: 'button',
                    content: 'save',
                    classed: 'ez3d-square-button ez3d-square-button-green',
                    functionOnClick: ['jsonEditorFunction', 'save']
                }
            }
        }
    };
    this.ez3dGuiManager.populateBlocks(elements, jsonEditorContainer);

    // Set jsonEditorContainer height
    var btnContainerHeight = d3.select('#jsoneditor .ez3d-panel-buttons').node().offsetHeight;
    jsonEditorContainer.style('height', 'calc(100vh - ' + btnContainerHeight + 'px)');

    // Select on change function and remove circle icon
    var selectInput = d3.select('#jsonEditorFileSelector');
    selectInput
        .on('change', function() {
            var json = ez3dScene.layoutRules[this.value];
            self.setInJsonEditor(json);
        });
    d3.select(selectInput.node().parentNode).select('.ez3d-circle-block').remove();

    // Create the editor
    var editorOptions = {};
    this.jsonEditor = new JSONEditor(jsonEditorContainer.node(), editorOptions);

    // Set json
    this.jsonEditor.set(ez3dScene.layoutRules.defaultColors);

    ee.addOnceListener('containerReady', function() {
        ee.emitEvent('modeReady');
    });
};

/**
 * method to draw the mode 6 - simple mode
 */
EZViewport.prototype.setMode6 = function() {
    var options = {
        grid: true,
        zoom: true,
        coord: 'area'
    };

    this.svgSimpleFactory('ez3d-canvas');

    d3.select('#playerContainer')
        .style('display', 'none');

    ee.addOnceListener('containerReady', function() {
        ee.emitEvent('modeReady');
    });
};

/**
 * method to draw the mode 7
 */
EZViewport.prototype.setMode7 = function() {
    this.svgProjectFactory('ez3d-canvas');

    d3.select('#ez3d-viewport-right-content')
        .append('div')
        .attr('id', 'ez3d-documentation')
        .on('mouseover', function() {
            d3.select('#ez3d-documentation iframe').style('pointer-events', 'auto');
        })
        .on('mouseout', function() {
            d3.select('#ez3d-documentation iframe').style('pointer-events', 'none');
        })
        .append('iframe')
        .attr('src', 'https://doc-layout.ezzing.com/README-userDoc.html')
        .style('width', '100%');

    var elements = {
        buttonsPanel: {
            type: 'buttonsPanelWidget',
            elements: {
                goToIndex: {
                    type: 'button',
                    id: 'ez3d-button-goToIndex',
                    classed: 'ez3d-square-button ez3d-square-button-green',
                    content: 'Go to index',
                    functionOnClick: ['viewportDocButtons', 'index']
                },
                // changeDocType: {
                //     type: 'button',
                //     id: 'ez3d-button-changeDocType',
                //     classed: 'ez3d-square-button ez3d-square-button-green',
                //     content: 'Integrators doc',
                //     functionOnClick: ['viewportDocButtons', 'docType']
                // }
            }
        }
    };
    this.ez3dGuiManager.populateBlocks(elements, d3.select('#ez3d-documentation'));

    // Set iframe height
    var btnContainerHeight = d3.select('#ez3d-documentation .ez3d-panel-buttons').node().offsetHeight;
    d3.select('#ez3d-documentation iframe').style('height', 'calc(100vh - ' + btnContainerHeight + 'px)');

    ee.addOnceListener('containerReady', function() {
        ee.emitEvent('modeReady');
    });
};

/**
 * method to draw the mode 8
 */
EZViewport.prototype.setMode8 = function() {
    this.svgProjectFactory('ez3d-canvas');
    this.svgAutoCADFactory('ez3d-viewport-right-content');

    ee.addOnceListener('containerReady', function() {
        ee.emitEvent('modeReady');
    });
};

/**
 * method to draw the mode 9
 */
EZViewport.prototype.setMode9 = function() {
    ee.emitEvent('modeReady');
};

/**
 * method to draw the mode 10
 */
EZViewport.prototype.setMode10 = function() {
    this.svgProjectFactory('ez3d-canvas');
    this.svgSLDFactory('ez3d-viewport-right-content');

    ee.addOnceListener('containerReady', function() {
        ee.emitEvent('modeReady');
    });
};

/**
 * method to draw the mode 11
 */
EZViewport.prototype.setMode11 = function() {
    this.svgProjectFactory('ez3d-canvas');

    d3.select('#ez3d-viewport-right-content')
        .append('div')
        .attr('id', 'street-view');

    if (window.google !== undefined) {
        this.setStreetView(ez3dScene.projectCenter.lat, ez3dScene.projectCenter.lng);
    }

    ee.addOnceListener('containerReady', function() {
        ee.emitEvent('modeReady');
    });
};

EZViewport.prototype.setInJsonEditor = function(json) {
    this.jsonEditor.set(json);
};

EZViewport.prototype.getInJsonEditor = function() {
    return this.jsonEditor.get();
};

/**
 * instantiate a new svg container with area coord system
 * to another container
 *
 * <br><br>
 * this will split the container horizontally and then create a new svgContainer in the lower part
 *
 * @param {d3Container} container - the container to be splitted
 */
EZViewport.prototype.addAreaContainer = function(container) {
    // console.log('adding AREA container to ' + container.name);
    this.svgElements.svgArea = new this.EZsvgContainer(this, 'svgArea', container.parentId, this.svgAreaOptions);
    this.svgElements.svgArea.splitView();

    ee.emitEvent('show_svgArea_spinner');
};

/**
 * method to redraw the container
 *
 *  this method takes into account the active objects and manages
 *  the split and collapse methods to draw the area canvas if needed.
 *
 *  It also manages where to draw an old method or a new one
 *
 * @param  {d3Container} container - the container to be updated
 */
EZViewport.prototype.updateContainer = function(container) {
    var self = this;
    var svgElement = self.svgElements[container.name];

    if (svgElement !== undefined) {
        if (ez3dScene.activeSubarea === false && svgElement.name === 'svgArea') {
            self.removeContainer(container.name);
        }
        if (svgElement.name === 'svgProject') {
            if (ez3dScene.active.constructor === EZModelSubarea && svgElement.split === false) {
                if (ez3dViewport.svgElements.svgProject.hideAreaContainer === false) {
                    svgElement.splitView();
                }
            }
            if (ez3dScene.active.constructor !== EZModelSubarea && svgElement.split === true) {
                svgElement.collapseView();
            }
        }
        svgElement.redraw(true);
    }
};

/**
 * method to update all the containers
 */
EZViewport.prototype.updateContainers = function() {
    var self = this;
    Object.keys(self.svgElements).forEach(function(containerName) {
        self.updateContainer(self.svgElements[containerName]);
    });
};

/**
 * Method to show modified loader when browser is other than Google Chrome
 */
EZViewport.prototype.showBrowserAlert = function() {
    var self = this;
    ez3dScene.layoutRules.scenePreferences.enableApi = false;
    self.ez3dGuiManager = new this.EZGuiManager(self);

    var loaderDiv = d3.select('#ez3d-loading-spinner-fullscreen');

    loaderDiv
        .classed('ez3d-flex-center-center-column', true)
        .style('background-image', 'none');

    var elements = {
        title: {
            type: 'title',
            htmlElement: 'h2',
            title: 'browserAlertTitle'
        },
        message1: {
            type: 'helper',
            classed: 'helperCenter',
            content: 'browserAlertMessage1'
        },
        message2: {
            type: 'helper',
            classed: 'helperCenter',
            content: 'browserAlertMessage2'
        },
        chromeLogo: {
            type: 'helper',
            classed: 'google-chrome-logo'
        },
        downloadLink: {
            type: 'helper',
            classed: 'helperCenter',
            content: 'browserAlertMessage3'
        },
        link: {
            type: 'link',
            classed: 'helperCenter',
            url: 'https://www.google.com/chrome/'
        },
    };
    self.ez3dGuiManager.populateBlocks(elements, d3.select('#ez3d-loading-spinner-fullscreen'));
    loaderDiv.selectAll('*').style('margin', '5px');

    console.warn(self.ez3dGuiManager.i18n('browserAlertMessage1') + '\n' +
        self.ez3dGuiManager.i18n('browserAlertMessage2') + '\n' +
        self.ez3dGuiManager.i18n('browserAlertMessage3') + ' https://www.google.com/chrome/');
};

/**
 * Method to show modified loader when defaultModules is empty
 */
EZViewport.prototype.showEmptyModulesAlert = function() {
    ez3dScene.layoutRules.scenePreferences.enableApi = false;
    this.ez3dGuiManager = new this.EZGuiManager(this);

    const loaderDiv = d3.select('#ez3d-loading-spinner-fullscreen');

    loaderDiv
        .classed('ez3d-flex-center-center-column', true)
        .style('background-image', 'none');

    const elements = {
        title: {
            type: 'title',
            htmlElement: 'h2',
            title: 'defaultModulesEmptyAlertTitle',
        },
        message: {
            type: 'helper',
            classed: 'helperCenter',
            content: 'defaultModulesEmptyAlertMessage',
        },
    };
    this.ez3dGuiManager.populateBlocks(elements, d3.select('#ez3d-loading-spinner-fullscreen'));
    loaderDiv.selectAll('*')
        .style('margin', '5px');

    console.warn(this.ez3dGuiManager.i18n('defaultModulesEmptyAlertTitle') + '\n' +
        this.ez3dGuiManager.i18n('defaultModulesEmptyAlertMessage'));
};

/**
 * Method to show modified loader when modules are not valid
 * @param {array} invalidModuleIds Ids of the invalid modules to inform the user
 */
EZViewport.prototype.showInvalidModulesAlert = function(invalidModuleIds) {
    ez3dScene.layoutRules.scenePreferences.enableApi = false;
    this.ez3dGuiManager = new this.EZGuiManager(this);

    const loaderDiv = d3.select('#ez3d-loading-spinner-fullscreen');

    loaderDiv
        .classed('ez3d-flex-center-center-column', true)
        .style('background-image', 'none');

    const elements = {
        title: {
            type: 'title',
            htmlElement: 'h2',
            title: 'invalidModulesTitle',
        },
        message1: {
            type: 'helper',
            classed: 'helperCenter',
            content: 'invalidModulesMessage1',
        },
        message2: {
            type: 'helper',
            classed: 'helperCenter',
            content: 'invalidModulesMessage2',
        },
        message3: {
            type: 'helper',
            classed: 'helperCenter',
            content: 'invalidModulesMessage3',
        },
        invalidModuleListPreamble: {
            type: 'helper',
            classed: 'helperCenter',
            content: 'invalidModulesListDescription',
        },
        invalidModulesList: {
            type: 'helper',
            classed: 'helperCenter',
            content: invalidModuleIds.join('<br>'),
        },
    };
    this.ez3dGuiManager.populateBlocks(elements, d3.select('#ez3d-loading-spinner-fullscreen'));
    loaderDiv.selectAll('*')
        .style('margin', '5px');

    console.warn(this.ez3dGuiManager.i18n('invalidModulesTitle') + '\n' +
        this.ez3dGuiManager.i18n('invalidModulesMessage1') + '\n' +
        this.ez3dGuiManager.i18n('invalidModulesMessage2') + '\n' +
        this.ez3dGuiManager.i18n('invalidModulesMessage3') + '\n' +
        `${this.ez3dGuiManager.i18n('invalidModulesListDescription')} ${invalidModuleIds.join(', ')}`);
};


/**
 * Updates 3D representation of a model
 * @param {EZModelObject} model model to be updated
 */
EZViewport.prototype.update3DObject = function(model) {
    if (model instanceof EZModelBuilding) {
        this.ez3dPlayer.updateBuilding(model);
    } else if (model instanceof EZModelRoof) {
        this.ez3dPlayer.updateRoof(model);
    } else if (model instanceof EZModelArea) {
        this.ez3dPlayer.updateRoof(model.parent);
    } else if (model instanceof EZModelSubarea) {
        this.ez3dPlayer.updateSubarea(model);
    } else if (model instanceof EZModelSystem) {
        this.ez3dPlayer.updateSystem(model);
    } else if (model instanceof EZModelTree) {
        this.ez3dPlayer.updateTree(model);
    } else if (model instanceof EZModelKeepout) {
        this.ez3dPlayer.updateKeepout(model);
    }
};

/**
 * Updates 3D representation of a model using ID
 * @param {string} modelId ID of model to be updated
 */
EZViewport.prototype.update3DObjectById = function(modelId) {
    const model = ez3dScene.findById(modelId);
    this.update3DObject(model);
};
